/* =====================================================================
   Browns Digital Studio — application (v4)
   Light bd-ios shell · Lucide icons · multi-page builder with a
   token-driven design system · hosted publishing (staging → promote
   → {slug}.brownsdigital.app) · site health auditing · developer
   override for the platform owner. No customer code export.
===================================================================== */
"use strict";

function uid(){ return Math.random().toString(36).slice(2, 9); }
function esc(s){ return String(s ?? "").replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;"); }
const $  = s => document.querySelector(s);
const $$ = s => [...document.querySelectorAll(s)];
const $hide = (s, on) => { const el = $(s); if (el) el.hidden = !!on; };
const $text = (s, v) => { const el = $(s); if (el) el.textContent = v; };
const VIEWS = ["auth","gate","onboard","app","pin"];
function show(v){
  VIEWS.forEach(x => {
    const el = $("#view-" + x);
    if (!el) return;
    const on = x === v;
    el.classList.toggle("active", on);
    el.hidden = !on;
  });
  const bootEl = $("#studio-boot");
  if (bootEl) bootEl.hidden = true;
  window.scrollTo(0, 0);
}
function showStudioBoot(mode){
  const el = $("#studio-boot");
  if (!el) return;
  el.hidden = false;
  if (mode === "offline") {
    el.innerHTML = `<div class="studio-boot-card"><p class="studio-boot-msg">Can't reach Browns Digital</p><button type="button" class="studio-boot-retry" id="studio-boot-retry">Retry</button></div>`;
    const btn = $("#studio-boot-retry");
    if (btn) btn.onclick = () => {
      btn.disabled = true;
      showStudioBoot();
      boot().catch(onStudioBootFail);
    };
    return;
  }
  el.innerHTML = `<div class="studio-boot-card"><span class="studio-boot-spin" aria-hidden="true"></span><p class="studio-boot-msg">Loading Studio</p></div>`;
}
function onStudioBootFail(e){
  console.error(e);
  show("auth");
  authShow("auth-signin");
  toast("Could not load Studio - try refreshing.");
}
function icons(){ if (!window.lucide) return; lucide.createIcons();
  /* decorative icons are silent for screen readers */
  $$("svg.lucide:not([aria-hidden]):not([aria-label])").forEach(s => s.setAttribute("aria-hidden","true")); }
function toast(msg, { kind = "default", ms = 2800 } = {}){
  const t = $("#toast");
  if (!t) return;
  const glyph = t.querySelector(".toast-glyph");
  const text = t.querySelector(".toast-text") || t;
  const marks = { success:"✓", error:"!", default:"" };
  t.className = "toast" + (kind !== "default" ? " toast--" + kind : "");
  if (glyph){ glyph.textContent = marks[kind] || ""; glyph.hidden = kind === "default"; }
  text.textContent = msg;
  t.hidden = false;
  t.classList.add("show");
  clearTimeout(t._h);
  t._h = setTimeout(() => { t.classList.remove("show"); setTimeout(() => { t.hidden = true; }, 320); }, ms);
}
/* Translate raw/technical errors into calm, human copy before they reach the UI.
   api.js already throws curated messages for known cases — this catches the rest
   (network drops, expired sessions, server hiccups) and never shows stack noise. */
function humanErr(e, fallback){
  if (typeof bdHumanErr === "function") return bdHumanErr(e, fallback);
  const raw = String((e && e.message) || e || "").trim();
  const low = raw.toLowerCase();
  if (/no account exists/i.test(raw)) return raw;
  if (/aal2 session is required/i.test(low))
    return "Two-factor authentication is required before you can set a new password. Enter your authenticator code below, then try again.";
  if (!raw) return fallback || "Something went wrong. Nothing has been lost — please try again.";
  if (/failed to fetch|networkerror|load failed|network request failed|err_internet|timeout|timed out|taking too long/.test(low))
    return "We can’t reach the server right now. Your work is safe on this device — check your connection and try again.";
  if (/jwt|token.*(expired|invalid)|session.*expired|not authenticated|401/.test(low))
    return "Your session has expired. Please sign in again to continue — nothing has been lost.";
  if (/rate limit|too many requests|429/.test(low))
    return "You’re moving faster than the server allows. Give it a minute, then try again.";
  if (/5\d\d|internal server|bad gateway|service unavailable/.test(low))
    return "We couldn’t complete that request right now. Nothing has been lost — please try again in a few moments.";
  if (/quota|storage.*full|exceeded/.test(low))
    return "Local storage is full on this device. Remove an unused asset or site, then try again.";
  if (/^\d{3}\b|[{}<>\\]|exception|undefined|null is not|cannot read|pgrst|postgrest|supabase|sqlstate/i.test(raw))
    return fallback || "Something unexpected happened. Nothing has been lost — please try again.";
  return raw;
}
/* skeleton rows shown while async content loads — never a frozen "Loading…" */
function skelRows(n = 3, { avatar = true } = {}){
  return `<div aria-hidden="true">` + Array.from({ length: n }, (_, i) =>
    `<div class="skel-row">${avatar ? '<span class="skel skel-avatar"></span>' : ""}
      <div class="skel-stack"><div class="skel skel-line w${[60,45,80][i % 3]}"></div><div class="skel skel-line w30"></div></div></div>`
  ).join("") + `</div>`;
}
function anchorPopover(menu, anchor, { above = false, align = "start", minW = 220, maxW = 380 } = {}){
  if (!menu || !anchor) return;
  menu.hidden = false;
  menu.style.position = "fixed";
  menu.style.visibility = "hidden";
  menu.style.left = "0";
  menu.style.top = "0";
  menu.style.right = "auto";
  menu.style.bottom = "auto";
  menu.style.minWidth = minW + "px";
  menu.style.maxWidth = Math.min(maxW, window.innerWidth - 24) + "px";
  menu.style.width = "max-content";
  const r = anchor.getBoundingClientRect();
  const mw = Math.max(minW, Math.min(menu.offsetWidth || minW, maxW, window.innerWidth - 24));
  const mh = menu.offsetHeight || 0;
  let left = align === "end" ? r.right - mw : r.left;
  left = Math.max(12, Math.min(left, window.innerWidth - mw - 12));
  let top = above ? r.top - mh - 8 : r.bottom + 8;
  if (!above && top + mh > window.innerHeight - 12 && r.top - mh - 8 > 12) top = r.top - mh - 8;
  top = Math.max(12, Math.min(top, window.innerHeight - mh - 12));
  menu.style.left = left + "px";
  menu.style.top = top + "px";
  menu.style.width = (align === "stretch" ? Math.max(minW, r.width) : mw) + "px";
  menu.style.visibility = "";
}
const fmtWhen = iso => { if (!iso) return "—"; const d = new Date(iso), diff = (Date.now()-d)/1e3;
  if (diff < 60) return "just now"; if (diff < 3600) return Math.floor(diff/60) + "m ago";
  if (diff < 86400) return Math.floor(diff/3600) + "h ago"; if (diff < 86400*7) return Math.floor(diff/86400) + "d ago";
  return d.toLocaleDateString(undefined,{day:"numeric",month:"short"}); };
/* forward-looking — for renewal/reset dates that sit in the future */
const fmtForward = iso => { if (!iso) return ""; const d = new Date(iso), diff = (d - Date.now())/1e3;
  if (diff <= 0) return "renews soon";
  const days = Math.ceil(diff/86400);
  if (days <= 1) return "renews tomorrow";
  if (days <= 30) return "renews in " + days + " days";
  return "renews " + d.toLocaleDateString(undefined,{day:"numeric",month:"short"}); };
function healthRingSVG(score, dia, stroke){
  dia = dia || 48; stroke = stroke || 4;
  const r = (dia - stroke) / 2, c = 2 * Math.PI * r;
  const col = score > 80 ? "var(--color-success)" : score >= 50 ? "var(--color-warning)" : "var(--color-destructive)";
  const off = c * (1 - Math.min(100, Math.max(0, score)) / 100);
  const anim = dia >= 72 ? ` class="health-ring-anim" style="--ring-circ:${c};--ring-off:${off}"` : "";
  const numSize = dia >= 100 ? 28 : dia >= 72 ? 20 : 13;
  return `<svg width="${dia}" height="${dia}" viewBox="0 0 ${dia} ${dia}" aria-hidden="true"${anim}><circle cx="${dia/2}" cy="${dia/2}" r="${r}" fill="none" stroke="color-mix(in srgb,${col} 14%,transparent)" stroke-width="${stroke}"/><circle class="health-ring-arc" cx="${dia/2}" cy="${dia/2}" r="${r}" fill="none" stroke="${col}" stroke-width="${stroke}" stroke-linecap="round" transform="rotate(-90 ${dia/2} ${dia/2})" stroke-dasharray="${c}" stroke-dashoffset="${off}"/><text x="${dia/2}" y="${dia/2}" text-anchor="middle" dominant-baseline="central" class="health-ring-num" style="font-size:${numSize}px;font-weight:700;letter-spacing:-.04em;fill:var(--ink)">${score}</text></svg>`;
}
function actIconClass(kind){
  if (kind === "published" || kind === "created" || kind === "saved" || kind === "restored") return "act-tint";
  if (kind === "staged") return "act-warn";
  if (kind === "domain") return "act-success";
  if (kind === "unpublished") return "act-muted";
  return "act-muted";
}
function parseSimpleMd(raw){
  const safeHref = h => {
    const u = String(h || "").trim();
    return /^(https?:|mailto:)/i.test(u) ? u : "#";
  };
  let s = esc(String(raw || ""));
  s = s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/\*(.+?)\*/g, "<em>$1</em>");
  s = s.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, t, h) =>
    `<a href="${esc(safeHref(h))}" target="_blank" rel="noopener noreferrer">${t}</a>`);
  s = s.replace(/^[-•]\s+(.+)$/gm, "<li>$1</li>");
  s = s.replace(/(<li>[\s\S]*<\/li>)+/g, m => `<ul>${m}</ul>`);
  return s.split(/\n{2,}/).map(p => p.startsWith("<") ? p : `<p>${p.replace(/\n/g, "<br>")}</p>`).join("");
}
const CHECKLIST_ITEMS = [
  { id:"created", label:"Site created" },
  { id:"page", label:"Add your first page" },
  { id:"content", label:"Edit content in builder" },
  { id:"domain", label:"Set a custom domain" },
  { id:"seo", label:"Configure SEO defaults" },
  { id:"stage", label:"Create a preview" },
  { id:"live", label:"Push live" }
];
function checklistKey(id){ return "bd_checklist_" + id; }
function checklistDone(p, id){
  if (id === "created") return true;
  if (id === "page") return (p.data.pages?.length || 0) > 1 || p.data.pages?.some(x => x.blocks?.length);
  if (id === "content") return siteHasContent(p);
  if (id === "domain") return !!p._domainVerified;
  if (id === "seo") return !!(p.data.seo?.title || p.data.seo?.description);
  if (id === "stage") return !!stagingOf(p.id);
  if (id === "live") return !!prodOf(p.id);
  return false;
}
function loadChecklistCollapsed(id){ return localStorage.getItem(checklistKey(id) + "_done") === "1"; }
function saveChecklistCollapsed(id){ localStorage.setItem(checklistKey(id) + "_done", "1"); }
function copyText(t){ navigator.clipboard?.writeText(t).then(() => toast("Copied")).catch(() => toast("Could not copy")); }
function dnsCopyField(label, value){
  const v = String(value ?? "").trim();
  return `<div class="dom-dns-pair">
    <span class="dom-dns-pair-label">${esc(label)}</span>
    <div class="dom-dns-pair-row">
      <code class="dom-dns-pair-val" title="${esc(v)}">${esc(v || "—")}</code>
      <button type="button" class="dom-icon-btn" data-copy-dns="${esc(v)}" ${v ? "" : "disabled"} aria-label="Copy ${esc(label)}">${lu("copy","lu14")}</button>
    </div>
  </div>`;
}
function dnsRecordCard(type, host, value, opts){
  const o = opts || {};
  const optional = !!o.optional;
  const label = o.label ? String(o.label) : "";
  return `<div class="dom-dns-card${optional ? " dom-dns-card--optional" : ""}">
    <div class="dom-dns-card-type"><span>${esc(type)}</span>${optional ? `<span class="dom-dns-optional">Optional</span>` : ""}${label ? `<span class="dom-dns-card-label">${esc(label)}</span>` : ""}</div>
    <div class="dom-dns-card-body">
      ${dnsCopyField("Host", host)}
      ${dnsCopyField("Value", value)}
    </div>
  </div>`;
}
function dnsRecordsFromApi(dns, token){
  const list = Array.isArray(dns?.records) && dns.records.length
    ? dns.records
    : null;
  if (list) {
    return list.map(r => dnsRecordCard(
      r.type || "CNAME",
      r.host || "",
      r.value || (r.type === "TXT" ? token : ""),
      { optional: r.required === false, label: r.label || "" }
    )).join("");
  }
  const cnameTarget = dns?.cname_target || "brownsdigi.com";
  const aHost = dns?.a_host || null;
  const aIp = dns?.a_ip || null;
  const cnameHost = dns?.cname_host || "www";
  const point = aIp && aHost
    ? dnsRecordCard("A", aHost, aIp)
    : dnsRecordCard("CNAME", cnameHost, cnameTarget);
  return dnsRecordCard("TXT", dns?.txt_host || "_bd-verify", token) + point;
}
function bindDnsCopy(root){
  (root || document).querySelectorAll("[data-copy-dns]").forEach(btn => {
    btn.onclick = () => {
      const t = btn.getAttribute("data-copy-dns") || "";
      navigator.clipboard?.writeText(t)
        .then(() => toast("Copied — paste at your registrar", { kind: "success" }))
        .catch(() => toast("Could not copy", { kind: "error" }));
    };
  });
}
function permTick(on){ return on ? `<span class="perm-y" aria-label="Yes">${lu("check","lu14")}</span>` : `<span class="perm-n" aria-label="No">${lu("minus","lu14")}</span>`; }
const REV_TRIGGER_LABELS = { SAVE:"SAVE", STAGED:"STAGED", RESTORED:"RESTORED" };
const DISCORD_EVENT_DEFS = [
  { id:"live", label:"Promoted to live", hint:"When this site goes public.", accent:"#57F287", icon:"rocket", env:"Production" },
  { id:"stage", label:"Published to staging", hint:"When a preview build is ready.", accent:"#5865F2", icon:"layers", env:"Staging" },
  { id:"offline", label:"Taken offline", hint:"When production is removed.", accent:"#ED4245", icon:"power", env:"Production" },
  { id:"domain", label:"Domain verified", hint:"When custom DNS verifies.", accent:"#FEE75C", icon:"globe", env:"Domain" }
];
const DISCORD_MSG_DEFAULTS = {
  live: { title: "Site promoted to live", description: "Your site is now live." },
  stage: { title: "Staging updated", description: "Your latest build is ready to preview." },
  offline: { title: "Site taken offline", description: "Production was removed for this site." },
  domain: { title: "Domain verified", description: "Custom domain DNS verified and connected." },
  test: { title: "Test alert", description: "If you can read this, Discord alerts are connected." }
};
function discordEventsOf(a){
  if (!a.discord_events) a.discord_events = { live:true, stage:true, offline:true, domain:true };
  return a.discord_events;
}
function discordMessagesOf(a){
  if (!a.discord_messages || typeof a.discord_messages !== "object") a.discord_messages = {};
  return a.discord_messages;
}
function discordMsgFor(a, eventId){
  const defs = DISCORD_MSG_DEFAULTS[eventId] || { title: "Studio alert", description: "" };
  const defAccent = (DISCORD_EVENT_DEFS.find(e => e.id === eventId) || {}).accent || "#57F287";
  const row = discordMessagesOf(a)[eventId] || {};
  const title = String(row.title ?? "").trim() || defs.title;
  const description = Object.prototype.hasOwnProperty.call(row, "description")
    ? String(row.description ?? "")
    : defs.description;
  const rawColor = String(row.color ?? row.accent ?? "").trim();
  const color = /^#[0-9A-Fa-f]{6}$/.test(rawColor) ? rawColor.toLowerCase() : defAccent.toLowerCase();
  return { title, description, color };
}
function discordFill(tpl, vars){
  let out = String(tpl || "");
  Object.keys(vars || {}).forEach(k => { out = out.split("{" + k + "}").join(String(vars[k] ?? "")); });
  return out;
}
function discordAutoGrow(el){
  if (!el || el.tagName !== "TEXTAREA") return;
  el.style.height = "auto";
  el.style.height = Math.max(96, el.scrollHeight) + "px";
}
const DISCORD_PLACEHOLDERS = [
  { key: "site", hint: "Site name" },
  { key: "url", hint: "Site URL" },
  { key: "env", hint: "Environment" },
  { key: "domain", hint: "Custom domain" }
];
/* HTML lang codes for Advanced → Language (BCP 47). */
const SITE_LOCALES = [
  ["en", "English"],
  ["en-GB", "English (UK)"],
  ["en-US", "English (US)"],
  ["af", "Afrikaans"],
  ["zu", "Zulu"],
  ["xh", "Xhosa"],
  ["nso", "Northern Sotho"],
  ["st", "Southern Sotho"],
  ["tn", "Tswana"],
  ["ts", "Tsonga"],
  ["ss", "Swati"],
  ["ve", "Venda"],
  ["nr", "Southern Ndebele"],
  ["fr", "French"],
  ["de", "German"],
  ["es", "Spanish"],
  ["pt", "Portuguese"],
  ["pt-BR", "Portuguese (Brazil)"],
  ["it", "Italian"],
  ["nl", "Dutch"],
  ["pl", "Polish"],
  ["ru", "Russian"],
  ["uk", "Ukrainian"],
  ["ar", "Arabic"],
  ["he", "Hebrew"],
  ["hi", "Hindi"],
  ["zh-Hans", "Chinese (Simplified)"],
  ["zh-Hant", "Chinese (Traditional)"],
  ["ja", "Japanese"],
  ["ko", "Korean"],
  ["tr", "Turkish"],
  ["sv", "Swedish"],
  ["da", "Danish"],
  ["nb", "Norwegian"],
  ["fi", "Finnish"],
  ["cs", "Czech"],
  ["hu", "Hungarian"],
  ["ro", "Romanian"],
  ["el", "Greek"],
  ["th", "Thai"],
  ["vi", "Vietnamese"],
  ["id", "Indonesian"],
  ["ms", "Malay"],
  ["sw", "Swahili"]
];
function normalizeSiteLocale(raw){
  const v = String(raw || "").trim();
  if (!v) return "en";
  const exact = SITE_LOCALES.find(([code]) => code.toLowerCase() === v.toLowerCase());
  if (exact) return exact[0];
  const base = v.split(/[-_]/)[0].toLowerCase();
  const byBase = SITE_LOCALES.find(([code]) => code.toLowerCase() === base);
  return byBase ? byBase[0] : "en";
}
function discordInsertAtCursor(el, text){
  if (!el) return;
  const start = el.selectionStart ?? el.value.length;
  const end = el.selectionEnd ?? start;
  const before = el.value.slice(0, start);
  const after = el.value.slice(end);
  el.value = before + text + after;
  const caret = before.length + text.length;
  el.focus();
  try { el.setSelectionRange(caret, caret); } catch {}
  el.dispatchEvent(new Event("input", { bubbles: true }));
}
function discordBindPlaceholderSuggest(el){
  if (!el || el._dcSuggestBound) return;
  el._dcSuggestBound = true;

  const menu = document.createElement("div");
  menu.className = "dc-suggest";
  menu.hidden = true;
  menu.setAttribute("role", "listbox");
  document.body.appendChild(menu);

  let active = -1;
  let matches = [];
  let tokenStart = -1;
  let open = false;

  const hide = () => {
    open = false;
    menu.hidden = true;
    menu.innerHTML = "";
    active = -1;
    matches = [];
    tokenStart = -1;
  };

  const placeMenu = () => {
    const r = el.getBoundingClientRect();
    const gap = 6;
    const maxH = 220;
    const spaceBelow = window.innerHeight - r.bottom - 12;
    const openUp = spaceBelow < 120 && r.top > spaceBelow;
    menu.style.position = "fixed";
    menu.style.left = Math.max(8, Math.min(r.left, window.innerWidth - Math.min(r.width, 320) - 8)) + "px";
    menu.style.width = Math.max(200, Math.min(r.width, 320)) + "px";
    menu.style.zIndex = "13050";
    if (openUp) {
      menu.style.top = "auto";
      menu.style.bottom = (window.innerHeight - r.top + gap) + "px";
      menu.style.maxHeight = Math.min(maxH, r.top - 16) + "px";
    } else {
      menu.style.bottom = "auto";
      menu.style.top = (r.bottom + gap) + "px";
      menu.style.maxHeight = Math.min(maxH, spaceBelow) + "px";
    }
  };

  const apply = (key) => {
    const start = tokenStart >= 0 ? tokenStart : (el.selectionStart ?? 0);
    const end = el.selectionStart ?? start;
    const before = el.value.slice(0, start);
    const after = el.value.slice(end);
    el.value = before + "{" + key + "}" + after;
    const caret = before.length + key.length + 2;
    el.focus();
    try { el.setSelectionRange(caret, caret); } catch {}
    hide();
    el.dispatchEvent(new Event("input", { bubbles: true }));
  };

  const render = () => {
    if (!matches.length) { hide(); return; }
    open = true;
    menu.innerHTML = matches.map((m, i) =>
      `<button type="button" class="dc-suggest-item${i === active ? " is-active" : ""}" role="option" data-key="${m.key}" aria-selected="${i === active ? "true" : "false"}">` +
      `<span class="dc-suggest-token">{${m.key}}</span>` +
      `<span class="dc-suggest-hint">${m.hint}</span>` +
      `</button>`
    ).join("");
    menu.hidden = false;
    placeMenu();
    menu.querySelectorAll("[data-key]").forEach(btn => {
      btn.onmousedown = e => { e.preventDefault(); apply(btn.dataset.key); };
    });
  };

  const refresh = () => {
    const pos = el.selectionStart ?? 0;
    const left = el.value.slice(0, pos);
    const m = left.match(/\{([a-zA-Z]*)$/);
    if (!m) { hide(); return; }
    tokenStart = pos - m[0].length;
    const q = (m[1] || "").toLowerCase();
    matches = DISCORD_PLACEHOLDERS.filter(p => !q || p.key.startsWith(q));
    if (!matches.length) { hide(); return; }
    active = 0;
    render();
  };

  el.addEventListener("input", refresh);
  el.addEventListener("keyup", e => {
    if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) refresh();
  });
  el.addEventListener("click", refresh);
  el.addEventListener("blur", () => setTimeout(() => {
    if (!menu.contains(document.activeElement)) hide();
  }, 150));
  el.addEventListener("keydown", e => {
    if (!open || !matches.length) {
      if (e.key === "{" ) setTimeout(refresh, 0);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      active = (active + 1) % matches.length;
      render();
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      active = (active - 1 + matches.length) % matches.length;
      render();
    } else if (e.key === "Enter" || e.key === "Tab") {
      if (active >= 0 && matches[active]) {
        e.preventDefault();
        apply(matches[active].key);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      hide();
    }
  });
  window.addEventListener("resize", () => { if (open) placeMenu(); });
  window.addEventListener("scroll", () => { if (open) placeMenu(); }, true);

  el._dcSuggestHide = hide;
  el._dcSuggestIsOpen = () => open;
  el._dcSuggestDestroy = () => {
    hide();
    menu.remove();
    el._dcSuggestBound = false;
  };
}
function normalizeDiscordWebhook(raw){
  let v = String(raw || "").trim();
  if (!v) return "";
  v = v.replace(/^http:\/\//i, "https://");
  return v;
}
function isDiscordWebhookUrl(raw){
  const v = normalizeDiscordWebhook(raw);
  return /^https:\/\/(discord|discordapp)\.com\/api\/webhooks\//i.test(v);
}
function maskDiscordWebhook(raw){
  const v = normalizeDiscordWebhook(raw);
  if (!isDiscordWebhookUrl(v)) return v;
  const parts = v.split("/");
  const token = parts[parts.length - 1] || "";
  const id = parts[parts.length - 2] || "";
  const maskedTok = token.length <= 8 ? "••••••••" : token.slice(0, 4) + "…••••" + token.slice(-4);
  return `https://discord.com/api/webhooks/${id}/${maskedTok}`;
}
function discordBrandMark(cls = "dc-mark"){
  return `<svg class="${cls}" viewBox="0 0 127.14 96.36" width="28" height="21" aria-hidden="true" focusable="false"><path fill="currentColor" d="M107.7 8.07A105.15 105.15 0 0 0 81.47 0a72.06 72.06 0 0 0-3.36 6.83 97.68 97.68 0 0 0-29.11 0A72.37 72.37 0 0 0 45.64 0 105.89 105.89 0 0 0 19.39 8.09C2.79 32.65-1.71 56.6.54 80.21a105.73 105.73 0 0 0 32.17 16.15 77.7 77.7 0 0 0 6.89-11.11 68.42 68.42 0 0 1-10.85-5.16c.91-.66 1.8-1.34 2.66-2.03a75.57 75.57 0 0 0 64.32 0c.87.71 1.76 1.39 2.66 2.03a68.68 68.68 0 0 1-10.87 5.19 77 77 0 0 0 6.89 11.1 105.25 105.25 0 0 0 32.19-16.14c2.64-27.38-4.51-51.11-18.87-72.15ZM42.45 65.69c-6.27 0-11.44-5.72-11.44-12.76s5.05-12.76 11.44-12.76c6.42 0 11.56 5.76 11.44 12.76 0 7.04-5.05 12.76-11.44 12.76Zm42.24 0c-6.27 0-11.44-5.72-11.44-12.76s5.05-12.76 11.44-12.76c6.42 0 11.56 5.76 11.44 12.76 0 7.04-5.02 12.76-11.44 12.76Z"/></svg>`;
}
function discordEmbedPreview(siteName, opts = {}){
  const name = esc(siteName || "Your site");
  const title = esc(opts.title || "Site promoted to live");
  const desc = esc(opts.description || "Your site is now live.");
  const env = esc(opts.env || "Production");
  const accent = opts.accent || "#57F287";
  return `<div class="dc-embed-preview" id="dc-embed-preview" aria-hidden="true">
    <div class="dc-embed-msg">
      <div class="dc-embed-avatar">${discordBrandMark("dc-embed-avatar-mark")}</div>
      <div class="dc-embed-body">
        <div class="dc-embed-meta"><span class="dc-embed-bot">Browns Digital</span><span class="dc-embed-tag">APP</span><span class="dc-embed-time">Today at 9:41</span></div>
        <div class="dc-embed-card">
          <div class="dc-embed-bar" style="background:${accent}"></div>
          <div class="dc-embed-content">
            <div class="dc-embed-author">Browns Digital Studio</div>
            <div class="dc-embed-title" id="dc-prev-title">${title}</div>
            <div class="dc-embed-desc" id="dc-prev-desc">${desc}</div>
            <div class="dc-embed-fields">
              <div class="dc-embed-field"><span>Site</span><strong id="dc-prev-site">${name}</strong></div>
              <div class="dc-embed-field"><span>Environment</span><strong id="dc-prev-env">${env}</strong></div>
            </div>
            <div class="dc-embed-foot">Browns Digital Studio</div>
          </div>
        </div>
      </div>
    </div>
  </div>`;
}
function passwordStrength(pw){
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/\d/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}
function paintPasswordStrength(input, meter, bar, label){
  if (!input || !meter || !bar || !label) return;
  const value = String(input.value || "");
  const score = value ? passwordStrength(value) : 0;
  const labels = value
    ? ["Too weak", "Weak", "Fair", "Strong", "Very strong"]
    : ["Not entered"];
  const colors = [
    "var(--color-destructive)", "var(--color-destructive)",
    "var(--color-warning)", "var(--color-tint)", "var(--color-success)"
  ];
  meter.dataset.strength = String(score);
  meter.querySelector('[role="meter"]')?.setAttribute("aria-valuenow", String(score));
  meter.querySelector('[role="meter"]')?.setAttribute("aria-valuetext", labels[score] || labels[0]);
  bar.style.width = value ? Math.max(12, score * 25) + "%" : "0";
  bar.style.background = colors[score];
  label.textContent = labels[score] || labels[0];
}
function revisionSummary(snap){
  const pages = snap?.pages || [];
  return { pages: pages.length, blocks: pages.reduce((n, pg) => n + (pg.blocks?.length || 0), 0) };
}
function diffRevisions(current, snap){
  const curBlocks = new Map();
  (current.pages || []).forEach(pg => (pg.blocks || []).forEach(b => curBlocks.set(b.id, { page: pg.name, type: b.type, props: b.props })));
  const revBlocks = new Map();
  (snap.pages || []).forEach(pg => (pg.blocks || []).forEach(b => revBlocks.set(b.id, { page: pg.name, type: b.type, props: b.props })));
  const added = [], removed = [], changed = [];
  revBlocks.forEach((v, id) => {
    if (!curBlocks.has(id)) added.push(Object.assign({ id }, v));
    else if (JSON.stringify(curBlocks.get(id).props) !== JSON.stringify(v.props)) changed.push({ id, page: v.page, type: v.type });
  });
  curBlocks.forEach((v, id) => { if (!revBlocks.has(id)) removed.push(Object.assign({ id }, v)); });
  return { added, removed, changed };
}
function openRevisionDiffSheet(p, revIndex, onRestore){
  const rev = (p.data._revisions || [])[revIndex];
  if (!rev) return;
  const diff = diffRevisions(p.data, rev.snap);
  const sum = revisionSummary(rev.snap);
  const wrap = document.createElement("div");
  wrap.className = "rev-diff-sheet";
  wrap.innerHTML = `<div class="rev-diff-backdrop" tabindex="-1"></div><div class="rev-diff-panel" role="dialog" aria-modal="true" aria-label="Revision preview">
    <div class="rev-diff-head"><h3>Revision preview</h3><button type="button" class="icon-btn rev-diff-x pill-hit" aria-label="Close">${lu("x","lu16")}</button></div>
    <p class="c-sub">${fmtWhen(rev.ts)} · ${esc(REV_TRIGGER_LABELS[rev.trigger] || "SAVE")}${rev.label ? " · " + esc(rev.label) : ""} · ${sum.blocks} sections · ${sum.pages} pages</p>
    <div class="rev-diff-stats">
      <span class="rev-diff-add">+${diff.added.length} added</span>
      <span class="rev-diff-rm">−${diff.removed.length} removed</span>
      <span class="rev-diff-chg">~${diff.changed.length} changed</span>
    </div>
    <div class="rev-diff-list">${[...diff.added.map(x => `<div class="rev-diff-block"><strong style="color:var(--color-success)">+ ${esc(x.type)}</strong> on ${esc(x.page)}</div>`),
      ...diff.removed.map(x => `<div class="rev-diff-block"><strong style="color:var(--color-destructive)">− ${esc(x.type)}</strong> on ${esc(x.page)}</div>`),
      ...diff.changed.map(x => `<div class="rev-diff-block"><strong style="color:var(--color-warning)">~ ${esc(x.type)}</strong> on ${esc(x.page)}</div>`)].join("") || `<p class="c-sub">No structural differences — tokens or SEO may still differ.</p>`}</div>
    <div class="row" style="margin-top:16px"><button type="button" class="btn btn-ghost btn-sm rev-diff-cancel">Cancel</button><button type="button" class="btn btn-cta btn-sm rev-diff-restore">Restore this revision</button></div>
  </div>`;
  document.body.appendChild(wrap);
  icons();
  const close = () => wrap.remove();
  wrap.querySelector(".rev-diff-backdrop").onclick = close;
  wrap.querySelector(".rev-diff-x").onclick = close;
  wrap.querySelector(".rev-diff-cancel").onclick = close;
  wrap.querySelector(".rev-diff-restore").onclick = () => { close(); onRestore(); };
}
function pubCopyRow(url, copyId, openId){
  return `<div class="pub-copy-row"><code>${esc(url)}</code>
    <button type="button" class="btn btn-ghost btn-sm" id="${copyId}" aria-label="Copy URL">${lu("copy","lu14")}</button>
    ${openId ? `<button type="button" class="btn btn-ghost btn-sm" id="${openId}" aria-label="Open URL">${lu("external-link","lu14")}</button>` : ""}</div>`;
}
function ensureSessions(){
  const key = "bd_sessions_" + (me?.email || "");
  let rows = [];
  try { rows = JSON.parse(localStorage.getItem(key) || "[]"); } catch {}
  if (!rows.length){
    rows = [{ id: uid(), device: "This browser", location: "Current session", current: true, last: new Date().toISOString() }];
    localStorage.setItem(key, JSON.stringify(rows));
  }
  return rows;
}
const lu = (n, cls) => `<i data-lucide="${n}"${cls?` class="${cls}"`:""}></i>`;
/* Curated symbols for the Console site mark (Settings → Site). */
const SITE_ICONS = [
  "layout-dashboard","globe","store","briefcase","building-2","home",
  "camera","palette","scissors","utensils","coffee","dumbbell",
  "heart-pulse","stethoscope","graduation-cap","book-open","music","mic",
  "cpu","smartphone","car","plane","leaf","flower-2",
  "sparkles","star","zap","anchor","compass","map-pin","users","handshake"
];
const siteIconOf = p => {
  const raw = String(p?.data?.icon || "").trim();
  return SITE_ICONS.includes(raw) ? raw : "layout-dashboard";
};
const siteIconColorOf = p => {
  const raw = String(p?.data?.iconColor || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw.toLowerCase();
  const accent = p?.data?.tokens?.accent || p?.data?.settings?.accent || "";
  if (/^#[0-9a-fA-F]{6}$/.test(String(accent).trim())) return String(accent).trim().toLowerCase();
  return "#355cff";
};
/** Merge defaults into project tokens without replacing the object identity.
 *  Design-tab UIs close over `tk = p.data.tokens` — replacing the object made
 *  accent edits vanish when switching Look/Colour/Type/Shape/Motion. */
function ensureProjectTokens(p){
  if (!p) return null;
  p.data = p.data || {};
  const defaults = typeof defTokens === "function" ? defTokens() : {};
  if (!p.data.tokens || typeof p.data.tokens !== "object"){
    p.data.tokens = Object.assign({}, defaults);
    return p.data.tokens;
  }
  for (const [k, v] of Object.entries(defaults)){
    if (p.data.tokens[k] === undefined) p.data.tokens[k] = v;
  }
  return p.data.tokens;
}
/** One site brand colour — Console icon + Design accent stay in sync. */
function setBrandAccent(p, hex){
  if (!p) return "";
  const v = String(hex || "").trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(v)) return siteIconColorOf(p);
  const lc = v.toLowerCase();
  const tk = ensureProjectTokens(p);
  tk.accent = lc;
  p.data.iconColor = lc;
  return lc;
}
/** Pull Console colour and Design accent onto one value before editing. */
function ensureBrandAccentAligned(p){
  if (!p?.data) return;
  const fromIcon = String(p.data.iconColor || "").trim();
  const fromTk = String(p.data.tokens?.accent || "").trim();
  if (/^#[0-9a-fA-F]{6}$/i.test(fromIcon)) setBrandAccent(p, fromIcon);
  else if (/^#[0-9a-fA-F]{6}$/i.test(fromTk)) setBrandAccent(p, fromTk);
}
let _brandTokenSaveTimer = null;
function queueBrandTokenSave(p){
  clearTimeout(_brandTokenSaveTimer);
  _brandTokenSaveTimer = setTimeout(async () => {
    _brandTokenSaveTimer = null;
    try { await api.saveProject(p); }
    catch (e){ toast(humanErr(e), { kind: "error" }); }
  }, 600);
}
async function flushBrandTokenSave(){
  if (!_brandTokenSaveTimer) return;
  clearTimeout(_brandTokenSaveTimer);
  _brandTokenSaveTimer = null;
  const p = active();
  if (p){
    try { await api.saveProject(p); }
    catch (e){ toast(humanErr(e), { kind: "error" }); }
  }
}
/* Email hygiene — real format check + a disposable-domain blocklist so
   throwaway inboxes can't be used to farm free accounts or spam invites. */
const DISPOSABLE_DOMAINS = ["mailinator.com","guerrillamail.com","10minutemail.com","tempmail.com","temp-mail.org","yopmail.com","sharklasers.com","trashmail.com","getnada.com","dispostable.com","maildrop.cc","fakeinbox.com","mintemail.com","throwawaymail.com","mohmal.com","tempr.email","spamgourmet.com","mytemp.email","burnermail.io","emailondeck.com"];
function validEmail(email){
  const e = String(email || "").trim().toLowerCase();
  if (!/^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/.test(e)) return { ok:false, why:"That doesn’t look like a valid email address." };
  const domain = e.split("@")[1];
  if (DISPOSABLE_DOMAINS.includes(domain)) return { ok:false, why:"Disposable email addresses aren’t supported — use a real inbox." };
  if (/(.)\1{7,}/.test(e.split("@")[0])) return { ok:false, why:"That doesn’t look like a real email address." };
  return { ok:true, email:e };
}
function validUsername(raw){
  const v = String(raw || "").trim().replace(/[^a-z0-9_]/gi, "");
  if (!v) return { ok:false, why:"Choose a username." };
  if (v.length < 3) return { ok:false, why:"Usernames need at least 3 characters." };
  if (v.length > 32) return { ok:false, why:"Usernames can be at most 32 characters." };
  return { ok:true, username:v };
}
const ACT_ICON = { created:"plus", saved:"save", staged:"upload", published:"rocket", unpublished:"circle-off", renamed:"pencil", deleted:"trash-2", restored:"history", domain:"globe", patched:"code-xml" };

/* ------------------------------ theme ------------------------------ */
function applyTheme(){ bdApplyThemePrefs(bdLoadThemePrefs()); }
applyTheme();
setTimeout(() => { if (typeof bdEnableThemeTransitions === "function") bdEnableThemeTransitions(); }, 120);
function openCustomizeModal(){ bdOpenCustomizeModal({ openModal, closeModal, setModalCloseHook, clearModalCloseHook, toast, esc, lu, icons, api }); }

/* ------------------------------ state ------------------------------ */
let me = null, plan = "free", admin = false, creditBalance = null;
let projects = [], deploys = { staging: [], production: [] }, activity = [];
let activeId = null, page = "dashboard";
/* builder */
let current = null, curPageId = null, selected = null, dirty = false;
/* Extra page-block ids when Shift/Ctrl-click multi-selecting (alongside `selected`). */
let multiSel = [];
let bp = 1440, builderBreakpoint = "desktop", past = [], future = [], autosaveT = null, libQuery = "", libGroup = "", builderPanel = "pages";
/* left-panel collapsible sections — open state persists per browser */
const lpOpen = k => { try { const s = JSON.parse(localStorage.getItem("bd_lp_open") || "{}"); return k in s ? !!s[k] : k !== "layers"; } catch { return k !== "layers"; } };
const setLpOpen = (k, v) => { try { const s = JSON.parse(localStorage.getItem("bd_lp_open") || "{}"); s[k] = v; localStorage.setItem("bd_lp_open", JSON.stringify(s)); } catch {} };
let previewMode = false, lastSavedSnap = null;
/* Live edit — immersive published look: rails hide, floating chrome + HUD,
   click-to-edit text, drawer inspector. Default edit already uses the real
   canvas; Live is the focused “work on the live page” mode. */
let liveEdit = false;
/* Do not restore Live edit across sessions — opening the canvas must stay in the
   block editor. Preview already opens the site in a new tab; restoring Live edit
   felt like the editor itself redirected to the published site. */
const BUILDER_LOG = [];
function blog(kind, msg){ BUILDER_LOG.unshift({ t: new Date().toLocaleTimeString(), kind, msg }); if (BUILDER_LOG.length > 80) BUILDER_LOG.length = 80; }

const active     = () => projects.find(p => p.id === activeId) || null;
const stagingOf  = id => api.deployFor(deploys.staging, id);
const prodOf     = id => api.deployFor(deploys.production, id);
const activePage = () => current?.data.pages.find(p => p.id === curPageId) || current?.data.pages[0] || null;
const newId      = () => crypto.randomUUID ? crypto.randomUUID() : uid()+uid()+uid();
const slugify    = s => String(s||"").toLowerCase().replace(/[^a-z0-9]+/g,"-").replace(/^-+|-+$/g,"").slice(0,48);
const slugOf     = p => slugify(p?.data?.seo?.title || p?.name) || "site";
/** Other projects already using this publish address (by deploy slug or derived name). */
function projectsUsingSlug(slug, exceptId){
  slug = slugify(slug);
  if (!slug) return [];
  return projects.filter(px => {
    if (px.id === exceptId) return false;
    const deployed = stagingOf(px.id)?.slug || prodOf(px.id)?.slug;
    return deployed === slug || slugOf(px) === slug;
  });
}
function slugTakenMessage(slug, forProject){
  const others = projectsUsingSlug(slug, forProject?.id);
  if (!others.length) return "";
  slug = slugify(slug);
  const names = others.map(x => `“${x.name}”`);
  if (others.length === 1) {
    return `The address “${slug}” is already used by ${names[0]}. Two sites can’t share the same URL — rename one of them or choose a different address.`;
  }
  return `Multiple sites (${names.join(", ")}) already use the address “${slug}”. Give each site a unique name or pick a different address.`;
}
function duplicateNameHint(name, exceptId){
  const slug = slugify(name);
  const others = projectsUsingSlug(slug, exceptId);
  if (!others.length) return "";
  return slugTakenMessage(slug, { id: exceptId });
}
function log(projectId, kind, message){ api.logActivity(projectId, kind, message).then(() => refreshActivity()); }
async function refreshActivity(){ activity = await api.listActivity(null, 30); paintBell(); }

/* ================================ AUTH ================================ */
function authErr(id, msg){ const el = $("#"+id); el.textContent = msg; el.classList.add("show"); }
function authOk(id, msg){ const el = $("#"+id); el.textContent = msg; el.classList.add("show"); }
function clearMsgs(){ $$(".error-msg,.ok-msg").forEach(e => e.classList.remove("show")); }
let pendingVerifyEmail = "";
const EMAIL_DOMAINS = ["gmail.com", "icloud.com", "outlook.com", "hotmail.com", "yahoo.com", "live.com", "brownsdigital.org"];
function usesCredentialSignIn(){
  return !!(QA_MODE || DEMO);
}
function credentialSignInReady(){
  return usesCredentialSignIn()
    && typeof BD_SIGNIN_CREDENTIALS !== "undefined"
    && BD_SIGNIN_CREDENTIALS.isMounted();
}
function bindCredentialSignInEvents(){
  if (!credentialSignInReady()) return;
  if (typeof BD_EMAIL_SUGGEST !== "undefined") {
    BD_EMAIL_SUGGEST.bindAuthForms([["si-domains", "si-email"]]);
  } else {
    bindEmailDomains("si-domains", "si-email");
  }
  bindPassToggles();
  icons();
  $("#si-email")?.addEventListener("keydown", e => {
    if (e.key === "Enter"){ e.preventDefault(); $("#si-pass")?.focus(); }
  });
  $("#si-pass")?.addEventListener("keydown", e => {
    if (e.key === "Enter") $("#btn-signin")?.click();
  });
  $("#btn-signin")?.addEventListener("click", submitEmailSignIn);
  $("#to-forgot")?.addEventListener("click", goToForgotFromSignin);
}
async function goToForgotFromSignin(){
  await bdInitApi();
  authShow("auth-forgot");
  resetForgotForm();
  const siEmail = $("#si-email")?.value.trim();
  if (siEmail) $("#fp-email").value = siEmail;
  $("#fp-email")?.focus();
}
async function submitEmailSignIn(){
  if (!credentialSignInReady()) return;
  clearMsgs();
  const email = $("#si-email").value.trim(), pass = $("#si-pass").value;
  if (!email || !pass) return authErr("signin-error", "Enter your email and password.");
  const b = $("#btn-signin"); b.disabled = true; b.textContent = "Signing in…";
  try {
    await api.signIn(email, pass);
    markDeviceSignInFreshIfNeeded();
    sessionStorage.removeItem(sessionPinUnlockKey(email));
    await boot();
  }
  catch (e) {
    if (isUnknownAccountError(e)) {
      try {
        await bdInitApi();
        const exists = await api.accountExists(email);
        if (exists === false) {
          openSignupWithPrefill({
            email,
            password: pass,
            message: "No account exists with that email — choose a username to create one."
          });
          return;
        }
        if (exists === true) {
          authErr("signin-error", "Incorrect password — try again or use Forgot password.");
          return;
        }
      } catch (_) { /* fall through */ }
    }
    authErr("signin-error", humanErr(e, "Sign-in failed — check your details."));
    if (isUnknownAccountError(e)) setSigninCreatePrompt(true);
  } finally {
    b.disabled = false;
    b.textContent = "Sign in";
  }
}
function authShow(card, opts = {}){
  /* Live production: OAuth/QR only. Demo + QA keep email signup/forgot. */
  if (!QA_MODE && !DEMO && card === "auth-signup") return;
  if (!QA_MODE && !DEMO && card === "auth-forgot" && !opts.recovery) return;
  if (typeof BD_DEVICE_AUTH !== "undefined") {
    if (card === "auth-signin" && !usesCredentialSignIn()) setTimeout(startQrSignIn, 0);
    else BD_DEVICE_AUTH.cancel();
  }
  ["auth-signin","auth-signup","auth-forgot","auth-verify"].forEach(c => {
    const el = $("#"+c);
    if (!el) return;
    const on = c === card;
    el.hidden = !on;
    if (on){ el.classList.remove("auth-in"); void el.offsetWidth; el.classList.add("auth-in"); }
  });
  if (card === "auth-signin") resetSignInForm();
  else if (card === "auth-signup" && !opts.keepPrefill) resetSignupPrefillDisplay();
  if (card === "auth-forgot" && !$("#fp-newpass-row")?.hidden) { /* keep recovery layout */ }
  else if (card === "auth-forgot") resetForgotForm();
  clearMsgs();
  icons();
}
function resetForgotForm(){
  const emailRow = $("#fp-email-row");
  const passRow = $("#fp-newpass-row");
  const mfaRow = $("#fp-mfa-row");
  const sub = $("#forgot-sub");
  const btn = $("#btn-forgot");
  if (mfaRow) mfaRow.hidden = true;
  if (DEMO){
    if (sub) sub.textContent = "Demo mode — enter your email and a new password to reset it right here.";
    if (passRow) passRow.hidden = false;
    if (btn) btn.textContent = "Reset password";
  } else {
    if (sub) sub.textContent = "Enter your email and we'll send a link to choose a new password.";
    if (emailRow) emailRow.hidden = false;
    if (passRow) passRow.hidden = true;
    if (btn) btn.textContent = "Send reset link";
  }
}
function showRecoveryPasswordForm(){
  const sub = $("#forgot-sub");
  const emailRow = $("#fp-email-row");
  const passRow = $("#fp-newpass-row");
  const mfaRow = $("#fp-mfa-row");
  const btn = $("#btn-forgot");
  if (sub) sub.textContent = "Choose a new password to finish resetting your account.";
  if (emailRow) emailRow.hidden = true;
  if (passRow) passRow.hidden = false;
  if (mfaRow) mfaRow.hidden = true;
  if (btn) btn.textContent = "Set new password";
}
async function prepareRecoveryForm(){
  if (DEMO) return;
  try {
    await bdInitApi();
    const aal = await api.getAalState();
    if (!aal.needsMfa) return;
    const mfaRow = $("#fp-mfa-row");
    const sub = $("#forgot-sub");
    if (mfaRow) mfaRow.hidden = false;
    if (sub) sub.textContent = "Two-factor authentication is enabled. Enter your authenticator code, then choose a new password.";
    $("#fp-mfa-code")?.focus();
  } catch (_) { /* recovery can still proceed if AAL check fails */ }
}
function isMfaRequiredError(e){
  return !!(e?.needsMfa || /aal2 session is required/i.test(String(e?.message || e || "")));
}
function resetSignInForm(){
  const pass = $("#si-pass");
  if (pass) pass.value = "";
  setSigninCreatePrompt(false);
}
function setSigninCreatePrompt(show){
  const el = $("#signin-create-prompt");
  if (!el) return;
  el.hidden = !show || QA_MODE;
}
function isUnknownAccountError(e){
  if (e?.credentialMismatch) return true;
  const msg = String(e?.message || e || "").toLowerCase();
  return /invalid login credentials|invalid email or password|email and password don.t match|no account found|user not found/.test(msg);
}
function isNoAccountResetError(e){
  return /no account exists/i.test(String(e?.message || e || ""));
}
function resetSignupPrefillDisplay(){
  const emailRow = $("#su-email-row");
  const passRow = $("#su-pass-row");
  if (emailRow) emailRow.hidden = false;
  if (passRow) passRow.hidden = false;
}
function openSignupWithPrefill({ email = "", password = "", message = "" } = {}){
  authShow("auth-signup", { keepPrefill: true });
  resetSignupPrefillDisplay();
  if (email) $("#su-email").value = email;
  if (password){
    $("#su-pass").value = password;
    $("#su-pass").dispatchEvent(new Event("input"));
  }
  $("#su-name").value = "";
  if (message) authErr("signup-error", message);
  $("#su-name").focus();
}
function goToSignupFromSignin(){
  openSignupWithPrefill({
    email: $("#si-email").value.trim(),
    password: $("#si-pass").value
  });
}
function goToSignupFromForgot(){
  openSignupWithPrefill({
    email: $("#fp-email").value.trim()
  });
}
function bindEmailDomains(containerId, inputId){
  const box = $("#" + containerId);
  const inp = $("#" + inputId);
  if (!box || !inp) return;

  const hide = () => { box.hidden = true; box.innerHTML = ""; };

  const pick = full => {
    inp.value = full;
    hide();
    inp.focus();
  };

  const sync = () => {
    const v = inp.value.trim();
    const at = v.indexOf("@");
    if (at < 0){ hide(); return; }
    const local = v.slice(0, at);
    if (!local){ hide(); return; }
    const partial = v.slice(at + 1).toLowerCase();
    if (EMAIL_DOMAINS.some(d => v.toLowerCase() === (local + "@" + d).toLowerCase())){ hide(); return; }
    const matches = EMAIL_DOMAINS.filter(d => !partial || d.toLowerCase().startsWith(partial));
    if (!matches.length){ hide(); return; }
    box.innerHTML = matches.map(d => {
      const full = local + "@" + d;
      return `<button type="button" class="email-ac-item" role="option">${esc(full)}</button>`;
    }).join("");
    box.querySelectorAll(".email-ac-item").forEach(b => {
      b.onmousedown = e => { e.preventDefault(); pick(b.textContent); };
    });
    box.hidden = false;
  };

  inp.addEventListener("input", sync);
  inp.addEventListener("focus", sync);
  inp.addEventListener("blur", () => setTimeout(hide, 180));
}
function bindPassToggles(){
  document.querySelectorAll(".pass-toggle").forEach(btn => {
    btn.onclick = () => {
      const inp = $("#" + btn.dataset.for);
      if (!inp) return;
      const show = inp.type === "password";
      inp.type = show ? "text" : "password";
      btn.setAttribute("aria-label", show ? "Hide password" : "Show password");
      btn.innerHTML = show ? lu("eye-off") : lu("eye");
      icons();
    };
  });
}
bindPassToggles();
if (typeof BD_EMAIL_SUGGEST !== "undefined") {
  BD_EMAIL_SUGGEST.bindAuthForms([["su-domains", "su-email"], ["fp-domains", "fp-email"]]);
} else {
bindEmailDomains("su-domains", "su-email");
bindEmailDomains("fp-domains", "fp-email");
}
$("#su-name")?.addEventListener("input", e => {
  e.target.value = e.target.value.replace(/[^a-z0-9_]/gi, "").slice(0, 32);
});
const signupPassword = $("#su-pass");
const paintSignupPassword = () => paintPasswordStrength(
  signupPassword, $("#su-pass-meter"), $("#su-pass-meter-bar"), $("#su-pass-strength")
);
signupPassword?.addEventListener("input", paintSignupPassword);
paintSignupPassword();
const recoveryPassword = $("#fp-pass");
const paintRecoveryPassword = () => paintPasswordStrength(
  recoveryPassword, $("#fp-pass-meter"), $("#fp-pass-meter-bar"), $("#fp-pass-strength")
);
recoveryPassword?.addEventListener("input", paintRecoveryPassword);
paintRecoveryPassword();
icons();
$("#to-signin2").onclick = () => { authShow("auth-signin"); $("#si-email")?.focus(); };
$("#signin-to-signup")?.addEventListener("click", goToSignupFromSignin);
$("#verify-back").onclick = () => authShow("auth-signin");
$("#btn-verify-done").onclick = async () => {
  clearMsgs();
  if (DEMO && pendingVerifyEmail){
    try {
      await api.verifyEmailCode(pendingVerifyEmail, $("#verify-code").value.trim());
      authShow("auth-signin");
      $("#si-email").value = pendingVerifyEmail;
      toast("Email verified — sign in now.");
      return;
    } catch (e) { authErr("verify-error", humanErr(e)); return; }
  }
  authShow("auth-signin");
  $("#si-email").value = pendingVerifyEmail || "";
  $("#si-email").focus();
  toast("Sign in once your email is verified.");
};
$("#btn-send-verify").onclick = async () => {
  clearMsgs();
  const email = pendingVerifyEmail || $("#si-email").value.trim();
  if (!email) return authErr("verify-error", "Enter your email on the sign-in screen first.");
  try {
    const r = await api.resendVerification(email);
    if (DEMO && r.demoCode){
      $("#verify-demo-row").hidden = false;
      $("#verify-code").value = r.demoCode;
      authOk("verify-ok", "Demo: your new code is " + r.demoCode + " (shown above).");
    } else authOk("verify-ok", "Verification email resent — check your inbox.");
  } catch (e) { authErr("verify-error", humanErr(e)); }
};
$("#forgot-back")?.addEventListener("click", () => authShow("auth-signin"));
$("#btn-forgot").onclick = async () => {
  clearMsgs();
  const b = $("#btn-forgot"); b.disabled = true;
  try {
    await bdInitApi();
    const passRow = $("#fp-newpass-row");
    const recovering = !passRow?.hidden && !DEMO;
    if (recovering){
      const np = $("#fp-pass").value;
      if (np.length < 8) throw new Error("Use a new password of at least 8 characters.");
      const aal = await api.getAalState();
      if (aal.needsMfa) {
        const code = $("#fp-mfa-code")?.value?.trim() || "";
        if (!code) {
          $("#fp-mfa-row").hidden = false;
          throw new Error("Enter the 6-digit code from your authenticator app first.");
        }
        await api.mfaElevateTotp(code);
      }
      await api.setNewPassword(np);
      $("#fp-pass").value = ""; paintRecoveryPassword();
      history.replaceState(null, "", location.pathname);
      authOk("forgot-ok", "Password updated — you’re signed in."); setTimeout(boot, 900);
    } else if (DEMO){
      const email = $("#fp-email").value.trim(), np = $("#fp-pass").value;
      if (!email) throw new Error("Enter your account email.");
      if (np.length < 8) throw new Error("Use a new password of at least 8 characters.");
      await api.demoReset(email, np);
      $("#fp-pass").value = ""; paintRecoveryPassword();
      authOk("forgot-ok", "Password updated — sign in with the new one.");
    } else {
      const email = $("#fp-email").value.trim();
      if (!email) throw new Error("Enter your account email.");
      await api.requestReset(email);
      authOk("forgot-ok", "Reset link sent — check your inbox, then follow the link back here.");
      setSigninCreatePrompt(false);
    }
  } catch (e) {
    if (isNoAccountResetError(e)){
      openSignupWithPrefill({
        email: $("#fp-email").value.trim(),
        message: humanErr(e)
      });
    } else {
      authErr("forgot-error", humanErr(e));
      if (isMfaRequiredError(e)) $("#fp-mfa-row").hidden = false;
    }
  }
  b.disabled = false;
  if (!$("#fp-newpass-row").hidden && !DEMO) b.textContent = "Set new password";
  else if (!DEMO) b.textContent = "Send reset link";
};
$("#btn-signin-github")?.addEventListener("click", async () => {
  clearMsgs();
  setSigninCreatePrompt(false);
  const b = $("#btn-signin-github");
  b.disabled = true;
  window.BD_DEVICE_AUTH?.setLoginFocus?.(true);
  try { await api.signInWithGitHub(); }
  catch (e) {
    window.BD_DEVICE_AUTH?.setLoginFocus?.(false);
    authErr("signin-error", humanErr(e, "GitHub sign-in failed."));
    b.disabled = false;
  }
});
$("#btn-signin-google")?.addEventListener("click", async () => {
  clearMsgs();
  setSigninCreatePrompt(false);
  const b = $("#btn-signin-google");
  b.disabled = true;
  window.BD_DEVICE_AUTH?.setLoginFocus?.(true);
  try { await api.signInWithGoogle(); }
  catch (e) {
    window.BD_DEVICE_AUTH?.setLoginFocus?.(false);
    authErr("signin-error", humanErr(e, "Google sign-in failed."));
    b.disabled = false;
  }
});
$("#btn-signin-passkey")?.addEventListener("click", async () => {
  clearMsgs();
  const b = $("#btn-signin-passkey");
  const label = b?.querySelector("[data-passkey-label]");
  b.disabled = true;
  if (label) label.textContent = "Opening passkey…";
  else b.textContent = "Opening passkey…";
  window.BD_DEVICE_AUTH?.setLoginFocus?.(true);
  try {
    await api.signInWithPasskey();
    markDeviceSignInFreshIfNeeded();
    await boot();
  } catch (e) {
    authErr("signin-error", humanErr(e, "Passkey sign-in failed."));
  } finally {
    window.BD_DEVICE_AUTH?.setLoginFocus?.(false);
    b.disabled = false;
    if (label) label.textContent = "Continue with passkey";
    else b.textContent = "Continue with passkey";
    icons();
  }
});
$("#btn-signup").onclick = async () => {
  clearMsgs();
  const uv = validUsername($("#su-name").value);
  if (!uv.ok) return authErr("signup-error", uv.why);
  const email = $("#su-email").value.trim(), pass = $("#su-pass").value;
  if (!email) return authErr("signup-error", "Enter your email.");
  const ev = validEmail(email);
  if (!ev.ok) return authErr("signup-error", ev.why);
  if (pass.length < 8)  return authErr("signup-error", "Use a password of at least 8 characters.");
  const b = $("#btn-signup"); b.disabled = true; b.textContent = "Creating…";
  try {
    const r = await api.signUp(uv.username, ev.email, pass);
    if (r.confirmed){ await boot(); return; }
    pendingVerifyEmail = r.email || ev.email;
    $("#verify-email").textContent = pendingVerifyEmail;
    $("#verify-demo-row").hidden = !DEMO;
    if (DEMO && r.demoCode){
      $("#verify-code").value = r.demoCode;
      authShow("auth-verify");
      authOk("verify-ok", "Demo: use code " + r.demoCode + " below, then sign in.");
    } else {
      authShow("auth-verify");
      authOk("verify-ok", "Link sent — open it in your inbox, then sign in here.");
    }
  } catch (e) { authErr("signup-error", humanErr(e, "Could not create the account.")); }
  b.disabled = false; b.textContent = "Create account";
};
async function signOutAll(){
  if (me?.email) sessionStorage.removeItem(sessionPinUnlockKey(me.email));
  await api.signOut(); me = null; current = null; projects = [];
  history.replaceState(null, "", "/");
  if (usesCredentialSignIn()) {
    show("auth"); authShow("auth-signin"); paintAuthMode();
    return;
  }
  if (typeof BD_AUTH !== "undefined") BD_AUTH.signInRedirect(location.origin + "/");
  else location.replace("/");
}
$("#gate-signout").onclick = signOutAll;
$("#gate-recheck").onclick = boot;

function paintAuthMode(){
  const shell = document.querySelector("#view-auth .auth-shell--device");
  const credMount = $("#auth-signin-credentials");
  const lead = $("#auth-signin-lead");
  if (usesCredentialSignIn()) {
    shell?.classList.add("auth-shell--credentials");
    if (lead) lead.textContent = "Sign in with the email and password your QA lead shared.";
    if (typeof BD_SIGNIN_CREDENTIALS !== "undefined") {
      BD_SIGNIN_CREDENTIALS.mount(credMount, bindCredentialSignInEvents);
    }
  } else {
    shell?.classList.remove("auth-shell--credentials");
    if (lead) lead.textContent = "Sign in to Studio, or scan the code with your phone.";
    if (typeof BD_SIGNIN_CREDENTIALS !== "undefined") BD_SIGNIN_CREDENTIALS.clear(credMount);
    $("#auth-oauth-divider")?.removeAttribute("hidden");
    $("#btn-signin-github")?.removeAttribute("hidden");
    $("#btn-signin-google")?.removeAttribute("hidden");
  }
  if (DEMO) {
    if (lead) lead.textContent = "Demo mode — create an account or sign in. Data stays in this browser.";
    setSigninCreatePrompt(true);
  } else if (QA_MODE) {
    setSigninCreatePrompt(false);
  }
  if (!QA_MODE) return;
  const banner = $("#qa-banner");
  if (banner) banner.hidden = false;
  document.title = "Studio | Browns Digital";
}
function showPlanGate(suspended){
  $text("#gate-plan-badge", suspended ? "Suspended" : (PLAN_RULES[plan] || PLAN_RULES.free).label);
  const badge = $("#gate-plan-badge");
  if (badge) badge.className = "badge " + (suspended ? "draft" : plan === "studio" ? "studio" : plan === "pro" ? "pro" : "");
  $text("#gate-sub", suspended
    ? "This account has been suspended by the platform team. If you believe this is a mistake, contact " + CONTACT + "."
    : "Your plan doesn’t currently include Studio. The builder is included on Free (1 site), Pro (3 sites), and Studio (unlimited).");
  $hide("#gate-points", false);
  $hide("#gate-cta", false);
  $text("#view-gate h1", "This account is paused.");
  show("gate");
}
function showQaGate(){
  $text("#gate-plan-badge", "QA only");
  const badge = $("#gate-plan-badge");
  if (badge) badge.className = "badge draft";
  $text("#view-gate h1", "Invite-only QA");
  $text("#gate-sub", "Studio is in private QA. Only invited testers can access. If you need an account, contact " + CONTACT + ".");
  $hide("#gate-points", true);
  $hide("#gate-cta", true);
  show("gate");
}
/* ============================= ONBOARDING =============================
   Every step validates before Continue — empty or placeholder values
   block progress with an inline error. There is no skip. */
const LEGAL_AGREEMENT_VERSION = "2.0-2026-06-04";
const OB_STEPS = 4;
const _obPolicyCache = {};
function obPolicyHtml(kind){
  if (_obPolicyCache[kind]) return Promise.resolve(_obPolicyCache[kind]);
  return fetch(`/assets/legal/${kind}.html`, { cache: "force-cache" })
    .then(r => { if (!r.ok) throw new Error("policy load failed"); return r.text(); })
    .then(html => { _obPolicyCache[kind] = html; return html; });
}
function preloadObPolicies(){
  obPolicyHtml("terms").catch(() => {});
  obPolicyHtml("privacy").catch(() => {});
}
const ob = {
  step: 0,
  legalAccepted: false,
  name: "",
  username: "",
  avatar: "",
  enterFromBottom: false
};
const obKey = () => "bd_onboarded_" + (me?.email || "");
/* Native wireframe thumbnails — live block HTML breaks at card scale. */
const _tplAccentCache = {};
function tplAccent(k){
  if (_tplAccentCache[k] !== undefined) return _tplAccentCache[k];
  try { _tplAccentCache[k] = TEMPLATES[k].make().tokens?.accent || "#355cff"; }
  catch { _tplAccentCache[k] = "#355cff"; }
  return _tplAccentCache[k];
}
function tplWireHTML(bars, accent){
  const rows = (bars || [1, 3, 2]).slice(1);
  return `<span class="tpl-wire" style="--tw-accent:${accent}">
    <span class="tw-bar tw-nav"></span>
    ${rows.map(h => {
      if (h >= 5) return `<span class="tw-bar tw-hero"></span>`;
      if (h >= 4) return `<span class="tw-bar tw-hero tw-hero-sm"></span>`;
      if (h >= 3) return `<span class="tw-grid"><i></i><i></i><i></i></span>`;
      return `<span class="tw-bar tw-line"></span>`;
    }).join("")}
    <span class="tw-bar tw-cta"></span>
  </span>`;
}
/* Real rendered template previews (marketplace-style), memoised per template.
   Falls back to the wireframe bars if a template fails to render. */
const _tplThumbs = {};
function tplThumb(k){
  if (_tplThumbs[k]) return _tplThumbs[k];
  let html = "", pages = 1;
  try {
    const data = TEMPLATES[k].make();
    pages = data.pages.length;
    const home = data.pages.find(pg => !pg.slug) || data.pages[0];
    html = pageThumbHTML(data, home);
  } catch { html = ""; }
  _tplThumbs[k] = { html, pages };
  return _tplThumbs[k];
}
function tplGrid(picked, catFilter = "All", query = ""){
  const q = String(query || "").trim().toLowerCase();
  const entries = Object.entries(TEMPLATES).filter(([k, t]) => {
    if (k === "blank") return false;
    if (catFilter !== "All" && t.cat !== catFilter) return false;
    if (!q) return true;
    return `${t.name || ""} ${t.desc || ""} ${t.cat || ""}`.toLowerCase().includes(q);
  });
  const showBlank = catFilter === "All" && !q;
  const blankCard = showBlank
    ? `<div class="tpl-card tpl-blank${picked === "blank" ? " sel" : ""}" data-tpl="blank" role="button" tabindex="0" aria-pressed="${picked === "blank"}">
      <span class="tpl-prev tpl-prev-blank" aria-hidden="true">
        <span class="tpl-blank-glow"></span>
        <span class="tpl-blank-frame">
          ${lu("plus","lu24")}
          <strong>Blank canvas</strong>
          <em>Start from scratch</em>
        </span>
      </span>
      <span class="tpl-meta"><span class="tpl-name">Blank canvas</span><span class="tpl-desc">Start from nothing — just a page and your ideas.</span></span></div>`
    : "";
  if (!showBlank && !entries.length){
    return `<div class="tpl-empty">No templates match${q ? ` “${esc(query.trim())}”` : ""}.</div>`;
  }
  return blankCard + entries.map(([k, t]) => {
    const accent = tplAccent(k);
    const { html: thumb, pages } = tplThumb(k);
    const prev = thumb
      ? `<span class="tpl-prev tpl-prev-live" style="--tpl-accent:${accent}"><span class="tpl-mini">${thumb}</span><button type="button" class="tpl-use">Use template</button></span>`
      : `<span class="tpl-prev tpl-prev-wire" style="--tpl-accent:${accent}">${tplWireHTML(t.bars, accent)}</span>`;
    return `<div class="tpl-card${k === picked ? " sel" : ""}" data-tpl="${k}" role="button" tabindex="0" aria-pressed="${k === picked}">
      ${prev}
      <span class="tpl-meta"><span class="tpl-name">${t.name}<em>${t.cat || ""} · ${pages} page${pages === 1 ? "" : "s"}</em></span>
      <span class="tpl-desc">${t.desc}</span></span></div>`;
  }).join("");
}
function bindTplCards(root, onPick, onUse){
  const select = (b, useHit) => {
    onPick(b.dataset.tpl);
    root.querySelectorAll(".tpl-card").forEach(x => {
      x.classList.toggle("sel", x === b);
      x.setAttribute("aria-pressed", x === b ? "true" : "false");
    });
    if (useHit && onUse) onUse(b.dataset.tpl);
  };
  root.querySelectorAll(".tpl-card").forEach(b => {
    b.onclick = e => {
      e.preventDefault();
      e.stopPropagation();
      select(b, !!(e.target.closest && e.target.closest(".tpl-use")));
    };
    b.onkeydown = e => {
      if (e.key !== "Enter" && e.key !== " ") return;
      e.preventDefault();
      select(b, e.key === "Enter" && onUse ? false : false);
      if (e.key === "Enter" && onUse) onUse(b.dataset.tpl);
    };
  });
}
async function safeBrandAsset(file){
  if (!file) return "";
  const ok = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
  if (!ok.includes(file.type)) throw new Error("Use PNG, JPEG, WebP, or SVG only.");
  if (file.size > 512000) throw new Error("Image must be under 500 KB.");
  const data = await new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || ""));
    r.onerror = () => rej(new Error("Could not read the file."));
    r.readAsDataURL(file);
  });
  if (file.type === "image/svg+xml" && /<script|on\w+\s*=|javascript:|data:text\/html/i.test(data))
    throw new Error("That SVG contains disallowed content.");
  return data;
}
function tplCatBar(active = "All", { scroll = false } = {}){
  const cats = typeof TEMPLATE_CATEGORIES !== "undefined" ? TEMPLATE_CATEGORIES : ["All"];
  return `<div class="tpl-cats${scroll ? " tpl-cats--scroll" : ""}" role="tablist" aria-label="Template categories">${cats.map(c =>
    `<button type="button" class="tpl-cat${c === active ? " on" : ""}" data-cat="${c}" role="tab" aria-selected="${c === active ? "true" : "false"}">${c}</button>`).join("")}</div>`;
}
function neutralizeThumbHTML(html){
  return String(html || "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<\/?(button|a|form|input|select|textarea|iframe)\b/gi, m =>
      m.startsWith("</") ? "</span" : m.replace(/<(button|a|form|input|select|textarea|iframe)/i, "<span data-inert=\"$1\""))
    .replace(/ (href|action|autofocus|autoplay|contenteditable|tabindex|onclick)="[^"]*"/gi, "")
    .replace(/ data-e="[^"]*"/g, "");
}
function pageThumbHTML(data, home){
  if (!home) return "";
  try {
    const has = (home.blocks?.length || 0) + (data.shared?.header ? 1 : 0) + (data.shared?.footer ? 1 : 0);
    if (!has) return "";
    return neutralizeThumbHTML(renderPageHTML(data, { ...home, blocks: (home.blocks || []).slice(0, 4) }));
  } catch { return ""; }
}
/** Intentional empty preview — blank sites / failed thumbs should not look broken. */
function emptyThumbPlaceholderHTML({ accent, icon, label, hint } = {}){
  const mark = accent || "var(--brand,#355cff)";
  const ico = icon || "layout-template";
  const title = label || "Blank canvas";
  const sub = hint || "No sections yet";
  return `<div class="pc-thumb-empty" style="--thumb-accent:${esc(mark)}" aria-hidden="true">
    <span class="pc-thumb-empty-glow"></span>
    <span class="pc-thumb-empty-frame">
      <span class="pc-thumb-empty-ico">${lu(ico,"lu24")}</span>
      <strong>${esc(title)}</strong>
      <em>${esc(sub)}</em>
    </span>
  </div>`;
}
function siteThumbMarkup(p){
  const home = p?.data?.pages?.find(x => !x.slug) || p?.data?.pages?.[0];
  const thumb = pageThumbHTML(p?.data, home);
  if (thumb) return `<div class="mini">${thumb}</div>`;
  return emptyThumbPlaceholderHTML({
    accent: siteIconColorOf(p) || "#355cff",
    icon: siteIconOf(p) || "layout-template",
    label: "Blank canvas",
    hint: "Add sections to preview"
  });
}
function siteHasContent(p){
  if (!p?.data?.pages?.length) return false;
  const d = p.data;
  const blocks = d.pages.reduce((n, pg) => n + (pg.blocks?.length || 0), 0);
  const shared = (d.shared?.header ? 1 : 0) + (d.shared?.footer ? 1 : 0);
  return blocks + shared > 0;
}
function startOnboarding(){
  ob.step = 0;
  ob.legalAccepted = false;
  ob.name = me?.name || "";
  ob.username = me?.username || String(me?.email || "").split("@")[0] || "";
  ob.avatar = me?.avatar || (me?.email ? localStorage.getItem("bd_avatar_" + me.email) : "") || "";
  ob.enterFromBottom = false;
  show("onboard");
  preloadObPolicies();
  paintOnboard();
  hydrateObProfileDefaults();
}
async function hydrateObProfileDefaults(){
  try {
    const ident = await api.fetchProfileIdentity();
    if (ident?.name) {
      ob.name = ident.name;
      if (me) me.name = ident.name;
    }
    if (ident?.oauthProvider && me) me.oauthProvider = ident.oauthProvider;
    const nextAvatar = ident?.avatar || me?.avatar || "";
    if (nextAvatar) {
      ob.avatar = nextAvatar;
      if (me) {
        me.avatar = nextAvatar;
        try { localStorage.setItem("bd_avatar_" + me.email, nextAvatar); } catch {}
      }
      /* Seed profiles so OAuth photo sticks without requiring an upload. */
      if (ident?.fromOAuth) {
        try { await api.syncAvatar(nextAvatar); } catch {}
      }
    }
  } catch {}
  if (!ob.name && me?.name) ob.name = me.name;
  if (!ob.avatar && me?.avatar) ob.avatar = me.avatar;
  if ((!ob.username || ob.username === String(me?.email || "").split("@")[0]) && ob.name) {
    const slug = String(ob.name).toLowerCase().replace(/[^a-z0-9_]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 24);
    if (slug.length >= 3) ob.username = slug;
  }
  if (ob.step === 3) paintOnboard();
}
function obClearFieldErrors(box){
  if (!box) return;
  box.querySelectorAll(".field.is-invalid").forEach(f => f.classList.remove("is-invalid"));
  box.querySelectorAll(".ob-field-hint").forEach(h => h.remove());
  const banner = box.querySelector(".ob-err");
  if (banner) banner.remove();
}
function obFail(box, msg){
  let el = box.querySelector(".ob-err");
  if (!el){
    el = document.createElement("div");
    el.className = "error-msg ob-err show";
    const profileFields = box.querySelector(".ob-profile-fields");
    const profileForm = box.querySelector(".ob-profile-form");
    const create = box.querySelector(".ob-create, .ob-welcome");
    if (profileFields) profileFields.prepend(el);
    else if (profileForm) profileForm.prepend(el);
    else if (create) create.appendChild(el);
    else {
      /* Legal sheets: keep the banner with the CTA, never float it alone. */
      const actions = box.querySelector(".ob-split-actions");
      if (actions) actions.before(el);
      else box.appendChild(el);
    }
  }
  el.textContent = msg;
  el.classList.add("show");
}
function obFieldFail(box, inputSel, msg){
  obClearFieldErrors(box);
  const input = box.querySelector(inputSel);
  const field = input?.closest(".field");
  if (!field || !input){
    const profileFields = box.querySelector(".ob-profile-fields");
    if (profileFields){
      const el = document.createElement("div");
      el.className = "ob-field-hint";
      el.textContent = msg;
      profileFields.prepend(el);
      return;
    }
    return obFail(box, msg);
  }
  field.classList.add("is-invalid");
  const hint = document.createElement("div");
  hint.className = "ob-field-hint";
  hint.id = input.id ? input.id + "-hint" : "";
  hint.textContent = msg;
  field.appendChild(hint);
  input.setAttribute("aria-invalid", "true");
  if (hint.id) input.setAttribute("aria-describedby", hint.id);
  input.focus();
}
function paintObDots(){
  const host = $("#ob-dots");
  if (!host) return;
  host.innerHTML = Array.from({ length: OB_STEPS }, (_, i) =>
    `<span class="${i === ob.step ? "on" : ""}" aria-current="${i === ob.step ? "step" : "false"}"></span>`
  ).join("");
}
function obLegalSheet({ title, lead, bodyHtml, nextLabel, stepLabel }){
  const anim = ob.enterFromBottom ? "ob-enter-up" : "ob-crossfade";
  if (ob.enterFromBottom) ob.enterFromBottom = false;
  const loading = !bodyHtml;
  return `
    <div class="ob-sheet ob-sheet--legal ${anim}">
      <div class="ob-split">
        <aside class="ob-split-left ob-split-left--legal">
          <div class="ob-split-brand">
            <svg class="bd-mark ob-split-logo" viewBox="0 0 512 512" width="28" height="28" aria-hidden="true" focusable="false"><defs><linearGradient id="bd-mark-g1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#a8c4ff"/><stop offset="45%" stop-color="#5b8cff"/><stop offset="100%" stop-color="#355cff"/></linearGradient><linearGradient id="bd-mark-g2" x1="100%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#dbeafe"/><stop offset="40%" stop-color="#6b93ff"/><stop offset="100%" stop-color="#1e40af"/></linearGradient><linearGradient id="bd-mark-g3" x1="50%" y1="0%" x2="50%" y2="100%"><stop offset="0%" stop-color="#8eb0ff"/><stop offset="50%" stop-color="#355cff"/><stop offset="100%" stop-color="#1e3a8a"/></linearGradient></defs><g transform="translate(-179.22 -136.73) scale(1.1483)"><path d="M 371 180 A 90 90 0 1 1 309 180" fill="none" stroke="url(#bd-mark-g1)" stroke-width="24" stroke-linecap="round"/><path d="M 275 283 A 90 90 0 1 1 197 328" fill="none" stroke="url(#bd-mark-g2)" stroke-width="24" stroke-linecap="round"/><path d="M 483 328 A 90 90 0 1 1 405 463" fill="none" stroke="url(#bd-mark-g3)" stroke-width="24" stroke-linecap="round"/></g></svg>
            <span>${esc(stepLabel || "Onboarding")}</span>
          </div>
          <div class="ob-split-copy">
            <h1>${esc(title)}</h1>
            <p class="sub">${esc(lead)}</p>
            <div class="ob-split-actions">
              <button type="button" class="btn ob-sheet-cta ob-sheet-cta--soft" id="ob-next" data-mode="read" hidden>
                <span>${esc(nextLabel)}</span>
                <i data-lucide="chevron-down" class="ob-welcome-arrow" aria-hidden="true"></i>
              </button>
            </div>
          </div>
        </aside>
        <div class="ob-split-right ob-split-right--policy">
          <div class="ob-policy-pane${loading ? " is-loading" : ""}" id="ob-policy-pane">
            <div class="ob-policy-scroll ob-doc" id="ob-policy-scroll" tabindex="0" role="document" aria-label="${esc(title)}">
              ${bodyHtml || `<p class="ob-doc-loading">Loading ${esc(title)}…</p>`}
            </div>
          </div>
        </div>
      </div>
    </div>`;
}
function obPolicyAtEnd(scroll){
  if (!scroll) return true;
  return scroll.scrollHeight <= scroll.clientHeight + 12
    || scroll.scrollTop + scroll.clientHeight >= scroll.scrollHeight - 48;
}
function wireObLegalScroll(box, { doneLabel, onDone }){
  const scroll = box.querySelector("#ob-policy-scroll");
  const btn = box.querySelector("#ob-next");
  const label = btn?.querySelector("span");
  if (!scroll || !btn || !label) return;
  let reachedEnd = false;

  const setIcon = (name) => {
    const icon = btn.querySelector("[data-lucide], svg.lucide");
    if (!icon) return;
    const next = document.createElement("i");
    next.setAttribute("data-lucide", name);
    next.className = "ob-welcome-arrow";
    next.setAttribute("aria-hidden", "true");
    icon.replaceWith(next);
    icons();
  };

  const sync = () => {
    const end = obPolicyAtEnd(scroll);
    if (end) reachedEnd = true;
    const mode = end ? "done" : (reachedEnd ? "resume" : "read");
    btn.dataset.mode = mode;
    btn.classList.toggle("ob-sheet-cta--soft", mode !== "done");
    btn.classList.toggle("ob-sheet-cta--solid", mode === "done");
    if (mode === "done") {
      btn.hidden = false;
      label.textContent = doneLabel;
      setIcon("arrow-right");
    } else if (mode === "resume") {
      btn.hidden = false;
      label.textContent = "Continue";
      setIcon("chevron-down");
    } else {
      btn.hidden = false;
      label.textContent = "Keep reading";
      setIcon("chevron-down");
    }
  };

  scroll.addEventListener("scroll", sync, { passive: true });
  btn.onclick = async () => {
    const mode = btn.dataset.mode || "read";
    if (mode === "done") {
      await onDone();
      return;
    }
    if (mode === "resume") {
      scroll.scrollTo({ top: scroll.scrollHeight, behavior: "smooth" });
      return;
    }
    const y = Math.max(360, scroll.clientHeight * 0.82);
    scroll.scrollBy({ top: y, behavior: "smooth" });
  };
  requestAnimationFrame(sync);
  window.setTimeout(sync, 80);
}
function fillObPolicyScroll(box, kind, step){
  const apply = (html) => {
    if (ob.step !== step) return;
    const scroll = box.querySelector("#ob-policy-scroll");
    const pane = box.querySelector("#ob-policy-pane");
    if (scroll) scroll.innerHTML = html;
    pane?.classList.remove("is-loading");
    scroll?.dispatchEvent(new Event("scroll"));
  };
  if (_obPolicyCache[kind]) {
    apply(_obPolicyCache[kind]);
    return;
  }
  obPolicyHtml(kind).then(html => apply(html)).catch(() => {
    apply(`<p class="ob-doc-loading">Could not load the document. Please refresh and try again.</p>`);
  });
}
function paintOnboard(){
  paintObDots();
  const box = $("#ob-step");
  if (!box) return;
  const stage = document.querySelector(".ob-stage");
  const view = $("#view-onboard");
  stage?.classList.remove("ob-stage--welcome");
  view?.classList.remove("ob-on-welcome", "ob-on-sheet");
  if (ob.step === 0 || ob.step === 4) view?.classList.add("ob-on-welcome");
  else if (ob.step >= 1 && ob.step <= 3) view?.classList.add("ob-on-sheet");

  if (ob.step === 0){
    box.innerHTML = `
      <div class="ob-welcome">
        <p class="ob-welcome-eyebrow ob-fade-item" style="--ob-fade-delay:0ms">Browns Digital</p>
        <h1 class="ob-fade-item" style="--ob-fade-delay:90ms">Welcome to Brown&rsquo;s Digital Studio</h1>
        <p class="sub ob-fade-item" style="--ob-fade-delay:180ms">When you&rsquo;re ready, start onboarding to set up your profile and first site.</p>
        <button type="button" class="btn ob-welcome-cta ob-fade-item" style="--ob-fade-delay:280ms" id="ob-next">
          <span>Start onboarding</span>
          <i data-lucide="arrow-right" class="ob-welcome-arrow" aria-hidden="true"></i>
        </button>
      </div>`;
    stage?.classList.add("ob-stage--welcome");
    box.querySelector("#ob-next").onclick = () => {
      ob.enterFromBottom = true;
      ob.step = 1;
      paintOnboard();
    };
  }

  if (ob.step === 1){
    box.innerHTML = obLegalSheet({
      title: "Terms of Service",
      lead: "Please review the rules that apply to your Browns Digital account and Studio.",
      bodyHtml: _obPolicyCache.terms || "",
      nextLabel: "Continue",
      stepLabel: "Step 1 of 4"
    });
    fillObPolicyScroll(box, "terms", 1);
    wireObLegalScroll(box, {
      doneLabel: "Continue",
      onDone: () => { ob.step = 2; paintOnboard(); }
    });
  }

  if (ob.step === 2){
    box.innerHTML = obLegalSheet({
      title: "Privacy Policy",
      lead: "Here's what data we collect, how we use it, and your rights.",
      bodyHtml: _obPolicyCache.privacy || "",
      nextLabel: "Agree and continue",
      stepLabel: "Step 2 of 4"
    });
    fillObPolicyScroll(box, "privacy", 2);
    wireObLegalScroll(box, {
      doneLabel: "Agree and continue",
      onDone: async () => {
        const btn = box.querySelector("#ob-next");
        btn.disabled = true;
        const label = btn.querySelector("span");
        if (label) label.textContent = "Saving…";
        try {
          await api.acceptLegalAgreement(LEGAL_AGREEMENT_VERSION);
          ob.legalAccepted = true;
          ob.step = 3;
          paintOnboard();
        } catch (e) {
          btn.disabled = false;
          if (label) label.textContent = "Agree and continue";
          obFail(box, humanErr(e, "We couldn’t save your agreement. Please try again."));
        }
      }
    });
  }

  if (ob.step === 3){
    const initial = (ob.name || ob.username || me?.email || "?").trim().charAt(0).toUpperCase() || "?";
    const hasPhoto = !!ob.avatar;
    const provider = String(me?.oauthProvider || "").toLowerCase();
    const providerLabel = provider === "google" ? "Google" : provider === "github" ? "GitHub" : "";
    const avatarImg = hasPhoto
      ? `<img src="${esc(ob.avatar)}" alt="" referrerpolicy="no-referrer">`
      : `<span id="ob-avatar-initial">${esc(initial)}</span>`;
    const photoHint = hasPhoto && providerLabel
      ? `Using your ${providerLabel} photo by default. Change it anytime if you want.`
      : hasPhoto
        ? "Change anytime from here or Account."
        : "Optional — add a photo now, or skip and set one later from Account.";
    const changeLabel = "Click to change";
    const avatarOverlay = `<span class="ob-avatar-overlay" aria-hidden="true">${lu("camera")}</span>`;
    box.innerHTML = `
      <div class="ob-sheet ob-sheet--legal ob-crossfade">
        <div class="ob-split">
          <aside class="ob-split-left ob-split-left--legal">
            <div class="ob-split-brand">
              <svg class="bd-mark ob-split-logo" viewBox="0 0 512 512" width="28" height="28" aria-hidden="true" focusable="false"><defs><linearGradient id="bd-mark-g1" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="#a8c4ff"/><stop offset="45%" stop-color="#5b8cff"/><stop offset="100%" stop-color="#355cff"/></linearGradient><linearGradient id="bd-mark-g2" x1="100%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#dbeafe"/><stop offset="40%" stop-color="#6b93ff"/><stop offset="100%" stop-color="#1e40af"/></linearGradient><linearGradient id="bd-mark-g3" x1="50%" y1="0%" x2="50%" y2="100%"><stop offset="0%" stop-color="#8eb0ff"/><stop offset="50%" stop-color="#355cff"/><stop offset="100%" stop-color="#1e3a8a"/></linearGradient></defs><g transform="translate(-179.22 -136.73) scale(1.1483)"><path d="M 371 180 A 90 90 0 1 1 309 180" fill="none" stroke="url(#bd-mark-g1)" stroke-width="24" stroke-linecap="round"/><path d="M 275 283 A 90 90 0 1 1 197 328" fill="none" stroke="url(#bd-mark-g2)" stroke-width="24" stroke-linecap="round"/><path d="M 483 328 A 90 90 0 1 1 405 463" fill="none" stroke="url(#bd-mark-g3)" stroke-width="24" stroke-linecap="round"/></g></svg>
              <span>Step 3 of 4</span>
            </div>
            <div class="ob-split-copy">
              <h1>Set up your profile</h1>
              <p class="sub">Personalise how you appear in Studio. ${esc(photoHint)} Click Next to save and continue.</p>
            </div>
            <div class="ob-split-actions">
              <button type="button" class="btn ob-sheet-cta" id="ob-next">
                <span>Next</span>
                <i data-lucide="arrow-right" class="ob-welcome-arrow" aria-hidden="true"></i>
              </button>
            </div>
          </aside>
          <div class="ob-split-right ob-split-right--form">
            <div class="ob-profile-form ob-profile-form--row">
              <div class="ob-profile-photo">
                <button type="button" class="ob-avatar" id="ob-avatar" aria-label="Change profile photo">${avatarImg}${avatarOverlay}</button>
                <button type="button" class="ob-avatar-change" id="ob-avatar-btn">${esc(changeLabel)}</button>
                <input type="file" id="ob-avatar-file" accept="image/jpeg,image/png,image/webp,image/gif" hidden>
              </div>
              <div class="ob-profile-fields">
                <div class="field"><label for="ob-name">Display name</label><input id="ob-name" value="${esc(ob.name)}" placeholder="Alex Brown" autocomplete="name"></div>
                <div class="field"><label for="ob-username">Username</label>
                  <div class="ob-at-wrap"><span class="at-pre">@</span><input id="ob-username" value="${esc(ob.username)}" placeholder="handle" maxlength="32" autocomplete="username"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>`;
    const openAvatar = () => {
      const file = box.querySelector("#ob-avatar-file");
      box.querySelector("#ob-avatar")?.blur();
      box.querySelector("#ob-avatar-btn")?.blur();
      file?.click();
    };
    box.querySelector("#ob-avatar-btn").onclick = openAvatar;
    box.querySelector("#ob-avatar").onclick = openAvatar;
    box.querySelector("#ob-avatar-file").onchange = e => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 2 * 1024 * 1024) return obFail(box, "Image must be under 2 MB.");
      if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) return obFail(box, "Only JPEG, PNG, WebP, or GIF allowed.");
      const reader = new FileReader();
      reader.onload = ev => {
        const src = String(ev.target.result || "");
        const testImg = new Image();
        testImg.onload = async () => {
          ob.avatar = src;
          if (me?.email) {
            localStorage.setItem("bd_avatar_" + me.email, src);
            me.avatar = src;
            try { await api.syncAvatar(src); } catch {}
          }
          const av = box.querySelector("#ob-avatar");
          if (av) {
            av.innerHTML = `<img src="${esc(src)}" alt="" referrerpolicy="no-referrer"><span class="ob-avatar-overlay" aria-hidden="true">${lu("camera")}</span>`;
            av.setAttribute("aria-label", "Change profile photo");
            icons();
          }
        };
        testImg.onerror = () => obFail(box, "Could not read that image.");
        testImg.src = src;
      };
      reader.readAsDataURL(file);
    };
    box.querySelector("#ob-next").onclick = async () => {
      const nameInput = box.querySelector("#ob-name");
      const userInput = box.querySelector("#ob-username");
      const name = nameInput.value.trim();
      const uv = validUsername(userInput.value);
      obClearFieldErrors(box);
      nameInput.removeAttribute("aria-invalid");
      userInput.removeAttribute("aria-invalid");
      if (name.length < 2) return obFieldFail(box, "#ob-name", "Add a display name");
      if (!uv.ok) return obFieldFail(box, "#ob-username", uv.why);
      const btn = box.querySelector("#ob-next");
      btn.disabled = true;
      const label = btn.querySelector("span");
      if (label) label.textContent = "Saving…";
      try {
        if (name !== me.name) {
          await api.updateName(name);
          me.name = name;
        }
        if (uv.username !== (me.username || "")) {
          await api.updateUsername(uv.username);
          me.username = uv.username;
        }
        if (ob.avatar && me) {
          try { await api.syncAvatar(ob.avatar); } catch {}
        }
        ob.name = name;
        ob.username = uv.username;
        paintSideUser();
        ob.step = 4;
        paintOnboard();
      } catch (e) {
        btn.disabled = false;
        if (label) label.textContent = "Next";
        obFail(box, humanErr(e, "Could not save your profile."));
      }
    };
    const clearNameErr = () => {
      const field = box.querySelector("#ob-name")?.closest(".field");
      if (!field?.classList.contains("is-invalid")) return;
      field.classList.remove("is-invalid");
      field.querySelector(".ob-field-hint")?.remove();
      box.querySelector("#ob-name")?.removeAttribute("aria-invalid");
    };
    const clearUserErr = () => {
      const field = box.querySelector("#ob-username")?.closest(".field");
      if (!field?.classList.contains("is-invalid")) return;
      field.classList.remove("is-invalid");
      field.querySelector(".ob-field-hint")?.remove();
      box.querySelector("#ob-username")?.removeAttribute("aria-invalid");
    };
    box.querySelector("#ob-name")?.addEventListener("input", clearNameErr);
    box.querySelector("#ob-username")?.addEventListener("input", clearUserErr);
    box.querySelector("#ob-name")?.focus();
  }

  if (ob.step === 4){
    const sites = (projects || []).slice();
    const listHtml = sites.length
      ? `<div class="ob-site-list" id="ob-site-list">${sites.map(p => `
          <button type="button" class="ob-site-row" data-id="${esc(p.id)}">
            <span class="ob-site-ico">${lu("layout-template")}</span>
            <span class="ob-site-meta"><strong>${esc(p.name || "Untitled site")}</strong><span>${esc(p.id)}</span></span>
            <span class="ob-chev">${lu("chevron-right","lu14")}</span>
          </button>`).join("")}</div>`
      : `<div class="ob-site-list" id="ob-site-list" hidden></div>`;
    box.innerHTML = `
      <div class="ob-create">
        <h1 class="ob-fade-item" style="--ob-fade-delay:0ms">Next, create a site</h1>
        <p class="sub ob-fade-item" style="--ob-fade-delay:90ms">Your account is ready. Create a site to get started, or explore the dashboard first.</p>
        <button type="button" class="btn ob-welcome-cta ob-fade-item" style="--ob-fade-delay:180ms" id="ob-create">
          <span>Create a site</span>
          <i data-lucide="arrow-right" class="ob-welcome-arrow" aria-hidden="true"></i>
        </button>
        <button type="button" class="ob-create-look ob-fade-item" style="--ob-fade-delay:260ms" id="ob-look">I&rsquo;m looking around</button>
        ${listHtml}
      </div>`;
    box.querySelector("#ob-create").onclick = () => {
      enterApp("dashboard");
      newSiteFlow();
    };
    box.querySelector("#ob-look").onclick = () => enterApp("dashboard");
    box.querySelectorAll(".ob-site-row").forEach(row => {
      row.onclick = () => {
        const id = row.dataset.id;
        enterApp("dashboard");
        openBuilderEdit(id);
      };
    });
  }

  icons();
}

/* ================================ SHELL ================================ */
const PAGES = {
  dashboard:  { label:"Console",  render: pageDashboard, wide: true },
  builder:    { label:"Sites", render: pageSites }, /* legacy key — redirects to sites in go() */
  "builder-edit": { label:"Canvas", render: pageBuilder, full: true },
  agent:      { label:"Pulse",  render: pageAgent, wide: true },
  health:     { label:"Health",render: pageHealth },
  team:       { label:"Crew",       render: pageTeam },
  settings:   { label:"Settings",   render: pageSettings },
  sites:      { label:"Sites",  render: pageSites },
  account:    { label:"Account",    render: pageAccount },
  activity:   { label:"Activity", render: pageActivity },
};
/* Clean path routes (History API) — longest match first when parsing. */
const PAGE_PATHS = {
  dashboard: "/console",
  "builder-edit": "/edit",
  agent: "/agent",
  health: "/health",
  team: "/crew",
  settings: "/settings",
  sites: "/sites",
  account: "/account",
  activity: "/activity",
};
const PATH_LOOKUP = Object.entries(PAGE_PATHS).sort((a, b) => b[1].length - a[1].length);
const pageToPath = p => PAGE_PATHS[p] || (p === "builder" ? "/sites" : "/console");
const pathToPage = path => {
  const p = (path || "/").replace(/\/$/, "") || "/";
  if (p === "/team" || p === "/cowork") return "team";
  if (p === "/builder" || p === "/design") return "sites"; /* Design hub merged into Sites */
  if (p === "/publishing") return "dashboard"; /* page removed — publish actions live on Console */
  if (p === "/revisions") return "builder-edit"; /* page removed — history lives on canvas */
  if (p === "/dashboard") return "dashboard"; /* legacy → rewritten to /console by go() */
  if (p === "/admin" || p.startsWith("/admin/") || p === "/developer") return "dashboard";
  for (const [page, route] of PATH_LOOKUP) if (p === route) return page;
  return "dashboard";
};
function consumeLegacyHashRoute(){
  const h = location.hash;
  if (!h.startsWith("#/")) return null;
  const legacy = h.replace(/^#\//, "").replace(/\//g, "-");
  const page = legacy in PAGES ? legacy : "dashboard";
  history.replaceState({ page }, "", pageToPath(page));
  return page;
}
function enterApp(start){
  localStorage.setItem(obKey(), "1");
  show("app");
  document.body.classList.remove("pin-just-unlocked");
  if (QA_MODE){ const b = $("#qa-banner"); if (b) b.hidden = false; }
  paintSideUser(); paintSitePick(); refreshActivity();
  const fromHash = consumeLegacyHashRoute();
  const fromPath = pathToPage(location.pathname);
  go(start || fromHash || (fromPath in PAGES ? fromPath : "dashboard"), { replace: true });
}
async function openBuilderEdit(id, pageId){
  if (id){ activeId = id; persistActive(); }
  try { await ensureProjectLoaded(activeId); }
  catch (e) { toast(humanErr(e, "Could not open this site."), { kind: "error" }); return; }
  if (!active() || !Array.isArray(active().data?.pages)) return go("sites");
  let want = pageId;
  if (!want){ try { want = localStorage.getItem("bd_last_page_" + activeId); } catch {} }
  if (want && active().data.pages.some(x => x.id === want)) curPageId = want;
  go("builder-edit");
}
async function go(p, { replace = false } = {}){
  if (p === "account") {
    /* Demo/QA keep Account in-app — /my handoff has no usable local session there. */
    if (!DEMO && !QA_MODE && typeof BD_ACCOUNT !== "undefined") { BD_ACCOUNT.go(); return; }
  }
  if (page === "builder-edit" && p !== "builder-edit" && dirty) await saveNow(true);
  await flushBrandTokenSave();
  if (p === "builder") p = "sites"; /* Design merged into Sites */
  if (!(p in PAGES)) p = "dashboard";
  if (!active() && ["builder","builder-edit","health","team","settings"].includes(p)) p = "sites";
  page = p;
  const url = pageToPath(p);
  if (replace) history.replaceState({ page: p }, "", url);
  else history.pushState({ page: p }, "", url);
  document.title = PAGES[p].label + " | Browns Digital";
  const navOn = b => b.dataset.page === p
    || (p === "builder-edit" && b.dataset.page === "sites");
  $$(".side-nav button").forEach(b => b.classList.toggle("on", navOn(b)));
  $("#crumb").textContent = PAGES[p].label + (active() && !["sites","account","agent"].includes(p) ? " · " + active().name : "");
  document.body.classList.toggle("builder-immersive", p === "builder-edit");
  if (p !== "builder-edit"){ document.body.classList.remove("builder-preview"); document.body.classList.remove("builder-live"); previewMode = false; liveEdit = false; }
  const el = $("#page");
  const pill = $("#pill-notch");
  const mainEl = document.querySelector("#view-app .main");
  if (pill) pill.remove();
  el.className = "page"
    + (PAGES[p].full ? " full" : "")
    + (PAGES[p].wide ? " page--wide" : "");
  el.innerHTML = "";
  if (p !== "builder-edit" && pill) el.prepend(pill);
  else if (pill && mainEl && pill.parentElement !== mainEl) mainEl.insertBefore(pill, el);
  if (p !== "builder-edit") el.insertAdjacentHTML("beforeend", pageSkeleton());
  const shown = p;
  const needsSiteBody = p === "dashboard" || p === "builder-edit" || p === "health" || p === "settings";
  Promise.resolve()
    .then(async () => {
      if (needsSiteBody && activeId) await ensureProjectLoaded(activeId);
      if (page !== shown) return;
      return PAGES[p].render(el);
    })
    .then(() => {
      if (page !== shown) return;
      dropPageSkeleton(el);
      playPageEnter(el, p);
    })
    .catch(err => {
      console.error("page " + p, err);
      if (err && err.name === "AbortError") return;
      if (page !== shown) return;
      dropPageSkeleton(el);
      if (el.querySelector(".card, .empty-mesh, .dash-card-head")) return;
      const msg = humanErr(err, "This page didn’t load. Try again in a moment.");
      el.insertAdjacentHTML("beforeend", `<div class="card"><div class="empty-mesh mesh error"><div class="big">Couldn’t load this page</div><p class="note">${esc(msg)}</p><button type="button" class="btn btn-dark btn-sm" id="page-retry">Try again</button></div></div>`);
      const btn = el.querySelector("#page-retry");
      if (btn) btn.onclick = () => go(shown, { replace: true });
    });
  const acctScroll = sessionStorage.getItem("bd_acct_scroll");
  if (acctScroll){
    sessionStorage.removeItem("bd_acct_scroll");
    requestAnimationFrame(() => document.getElementById("acct-" + acctScroll)?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }
  paintLiveInd(); paintSitePick(); icons();
  syncTopbarPill();
  document.body.classList.toggle("in-builder-edit", p === "builder-edit");
  $$("#m-tabbar [data-page]").forEach(b => b.classList.toggle("on", navOn(b)));
  document.body.classList.remove("side-open");
  syncSideToggleUi();
  window.scrollTo(0, 0);
  const pageEl = $("#page");
  if (pageEl) pageEl.scrollTop = 0;
}
window.addEventListener("popstate", e => {
  if (!$("#view-app").classList.contains("active")) return;
  const p = e.state?.page || pathToPage(location.pathname);
  if (p in PAGES && p !== page) go(p, { replace: true });
});
$$(".side-nav button").forEach(b => b.onclick = () => go(b.dataset.page));
/* mobile tab bar */
$$("#m-tabbar [data-page]").forEach(b => b.onclick = () => go(b.dataset.page));
const mMenu = $("#m-tab-menu");
if (mMenu) mMenu.onclick = () => {
  document.body.classList.toggle("side-open");
  syncSideToggleUi();
};
function syncSideToggleUi(){
  const btn = $("#side-toggle");
  if (!btn) return;
  const mobile = window.innerWidth <= 900;
  if (mobile){
    const open = document.body.classList.contains("side-open");
    btn.setAttribute("aria-expanded", open ? "true" : "false");
    btn.title = open ? "Close sidebar" : "Open sidebar";
    btn.setAttribute("aria-label", open ? "Close sidebar" : "Open sidebar");
  } else {
    const collapsed = document.body.classList.contains("side-collapsed");
    btn.setAttribute("aria-expanded", collapsed ? "false" : "true");
    btn.title = collapsed ? "Show sidebar" : "Hide sidebar";
    btn.setAttribute("aria-label", collapsed ? "Show sidebar" : "Hide sidebar");
  }
}
function applySideCollapsedPref(){
  if (window.innerWidth <= 900) return;
  try {
    if (localStorage.getItem("bd_side_collapsed") === "1") document.body.classList.add("side-collapsed");
    else if (localStorage.getItem("bd_side_collapsed") === "0") document.body.classList.remove("side-collapsed");
  } catch {}
  syncSideToggleUi();
}
$("#side-toggle").onclick = () => {
  if (window.innerWidth <= 900) document.body.classList.toggle("side-open");
  else {
    document.body.classList.toggle("side-collapsed");
    try {
      localStorage.setItem("bd_side_collapsed", document.body.classList.contains("side-collapsed") ? "1" : "0");
    } catch {}
  }
  syncSideToggleUi();
};
window.addEventListener("resize", () => {
  if (window.innerWidth > 900) document.body.classList.remove("side-open");
  syncSideToggleUi();
});
applySideCollapsedPref();
/* Browns Digital UI condensing nav — scroll and the top bar becomes a floating pill. */
let _pillRaf = null;
function syncTopbarPill(){
  const tb = document.querySelector(".pill-notch,.topbar");
  if (!tb) return;
  const y = Math.max(window.scrollY || 0, document.documentElement.scrollTop || 0, $("#page")?.scrollTop || 0);
  tb.classList.toggle("is-scrolled", y > 8 && !document.body.classList.contains("builder-immersive"));
}
window.addEventListener("scroll", () => {
  if (_pillRaf) return;
  _pillRaf = requestAnimationFrame(() => { _pillRaf = null; syncTopbarPill(); });
}, { passive: true });
const _pageScroll = $("#page");
if (_pageScroll) _pageScroll.addEventListener("scroll", () => syncTopbarPill(), { passive: true });

function handleOf(person){
  if (!person) return "you";
  const u = String(person.username || "").replace(/^@/, "").trim();
  if (u) return u;
  return String(person.email || "you").split("@")[0] || "you";
}
/** Display name from the signed-in profile — never a raw email when a name exists. */
function displayNameOf(person){
  if (!person) return "You";
  const n = String(person.name || "").trim();
  if (n && !n.includes("@")) return n;
  return handleOf(person);
}
function avatarUrlOf(person){
  if (!person) return null;
  const direct = person.avatar || null;
  if (direct) return direct;
  if (person.email){
    try {
      return localStorage.getItem("bd_avatar_" + person.email) || null;
    } catch {}
  }
  return null;
}
/** Safe CSS url() for style attrs (data: URLs break if double-quoted inside style="..."). */
function cssBgUrl(url){
  const safe = String(url || "").replace(/\\/g, "\\\\").replace(/'/g, "\\'");
  return `url('${safe}')`;
}
function avatarHtml(url, label, sizePx, extraClass){
  const initial = String(label || "?").replace(/^@/, "").trim().charAt(0).toUpperCase() || "?";
  const size = sizePx ? `width:${sizePx}px;height:${sizePx}px;` : "";
  const cls = extraClass ? ` ${extraClass}` : "";
  if (url){
    return `<span class="avatar${cls}" style="${size}" title="${esc(label || "")}"><img src="${esc(url)}" alt="" referrerpolicy="no-referrer"></span>`;
  }
  const fs = sizePx ? `font-size:${Math.max(10, Math.round(sizePx * 0.4))}px;` : "";
  return `<span class="avatar${cls}" style="${size}${fs}" title="${esc(label || "")}">${esc(initial)}</span>`;
}

function paintSideUser(){
  if (!me) return;
  const nameEl = $("#user-name");
  const planEl = $("#user-plan");
  if (nameEl) nameEl.textContent = displayNameOf(me);
  if (planEl) planEl.textContent = bdPlanDisplayLabel(plan) + (admin ? " · Owner" : "");
  const credEl = $("#user-credits");
  if (credEl) credEl.hidden = true;
  const avatarEl = $("#user-avatar");
  if (!avatarEl) return;
  const savedAvatar = avatarUrlOf(me);
  if (savedAvatar) {
    avatarEl.textContent = "";
    avatarEl.style.backgroundImage = "";
    avatarEl.innerHTML = `<img src="${esc(savedAvatar)}" alt="" referrerpolicy="no-referrer">`;
  } else {
    avatarEl.innerHTML = "";
    avatarEl.textContent = displayNameOf(me).charAt(0).toUpperCase() || "?";
    avatarEl.style.backgroundImage = "";
  }
}
async function hydrateAvatar(){
  if (!me?.email) return;
  try {
    const ident = await api.fetchProfileIdentity();
    if (ident?.name) me.name = ident.name;
    if (ident?.oauthProvider) me.oauthProvider = ident.oauthProvider;
    if (ident?.avatar) {
      me.avatar = ident.avatar;
      try { localStorage.setItem("bd_avatar_" + me.email, ident.avatar); } catch {}
      if (ident.fromOAuth) {
        try { await api.syncAvatar(ident.avatar); } catch {}
      }
    } else if (me.avatar) {
      try { localStorage.setItem("bd_avatar_" + me.email, me.avatar); } catch {}
      try { await api.syncAvatar(me.avatar); } catch {}
    }
  } catch {}
  paintSideUser();
}
async function hydrateBuilderPrefs(){
  try {
    const remote = await api.fetchBuilderPrefs();
    if (!remote || typeof remote !== "object") return;
    const merged = Object.assign({}, bprefs(), remote);
    localStorage.setItem("bd_builder_prefs", JSON.stringify(merged));
    if (merged.tourDone === "yes") localStorage.setItem("bd_tour_done", "1");
  } catch {}
}
function collectDashWidgetsFromLocal(){
  const out = {};
  try {
    for (let i = 0; i < localStorage.length; i++){
      const k = localStorage.key(i);
      if (!k || !k.startsWith("bd_dash_widgets_")) continue;
      out[k.slice("bd_dash_widgets_".length)] = JSON.parse(localStorage.getItem(k) || "null");
    }
  } catch {}
  return out;
}
function collectLocalAccountPrefs(){
  if (!me?.email) return null;
  return {
    username: me.username || "",
    privacy: JSON.parse(localStorage.getItem("bd_prefs_" + me.email) || "{}"),
    ui: JSON.parse(localStorage.getItem("bd_uiprefs_" + me.email) || "{}"),
    notifications: JSON.parse(localStorage.getItem("bd_notif_" + me.email) || "{}"),
    keybinds: JSON.parse(localStorage.getItem("bd_keybinds") || "{}"),
    pinnedSites: JSON.parse(localStorage.getItem(PIN_KEY) || "[]"),
    activeProjectId: localStorage.getItem("bd_active_" + me.email) || "",
    dashWidgets: collectDashWidgetsFromLocal()
  };
}
function syncAccountPrefsNow(){
  if (DEMO || !me?.email) return;
  const prefs = collectLocalAccountPrefs();
  if (prefs) api.syncAccountPrefs(prefs);
}
function uiPrefs(){
  if (!me?.email) return {};
  try { return JSON.parse(localStorage.getItem("bd_uiprefs_" + me.email) || "{}") || {}; }
  catch { return {}; }
}
function setUiPref(key, val){
  if (!me?.email) return;
  const next = { ...uiPrefs(), [key]: val };
  try { localStorage.setItem("bd_uiprefs_" + me.email, JSON.stringify(next)); } catch {}
  syncAccountPrefsNow();
}
function motionEnabled(){
  const pref = uiPrefs().animations;
  if (pref === false) return false;
  if (pref === true) return true;
  return !window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
}
function applyMotionPref(){
  const on = motionEnabled();
  document.documentElement.classList.toggle("bd-motion-off", !on);
  document.documentElement.classList.toggle("bd-motion-on", on);
}
function playPageEnter(el, p){
  if (!el || p === "builder-edit" || !motionEnabled()) return;
  el.classList.remove("is-entering");
  const nodes = [...el.querySelectorAll(".reveal")];
  nodes.forEach(n => {
    n.style.animation = "none";
    n.style.animationDelay = "";
  });
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      el.classList.add("is-entering");
      nodes.forEach((n, i) => {
        n.style.animation = "";
        n.style.animationDelay = (Math.min(i, 10) * 0.05) + "s";
      });
      window.setTimeout(() => {
        el.classList.remove("is-entering");
        el.querySelectorAll(".reveal").forEach(n => {
          n.style.animationDelay = "";
        });
      }, 900);
    });
  });
}
async function hydrateAccountPrefs(){
  if (!me?.email) return;
  try {
    const remote = await api.fetchAccountPrefs();
    if (!remote || typeof remote !== "object") return;
    if (remote.username && !me.username) me.username = String(remote.username).replace(/[^a-z0-9_]/gi, "").slice(0, 32);
    if (remote.privacy) localStorage.setItem("bd_prefs_" + me.email, JSON.stringify(remote.privacy));
    if (remote.ui) localStorage.setItem("bd_uiprefs_" + me.email, JSON.stringify(remote.ui));
    if (remote.notifications) localStorage.setItem("bd_notif_" + me.email, JSON.stringify(remote.notifications));
    if (remote.keybinds) localStorage.setItem("bd_keybinds", JSON.stringify(remote.keybinds));
    if (Array.isArray(remote.pinnedSites)) localStorage.setItem(PIN_KEY, JSON.stringify(remote.pinnedSites));
    if (remote.activeProjectId) localStorage.setItem("bd_active_" + me.email, remote.activeProjectId);
    if (remote.dashWidgets && typeof remote.dashWidgets === "object"){
      Object.entries(remote.dashWidgets).forEach(([siteId, widgets]) => {
        try { localStorage.setItem("bd_dash_widgets_" + siteId, JSON.stringify(widgets)); } catch {}
      });
    }
  } catch {}
}

async function hydrateCredits(){
  try {
    creditBalance = await api.fetchCredits();
    if (creditBalance && bdNormalizePlan(creditBalance.plan) === "free") plan = "free";
    paintCreditsUI();
  } catch {}
}

function paintCreditsUI(){
  paintSideUser();
  paintAccountMembershipSummary?.();
}

function paintAccountMembershipSummary(){
  const host = document.querySelector("#acct-membership-summary");
  if (!host) return;
  const hasSub = bdHasActiveSubscription(creditBalance);
  const label = hasSub ? bdPlanDisplayLabel(plan) : "Draft";
  const sub = creditBalance?.subscription;
  const subCancelling = sub?.status === "non-renewing";
  host.innerHTML = `
    <section class="acct-membership reveal">
      <div class="acct-membership-main">
        <p class="acct-membership-kicker">Membership</p>
        <div class="acct-membership-row">
          <strong>${esc(label)}</strong>
          ${subCancelling ? `<span class="plan-status ending">Ending</span>` : hasSub ? `<span class="plan-status active">Active</span>` : ""}
        </div>
        <p class="acct-membership-sub">${!hasSub
          ? "Upgrade for more sites, collaborators, and custom CSS."
          : "Manage billing, switch plans, or update your payment method."}</p>
      </div>
      <button type="button" class="btn btn-ghost btn-sm" id="acct-go-subscription">${lu("chevron-right","lu14")} Manage</button>
    </section>`;
  host.querySelector("#acct-go-subscription")?.addEventListener("click", () => {
    if (!DEMO && !QA_MODE && typeof BD_ACCOUNT !== "undefined") BD_ACCOUNT.go("subscription");
    else { sessionStorage.setItem("bd_acct_scroll", "subscription"); go("account"); }
  });
  icons();
}

function stashPaystackReturn(){
  const params = new URLSearchParams(location.search);
  if (params.get("pay") === "verify") {
    const ref = params.get("reference") || params.get("trxref");
    if (ref) {
      sessionStorage.setItem("bd_pay_ref", ref);
      sessionStorage.setItem("bd_pay_return", "1");
    }
    history.replaceState(null, "", location.pathname);
    return true;
  }
  return sessionStorage.getItem("bd_pay_return") === "1" && !!sessionStorage.getItem("bd_pay_ref");
}

async function handlePaystackReturn(){
  const fromUrl = stashPaystackReturn();
  const ref = sessionStorage.getItem("bd_pay_ref");
  if (!ref) return fromUrl;
  sessionStorage.removeItem("bd_pay_ref");
  sessionStorage.removeItem("bd_pay_return");
  try {
    const r = await api.paystackVerify(ref);
    await runCheckoutSuccess(r);
    toast("Payment confirmed — your plan is updated.", { kind: "success" });
  } catch (e) {
    toast(humanErr(e, "Payment verification failed."), { kind: "error" });
  }
  return true;
}

function stashCoworkReturn(){
  const params = new URLSearchParams(location.search);
  const token = params.get("cowork") || params.get("crew");
  const site = params.get("site");
  if (token && site) {
    sessionStorage.setItem("bd_cowork_return", JSON.stringify({ token, site }));
    history.replaceState(null, "", location.pathname + location.hash);
    return true;
  }
  return !!sessionStorage.getItem("bd_cowork_return");
}

async function handleCoworkReturn(){
  const raw = sessionStorage.getItem("bd_cowork_return");
  if (!raw) return null;
  let payload;
  try { payload = JSON.parse(raw); } catch { sessionStorage.removeItem("bd_cowork_return"); return null; }
  const { token, site } = payload || {};
  if (!token || !site) { sessionStorage.removeItem("bd_cowork_return"); return null; }
  sessionStorage.removeItem("bd_cowork_return");
  try {
    const r = await api.acceptCoworkInvite(site, token);
    toast(
      r.already ? `You already have access to “${r.site_name}”.` : `You're now collaborating on “${r.site_name}”.`,
      { kind: "success" }
    );
    return site;
  } catch (e) {
    toast(humanErr(e, "Could not accept invite."), { kind: "error" });
    return null;
  }
}

async function runCheckoutSuccess(v){
  if (v?.balance) creditBalance = v.balance;
  if (v?.plan) plan = v.plan;
  else plan = await api.plan();
  try { creditBalance = await api.fetchCredits(); } catch {}
  paintCreditsUI();
  paintSubscriptionPanel?.();
}

async function startSubscribeCheckout(planKey, interval, opts = {}){
  const pricing = bdSubPricing(planKey, interval);
  const planMeta = CREDIT_PLANS[planKey];
  const intLabel = interval === "annual" ? "Annual" : "Monthly";
  const hasActiveSub = bdHasActiveSubscription(creditBalance);
  const memberTier = hasActiveSub ? bdNormalizePlan(plan) : "free";
  if (hasActiveSub && subPlanRank(planKey) <= subPlanRank(memberTier)) {
    showPlanChangeBlockedModal(
      bdPlanDisplayLabel(plan),
      subPlanRank(planKey) < subPlanRank(memberTier)
        ? "Downgrading to a lower tier isn't self-serve."
        : "Changing billing interval on your current tier isn't self-serve."
    );
    return;
  }
  if (opts.switching && !opts.confirmed) {
    return new Promise(resolve => {
      const m = openModal(`<h2>Upgrade to ${esc(planMeta.label)}?</h2>
        <p class="sub">You'll complete secure checkout for <strong>${esc(planMeta.label)}</strong> (${intLabel.toLowerCase()}). Your membership upgrades immediately after payment — this cannot be undone from this screen.</p>
        <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button><button class="btn btn-brand btn-sm" data-x="ok">Continue to checkout</button></div>`);
      m.querySelector('[data-x="c"]').onclick = () => { closeModal(); resolve(); };
      m.querySelector('[data-x="ok"]').onclick = () => {
        closeModal();
        startSubscribeCheckout(planKey, interval, { switching: true, confirmed: true }).then(resolve);
      };
    });
  }
  try {
    bdOpenCheckoutSheet({
      title: opts.switching ? `Upgrade to ${planMeta.label}` : `${planMeta.label} plan`,
      subtitle: `${intLabel} billing`,
      rows: [
        { label: "Plan", value: planMeta.label },
        { label: "Billing", value: intLabel },
        ...(opts.switching ? [{ label: "Account", value: "Updates after payment" }] : [])
      ],
      totalLabel: interval === "annual" ? "Due today (annual)" : "Due today",
      totalDisplay: `${bdFormatZARFromRands(pricing.zar)} / ${pricing.per}`,
      confirmLabel: opts.switching ? `Upgrade · ${bdFormatZARFromRands(pricing.zar)}` : `Subscribe · ${bdFormatZARFromRands(pricing.zar)}`,
      onInit: () => api.paystackInitSubscribe(planKey, interval),
      onDemo: async (init) => {
        await runCheckoutSuccess({ balance: init?.balance, plan: planKey });
        toast("Demo subscription activated.", { kind: "success" });
      },
      onVerified: async (ref, { poll }) => {
        const v = await api.paystackVerify(ref);
        if (v?.ok || v?.balance) {
          await runCheckoutSuccess(v);
          toast("You're subscribed — your plan is active.", { kind: "success" });
        }
        return v;
      }
    });
  } catch (e) {
    toast(humanErr(e), { kind: "error" });
  }
}

let paintSubscriptionPanel = null;

const SUB_PLAN_RANK = { build: 1, forge: 2, architect: 3 };
function subPlanRank(key){ return SUB_PLAN_RANK[bdNormalizePlan(key)] ?? 0; }

function showPlanChangeBlockedModal(currentLabel, reason){
  const m = openModal(`<h2>Plan change not available</h2>
    <p class="sub">${esc(reason)}</p>
    <p class="sub">You'll keep <strong>${esc(currentLabel)}</strong> until your current billing period ends. Email <a href="mailto:support@brownsdigital.org">support@brownsdigital.org</a> for billing changes, or turn off auto-renew and pick a new plan when your cycle ends.</p>
    <div class="row"><button class="btn btn-brand btn-sm" data-x="ok">Got it</button></div>`);
  m.querySelector('[data-x="ok"]').onclick = closeModal;
}

function persistActive(){ localStorage.setItem("bd_active_" + me.email, activeId || ""); syncAccountPrefsNow(); }
function siteDotClass(id){ return prodOf(id) ? " live" : stagingOf(id) ? " staging" : ""; }
function siteStatusLabel(p){
  if (!p) return "Offline";
  if (prodOf(p.id)) return "Live";
  if (stagingOf(p.id)) return "Staged";
  return "Offline";
}
/* pinned sites — favourites float to the top of the switcher and All sites */
const PIN_KEY = "bd_pinned_sites";
function pinnedIds(){
  try { const a = JSON.parse(localStorage.getItem(PIN_KEY) || "[]"); return Array.isArray(a) ? a : []; }
  catch { return []; }
}
function isPinned(id){ return pinnedIds().includes(id); }
function togglePin(id){
  const a = pinnedIds();
  const i = a.indexOf(id);
  if (i >= 0) a.splice(i, 1); else a.unshift(id);
  localStorage.setItem(PIN_KEY, JSON.stringify(a.slice(0, 24)));
  syncAccountPrefsNow();
  return i < 0;
}
function pinSort(list){
  const pins = pinnedIds();
  return [...list].sort((a, b) => {
    const pa = pins.includes(a.id), pb = pins.includes(b.id);
    if (pa !== pb) return pa ? -1 : 1;
    return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
  });
}
function paintSitePick(){
  const p = active();
  $("#sp-name").textContent = p ? p.name : "No site selected";
  const dotCls = p ? siteDotClass(p.id) : "";
  const dot = $("#sp-dot");
  dot.className = "sp-dot" + dotCls + ((!p || !dotCls) ? " is-empty" : "");
  const st = $("#sp-status");
  if (st) st.textContent = siteStatusLabel(p);
  paintSiteNav();
}
function paintSiteNav(){
  const hasSite = projects.length > 0;
  ["health","team","settings"].forEach(pg => {
    const b = document.querySelector(`.side-nav button[data-page="${pg}"]`);
    if (b) b.hidden = !hasSite;
  });
  const pick = $("#site-pick");
  if (pick) pick.disabled = !projects.length;
  const cwPick = $("#cw-site-pick");
  if (cwPick) cwPick.disabled = !projects.length;
}
function closeSiteMenu(){
  const menu = $("#site-menu");
  if (menu) menu.hidden = true;
  $("#site-pick")?.setAttribute("aria-expanded", "false");
  $("#cw-site-pick")?.setAttribute("aria-expanded", "false");
}
function openSiteMenu(anchor, { align = "stretch", minW = 268, maxW = 320 } = {}){
  const menu = $("#site-menu");
  if (!menu || !anchor || !projects.length) return;
  const alreadyOpen = !menu.hidden && menu.dataset.for === (anchor.id || "");
  if (alreadyOpen){ closeSiteMenu(); return; }
  closeSiteMenu();
  menu.dataset.for = anchor.id || "";
  const rule = PLAN_RULES[plan] || PLAN_RULES.free;
  const cap = rule.sites;
  const mine = ownProjects();
  const shared = sharedProjects();
  const used = mine.length;
  const siteBtn = p => {
    const on = p.id === activeId;
    const initial = ((p.name || "S").trim().charAt(0) || "S").toUpperCase();
    const live = !!prodOf(p.id);
    const stg = !live && !!stagingOf(p.id);
    const statusCls = live ? "live" : stg ? "staging" : "draft";
    const status = live ? "Live" : stg ? "Staging" : "Draft";
    const host = bdHostOf(p);
    const shortHost = host ? ("/" + host.split("/").slice(1).join("/")) : "";
    const sub = shortHost ? `${status} · ${shortHost}` : status;
    return `<button type="button" class="sm-site${on ? " on" : ""}" data-id="${esc(p.id)}" role="option" aria-selected="${on ? "true" : "false"}">
      <span class="sm-avatar ${statusCls}" aria-hidden="true">${esc(initial)}</span>
      <span class="sm-text"><span class="sm-name">${esc(p.name || "Untitled")}</span><span class="sm-sub">${esc(sub)}</span></span>
      ${isPinned(p.id) ? `<span class="sm-pin-wrap" title="Pinned">${lu("pin","lu14")}</span>` : ""}
      ${on ? `<span class="sm-check-wrap" aria-hidden="true">${lu("check","lu14")}</span>` : ""}
    </button>`;
  };
  const mineRows = pinSort(mine).map(siteBtn).join("");
  const sharedRows = pinSort(shared).map(siteBtn).join("");
  const rows = (sharedRows && mineRows ? `<p class="sm-label">My sites</p>` : "") + mineRows
    + (sharedRows ? `<p class="sm-label">Shared with you</p>${sharedRows}` : "");
  const quota = Number.isFinite(cap)
    ? `<p class="sm-quota">${used} of ${cap} site${cap === 1 ? "" : "s"}</p>`
    : `<p class="sm-quota">Unlimited on ${esc(rule.label || "your plan")}</p>`;
  menu.innerHTML = `<div class="sm-list" role="listbox">${rows}</div><div class="sm-foot">${quota}<button type="button" class="sm-new" data-new="1">${lu("plus","lu14")} New site</button></div>`;
  anchorPopover(menu, anchor, { minW, maxW, align });
  menu.hidden = false;
  anchor.setAttribute("aria-expanded", "true");
  icons();
  menu.querySelectorAll("button").forEach(b => b.onclick = () => {
    closeSiteMenu();
    if (b.dataset.new) return newSiteFlow();
    activeId = b.dataset.id; persistActive(); go(["sites","account"].includes(page) ? "dashboard" : page);
  });
}
$("#site-pick").onclick = e => {
  e.stopPropagation();
  openSiteMenu($("#site-pick"), { align: "stretch", minW: 248, maxW: 300 });
};
$("#side-user").onclick = e => {
  e.stopPropagation();
  const user = $("#side-user");
  const menu = $("#user-menu");
  const open = menu.hidden;
  menu.hidden = !open;
  user.setAttribute("aria-expanded", open ? "true" : "false");
};
$("#user-menu").addEventListener("click", e => e.stopPropagation());
$("#um-customize").onclick = () => { $("#user-menu").hidden = true; openCustomizeModal(); };
$("#um-account").onclick = () => {
  $("#user-menu").hidden = true;
  if (!DEMO && !QA_MODE && typeof BD_ACCOUNT !== "undefined") BD_ACCOUNT.go();
  else go("account");
};
$("#um-lock").onclick = () => { $("#user-menu").hidden = true; lockStudioScreen(); };
$("#um-signout").onclick = signOutAll;
document.addEventListener("click", () => {
  closeSiteMenu(); $("#user-menu").hidden = true; $("#bell-menu").hidden = true;
  $("#side-user")?.setAttribute("aria-expanded", "false");
  closeCtx();
});

function paintLiveInd(){
  const p = active();
  const el = $("#top-live");
  const lbl = $("#live-label");
  el.className = "live-chip pill-hit" + (p && prodOf(p.id) ? " live" : p && stagingOf(p.id) ? " staging" : "");
  if (lbl) lbl.textContent = siteStatusLabel(p);
  el.title = p ? (prodOf(p.id) ? "Live at " + prodOf(p.id).url : stagingOf(p.id) ? "Preview ready — not live yet" : "Not published") : "No site";
}
$("#top-live").onclick = e => {
  e.stopPropagation();
  const p = active();
  if (!p) return;
  const prod = prodOf(p.id), stg = stagingOf(p.id);
  if (prod) openDeploy(prod);
  else if (stg) openDeploy(stg);
  else stageFlow(p);
};
function paintBell(){
  if (!me) return;
  const lastSeen = +localStorage.getItem("bd_bell_" + me.email) || 0;
  const show = activity.some(a => new Date(a.created_at) > lastSeen);
  const badge = $("#bell-n");
  if (!badge) return;
  if (show && badge.hidden){
    badge.hidden = false;
    badge.classList.remove("bell-pop");
    void badge.offsetWidth;
    badge.classList.add("bell-pop");
  } else if (!show){
    badge.hidden = true;
    badge.classList.remove("bell-pop");
  }
}
$("#top-bell").onclick = e => {
  e.stopPropagation();
  const bell = $("#top-bell");
  const menu = $("#bell-menu");
  if (!menu.hidden){ menu.hidden = true; return; }
  menu.innerHTML = `<h4>Recent activity</h4>` + (activity.length ? activity.slice(0,10).map(a => {
    const proj = projects.find(p => p.id === a.project_id);
    return `<div class="lr"><span class="ic ${a.kind==="published"?"g":a.kind==="created"?"b":a.kind==="staged"?"w":""}">${lu(ACT_ICON[a.kind]||"dot")}</span>
      <span class="lr-main"><strong>${esc(a.message)}</strong>${proj?`<em>${esc(proj.name)}</em>`:""}</span><time>${fmtWhen(a.created_at)}</time></div>`;
  }).join("") : `<p class="note" style="padding:6px 8px 10px">Nothing yet — activity from your sites lands here.</p>`);
  anchorPopover(menu, bell, { minW: 320, maxW: 360, align: "end" });
  menu.hidden = false; icons();
  localStorage.setItem("bd_bell_" + me.email, Date.now());
  $("#bell-n").hidden = true;
};
$("#bell-menu").addEventListener("click", e => e.stopPropagation());

/* ---------- shared furniture ---------- */
function head(el, icon, title, sub, actionsHtml){
  const h = document.createElement("div");
  h.className = "page-head reveal";
  h.innerHTML = `<div class="ph-icon">${lu(icon)}</div><div class="ph-titles"><h1>${esc(title)}</h1></div><div class="actions">${actionsHtml || ""}</div>${sub ? `<div class="ph-sub">${sub}</div>` : ""}`;
  el.appendChild(h);
  return h;
}
/** Alias for unified page hero pattern (same markup as head). */
function pageHero(el, icon, title, sub, actionsHtml){
  return head(el, icon, title, sub, actionsHtml);
}
function emptyStateHtml(icon, title, body, ctaHtml){
  return `<div class="empty-well" aria-hidden="true">${lu(icon)}</div>
    <div class="big">${esc(title)}</div>
    <p class="ph-sub" style="margin:0 auto 18px;max-width:42ch">${body}</p>
    ${ctaHtml || ""}`;
}
function pageSkeleton(){
  return `<div class="page-skel" data-page-skel role="status" aria-label="Loading"><div class="card liquid-glass" aria-hidden="true">${skelRows(4)}</div></div>`;
}
function dropPageSkeleton(root){
  root?.querySelectorAll?.("[data-page-skel]").forEach(n => n.remove());
}
function card(el, html, cls){
  dropPageSkeleton(el);
  const c = document.createElement("div");
  c.className = "card liquid-glass reveal" + (cls ? " " + cls : "");
  c.innerHTML = html;
  el.appendChild(c);
  return c;
}
let _modalReturnFocus = null;
let _modalCloseHook = null;
function setModalCloseHook(fn){ _modalCloseHook = fn; }
function clearModalCloseHook(){ _modalCloseHook = null; }
function _modalFocusables(box){
  return [...box.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
    .filter(el => !el.disabled && !el.hidden && el.getAttribute("aria-hidden") !== "true" && el.offsetParent !== null);
}
function openModal(html, wide){
  const box = $("#modal-box");
  box.className = "modal" + (wide ? " wide" : "");
  box.innerHTML = html;
  /* label the dialog from its heading for screen readers */
  const hd = box.querySelector("h2, h3");
  if (hd){ if (!hd.id) hd.id = "modal-title"; box.setAttribute("aria-labelledby", hd.id); }
  else box.removeAttribute("aria-labelledby");
  _modalReturnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  $("#modal-veil").classList.add("show");
  icons();
  /* move focus into the dialog unless a flow already placed it there */
  requestAnimationFrame(() => {
    if (box.contains(document.activeElement)) return;
    const first = box.querySelector("[autofocus]") || _modalFocusables(box)[0];
    if (first) first.focus({ preventScroll: true });
    else { box.setAttribute("tabindex", "-1"); box.focus({ preventScroll: true }); }
  });
  return box;
}
function closeModal(){
  const veil = $("#modal-veil");
  if (!veil.classList.contains("show")) return;
  if (_modalCloseHook){
    const allow = _modalCloseHook();
    if (allow === false) return;
  }
  clearModalCloseHook();
  veil.classList.remove("show");
  if (_modalReturnFocus && document.contains(_modalReturnFocus)) _modalReturnFocus.focus({ preventScroll: true });
  _modalReturnFocus = null;
}
$("#modal-veil").addEventListener("click", e => { if (e.target.id === "modal-veil") closeModal(); });
/* Escape closes the dialog; Tab is contained inside it (WAI-ARIA dialog pattern) */
document.addEventListener("keydown", e => {
  if (!$("#modal-veil").classList.contains("show")) return;
  if (e.key === "Escape"){ e.preventDefault(); closeModal(); return; }
  if (e.key !== "Tab") return;
  const box = $("#modal-box"), f = _modalFocusables(box);
  if (!f.length){ e.preventDefault(); return; }
  const first = f[0], last = f[f.length - 1], inBox = box.contains(document.activeElement);
  if (e.shiftKey && (document.activeElement === first || !inBox)){ e.preventDefault(); last.focus(); }
  else if (!e.shiftKey && (document.activeElement === last || !inBox)){ e.preventDefault(); first.focus(); }
});
/* Themed replacement for window.prompt — same shell as every other dialog. */
function promptModal(title, sub, { placeholder = "", confirmLabel = "OK", danger = false, value = "" } = {}){
  return new Promise(resolve => {
    const m = openModal(`
      <h2>${title}</h2>
      ${sub ? `<p class="sub">${sub}</p>` : ""}
      <div class="field"><input id="pm-in" placeholder="${esc(placeholder)}" value="${esc(value)}" autocomplete="off" spellcheck="false" autofocus></div>
      <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button>
      <button class="btn ${danger ? "btn-danger" : "btn-cta"} btn-sm" data-x="ok">${esc(confirmLabel)}</button></div>`);
    let settled = false;
    const done = v => { if (settled) return; settled = true; closeModal(); resolve(v); };
    m.querySelector('[data-x="c"]').onclick = () => done(null);
    m.querySelector('[data-x="ok"]').onclick = () => done(m.querySelector("#pm-in").value);
    m.querySelector("#pm-in").addEventListener("keydown", e => { if (e.key === "Enter"){ e.preventDefault(); done(m.querySelector("#pm-in").value); } });
  });
}
/** Themed 6-box authenticator challenge. Returns code string, or null if cancelled/signed out. */
function promptAuthenticatorCode({
  title = "Enter authenticator code",
  product = "Browns Digital",
  sub = "",
  maxAttempts = 5,
  lockClose = false,
  showPasskeyOption = false,
  showSupport = true,
  showCancel = true,
  onPasskey = null,
  verify = null
} = {}){
  if (typeof BD_OTP === "undefined") {
    document.body.classList.add("mfa-lock");
    return promptModal(title, sub || `Open your authenticator app and enter the 6-digit code for ${product}.`, {
      placeholder: "123456",
      confirmLabel: "Verify"
    }).finally(() => document.body.classList.remove("mfa-lock"));
  }
  return new Promise(resolve => {
    let attemptsLeft = maxAttempts;
    let settled = false;
    /* Never let the app shell show through the OTP veil (blur leaked the dashboard). */
    document.body.classList.add("mfa-lock");
    const app = $("#view-app");
    if (app){ app.classList.remove("active"); app.hidden = true; }
    const m = openModal(BD_OTP.challengeMarkup({
      title, product, sub, attemptsLeft,
      showOther: showSupport || showPasskeyOption || showCancel,
      showPasskey: showPasskeyOption,
      showSupport,
      showCancel
    }));
    m.classList.add("modal-otp");
    const veil = $("#modal-veil");
    if (veil) veil.classList.add("modal-veil--otp");
    if (lockClose) setModalCloseHook(() => false);
    const done = (value) => {
      if (settled) return;
      settled = true;
      clearModalCloseHook();
      closeModal();
      veil?.classList.remove("modal-veil--otp");
      document.body.classList.remove("mfa-lock");
      resolve(value);
    };
    const failAttempt = () => {
      attemptsLeft -= 1;
      BD_OTP.setAttempts(m, attemptsLeft);
      BD_OTP.setError(m, "");
      BD_OTP.pulseFail(m);
      BD_OTP.clear(m);
      if (attemptsLeft <= 0) done(null);
    };
    BD_OTP.bind(m, {
      autoSubmit: true,
      onComplete: async (code) => {
        if (settled) return;
        if (!/^\d{6}$/.test(code)) {
          BD_OTP.pulseFail(m);
          return;
        }
        if (!verify) { done(code); return; }
        try {
          await verify(code);
          done(code);
        } catch (e) {
          failAttempt();
        }
      }
    });
    const passkeyBtn = m.querySelector("[data-otp-passkey]");
    BD_OTP.wireChallenge(m, {
      onOptionsOpen: () => icons(),
      onPasskey: passkeyBtn ? async () => {
        if (settled) return;
        passkeyBtn.disabled = true;
        BD_OTP.setError(m, "");
        try {
          if (onPasskey) await onPasskey();
          else {
            await api.signInWithPasskey();
            const aal = await api.getAalState();
            if (aal.needsMfa) throw new Error("Passkey sign-in still needs your authenticator code.");
          }
          done("passkey");
        } catch (e) {
          BD_OTP.setError(m, humanErr(e, "Passkey sign-in could not continue."));
        } finally {
          passkeyBtn.disabled = false;
          icons();
        }
      } : null,
      onCancel: showCancel ? () => done(null) : null
    });
    BD_OTP.focus(m);
    icons();
  });
}
function confirmModal(title, sub, cta, cb){
  const m = openModal(`<h2>${title}</h2><p class="sub">${sub}</p>
    <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button><button class="btn btn-danger btn-sm" data-x="ok">${cta}</button></div>`);
  m.querySelector('[data-x="c"]').onclick = closeModal;
  m.querySelector('[data-x="ok"]').onclick = () => { closeModal(); cb(); };
}
/* context menu / mini palette */
let _pillPopBtn = null;
function openCtx(x, y, html){
  const m = $("#ctx-menu");
  m.innerHTML = html; m.hidden = false;
  m.style.left = Math.min(x, innerWidth - 230) + "px";
  m.style.top  = Math.min(y, innerHeight - Math.min(m.offsetHeight || 300, 320) - 10) + "px";
  icons();
  return m;
}
function closeCtx(){
  const m = $("#ctx-menu");
  if (m) m.hidden = true;
  _pillPopBtn = null;
}
$("#ctx-menu").addEventListener("click", e => e.stopPropagation());
/* Outside dismiss must beat pill stopPropagation: close on pointerdown in capture */
document.addEventListener("pointerdown", e => {
  const m = $("#ctx-menu");
  if (!m || m.hidden) return;
  if (m.contains(e.target)) return;
  if (_pillPopBtn && (e.target === _pillPopBtn || _pillPopBtn.contains(e.target))) return;
  closeCtx();
}, true);

/* deploy/preview helpers */
function openFiles(files, path){
  const f = (files || []).find(x => x.path === (path || "index.html")) || files?.[0];
  if (!f) return toast("Nothing to open yet.");
  const url = URL.createObjectURL(new Blob([f.content], { type: "text/html" }));
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}
/* Same rules as blocks.js — used by Console and Settings before the builder loads. */
function normalizeLegalSlug(slug){
  return String(slug || "").replace(/^\/+|\/+$/g, "").toLowerCase();
}
function sitePageBySlug(data, ...candidates){
  const want = new Set(candidates.map(normalizeLegalSlug).filter(Boolean));
  return (data?.pages || []).find(pg => want.has(normalizeLegalSlug(pg.slug))) || null;
}
function siteLegalPack(project){
  const d = project?.data || {};
  const a = d.advanced || {};
  const privacy = !!sitePageBySlug(d, "privacy", "legal");
  const terms = !!sitePageBySlug(d, "terms");
  const cookie = !!a.cookieBanner;
  const missing = [];
  if (!privacy) missing.push("privacy page");
  if (!terms) missing.push("terms page");
  if (!cookie) missing.push("cookie banner");
  const ready = missing.length === 0;
  return {
    ready,
    privacy,
    terms,
    cookie,
    missing,
    visitsOn: ready && !!a.studioAnalytics,
    plausibleOn: ready && !!String(a.analyticsId || "").trim(),
  };
}
function migrateProjectData(data, projectName){
  if (typeof migrateData === "function") return migrateData(data, projectName);
  if (data?.v === 4){
    if (!data.shared) data.shared = { header: null, footer: null };
    if (!data.icon) data.icon = "layout-dashboard";
    if (!data.advanced) data.advanced = {};
    if (data.advanced.studioAnalytics == null) data.advanced.studioAnalytics = false;
    (data.pages || []).forEach(pg => { if (!Array.isArray(pg.blocks)) pg.blocks = []; if (!pg.seo) pg.seo = { title:"", description:"", ogImage:"" }; });
    return data;
  }
  return data;
}
/* The site list is names only. Full data, including uploaded images, loads
   when that site is opened and stays cached until the server copy is newer. */
function applyProjectRows(rows){
  const prev = new Map(projects.map(p => [p.id, p]));
  projects = (rows || []).map(row => {
    const old = prev.get(row.id);
    let data = null;
    let updated = row.updated_at;
    if (row.data && typeof row.data === "object") {
      data = migrateProjectData(row.data, row.name);
    } else if (old && Array.isArray(old.data?.pages)) {
      const local = String(old.updated_at || "");
      const remote = String(row.updated_at || "");
      if (!remote || local === remote || local > remote) {
        data = old.data;
        if (local > remote) updated = old.updated_at;
      }
    }
    return {
      id: row.id,
      name: row.name,
      updated_at: updated,
      user_id: row.user_id || old?.user_id || null,
      ownerName: row.ownerName || old?.ownerName || "",
      data
    };
  });
}
function siteIsMine(p){
  if (!p?.user_id || !me?.id) return true;
  return String(p.user_id) === String(me.id);
}
function ownProjects(){ return projects.filter(siteIsMine); }
function sharedProjects(){ return projects.filter(p => !siteIsMine(p)); }
async function attachOwnerNames(rows){
  const ids = [...new Set((rows || []).map(r => r?.user_id).filter(id => id && String(id) !== String(me?.id || "")))];
  if (!ids.length) return rows;
  let labels = {};
  try { labels = await api.ownerLabels(ids); } catch {}
  (rows || []).forEach(r => {
    if (r?.user_id && labels[r.user_id]) r.ownerName = labels[r.user_id];
  });
  return rows;
}
const _projectLoad = new Map();
async function ensureProjectLoaded(id){
  if (!id) return null;
  const have = projects.find(p => p.id === id);
  if (have && Array.isArray(have.data?.pages)) return have;
  if (typeof DEMO !== "undefined" && DEMO) return have || null;
  let job = _projectLoad.get(id);
  if (!job) {
    job = fetchProjectBody(id).finally(() => { _projectLoad.delete(id); });
    _projectLoad.set(id, job);
  }
  return job;
}
async function fetchProjectBody(id){
  const row = await api.loadProject(id);
  if (!row) return projects.find(p => p.id === id) || null;
  if (row.data && row.data.v !== 4) {
    try { await loadStudioBlocks(); } catch {}
  }
  const prev = projects.find(p => p.id === id);
  const next = {
    id: row.id,
    name: row.name || "",
    updated_at: row.updated_at,
    user_id: row.user_id || prev?.user_id || null,
    ownerName: prev?.ownerName || "",
    data: migrateProjectData(row.data, row.name)
  };
  const i = projects.findIndex(p => p.id === id);
  if (i >= 0) projects[i] = next;
  else projects.unshift(next);
  return next;
}
let _previewBlobUrl = null;
async function previewSite(p, pgSlug){
  try { await loadStudioBlocks(); }
  catch (e) { toast(humanErr(e, "Could not load the builder.")); return; }
  if (p && typeof migrateData === "function") p = Object.assign({}, p, { data: migrateData(p.data, p.name) });
  if (!p?.data?.pages?.length) return toast("No page to preview.");
  if (typeof buildInteractivePreviewHTML !== "function"){
    const pg = pgSlug ? p.data.pages.find(x => x.slug === pgSlug) : p.data.pages.find(x => !x.slug) || p.data.pages[0];
    if (!pg) return toast("No page to preview.");
    const html = buildPageHTML(p, pg, "https://preview.local/");
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  }
  if (_previewBlobUrl){
    try { URL.revokeObjectURL(_previewBlobUrl); } catch {}
    _previewBlobUrl = null;
  }
  const html = buildInteractivePreviewHTML(p, pgSlug || "");
  _previewBlobUrl = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  const win = window.open(_previewBlobUrl, "_blank");
  if (!win){
    toast("Allow pop-ups to open the interactive preview.", { kind: "error" });
    return;
  }
  toast("Interactive preview — links, buttons, and forms work like the live site.");
}
async function openDeploy(rec, path){
  /* Prefer the hosted URL so staging/live always reflect the last deploy — not a local blob of builder state. */
  if (rec?.url && !(typeof DEMO !== "undefined" && DEMO && rec.files)) {
    const suffix = path && path !== "index.html" && path !== "index.php"
      ? String(path).replace(/index\.html$/, "").replace(/^\//, "")
      : "";
    window.open(rec.url + suffix, "_blank");
    return;
  }
  if (rec.files){
    const p = projects.find(x => x.id === rec.project_id);
    if (p) {
      try { await ensureProjectLoaded(p.id); }
      catch (e) { toast(humanErr(e, "Could not open this site."), { kind: "error" }); return; }
      return previewSite(projects.find(x => x.id === p.id) || p);
    }
    openFiles(rec.files, path || "index.php");
    return;
  }
  toast("Nothing to open yet.");
}

/* ============================== DASHBOARD ============================== */
function groupActivityByDay(rows){
  const groups = [];
  rows.forEach(a => {
    const d = new Date(a.created_at);
    const today = new Date(); today.setHours(0,0,0,0);
    const yest = new Date(today); yest.setDate(yest.getDate() - 1);
    const label = d >= today ? "Today" : d >= yest ? "Yesterday" : d.toLocaleDateString(undefined, { day:"numeric", month:"short" });
    let g = groups[groups.length - 1];
    if (!g || g.label !== label){ g = { label, rows: [] }; groups.push(g); }
    g.rows.push(a);
  });
  return groups;
}

const DASH_WIDGET_DEFAULTS = ["pages", "sections", "revisions", "status", "last_published"];
const DASH_WIDGET_MAX = 5;
const DASH_WIDGET_CATALOG = {
  pages:      { label: "Pages", icon: "files", sub: "in this site" },
  sections:   { label: "Sections", icon: "layers", sub: "incl. header & footer" },
  revisions:  { label: "Revisions", icon: "git-branch", sub: "saved snapshots" },
  status:     { label: "Status", icon: "radio" },
  last_published: { label: "Last published", icon: "clock", sub: "promoted to live" },
  health:     { label: "Health score", icon: "heart-pulse", sub: "out of 100" },
  critical:   { label: "Critical issues", icon: "octagon-alert", sub: "need attention" },
  warnings:   { label: "Warnings", icon: "triangle-alert", sub: "across all pages" },
  last_edited:{ label: "Last edited", icon: "clock", sub: "most recent change" },
  production: { label: "Production", icon: "globe" },
  staging:    { label: "Staging", icon: "upload" }
};
function dashWidgetsKey(siteId){ return "bd_dash_widgets_" + siteId; }
function loadDashWidgets(siteId){
  try {
    const raw = localStorage.getItem(dashWidgetsKey(siteId));
    if (raw === null) return null;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    return parsed.filter(w => w && w.id && DASH_WIDGET_CATALOG[w.id]).map(w => ({ id: w.id, label: w.label || null }));
  } catch { return null; }
}
function saveDashWidgets(siteId, widgets){ localStorage.setItem(dashWidgetsKey(siteId), JSON.stringify(widgets)); syncAccountPrefsNow(); }
function defaultDashWidgets(){ return DASH_WIDGET_DEFAULTS.map(id => ({ id })); }
function resolveDashWidgets(siteId){
  const saved = loadDashWidgets(siteId);
  const list = saved === null ? defaultDashWidgets() : saved;
  return list.slice(0, DASH_WIDGET_MAX);
}
function buildDashWidgetData(id, ctx){
  const cat = DASH_WIDGET_CATALOG[id];
  if (!cat) return null;
  const { p, prod, stg, revs, secs, audit, hasContent, statusLabel, statusCls } = ctx;
  const base = { id, icon: cat.icon, label: cat.label, sub: cat.sub || "", valueClass: "" };
  switch (id){
    case "pages": return { ...base, value: String(p.data.pages.length), sub: cat.sub };
    case "sections": return { ...base, value: String(secs), sub: cat.sub };
    case "revisions": return { ...base, value: String(revs.length), sub: cat.sub };
    case "status": return { ...base, value: statusLabel, valueClass: "dash-stat-status " + statusCls,
      sub: prod ? "published " + fmtWhen(prod.published_at) : stg ? "staged " + fmtWhen(stg.published_at) : "publish when ready" };
    case "health":
      if (!hasContent || !audit) return { ...base, value: "—", sub: "add content to score" };
      return { ...base, value: String(audit.score), sub: cat.sub };
    case "critical":
      if (!hasContent || !audit) return { ...base, value: "—", sub: "no audit yet" };
      return { ...base, value: String(audit.crit || 0), sub: cat.sub };
    case "warnings":
      if (!hasContent || !audit) return { ...base, value: "—", sub: "no audit yet" };
      return { ...base, value: String(audit.warn || 0), sub: cat.sub };
    case "last_edited": return { ...base, value: fmtWhen(p.updated_at), valueClass: "dash-stat-sm", sub: cat.sub };
    case "production": return { ...base, value: prod ? "Live" : "Offline", valueClass: "dash-stat-status " + (prod ? "live" : "draft"),
      sub: prod ? fmtWhen(prod.published_at) : "not published" };
    case "staging": return { ...base, value: stg ? "Staged" : "—", valueClass: "dash-stat-status " + (stg ? "staged" : "draft"),
      sub: stg ? fmtWhen(stg.published_at) : "not staged" };
    case "last_published":
      if (!prod) return { ...base, value: "Never", valueClass: "dash-stat-muted", sub: "not live yet" };
      return { ...base, value: fmtWhen(prod.published_at), valueClass: "dash-stat-sm", sub: cat.sub };
    default: return null;
  }
}
function renderDashStatCard(w, data, edit){
  const label = w.label || data.label;
  const accentCls =
    data.id === "critical" && parseInt(data.value, 10) > 0 ? " is-critical" :
    data.id === "production" && data.value === "Live" ? " is-live" :
    data.id === "status" && data.valueClass.includes("staged") ? " is-staged" : "";
  return `<div class="dash-stat liquid-glass${edit ? " dash-stat-editing" : ""}${accentCls}" data-id="${esc(data.id)}"${edit ? ' draggable="true"' : ""} role="group" aria-label="${esc(label)} stat card">
    <div class="dash-stat-top">
      ${edit
        ? `<button type="button" class="dash-stat-lbl dash-stat-rename" title="Rename ${esc(label)}">${esc(label)}${lu("pencil","lu12")}</button>`
        : `<span class="dash-stat-lbl">${esc(label)}</span>`}
      ${edit
        ? `<button type="button" class="dash-stat-rm" aria-label="Remove ${esc(label)} card">${lu("x","lu16")}</button>`
        : `<span class="dash-stat-ico" aria-hidden="true">${lu(data.icon)}</span>`}
    </div>
    <div class="dash-stat-n ${data.valueClass}" aria-live="polite">${esc(data.value)}</div>
    <div class="dash-stat-d">${esc(data.sub)}</div>
  </div>`;
}
function mountDashStats(root, p, ctx, toggleHost){
  let widgets = resolveDashWidgets(p.id);
  let editMode = false;
  let dragId = null;

  const wrap = document.createElement("div");
  wrap.className = "dash-stats-wrap reveal";
  wrap.innerHTML = `<div class="dash-stats" id="dash-stats-grid"></div>`;
  root.appendChild(wrap);
  const grid = wrap.querySelector("#dash-stats-grid");

  const toggleBtn = document.createElement("button");
  toggleBtn.type = "button";
  toggleBtn.className = "dash-customize-btn";
  toggleBtn.id = "dash-stats-toggle";
  toggleBtn.innerHTML = `${lu("sliders-horizontal","lu14")}<span>Customize stats</span>`;
  toggleHost.appendChild(toggleBtn);

  function save(){
    if (!widgets.length) return;
    saveDashWidgets(p.id, widgets.slice(0, DASH_WIDGET_MAX));
  }

  function openPicker(){
    if (widgets.length >= DASH_WIDGET_MAX){ toast(`You can show up to ${DASH_WIDGET_MAX} stats — remove one to add another.`); return; }
    const used = new Set(widgets.map(w => w.id));
    const avail = Object.keys(DASH_WIDGET_CATALOG).filter(id => !used.has(id));
    if (!avail.length){ toast("All available stats are already on your dashboard."); return; }
    const m = openModal(`<h2>Add stat card</h2><p class="sub">Choose one metric (${widgets.length}/${DASH_WIDGET_MAX} slots used).</p>
      <div class="dash-widget-pick">${avail.map(id => {
        const c = DASH_WIDGET_CATALOG[id];
        const d = buildDashWidgetData(id, ctx);
        return `<button type="button" class="dash-widget-opt" data-id="${id}">
          <span class="dwo-icon">${lu(c.icon)}</span>
          <span class="dwo-body"><strong>${esc(c.label)}</strong><em>${esc(d?.sub || c.sub || "")}</em></span>
          ${lu("plus","lu16")}</button>`;
      }).join("")}</div>
      <div class="row" style="margin-top:16px"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button></div>`);
    m.querySelector('[data-x="c"]').onclick = closeModal;
    m.querySelectorAll(".dash-widget-opt").forEach(b => {
      b.onclick = () => {
        if (widgets.length >= DASH_WIDGET_MAX) return;
        widgets.push({ id: b.dataset.id });
        save();
        closeModal();
        paint();
      };
    });
    icons();
  }

  function bindCards(){
    grid.querySelectorAll(".dash-stat-rm").forEach(btn => {
      btn.onclick = e => {
        e.stopPropagation();
        const id = btn.closest(".dash-stat").dataset.id;
        widgets = widgets.filter(w => w.id !== id);
        if (widgets.length) save();
        paint();
      };
    });
    grid.querySelectorAll(".dash-stat-lbl").forEach(lbl => {
      if (!editMode) return;
      lbl.onclick = async e => {
        e.stopPropagation();
        const id = lbl.closest(".dash-stat").dataset.id;
        const w = widgets.find(x => x.id === id);
        const cur = w.label || DASH_WIDGET_CATALOG[id].label;
        const next = await promptModal(
          "Rename stat card",
          "Use a short label that remains easy to scan on the dashboard.",
          { value:cur, placeholder:DASH_WIDGET_CATALOG[id].label, confirmLabel:"Save label" }
        );
        if (next === null) return;
        const trimmed = next.trim();
        if (trimmed) w.label = trimmed.slice(0, 32); else delete w.label;
            save();
          paint();
      };
    });
    grid.querySelectorAll(".dash-stat[data-id]").forEach(el => {
      el.ondragstart = e => {
        if (e.target.closest("button")){ e.preventDefault(); return; }
        dragId = el.dataset.id; e.dataTransfer.effectAllowed = "move"; el.classList.add("dragging");
      };
      el.ondragend = () => { dragId = null; el.classList.remove("dragging"); };
      el.ondragover = e => { e.preventDefault(); };
      el.ondrop = e => {
        e.preventDefault();
        const targetId = el.dataset.id;
        if (!dragId || dragId === targetId) return;
        const from = widgets.findIndex(w => w.id === dragId);
        const to = widgets.findIndex(w => w.id === targetId);
        if (from < 0 || to < 0) return;
        const [item] = widgets.splice(from, 1);
        widgets.splice(to, 0, item);
        save();
        paint();
      };
    });
    const add = grid.querySelector("#dash-add-card");
    if (add) add.onclick = openPicker;
  }

  function paint(){
    toggleBtn.classList.toggle("is-active", editMode);
    const canFinish = widgets.length > 0;
    toggleBtn.disabled = editMode && !canFinish;
    toggleBtn.title = editMode && !canFinish ? "Add at least one stat card to finish" : "";
    if (!widgets.length){
      grid.className = "dash-stats dash-stats-empty-state";
      grid.innerHTML = `<div class="dash-stats-prompt">
        <p>No stat cards yet</p>
        <p class="c-sub">Add the metrics you care about, or restore the default set.</p>
        <div class="dash-stats-prompt-actions">
          <button type="button" class="btn btn-cta btn-sm" id="dash-prompt-add">${lu("plus")} Add stat card</button>
          <button type="button" class="btn btn-ghost btn-sm" id="dash-prompt-defaults">Restore defaults</button>
        </div></div>`;
      grid.querySelector("#dash-prompt-add").onclick = openPicker;
      grid.querySelector("#dash-prompt-defaults").onclick = () => { widgets = defaultDashWidgets(); save(); paint(); };
      toggleBtn.innerHTML = editMode ? `${lu("check","lu14")}<span>Done</span>` : `${lu("sliders-horizontal","lu14")}<span>Customize stats</span>`;
      icons();
      return;
    }
    grid.className = "dash-stats" + (editMode ? " dash-stats-editing" : "");
    const canAdd = editMode && widgets.length < DASH_WIDGET_MAX;
    grid.innerHTML = widgets.map(w => {
      const data = buildDashWidgetData(w.id, ctx);
      if (!data) return "";
      return renderDashStatCard(w, data, editMode);
    }).join("") + (canAdd ? `<button type="button" class="dash-stat dash-stat-add" id="dash-add-card">${lu("plus")}<span>Add stat</span></button>` : "");
    bindCards();
    toggleBtn.innerHTML = editMode ? `${lu("check","lu14")}<span>Done</span>` : `${lu("sliders-horizontal","lu14")}<span>Customize stats</span>`;
    icons();
  }

  toggleBtn.onclick = () => {
    if (!editMode){ editMode = true; paint(); return; }
    if (!widgets.length){ toast("Add at least one stat card before finishing."); return; }
    editMode = false;
    save();
    paint();
  };
  paint();
}

async function pageDashboard(el){
  const p = active();
  if (!p){
    const w = document.createElement("div");
    w.className = "empty-mesh mesh reveal";
    w.style.cssText = "padding:80px 28px;margin-top:24px";
    w.innerHTML = emptyStateHtml("pencil-ruler", "Create your first site",
      "Design on a real canvas, publish to staging, promote to live — no code, no exports.",
      `<button class="btn btn-brand" id="dw-new">${lu("plus")} Create your first site</button>`);
    el.appendChild(w);
    w.querySelector("#dw-new").onclick = newSiteFlow;
    icons();
    return;
  }
  if (!Array.isArray(p.data?.pages)) throw new Error("This site has not finished loading. Try again.");
  const prod = prodOf(p.id), stg = stagingOf(p.id);
  const revs = p.data._revisions || [];
  const pageCount = p.data.pages.length;
  const secs = p.data.pages.reduce((n,pg) => n + pg.blocks.length, 0) + (p.data.shared.header?1:0) + (p.data.shared.footer?1:0);
  const hasContent = siteHasContent(p);
  const audit = hasContent ? auditSite(p) : null;
  const healthScore = audit?.score ?? null;
  const statusLabel = prod ? "Live" : stg ? "Preview" : "Draft";
  const statusCls = prod ? "live" : stg ? "staged" : "draft";
  const addr = prod
    ? prod.url.replace(/^https?:\/\//, "").replace(/\/$/, "")
    : stg ? stg.slug : "—";
  const meName = displayNameOf(me);
  const meAvatar = avatarUrlOf(me);
  const meAvatarHtml = avatarHtml(meAvatar, meName, 26, "console-avatar");
  const meAvatarSmHtml = avatarHtml(meAvatar, meName, 22, "console-avatar sm");

  const root = document.createElement("div");
  root.className = "dash-page console-page";
  el.appendChild(root);

  /* Quiet Browns Digital UI page header — one primary action */
  const head = document.createElement("header");
  head.className = "console-top reveal";
  head.innerHTML = `
    <div class="console-top-left">
      <span class="console-top-mark" style="--site-mark:${esc(siteIconColorOf(p))};color:var(--site-mark)" aria-hidden="true">${lu(siteIconOf(p))}</span>
      <div>
        <p class="console-eyebrow">Console</p>
        <h1>${esc(p.name)}</h1>
      </div>
      <span class="dash-status-pill ${statusCls}">${statusLabel}</span>
    </div>
    <button type="button" class="console-top-link" id="console-open-canvas">Edit on canvas ${lu("arrow-up-right","lu14")}</button>`;
  root.appendChild(head);
  head.querySelector("#console-open-canvas").onclick = () => openBuilderEdit(p.id);
  icons();

  /* Single next step — no checklist card */
  const nextUp = CHECKLIST_ITEMS.map(item => ({ ...item, done: checklistDone(p, item.id) })).find(x => !x.done);
  if (nextUp && !loadChecklistCollapsed(p.id)){
    const tip = document.createElement("p");
    tip.className = "console-next-line reveal";
    tip.innerHTML = `<span>Next</span><button type="button" id="console-next-go">${esc(nextUp.label)}</button>`;
    root.appendChild(tip);
    tip.querySelector("#console-next-go").onclick = () => {
      const id = nextUp.id;
      if (id === "page" || id === "content") openBuilderEdit(p.id);
      else if (id === "domain" || id === "seo"){ sessionStorage.setItem("bd_settab", id === "domain" ? "domain" : "site"); go("settings"); }
      else if (id === "stage") openPublishModal(p, { intent: "preview" });
      else if (id === "live") openPublishModal(p, { intent: stg || prod ? "live" : "preview" });
    };
  }

  /* Compact Browns Digital UI stat strip */
  const statsBar = document.createElement("div");
  statsBar.className = "dash-stats-bar console-stats-bar reveal";
  statsBar.innerHTML = `<span class="dash-stats-bar-label">Overview</span>`;
  root.appendChild(statsBar);
  mountDashStats(root, p, { p, prod, stg, revs, secs, audit, hasContent, statusLabel, statusCls }, statsBar);

  const actRows = await api.listActivity(p.id, 12);
  const pages = [...(p.data.pages || [])];

  /* Mid: recently edited + aura status */
  const mid = document.createElement("div");
  mid.className = "console-mid reveal";
  root.appendChild(mid);

  const recentCard = document.createElement("section");
  recentCard.className = "console-panel console-panel--recent liquid-glass";
  mid.appendChild(recentCard);

  const paintRecent = (filter = "all") => {
    const pageItems = pages.slice(0, 10).map(pg => ({
      kind: "page",
      id: pg.id,
      title: pg.name || (!pg.slug ? "Home" : pg.slug),
      sub: `${pg.blocks?.length || 0} section${(pg.blocks?.length || 0) === 1 ? "" : "s"} · ${pg.slug ? "/" + pg.slug : "/"}`,
      when: fmtWhen(p.updated_at),
      run: () => openBuilderEdit(activeId, pg.id),
    }));
    const actItems = actRows.slice(0, 10).map(a => ({
      kind: "activity",
      id: a.id || a.created_at,
      title: a.message,
      sub: a.kind || "activity",
      when: fmtWhen(a.created_at),
      run: () => go("activity"),
    }));
    let items = filter === "pages" ? pageItems
      : filter === "activity" ? actItems
      : [...pageItems.slice(0, 5), ...actItems.slice(0, 4)];
    if (!items.length){
      recentCard.querySelector(".console-recent-list").innerHTML =
        `<div class="console-empty-state"><span class="console-empty-orb" aria-hidden="true"></span><p>Nothing here yet</p><em>Edits and pages will land in this feed.</em></div>`;
      return;
    }
    recentCard.querySelector(".console-recent-list").innerHTML = items.map(item => `
      <button type="button" class="console-recent-row" data-kind="${esc(item.kind)}">
        <span class="console-recent-ico ${item.kind === "activity" ? "is-act" : ""}">${lu(item.kind === "activity" ? (ACT_ICON[item.sub] || "activity") : (!pages.find(x => x.id === item.id)?.slug ? "home" : "file-text"))}</span>
        <span class="console-recent-meta">
          <strong>${esc(item.title)}</strong>
        </span>
        <span class="console-recent-when">${esc(item.when)} by</span>
        ${meAvatarHtml}
      </button>`).join("");
    recentCard.querySelectorAll(".console-recent-row").forEach((row, i) => {
      row.onclick = () => items[i].run();
    });
    icons();
  };

  recentCard.innerHTML = `
    <div class="console-panel-head">
      <div class="console-panel-title"><h2>Recently edited</h2></div>
      <div class="console-filters" role="tablist" aria-label="Filter recent">
        <button type="button" class="console-filter on" data-filter="all" role="tab" aria-selected="true"><span class="console-filter-txt is-static">All</span></button>
        <button type="button" class="console-filter console-filter--ico" data-filter="pages" role="tab" aria-selected="false" aria-label="Pages">${lu("file-text","lu14")}<span class="console-filter-txt">Pages</span></button>
        <button type="button" class="console-filter console-filter--ico" data-filter="activity" role="tab" aria-selected="false" aria-label="Activity">${lu("activity","lu14")}<span class="console-filter-txt">Activity</span></button>
    </div>
    </div>
    <div class="console-recent-list" role="list"></div>`;
  recentCard.querySelectorAll(".console-filter").forEach(btn => {
    btn.onclick = () => {
      recentCard.querySelectorAll(".console-filter").forEach(b => {
        b.classList.toggle("on", b === btn);
        b.setAttribute("aria-selected", b === btn ? "true" : "false");
      });
      paintRecent(btn.dataset.filter);
    };
  });
  paintRecent("all");

  const statusCard = document.createElement("section");
  statusCard.className = `console-panel console-panel--aura console-aura--${statusCls} liquid-glass`;
  mid.appendChild(statusCard);
  const stageHost = stg ? String(stg.url || stg.slug || "").replace(/^https?:\/\//, "").replace(/\/$/, "") : "";
  const liveHost = prod ? String(prod.url || "").replace(/^https?:\/\//, "").replace(/\/$/, "") : "";
  statusCard.innerHTML = `
    <div class="console-aura-stage" aria-hidden="true">
      <div class="console-aura-glow"></div>
      <div class="console-aura-rings">
        <i></i><i></i><i></i>
      </div>
      <div class="console-aura-glyph">${lu(prod ? "globe" : stg ? "upload" : "moon")}</div>
    </div>
    <div class="console-aura-foot">
      <button type="button" class="console-status-pill" id="console-status-cta">
        ${meAvatarSmHtml}
        <span>${esc(statusLabel)}${addr && addr !== "—" ? ` · ${esc(addr)}` : ""}</span>
      </button>
      <div class="console-aura-addrs">
        <button type="button" class="console-addr" id="console-addr-stage" ${stg ? "" : "disabled"}>
          <em>Preview</em><strong>${esc(stageHost || "Not previewed")}</strong>
        </button>
        <button type="button" class="console-addr" id="console-addr-live" ${prod ? "" : "disabled"}>
          <em>Live</em><strong>${esc(liveHost || "Not live")}</strong>
        </button>
        <button type="button" class="console-addr" id="console-addr-domain">
          <em>Domain</em><strong id="console-domain-label">Checking…</strong>
        </button>
      </div>
      <div class="console-aura-actions">
        <button type="button" class="btn ${prod ? "btn-ghost" : "btn-brand"} btn-sm" id="ss-stage">${stg ? "Update preview" : "Create preview"}</button>
        <button type="button" class="btn ${prod || stg ? "btn-brand" : "btn-ghost"} btn-sm" id="ss-promote" ${stg || prod ? "" : "disabled"}>${prod ? "Update live" : "Push to live"}</button>
        ${prod && siteIsMine(p) ? `<button type="button" class="btn btn-ghost btn-sm" id="ss-history">History</button>` : ""}
      </div>
    </div>`;
  statusCard.querySelector("#console-status-cta").onclick = () => {
    if (prod) openDeploy(prod);
    else if (stg) openDeploy(stg);
    else stageFlow(p);
  };
  statusCard.querySelector("#console-addr-stage").onclick = () => { if (stg) openDeploy(stg); else stageFlow(p); };
  statusCard.querySelector("#console-addr-live").onclick = () => { if (prod) openDeploy(prod); };
  statusCard.querySelector("#ss-stage").onclick = () => openPublishModal(p, { intent: "preview" });
  const promoteBtn = statusCard.querySelector("#ss-promote");
  if (promoteBtn && !promoteBtn.disabled) promoteBtn.onclick = () => openPublishModal(p, { intent: "live" });
  const historyBtn = statusCard.querySelector("#ss-history");
  if (historyBtn) historyBtn.onclick = () => openPublishHistory(p);
  const domainBtn = statusCard.querySelector("#console-addr-domain");
  const domainLabel = statusCard.querySelector("#console-domain-label");
  domainBtn.onclick = () => { sessionStorage.setItem("bd_settab", "domain"); go("settings"); };
  api.getDomain(p.id).then(d => {
    if (d?.status === "verified"){
      p._domainVerified = true;
      domainLabel.textContent = d.domain || "Connected";
    } else if (d){
      domainLabel.textContent = "Setup needed";
    } else {
      domainLabel.textContent = "Add a custom domain";
    }
  }).catch(() => { domainLabel.textContent = "Add a custom domain"; });
  if (prod && siteIsMine(p)) {
    ensurePublishRuntime().then(() => {
      if (!statusCard.isConnected || !sitePublishStale(p.id)) return;
      const span = statusCard.querySelector("#console-status-cta span");
      if (span && !span.querySelector(".badge.update")) {
        span.insertAdjacentHTML("beforeend", ` <span class="badge update">Update available</span>`);
      }
      const actions = statusCard.querySelector(".console-aura-actions");
      if (!actions || actions.querySelector("#ss-republish")) return;
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn btn-brand btn-sm";
      btn.id = "ss-republish";
      btn.textContent = "Republish";
      btn.onclick = async () => {
        btn.disabled = true;
        btn.textContent = "Republishing…";
        try {
          await republishLive(p);
          toast("Live site updated", { kind: "success" });
          go("dashboard");
        } catch (err) {
          btn.disabled = false;
          btn.textContent = "Republish";
          toast(humanErr(err), { kind: "error" });
        }
      };
      actions.appendChild(btn);
    }).catch(() => {});
  }

  /* Shared hover tip for insight charts */
  const tip = document.createElement("div");
  tip.className = "console-tip";
  tip.hidden = true;
  root.appendChild(tip);
  const showTip = (html, x, y) => {
    tip.innerHTML = html;
    tip.hidden = false;
    tip.style.opacity = "0";
    const pad = 14;
    const tw = tip.offsetWidth || 140;
    const th = tip.offsetHeight || 40;
    const left = Math.min(window.innerWidth - tw - pad, Math.max(pad, x + 16));
    const top = Math.min(window.innerHeight - th - pad, Math.max(pad, y - th - 14));
    tip.style.left = left + "px";
    tip.style.top = top + "px";
    tip.style.opacity = "1";
  };
  const hideTip = () => { tip.hidden = true; tip.innerHTML = ""; };

  /* Bottom insight cards */
  const insights = document.createElement("div");
  insights.className = "console-insights reveal";
  root.appendChild(insights);

  /* Real counts only — never floor empty buckets to 1 (that made 0 revisions show ~9%). */
  const counts = [pageCount, secs, revs.length];
  const totalShare = counts[0] + counts[1] + counts[2];
  let pagePct = 0, secPct = 0, revPct = 0;
  if (totalShare > 0) {
    pagePct = Math.round((pageCount / totalShare) * 100);
    secPct = Math.round((secs / totalShare) * 100);
    revPct = Math.round((revs.length / totalShare) * 100);
    const drift = 100 - pagePct - secPct - revPct;
    if (drift) {
      const i = [0, 1, 2].sort((a, b) => counts[b] - counts[a]).find(ix => counts[ix] > 0);
      if (i === 0) pagePct += drift;
      else if (i === 1) secPct += drift;
      else if (i === 2) revPct += drift;
    }
  }
  const compParts = [
    { key:"pages", label:"Pages", count:pageCount, pct:pagePct, tip:`${pageCount} page${pageCount === 1 ? "" : "s"} · ${pagePct}% of composition`, run: () => openBuilderEdit(p.id) },
    { key:"sections", label:"Sections", count:secs, pct:secPct, tip:`${secs} section${secs === 1 ? "" : "s"} · ${secPct}% of composition`, run: () => openBuilderEdit(p.id) },
    { key:"revs", label:"Revisions", count:revs.length, pct:revPct, tip:`${revs.length} snapshot${revs.length === 1 ? "" : "s"} · ${revPct}% of composition`, run: () => openBuilderEdit(p.id) },
  ];

  /* SVG semicircle — butt caps keep tiny segments (e.g. 1 revision) true to %. */
  const gaugeArc = "M 20 100 A 80 80 0 0 1 180 100";
  const gaugeSegs = [
    { key: "pages", pct: pagePct, color: "#ff9f0a", offset: 0 },
    { key: "sections", pct: secPct, color: "#5b8cff", offset: pagePct },
    { key: "revs", pct: revPct, color: "#34c759", offset: pagePct + secPct },
  ];
  const gaugePaths = totalShare > 0
    ? gaugeSegs.filter(s => s.pct > 0).map(s => `
        <path class="console-gauge-seg" data-key="${s.key}" d="${gaugeArc}"
          fill="none" stroke="${s.color}" stroke-width="16" stroke-linecap="butt"
          pathLength="100" stroke-dasharray="${s.pct} ${100 - s.pct}" stroke-dashoffset="${-s.offset}" />`)
      .join("")
    : `<path class="console-gauge-track" d="${gaugeArc}" fill="none" stroke="var(--line-soft,#d7d7d7)" stroke-width="16" stroke-linecap="butt" />`;

  const comp = document.createElement("section");
  comp.className = "console-panel console-insight console-insight--comp liquid-glass";
  comp.innerHTML = `
    <div class="console-panel-head"><h2>Site composition</h2></div>
    <div class="console-comp-list" role="list">
      ${compParts.map(part => `
        <button type="button" class="console-comp-item" data-key="${part.key}" aria-label="${esc(part.tip)}">
          <i class="console-swatch ${part.key}" aria-hidden="true"></i>
          <strong>${esc(part.label)}</strong>
          <em>${part.count}</em>
        </button>`).join("")}
    </div>
    <div class="console-gauge" role="img" aria-label="Site composition gauge">
      <svg class="console-gauge-visual" viewBox="0 0 200 100" aria-hidden="true">${gaugePaths}</svg>
    </div>`;
  insights.appendChild(comp);
  const bindCompHover = (el, part) => {
    const setHot = on => {
      if (on) comp.dataset.hot = part.key;
      else if (comp.dataset.hot === part.key) delete comp.dataset.hot;
      comp.querySelectorAll(`[data-key="${part.key}"]`).forEach(n => n.classList.toggle("is-hot", on));
    };
    el.addEventListener("pointerenter", e => {
      setHot(true);
      showTip(`<strong>${esc(part.label)}</strong><span>${esc(part.tip)}</span>`, e.clientX, e.clientY);
    });
    el.addEventListener("pointermove", e => showTip(`<strong>${esc(part.label)}</strong><span>${esc(part.tip)}</span>`, e.clientX, e.clientY));
    el.addEventListener("pointerleave", () => { setHot(false); hideTip(); });
    el.addEventListener("click", () => part.run?.());
    el.addEventListener("keydown", e => { if (e.key === "Enter" || e.key === " "){ e.preventDefault(); part.run?.(); } });
  };
  compParts.forEach(part => {
    const row = comp.querySelector(`.console-comp-item[data-key="${part.key}"]`);
    if (row) bindCompHover(row, part);
    const seg = comp.querySelector(`.console-gauge-seg[data-key="${part.key}"]`);
    if (seg) bindCompHover(seg, part);
  });

  const days = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  const smoothPath = pts => {
    if (!pts.length) return "";
    if (pts.length === 1) return `M ${pts[0].x} ${pts[0].y}`;
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i++){
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C ${cp1x.toFixed(2)} ${cp1y.toFixed(2)}, ${cp2x.toFixed(2)} ${cp2y.toFixed(2)}, ${p2.x} ${p2.y}`;
    }
    return d;
  };

  /* —— Site uptime (line chart) —— */
  const usage = document.createElement("section");
  usage.className = "console-panel console-insight console-insight--uptime liquid-glass";
  const liveAt = prod?.published_at ? new Date(prod.published_at) : null;
  const liveOk = Number.isFinite(liveAt?.getTime?.());
  const upSeries = [];
  for (let i = 6; i >= 0; i--){
    const day = new Date();
    day.setHours(0, 0, 0, 0);
    day.setDate(day.getDate() - i);
    const next = new Date(day); next.setDate(next.getDate() + 1);
    const tracked = liveOk && liveAt < next;
    const hours = tracked ? 24 : 0;
    upSeries.push({
      label: days[day.getDay()].slice(0, 3),
      full: day.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" }),
      hours,
      tracked,
    });
  }
  const chartW = 320, chartH = 188, padL = 38, padR = 12, padT = 14, padB = 36;
  const plotW = chartW - padL - padR, plotH = chartH - padT - padB;
  const upPts = upSeries.map((s, i) => ({
    ...s,
    x: +(padL + (upSeries.length === 1 ? plotW / 2 : (i / (upSeries.length - 1)) * plotW)).toFixed(2),
    y: +(padT + plotH - (s.hours / 24) * plotH).toFixed(2),
  }));
  const yTicks = [24, 18, 12, 6, 0];

  const buildUptimePts = (w, h, pL = 44, pR = 16, pT = 16, pB = 36) => {
    const pw = w - pL - pR, ph = h - pT - pB;
    return {
      pad: { l: pL, r: pR, t: pT, b: pB },
      pts: upSeries.map((s, i) => ({
        ...s,
        x: +(pL + (upSeries.length === 1 ? pw / 2 : (i / (upSeries.length - 1)) * pw)).toFixed(2),
        y: +(pT + ph - (s.hours / 24) * ph).toFixed(2),
      })),
      plotH: ph,
    };
  };

  const uptimeChartHtml = (opts) => {
    const { w, h, fillId, pts, pad, plotH: ph } = opts;
    const line = smoothPath(pts);
    const area = pts.length
      ? `${line} L ${pts[pts.length - 1].x} ${pad.t + ph} L ${pts[0].x} ${pad.t + ph} Z`
      : "";
    return `
      <div class="console-uptime-chart${opts.large ? " console-uptime-chart--lg" : ""}" role="img" aria-label="Site uptime over the last 7 days">
        <svg viewBox="0 0 ${w} ${h}" preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="${fillId}" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stop-color="var(--good)" stop-opacity=".28"/>
              <stop offset="100%" stop-color="var(--good)" stop-opacity="0"/>
            </linearGradient>
          </defs>
          ${yTicks.map(hh => {
            const y = pad.t + ph - (hh / 24) * ph;
            return `<line class="console-up-grid" x1="${pad.l}" y1="${y}" x2="${w - pad.r}" y2="${y}"/><text class="console-up-ylab" x="${pad.l - 8}" y="${y + 3}" text-anchor="end">${hh}h</text>`;
          }).join("")}
          ${area ? `<path class="console-up-area" d="${area}" fill="url(#${fillId})"/>` : ""}
          ${line ? `<path class="console-up-line" d="${line}" fill="none"/>` : ""}
          ${pts.map((p, i) => `<circle class="console-up-dot" cx="${p.x}" cy="${p.y}" r="${opts.large ? 4 : 3.5}" data-i="${i}"/>`).join("")}
          <line class="console-up-guide" x1="0" y1="${pad.t}" x2="0" y2="${pad.t + ph}" hidden/>
          <circle class="console-up-scrub" cx="0" cy="0" r="${opts.large ? 6 : 5}" hidden/>
          ${pts.map(p => `<text class="console-up-xlab" x="${p.x}" y="${h - 8}" text-anchor="middle">${esc(p.label)}</text>`).join("")}
        </svg>
        <div class="console-up-hits" role="presentation">
          ${pts.map((p, i) => {
            const colW = upSeries.length > 1 ? (100 / upSeries.length) : 100;
            const left = Math.max(0, ((p.x / w) * 100) - colW / 2);
            return `<button type="button" class="console-up-hit" data-i="${i}"
              style="left:${left.toFixed(2)}%;width:${colW.toFixed(2)}%"
              aria-label="${esc(p.full)}: ${p.tracked ? p.hours + " hours online" : "Not tracked yet"}"></button>`;
          }).join("")}
        </div>
      </div>`;
  };

  const bindUptimeScrub = (scope, pts, tipFn, hideFn) => {
    const chart = scope.querySelector(".console-uptime-chart");
    const guide = scope.querySelector(".console-up-guide");
    const scrub = scope.querySelector(".console-up-scrub");
    const dots = [...scope.querySelectorAll(".console-up-dot")];
    const hits = [...scope.querySelectorAll(".console-up-hit")];
    const baseR = dots[0] ? (+dots[0].getAttribute("r") || 3.5) : 3.5;
    const setHover = (i, clientX, clientY) => {
      const s = pts[i];
      if (!s) return;
      chart?.classList.add("is-scrubbing");
      hits.forEach((h, hi) => h.classList.toggle("is-hot", hi === i));
      dots.forEach((d, di) => {
        const on = di === i;
        d.classList.toggle("is-hot", on);
        d.setAttribute("r", on ? "0.01" : String(baseR));
        d.style.opacity = on ? "0" : "";
      });
      if (guide) {
        guide.setAttribute("x1", s.x);
        guide.setAttribute("x2", s.x);
        guide.removeAttribute("hidden");
      }
      if (scrub) {
        scrub.setAttribute("cx", s.x);
        scrub.setAttribute("cy", s.y);
        scrub.removeAttribute("hidden");
      }
      const day = s.full.split(",")[0] || s.full;
      const detail = s.tracked
        ? (s.hours === 24 ? "24 hours online" : `${s.hours} hour${s.hours === 1 ? "" : "s"} online`)
        : "Not tracked yet";
      tipFn(`<strong>${esc(day)}</strong><span>${esc(detail)}</span>`, clientX, clientY);
    };
    const clearHover = () => {
      chart?.classList.remove("is-scrubbing");
      hits.forEach(h => h.classList.remove("is-hot"));
      dots.forEach(d => {
        d.classList.remove("is-hot");
        d.setAttribute("r", String(baseR));
        d.style.opacity = "";
      });
      if (guide) guide.setAttribute("hidden", "");
      if (scrub) scrub.setAttribute("hidden", "");
      hideFn();
    };
    hits.forEach(hit => {
      hit.addEventListener("pointerenter", e => setHover(+hit.dataset.i, e.clientX, e.clientY));
      hit.addEventListener("pointermove", e => setHover(+hit.dataset.i, e.clientX, e.clientY));
      hit.addEventListener("pointerleave", clearHover);
      hit.addEventListener("focus", () => {
        const r = hit.getBoundingClientRect();
        setHover(+hit.dataset.i, r.left + r.width / 2, r.top + 8);
      });
      hit.addEventListener("blur", clearHover);
    });
    scope.addEventListener("pointerleave", clearHover);
    return clearHover;
  };

  const cardGeom = buildUptimePts(chartW, chartH, padL, padR, padT, padB);
  const hasUptime = upSeries.some(s => s.tracked && s.hours > 0);
  usage.innerHTML = `
    <div class="console-panel-head">
      <h2>Site uptime</h2>
      <button type="button" class="console-chart-expand" id="up-expand" title="Open larger chart" aria-label="Open larger chart">${lu("maximize-2","lu14")}</button>
    </div>
    ${uptimeChartHtml({
      w: chartW, h: chartH, fillId: "consoleUpFill",
      pts: cardGeom.pts, pad: cardGeom.pad, plotH: cardGeom.plotH,
    })}
    ${hasUptime ? "" : `<p class="console-uptime-empty">Tracking starts after the first production publish. Axes show the last 7 days.</p>`}`;
  insights.appendChild(usage);
  const clearCardHover = bindUptimeScrub(usage, cardGeom.pts, showTip, hideTip);

  /* —— Page visits —— */
  const legalPack = siteLegalPack(p);
  const visitsWanted = !!p.data.advanced?.studioAnalytics && legalPack.ready;
  const visits = document.createElement("section");
  visits.className = "console-panel console-insight console-insight--visits liquid-glass";
  visits.innerHTML = `
    <div class="console-panel-head">
      <h2>Visits</h2>
      <span class="console-uptime-chip" id="visits-chip">${visitsWanted ? "Loading" : "Off"}</span>
    </div>
    <div class="console-visits" id="visits-body">
      ${visitsWanted
        ? `<p class="console-uptime-sub">Loading page views…</p>`
        : `<div class="console-visits-cta">
            <p class="console-uptime-sub">Connect a custom domain, enable privacy &amp; terms, then turn on Studio visits to see cookieless page views here.</p>
            <button type="button" class="btn btn-ghost btn-sm" id="visits-enable">Open privacy &amp; analytics</button>
          </div>`}
    </div>`;
  insights.appendChild(visits);
  visits.querySelector("#visits-enable")?.addEventListener("click", () => goSettings("advanced"));

  const paintVisits = (stats) => {
    const body = visits.querySelector("#visits-body");
    const chip = visits.querySelector("#visits-chip");
    if (!body) return;
    if (!visitsWanted) return;
    const period = stats?.period_views ?? 0;
    const series = Array.isArray(stats?.days) ? stats.days : [];
    const top = Array.isArray(stats?.top_paths) ? stats.top_paths : [];
    const max = Math.max(1, ...series.map(d => d.views || 0));
    if (chip) {
      chip.textContent = period ? `${period} / 7d` : "Live";
      chip.className = `console-uptime-chip ${period ? "good" : ""}`;
    }
    const spark = series.length
      ? `<div class="console-uptime-bars console-visits-bars" role="img" aria-label="Page views last 7 days">
          ${series.map(d => {
            const h = Math.max(d.views ? 14 : 6, Math.round(((d.views || 0) / max) * 36));
            return `<div class="console-uptime-day ${d.views ? "is-up" : "is-idle"}" title="${esc((d.day || "") + ": " + (d.views || 0) + " view" + ((d.views || 0) === 1 ? "" : "s"))}">
              <i style="height:${h}px"></i><span>${esc((d.label || "").slice(0, 1))}</span>
            </div>`;
          }).join("")}
        </div>`
      : "";
    const paths = top.length
      ? `<ul class="console-visits-paths">${top.map(row =>
          `<li><span>${esc(row.path)}</span><em>${row.views}</em></li>`).join("")}</ul>`
      : `<p class="console-uptime-empty">Waiting for traffic after you publish with visits on.</p>`;
    body.innerHTML = `
      <div class="console-uptime-hero">
        <button type="button" class="console-uptime-pct ${period ? "good" : ""}" id="visits-total" title="Views last 7 days">${period}</button>
        <p class="console-uptime-sub">${period ? "views in the last 7 days" : "No views yet · cookieless counts only"}</p>
      </div>
      ${spark}
      ${paths}`;
  };

  if (visitsWanted) {
    api.getVisitStats(p.id).then(paintVisits).catch(() => {
      const chip = visits.querySelector("#visits-chip");
      if (chip) { chip.textContent = "—"; chip.className = "console-uptime-chip"; }
      const body = visits.querySelector("#visits-body");
      if (body) body.innerHTML = `<p class="console-uptime-empty">Could not load visit stats right now.</p>`;
    });
  }

  usage.querySelector("#up-expand").onclick = () => {
    clearCardHover();
    const mw = 760, mh = 380;
    const modalGeom = buildUptimePts(mw, mh, 56, 24, 24, 48);
    const m = openModal(`
      <div class="console-chart-modal-head">
        <div>
          <h2 id="modal-title">Site uptime</h2>
          <p class="sub">Last 7 days · hours online per day</p>
        </div>
        <button type="button" class="icon-close-btn" data-x="c" aria-label="Close">${lu("x","lu16")}</button>
      </div>
      ${uptimeChartHtml({
        w: mw, h: mh, fillId: "consoleUpFillModal", large: true,
        pts: modalGeom.pts, pad: modalGeom.pad, plotH: modalGeom.plotH,
      })}
      <div class="console-chart-modal-foot">
        <p class="c-sub">Hover or focus a day to inspect uptime. Live tracking starts after the first production publish.</p>
      </div>`, true);
    m.classList.add("console-chart-modal");
    m.querySelector("[data-x=c]").onclick = closeModal;
    const modalTip = document.createElement("div");
    modalTip.className = "console-tip";
    modalTip.hidden = true;
    m.appendChild(modalTip);
    const showModalTip = (html, x, y) => {
      modalTip.innerHTML = html;
      modalTip.hidden = false;
      modalTip.style.opacity = "0";
      const pad = 14;
      const tw = modalTip.offsetWidth || 140;
      const th = modalTip.offsetHeight || 40;
      const box = m.getBoundingClientRect();
      const left = Math.min(box.right - tw - 12, Math.max(box.left + 12, x + 16));
      const top = Math.min(box.bottom - th - 12, Math.max(box.top + 12, y - th - 14));
      modalTip.style.left = left + "px";
      modalTip.style.top = top + "px";
      modalTip.style.opacity = "1";
    };
    const hideModalTip = () => { modalTip.hidden = true; modalTip.innerHTML = ""; };
    bindUptimeScrub(m, modalGeom.pts, showModalTip, hideModalTip);
    icons();
  };

  /* —— Health & rhythm (score + day bars, former uptime layout) —— */
  const spark = [];
  for (let i = 6; i >= 0; i--){
    const day = new Date();
    day.setHours(0,0,0,0);
    day.setDate(day.getDate() - i);
    const next = new Date(day); next.setDate(next.getDate() + 1);
    const n = actRows.filter(a => {
      const t = new Date(a.created_at).getTime();
      return t >= day.getTime() && t < next.getTime();
    }).length;
    spark.push({
      label: days[day.getDay()].slice(0, 1),
      n,
      full: day.toLocaleDateString(undefined, { weekday:"long", month:"short", day:"numeric" }),
    });
  }
  const sparkMax = Math.max(1, ...spark.map(s => s.n));
  const health = document.createElement("section");
  health.className = "console-panel console-insight console-insight--health liquid-glass";
  const healthTone = healthScore == null ? "" : healthScore >= 80 ? "good" : healthScore >= 50 ? "warn" : "bad";
  const healthStatus = healthScore == null ? "No audit" : healthScore >= 80 ? "Healthy" : healthScore >= 50 ? "Needs work" : "At risk";
  const healthSub = hasContent
    ? `${audit?.crit || 0} critical · ${audit?.warn || 0} warnings · last 7 days of edits`
    : "Run a health check after you add content";
  health.innerHTML = `
    <div class="console-panel-head">
      <h2>Health &amp; rhythm</h2>
      <span class="console-uptime-chip ${healthTone}">${esc(healthStatus)}</span>
    </div>
    <div class="console-uptime">
      <div class="console-uptime-hero">
        <button type="button" class="console-uptime-pct ${healthTone}" id="insight-health-score" title="Open health">${healthScore == null ? "—" : healthScore}</button>
        <p class="console-uptime-sub">${esc(healthSub)}</p>
    </div>
      <div class="console-uptime-bars" role="img" aria-label="Edit rhythm last 7 days">
        ${spark.map(d => {
          const h = Math.max(d.n ? 18 : 8, Math.round((d.n / sparkMax) * 36));
          return `<div class="console-uptime-day ${d.n ? "is-up" : "is-idle"}" title="${esc(d.full + ": " + d.n + " edit" + (d.n === 1 ? "" : "s"))}">
            <i style="height:${h}px"></i><span>${esc(d.label)}</span>
    </div>`;
        }).join("")}
      </div>
      <button type="button" class="console-health-cta" id="insight-health">
        ${hasContent
          ? `<span class="console-health-counts"><em class="crit">${audit?.crit || 0} critical</em><em class="warn">${audit?.warn || 0} warnings</em></span>`
          : `<span class="console-health-counts"><em>No audit yet</em></span>`}
        <span class="console-health-go">Open health ${lu("chevron-right","lu14")}</span>
      </button>
    </div>`;
  insights.appendChild(health);
  health.querySelector("#insight-health").onclick = () => go("health");
  health.querySelector("#insight-health-score").onclick = () => go("health");

  icons();
}

/* ============================== SITES (unified Design + All sites) ============================== */
/* ================================ AGENT ================================ */
/* ================================ LUMEN ================================ */
const LUMEN_PREFS_KEY = "bd_lumen_prefs";
const LUMEN_PROFILES = [
  { id: "pulse-1-flash", label: "Pulse 1 Flash", hint: "Nearly instant answers and drafts", icon: "/assets/img/pulse-1-flash.png" },
  { id: "pulse-1", label: "Pulse 1", hint: "Highest craft for final sites", icon: "/assets/img/pulse-1.png" },
];
const LUMEN_STYLES = [
  { id: "concise", label: "Concise", hint: "Tighter pages, fewer sections" },
  { id: "balanced", label: "Balanced", hint: "Solid default for most briefs" },
  { id: "detailed", label: "Detailed", hint: "Denser stacks and richer copy" },
];
function lumenNormalizeProfile(id){
  let s = String(id || "").trim();
  const legacy = { lumen: "pulse-1-flash", "lumen-1-flash": "pulse-1-flash", "lumen-1": "pulse-1", "studio-ai-flash": "pulse-1-flash", "studio-ai": "pulse-1" };
  if (legacy[s]) s = legacy[s];
  if (LUMEN_PROFILES.some(p => p.id === s)) return s;
  return "pulse-1-flash";
}
function lumenProfileById(id){
  const pid = lumenNormalizeProfile(id);
  return LUMEN_PROFILES.find(p => p.id === pid) || LUMEN_PROFILES[0];
}
function lumenModelIconHtml(profile, cls = "lumen-model-icon"){
  const p = profile && profile.icon ? profile : lumenProfileById(profile);
  if (!p?.icon) return "";
  return `<img class="${esc(cls)}" src="${esc(p.icon)}" alt="" width="20" height="20" decoding="async">`;
}
function lumenNormalizeStyle(id){
  const s = String(id || "").trim();
  if (LUMEN_STYLES.some(p => p.id === s)) return s;
  return "balanced";
}
function lumenPrefs(){
  let raw = {};
  try { raw = JSON.parse(localStorage.getItem(LUMEN_PREFS_KEY) || "{}"); } catch { raw = {}; }
  return {
    modelProfile: lumenNormalizeProfile(raw.modelProfile),
    responseStyle: lumenNormalizeStyle(raw.responseStyle),
    /* Studio tools are always required - Pulse cannot be bypassed. */
    allowTools: true,
    streamReplies: !!raw.streamReplies,
  };
}
function savePulsePrefs(patch){
  const next = Object.assign(lumenPrefs(), patch || {});
  next.modelProfile = lumenNormalizeProfile(next.modelProfile);
  next.responseStyle = lumenNormalizeStyle(next.responseStyle);
  next.allowTools = true;
  try { localStorage.setItem(LUMEN_PREFS_KEY, JSON.stringify(next)); } catch {}
  return next;
}
function lumenProfileCardsHtml(selected, attr = "data-lumen-profile"){
  return `<div class="lumen-choice-grid" role="radiogroup" aria-label="Pulse model">
    ${LUMEN_PROFILES.map(p => `
      <button type="button" class="lumen-choice-card${selected === p.id ? " on" : ""}" role="radio" aria-checked="${selected === p.id ? "true" : "false"}" ${attr}="${esc(p.id)}">
        <span class="lumen-choice-head">
          ${lumenModelIconHtml(p, "lumen-model-icon lumen-model-icon--lg")}
          <span class="lumen-choice-name">${esc(p.label)}</span>
        </span>
        <span class="lumen-choice-hint">${esc(p.hint)}</span>
      </button>`).join("")}
  </div>`;
}
function lumenStyleCardsHtml(selected){
  return `<div class="lumen-choice-grid lumen-choice-grid--3" role="radiogroup" aria-label="Response style">
    ${LUMEN_STYLES.map(p => `
      <button type="button" class="lumen-choice-card${selected === p.id ? " on" : ""}" role="radio" aria-checked="${selected === p.id ? "true" : "false"}" data-lumen-style="${esc(p.id)}">
        <span class="lumen-choice-name">${esc(p.label)}</span>
        <span class="lumen-choice-hint">${esc(p.hint)}</span>
      </button>`).join("")}
  </div>`;
}
/** Custom pill dropdown for Pulse settings (avoids native OS select chrome). */
function lumenPickerHtml(id, options, selected, ariaLabel){
  const cur = options.find(o => o.id === selected) || options[0] || { id: "", label: "Select" };
  const showIcons = options.some(o => o.icon);
  return `<div class="lumen-picker${showIcons ? " lumen-picker--models" : ""}" data-picker-id="${esc(id)}">
    <button type="button" class="lumen-picker-btn" id="${esc(id)}-btn" aria-haspopup="listbox" aria-expanded="false" aria-label="${esc(ariaLabel)}">
      ${showIcons ? `<span class="lumen-picker-icon" data-picker-icon>${lumenModelIconHtml(cur)}</span>` : ""}
      <span class="lumen-picker-label">${esc(cur.label)}</span>
      ${lu("chevron-down","lu14")}
    </button>
    <div class="lumen-picker-menu" id="${esc(id)}-menu" role="listbox" hidden>
      ${options.map(o => `
        <button type="button" class="lumen-picker-option${o.id === cur.id ? " on" : ""}" role="option" aria-selected="${o.id === cur.id ? "true" : "false"}" data-value="${esc(o.id)}">
          ${o.icon ? lumenModelIconHtml(o) : ""}
          <span class="lumen-picker-option-main">
            <span class="lumen-picker-option-name">${esc(o.label)}</span>
            ${o.hint ? `<span class="lumen-picker-option-hint">${esc(o.hint)}</span>` : ""}
          </span>
          <span class="lumen-picker-check" aria-hidden="true">${o.id === cur.id ? lu("check","lu14") : ""}</span>
        </button>`).join("")}
    </div>
  </div>`;
}
function bindLumenPicker(root, id, { onChange } = {}){
  const wrap = root.querySelector(`[data-picker-id="${id}"]`);
  if (!wrap) return;
  const btn = wrap.querySelector(".lumen-picker-btn");
  const menu = wrap.querySelector(".lumen-picker-menu");
  const label = wrap.querySelector(".lumen-picker-label");
  const iconHost = wrap.querySelector("[data-picker-icon]");
  const close = () => {
    menu.hidden = true;
    wrap.classList.remove("open");
    btn.setAttribute("aria-expanded", "false");
    menu.classList.remove("lumen-picker-menu--fixed", "lumen-picker-menu--up");
    menu.style.top = "";
    menu.style.bottom = "";
    menu.style.left = "";
    menu.style.right = "";
    menu.style.width = "";
    if (menu.parentElement !== wrap) wrap.appendChild(menu);
  };
  const placeMenu = () => {
    const rect = btn.getBoundingClientRect();
    const gap = 8;
    const minW = Math.max(rect.width, 260);
    const pad = 12;
    if (menu.parentElement !== document.body) document.body.appendChild(menu);
    menu.classList.add("lumen-picker-menu--fixed");
    menu.style.width = `${minW}px`;
    menu.hidden = false;
    const menuH = menu.offsetHeight || 180;
    const spaceBelow = window.innerHeight - rect.bottom - pad;
    const spaceAbove = rect.top - pad;
    const openUp = spaceBelow < menuH + 8 && spaceAbove > spaceBelow;
    menu.classList.toggle("lumen-picker-menu--up", openUp);
    let left = rect.right - minW;
    left = Math.max(pad, Math.min(left, window.innerWidth - minW - pad));
    menu.style.left = `${Math.round(left)}px`;
    menu.style.right = "auto";
    if (openUp) {
      menu.style.top = "auto";
      menu.style.bottom = `${Math.round(window.innerHeight - rect.top + gap)}px`;
    } else {
      menu.style.bottom = "auto";
      menu.style.top = `${Math.round(rect.bottom + gap)}px`;
    }
  };
  const open = () => {
    root.querySelectorAll(".lumen-picker.open").forEach(other => {
      if (other === wrap) return;
      other.classList.remove("open");
      const m = other.querySelector(".lumen-picker-menu") || document.querySelector(`.lumen-picker-menu[data-for="${other.getAttribute("data-picker-id")}"]`);
      const b = other.querySelector(".lumen-picker-btn");
      if (m) {
        m.hidden = true;
        m.classList.remove("lumen-picker-menu--fixed", "lumen-picker-menu--up");
        m.style.top = m.style.bottom = m.style.left = m.style.right = m.style.width = "";
        const home = document.querySelector(`[data-picker-id="${other.getAttribute("data-picker-id")}"]`);
        if (home && m.parentElement !== home) home.appendChild(m);
      }
      if (b) b.setAttribute("aria-expanded", "false");
    });
    menu.setAttribute("data-for", id);
    wrap.classList.add("open");
    btn.setAttribute("aria-expanded", "true");
    placeMenu();
    icons();
  };
  btn.onclick = (e) => {
    e.stopPropagation();
    if (wrap.classList.contains("open")) close();
    else open();
  };
  menu.querySelectorAll("[data-value]").forEach(opt => {
    opt.onclick = async (e) => {
      e.stopPropagation();
      const value = opt.getAttribute("data-value");
      const name = opt.querySelector(".lumen-picker-option-name")?.textContent || value;
      label.textContent = name;
      if (iconHost) {
        const optIcon = opt.querySelector(".lumen-model-icon");
        if (optIcon) iconHost.innerHTML = optIcon.outerHTML;
        else iconHost.innerHTML = lumenModelIconHtml(lumenProfileById(value));
      }
      menu.querySelectorAll(".lumen-picker-option").forEach(o => {
        const on = o.getAttribute("data-value") === value;
        o.classList.toggle("on", on);
        o.setAttribute("aria-selected", on ? "true" : "false");
        const check = o.querySelector(".lumen-picker-check");
        if (check) check.innerHTML = on ? lu("check","lu14") : "";
      });
      icons();
      close();
      if (typeof onChange === "function") await onChange(value);
    };
  });
  const onDoc = (e) => {
    if (!wrap.contains(e.target) && !menu.contains(e.target)) close();
  };
  const onScroll = () => {
    if (wrap.classList.contains("open")) placeMenu();
  };
  document.addEventListener("mousedown", onDoc);
  window.addEventListener("resize", onScroll);
  window.addEventListener("scroll", onScroll, true);
  wrap._lumenPickerCleanup = () => {
    close();
    document.removeEventListener("mousedown", onDoc);
    window.removeEventListener("resize", onScroll);
    window.removeEventListener("scroll", onScroll, true);
  };
}
async function persistLumenProfile(profileId){
  const profile = lumenNormalizeProfile(profileId);
  savePulsePrefs({ modelProfile: profile });
  try {
    await openAIRequest({ action: "save-config", profile });
  } catch (e) {
    console.warn("Could not sync Pulse profile", e);
  }
  return profile;
}

function openLumenSettings(startTab = "model"){
  let prefs = lumenPrefs();
  let tab = startTab;
  let config = { ready: false, has_key: false, profile: prefs.modelProfile, model: "", model_label: "Pulse", ai_credits_used: 0, profiles: LUMEN_PROFILES };
  const planLabel = (typeof bdPlanDisplayLabel === "function" ? bdPlanDisplayLabel(plan) : null)
    || (PLAN_RULES[plan] || PLAN_RULES.free)?.label
    || "your plan";
  const paidLumen = ["build", "forge", "architect", "pro", "studio"].includes(String(plan || "").toLowerCase());

  const m = openModal(`
    <div class="lumen-settings">
      <aside class="lumen-settings-nav" aria-label="Pulse settings">
        <button type="button" class="lumen-nav-btn on" data-lumen-tab="model">${lu("sparkles","lu16")}<span>Model</span></button>
        <button type="button" class="lumen-nav-btn" data-lumen-tab="behavior">${lu("sliders-horizontal","lu16")}<span>Behavior</span></button>
        <button type="button" class="lumen-nav-btn" data-lumen-tab="spendings">${lu("wallet","lu16")}<span>Spendings</span></button>
        <div class="lumen-nav-sep"></div>
        <button type="button" class="lumen-nav-btn" data-lumen-tab="about">${lu("info","lu16")}<span>About</span></button>
      </aside>
      <div class="lumen-settings-main">
        <div class="lumen-settings-head">
          <div>
            <h2>Pulse settings</h2>
            <p class="lumen-settings-sub" id="lumen-settings-sub">Choose how Pulse thinks and builds.</p>
          </div>
          <button type="button" class="modal-close" data-modal-close aria-label="Close">&times;</button>
        </div>
        <div class="lumen-settings-body" id="lumen-settings-body"></div>
      </div>
    </div>
  `, true);
  m.classList.add("lumen-settings-modal");
  icons();
  m.querySelectorAll("[data-modal-close]").forEach(b => b.onclick = closeModal);

  const subs = {
    model: "Choose how Pulse thinks and replies.",
    behavior: "Control how Pulse uses Studio tools and replies.",
    spendings: "Track weekly Pulse token usage and top-up spend.",
    about: "What Pulse is inside Browns Digital Studio.",
  };

  const paintBody = () => {
    prefs = lumenPrefs();
    const body = m.querySelector("#lumen-settings-body");
    const sub = m.querySelector("#lumen-settings-sub");
    if (sub) sub.textContent = subs[tab] || subs.model;
    m.querySelectorAll("[data-lumen-tab]").forEach(b => b.classList.toggle("on", b.dataset.lumenTab === tab));

    if (tab === "model"){
      const ready = !!(config.ready || config.has_key);
      const profiles = config.profiles || LUMEN_PROFILES;
      const activeLabel = profiles.find(p => p.id === prefs.modelProfile)?.label
        || LUMEN_PROFILES.find(p => p.id === prefs.modelProfile)?.label
        || "Pulse 1 Flash";
      body.innerHTML = `
        <div class="lumen-card">
          <div class="lumen-row">
            <div>
              <strong>Model profile</strong>
              <p>Pick Pulse 1 Flash for nearly instant answers or Pulse 1 for maximum craft.</p>
            </div>
            ${lumenPickerHtml("lumen-profile", profiles, prefs.modelProfile, "Model profile")}
          </div>
          <div class="lumen-row">
            <div>
              <strong>Response style</strong>
              <p>How dense and detailed generated sites should feel.</p>
            </div>
            ${lumenPickerHtml("lumen-style", LUMEN_STYLES, prefs.responseStyle, "Response style")}
          </div>
          <div class="lumen-row">
            <div>
              <strong>Status</strong>
              <p>${ready
                ? `Ready · ${esc(activeLabel)}`
                : "Pulse is not available on this server yet. Contact support@brownsdigital.org."}</p>
            </div>
            <span class="acct-status ${ready ? "on" : ""}">${ready ? "Active" : "Offline"}</span>
          </div>
        </div>`;
      icons();
      bindLumenPicker(body, "lumen-profile", {
        onChange: async (id) => {
          await persistLumenProfile(id);
          config.profile = id;
          paintBody();
        }
      });
      bindLumenPicker(body, "lumen-style", {
        onChange: (id) => { savePulsePrefs({ responseStyle: id }); }
      });
    } else if (tab === "behavior"){
      body.innerHTML = `
        <div class="lumen-card">
          <div class="lumen-row">
            <div>
              <strong>Studio tools</strong>
              <p>Always on. Pulse must use real Studio builder blocks — this cannot be turned off.</p>
            </div>
            <span class="acct-status on">Required</span>
          </div>
          <div class="lumen-row">
            <div>
              <strong>Ask before building</strong>
              <p>If Pulse is missing critical details, it asks questions first instead of guessing.</p>
            </div>
            <span class="acct-status on">Required</span>
          </div>
          <div class="lumen-row">
            <div>
              <strong>Show build progress</strong>
              <p>Updates the Pulse status while pages are generated.</p>
            </div>
            <label class="lumen-switch"><input type="checkbox" id="lumen-stream" ${prefs.streamReplies ? "checked" : ""}><span></span></label>
          </div>
        </div>`;
      body.querySelector("#lumen-stream").onchange = e => savePulsePrefs({ streamReplies: !!e.target.checked });
    } else if (tab === "spendings"){
      const used = Number(config.ai_credits_used || 0);
      const usedLabel = Number.isFinite(used) ? used.toLocaleString() : "0";
      body.innerHTML = `
        <div class="lumen-spend">
          <div class="lumen-spend-grid">
            <div class="lumen-card lumen-spend-card">
              <p class="lumen-kicker">This week</p>
              <strong class="lumen-metric">${esc(usedLabel)} used</strong>
              <p>${paidLumen ? "Each 1,000 tokens uses 1 credit from your plan, then top-ups." : "Upgrade to Build for Pulse site generation."}</p>
            </div>
            <div class="lumen-card lumen-spend-card">
              <p class="lumen-kicker">Top-up spend</p>
              <strong class="lumen-metric">R0.00</strong>
              <p>No Pulse token packs yet.</p>
            </div>
          </div>
          <div class="lumen-card lumen-spend-panel">
            <strong>${paidLumen ? "Paid plan" : "Upgrade for Pulse"}</strong>
            <p>${paidLumen
              ? `Pulse on ${esc(planLabel)} spends your monthly credits. Top-ups cover the rest.`
              : "Pulse builds Studio sites on Build, Forge, and Architect. Free accounts can browse settings but cannot generate yet."}</p>
            <button type="button" class="lumen-text-link" id="lumen-plans">View plans</button>
          </div>
          <div class="lumen-card lumen-spend-panel">
            <strong>Purchase history</strong>
            <p>Fulfilled Pulse token packs on this account.</p>
            <div class="lumen-empty">
              <p>No Pulse token purchases yet.</p>
              <a class="lumen-text-link" href="https://brownsdigital.org/#pricing" target="_blank" rel="noopener">Open shop</a>
            </div>
          </div>
        </div>`;
      body.querySelector("#lumen-plans")?.addEventListener("click", () => {
        closeModal();
        go("account");
        setTimeout(() => document.querySelector('[data-acct-tab="subscription"]')?.click(), 120);
      });
    } else {
      body.innerHTML = `
        <div class="lumen-card">
          <p class="lumen-about">Pulse is Browns Digital's site-building AI. It plans multi-page sites and writes Studio builder blocks — production-ready composition, not generic template dumps. Browns Digital runs the model; you just describe what to build.</p>
        </div>`;
    }
  };

  m.querySelectorAll("[data-lumen-tab]").forEach(btn => {
    btn.onclick = () => { tab = btn.dataset.lumenTab; paintBody(); };
  });

  paintBody();
  openAIRequest({ action: "get-config" }).then(data => {
    config = Object.assign(config, data || {});
    if (data?.profile) savePulsePrefs({ modelProfile: data.profile });
    if (Array.isArray(data?.profiles) && data.profiles.length) config.profiles = data.profiles;
    paintBody();
  }).catch(() => {});
}

async function pageAgent(el){
  const first = displayNameOf(me).split(/\s+/)[0] || "there";
  const placeholders = [
    "Build a premium SaaS homepage for a design-ops product",
    "Create a polished agency site with case studies and pricing",
    "Plan a multi-page brand site for a boutique coffee roaster",
    "Design a product launch page with proof, pricing, and FAQ",
    "Build a studio portfolio with a bold hero and project gallery",
    "Create a fintech marketing site with trust stats and clear CTAs",
    "Make a restaurant site with menu highlights and reservation CTA",
    "Generate a nonprofit site with impact stats and a donate section",
    "Draft a wellness brand site with services, team, and booking CTA",
    "Build an e-commerce launch page with product grid and reviews",
  ];
  const shuffle = (arr) => {
    const a = arr.slice();
    for (let i = a.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      const tmp = a[i]; a[i] = a[j]; a[j] = tmp;
    }
    return a;
  };
  let phrases = shuffle(placeholders);
  let phIdx = 0;
  const shell = document.createElement("div");
  shell.className = "agent-page reveal";
  shell.innerHTML = `
    <div class="agent-rail" role="toolbar" aria-label="Pulse">
      <div class="agent-switch">
        <button type="button" class="agent-switch-btn on" id="agent-tab-chat" title="Chat" aria-label="Chat" aria-pressed="true">${lu("message-square","lu16")}</button>
        <button type="button" class="agent-switch-btn" id="agent-tab-settings" title="Pulse settings" aria-label="Pulse settings" aria-pressed="false">${lu("settings","lu16")}</button>
      </div>
    </div>
    <div class="agent-stage">
      <h1 class="agent-title">What can I help with, ${esc(first)}?</h1>
      <div class="agent-thread" id="agent-thread" hidden></div>
      <form class="agent-compose" id="agent-form" autocomplete="off">
        <button type="button" class="agent-plus" id="agent-plus" title="New chat" aria-label="New chat">${lu("plus","lu18")}</button>
        <div class="agent-input-wrap">
          <span class="agent-ph" id="agent-ph" aria-hidden="true"></span>
          <input type="text" id="agent-input" name="prompt" maxlength="2000"
            placeholder=""
            aria-label="Ask Pulse" />
        </div>
        <button type="submit" class="agent-send is-idle" id="agent-send" title="Send" aria-label="Send" disabled>${lu("arrow-up","lu18")}</button>
      </form>
      <p class="agent-disclaimer">Pulse · Studio builder blocks. It asks when details are missing. Can make mistakes — check important information and actions.</p>
      <p class="agent-status" id="agent-status" role="status" hidden></p>
    </div>`;
  el.appendChild(shell);
  icons();

  const form = shell.querySelector("#agent-form");
  const input = shell.querySelector("#agent-input");
  const phEl = shell.querySelector("#agent-ph");
  const status = shell.querySelector("#agent-status");
  const threadEl = shell.querySelector("#agent-thread");
  const titleEl = shell.querySelector(".agent-title");
  const sendBtn = shell.querySelector("#agent-send");
  let turns = [];
  let awaitingAnswers = false;
  const paintThread = () => {
    if (!turns.length){
      threadEl.hidden = true;
      threadEl.innerHTML = "";
      return;
    }
    threadEl.hidden = false;
    threadEl.innerHTML = turns.map(turn => {
      const cls = turn.role === 'luna' ? 'agent-msg agent-msg--luna' : 'agent-msg agent-msg--user';
      const body = turn.html || `<p>${esc(turn.text || '')}</p>`;
      return `<div class="${cls}">${body}</div>`;
    }).join('');
  };
  shell.querySelector("#agent-tab-settings").onclick = () => openLumenSettings("model");
  shell.querySelector("#agent-tab-chat").onclick = () => input.focus();

  let phAlive = true;
  let phTimer = 0;
  let phToken = 0;
  const wait = (ms) => new Promise(r => {
    phTimer = window.setTimeout(r, ms);
  });
  const stopPh = () => {
    phAlive = false;
    window.clearTimeout(phTimer);
    phToken += 1;
  };
  const inputEmpty = () => !(input.value || "").trim();
  const syncSend = () => {
    const busy = sendBtn.classList.contains("is-busy");
    const empty = inputEmpty();
    sendBtn.classList.toggle("is-idle", empty && !busy);
    sendBtn.disabled = busy || empty;
    sendBtn.setAttribute("aria-disabled", sendBtn.disabled ? "true" : "false");
  };
  const syncPhVisibility = () => {
    phEl.hidden = !inputEmpty();
    phEl.classList.toggle("agent-ph--hidden", !inputEmpty());
    syncSend();
  };
  const typePhrase = async (text, token) => {
    phEl.textContent = "";
    phEl.classList.remove("agent-ph--out");
    for (let i = 1; i <= text.length; i += 1) {
      if (!phAlive || token !== phToken || !inputEmpty()) return false;
      phEl.textContent = text.slice(0, i);
      await wait(i === 1 ? 40 : 16 + (i % 5 === 0 ? 10 : 0));
    }
    return true;
  };
  const runPhCycle = async () => {
    const token = ++phToken;
    while (phAlive && token === phToken) {
      if (!inputEmpty()) {
        syncPhVisibility();
        await wait(400);
        continue;
      }
      syncPhVisibility();
      if (phIdx >= phrases.length) {
        phrases = shuffle(placeholders);
        phIdx = 0;
      }
      const next = phrases[phIdx++];
      phEl.classList.add("agent-ph--out");
      await wait(220);
      if (!phAlive || token !== phToken) return;
      const ok = await typePhrase(next, token);
      if (!ok || !phAlive || token !== phToken) return;
      await wait(3400);
      if (!phAlive || token !== phToken) return;
      phEl.classList.add("agent-ph--out");
      await wait(280);
    }
  };
  const resetConversation = () => {
    turns = [];
    awaitingAnswers = false;
    paintThread();
    titleEl.textContent = `What can I help with, ${first}?`;
    status.hidden = true;
    status.textContent = '';
    input.value = '';
    syncPhVisibility();
    input.focus();
  };
  shell.querySelector("#agent-plus").onclick = () => resetConversation();
  syncPhVisibility();
  runPhCycle();
  input.addEventListener("input", syncPhVisibility);
  input.addEventListener("focus", syncPhVisibility);
  input.addEventListener("blur", () => {
    if (inputEmpty() && phAlive) syncPhVisibility();
  });
  el.addEventListener("bd:pageleave", stopPh, { once: true });
  const obs = new MutationObserver(() => {
    if (!document.body.contains(shell)){ stopPh(); obs.disconnect(); }
  });
  obs.observe(document.body, { childList: true, subtree: true });

  const setBusy = (on, msg) => {
    sendBtn.classList.toggle("is-busy", !!on);
    input.disabled = on;
    if (on){
      sendBtn.innerHTML = lu("loader-circle","lu18");
      sendBtn.disabled = true;
    } else {
      sendBtn.innerHTML = lu("arrow-up","lu18");
      syncSend();
    }
    icons();
    if (msg){
      status.hidden = false;
      status.innerHTML = `${lu("loader-circle")} ${esc(msg)}`;
      icons();
    } else if (!on){
      status.hidden = true;
      status.textContent = "";
    }
  };

  form.onsubmit = async (e) => {
    e.preventDefault();
    const prompt = (input.value || "").trim();
    if (!prompt){ input.focus(); return; }

    let ready = false;
    try {
      const data = await openAIRequest({ action: "get-config" });
      ready = !!(data.ready || data.has_key);
      if (data.profile) savePulsePrefs({ modelProfile: data.profile });
    } catch {
      ready = false;
    }
    if (!ready){
      status.hidden = false;
      status.textContent = "Pulse is not available right now. Try again later or contact support@brownsdigital.org.";
      return;
    }

    turns.push({ role: 'user', text: prompt });
    paintThread();
    input.value = '';
    syncPhVisibility();
    stopPh();

    try {
      setBusy(true, awaitingAnswers ? "Checking your answers..." : "Understanding your brief...");
      const brief = composeAgentBrief(turns);
      const clarified = await clarifyAIBrief(brief, (message) => setBusy(true, message));
      if (!clarified.ready){
        const qHtml = clarified.questions.length
          ? `<ol>${clarified.questions.map(q => `<li>${esc(q)}</li>`).join('')}</ol>`
          : '';
        turns.push({
          role: 'luna',
          text: [clarified.message, ...clarified.questions].filter(Boolean).join('\n'),
          html: `<p>${esc(clarified.message || 'I need a few details before I build.')}</p>${qHtml}<p class="agent-msg-hint">Answer below — or say “build with what you have”.</p>`
        });
        awaitingAnswers = true;
        titleEl.textContent = 'A few questions first';
        paintThread();
        setBusy(false);
        status.hidden = true;
        status.textContent = '';
        input.focus();
        return;
      }

      turns.push({
        role: 'luna',
        text: clarified.message || 'Building your site now.',
        html: `<p>${esc(clarified.message || 'I have enough to build. Creating your site now.')}</p>`
      });
      paintThread();
      awaitingAnswers = false;
      titleEl.textContent = 'Building your site…';

      const style = lumenPrefs().responseStyle || "balanced";
      const styleHint = style === "concise"
        ? " Prefer tighter pages with fewer sections but still premium."
        : style === "detailed"
          ? " Prefer denser pages with richer section stacks and specific copy."
          : "";
      const builtPrompt = composeAgentBrief(turns) + styleHint;
      const showProgress = lumenPrefs().streamReplies !== false;
      const generatedPages = await generateAISitePages(builtPrompt, (message, done, total) => {
        if (showProgress) setBusy(true, total ? `${message} (${done}/${total})` : message);
      });
      const siteSeed = turns.find(t => t.role === 'user')?.text || 'Pulse site';
      const siteName = siteSeed.slice(0, 48).replace(/\s+/g, " ").trim() || "Pulse site";
      await loadStudioBlocks();
      const newProj = {
        id: newId(),
        name: siteName,
        data: {
          tokens: agentLunaTokens(),
          seo: { title: "", description: "", ogImage: "", favicon: "" },
          shared: { header: null, footer: null },
          pages: generatedPages.map(page => {
            if (!page.id) page.id = newId();
            if (!page.seo) page.seo = { title: "", description: "", ogImage: "" };
            if (!page.type) page.type = "page";
            if (!page.slug) page.slug = page.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
            page.blocks = (page.blocks || []).map(block => {
              if (!block.id) block.id = newId();
              return block;
            });
            return page;
          }),
          advanced: advancedDefaults()
        }
      };
      await api.saveProject(newProj);
      projects.unshift(newProj);
      log(newProj.id, "created", `Site "${newProj.name}" created with Pulse (${generatedPages.length} pages)`);
      setBusy(false);
      toast(`Generated site with ${generatedPages.length} pages`, { kind: "success" });
      activeId = newProj.id;
      persistActive();
      go("dashboard");
      setTimeout(() => openBuilderEdit(newProj.id), 100);
    } catch (err) {
      setBusy(false);
      status.hidden = false;
      status.textContent = humanErr(err);
    }
  };

  requestAnimationFrame(() => input.focus());
}

let publishRuntimeState = null;
let publishRuntimeJob = null;
function ensurePublishRuntime(){
  if (publishRuntimeState) return Promise.resolve(publishRuntimeState);
  if (publishRuntimeJob) return publishRuntimeJob;
  publishRuntimeJob = api.publishRuntime().then(state => {
    publishRuntimeState = {
      runtime: String(state?.runtime || ""),
      sites: state?.sites && typeof state.sites === "object" ? state.sites : {}
    };
    return publishRuntimeState;
  }).catch(err => {
    publishRuntimeJob = null;
    throw err;
  });
  return publishRuntimeJob;
}
function sitePublishStale(id){
  if (!publishRuntimeState || !prodOf(id)) return false;
  return String(publishRuntimeState.sites?.[id] || "") !== publishRuntimeState.runtime;
}
function staleLiveProjects(){
  return ownProjects().filter(p => sitePublishStale(p.id));
}
async function republishLive(project){
  const prod = prodOf(project.id);
  if (!prod?.slug) throw new Error("This site is not live yet.");
  await loadStudioBlocks();
  const loaded = await ensureProjectLoaded(project.id);
  const src = await preparePublishSource(loaded || project, "REPUBLISHED");
  await api.deployStaging(src, prod.slug);
  await api.promote(src);
  if (publishRuntimeState) publishRuntimeState.sites[src.id] = publishRuntimeState.runtime;
  try { deploys = await api.listDeploys(); } catch {}
  return src;
}
async function republishStaleSites(list){
  const done = [];
  for (const project of list) {
    try {
      await republishLive(project);
      done.push(project);
    } catch (err) {
      const msg = String(err?.message || "");
      if (/too many requests/i.test(msg)) {
        throw new Error(done.length
          ? `Updated ${done.length} site${done.length === 1 ? "" : "s"}. Wait a few minutes, then republish the rest.`
          : "Too many publishes just now. Wait a few minutes and try again.");
      }
      const name = project?.name || "A site";
      throw new Error(msg ? `${name} could not be republished. ${msg}` : `${name} could not be republished.`);
    }
  }
  return done;
}

function publishHistoryWhen(row){
  return fmtWhen(row?.published_at || row?.created_at);
}
async function applyPublishCanvas(project, snap){
  const loaded = await ensureProjectLoaded(project.id) || project;
  const data = migrateProjectData(snap?.data, snap?.name || loaded.name);
  if (!Array.isArray(data?.pages)) throw new Error("That publish has no canvas snapshot.");
  pushRevisionFor(loaded, "RESTORED", "Before restoring a publish");
  const revs = loaded.data._revisions;
  loaded.data = data;
  loaded.data._revisions = revs;
  if (snap?.name) loaded.name = snap.name;
  if (current?.id === loaded.id) {
    current = loaded;
    curPageId = loaded.data.pages.some(pg => pg.id === curPageId) ? curPageId : loaded.data.pages[0]?.id;
  }
  await api.saveProject(loaded);
  return loaded;
}
function offerCanvasRestore(project, deploymentId){
  const m = openModal(`<h2>Restore the canvas too?</h2>
    <p class="sub">The live site is back. The canvas can return to that same publish. What you have open now is kept as a revision.</p>
    <div class="row"><button type="button" class="btn btn-ghost btn-sm" data-x="c">Keep canvas</button><button type="button" class="btn btn-brand btn-sm" data-x="ok">Restore canvas</button></div>`);
  m.querySelector("[data-x=c]").onclick = () => { closeModal(); go("dashboard"); };
  m.querySelector("[data-x=ok]").onclick = async () => {
    closeModal();
    try {
      const snap = await api.deploymentCanvas(project.id, deploymentId);
      await applyPublishCanvas(project, snap);
      toast("Canvas restored", { kind: "success" });
    } catch (err) {
      toast(humanErr(err), { kind: "error" });
    }
    go("dashboard");
  };
}
function confirmRestorePublish(project, row){
  const version = Number(row?.version) || "";
  const m = openModal(`<h2>Restore publish ${esc(String(version))}?</h2>
    <p class="sub">The live site goes back to the publish from ${esc(publishHistoryWhen(row))}. Your canvas stays as it is until you choose to restore that too.</p>
    <div class="row"><button type="button" class="btn btn-ghost btn-sm" data-x="c">Cancel</button><button type="button" class="btn btn-brand btn-sm" data-x="ok">Restore live site</button></div>`);
  m.querySelector("[data-x=c]").onclick = closeModal;
  m.querySelector("[data-x=ok]").onclick = async () => {
    const btn = m.querySelector("[data-x=ok]");
    btn.disabled = true;
    btn.textContent = "Restoring…";
    try {
      const out = await api.rollbackDeployment(project.id, row.id);
      if (publishRuntimeState) publishRuntimeState.sites[project.id] = String(out?.runtime || "");
      try { deploys = await api.listDeploys(); } catch {}
      closeModal();
      toast("Live site restored", { kind: "success" });
      if (row.canvas || out?.canvas) offerCanvasRestore(project, row.id);
      else go("dashboard");
    } catch (err) {
      btn.disabled = false;
      btn.textContent = "Restore live site";
      toast(humanErr(err), { kind: "error" });
    }
  };
}
async function openPublishHistory(project){
  let payload;
  try { payload = await api.listDeployments(project.id); }
  catch (err) { return toast(humanErr(err), { kind: "error" }); }
  const rows = Array.isArray(payload?.deployments) ? payload.deployments : [];
  const earlier = rows.filter(row => !row.live && row.status !== "published" && row.status !== "failed");
  const emptyCopy = payload?.legacy
    ? "This site was published before version history. Update the live site once, and each later publish can be restored."
    : (rows.length
      ? "This is the only live publish so far. The next update is kept here."
      : "No earlier publishes yet. The next time you update the live site, the previous one is kept here.");
  const list = rows.filter(row => row.status !== "failed" && row.status !== "preparing" && row.status !== "building").map(row => {
    const live = !!row.live || row.status === "published";
    const when = publishHistoryWhen(row);
    const note = live ? "Currently live" : (row.restorable === false ? "Files no longer stored" : when);
    return `<div class="hist-row">
      <span class="hist-ic hist-ic--rev">${lu("globe")}</span>
      <span class="hist-main"><strong>Publish ${esc(String(row.version || ""))}${live ? " · Live" : ""}</strong><em>${esc(note)}</em></span>
      ${live || row.restorable === false ? "" : `<button type="button" class="btn btn-ghost btn-sm" data-restore="${esc(row.id)}">Restore</button>`}
    </div>`;
  }).join("");
  const m = openModal(`
    <div class="hist-modal">
      <div class="hist-head">
        <div>
          <h2 id="modal-title">Publish history</h2>
          <p class="sub">${earlier.length ? "Restore an earlier live publish if a later one breaks the site." : emptyCopy}</p>
        </div>
        <button type="button" class="icon-close-btn" data-x="c" aria-label="Close">${lu("x","lu16")}</button>
      </div>
      <section class="hist-sec">
        <div class="hist-panel">${list || `<div class="hist-empty"><span class="hist-empty-ic">${lu("history")}</span><strong>Nothing to restore</strong><p>${esc(emptyCopy)}</p></div>`}</div>
      </section>
      <div class="hist-foot"><button type="button" class="btn btn-ghost btn-sm" data-x="c2">Done</button></div>
    </div>`, true);
  m.classList.add("hist-modal-box");
  m.querySelector("[data-x=c]").onclick = closeModal;
  m.querySelector("[data-x=c2]").onclick = closeModal;
  m.querySelectorAll("[data-restore]").forEach(btn => {
    btn.onclick = () => {
      const row = rows.find(item => item.id === btn.dataset.restore);
      if (!row) return;
      closeModal();
      confirmRestorePublish(project, row);
    };
  });
  icons();
}

async function pageSites(el){
  const rule = PLAN_RULES[plan] || PLAN_RULES.free;
  const shell = document.createElement("div");
  shell.className = "studio-page studio-page--design studio-page--sites";
  el.appendChild(shell);

  try {
    const projs = await api.listProjects();
    if (projs.some(px => px?.data && px.data.v !== 4)) await loadStudioBlocks();
    await attachOwnerNames(projs);
    applyProjectRows(projs);
  } catch {}

  let runtimeReady = false;
  try { await ensurePublishRuntime(); runtimeReady = true; } catch {}

  const mine = ownProjects();
  const shared = sharedProjects();
  const used = mine.length;
  const liveN = mine.filter(p => prodOf(p.id)).length;
  const stagedN = mine.filter(p => !prodOf(p.id) && stagingOf(p.id)).length;
  const quota = rule.sites === Infinity
    ? `${used} site${used === 1 ? "" : "s"} · unlimited on your plan`
    : `${used} of ${rule.sites} on your plan`;
  const statBits = [
    quota,
    liveN ? `${liveN} live` : "",
    stagedN ? `${stagedN} staged` : "",
  ].filter(Boolean).join(" · ");

  head(
    shell,
    "layout-grid",
    "Sites",
    used
      ? `${statBits}. Open the canvas to design, or the console for publish and health.`
      : "Create a site, design on the canvas, then stage and promote when you are ready.",
    `<button type="button" class="btn btn-brand btn-sm" id="sites-new">${lu("plus")} New site</button>`
  );
  shell.querySelector("#sites-new").onclick = newSiteFlow;

  if (!mine.length && !shared.length){
    const w = document.createElement("div");
    w.className = "empty-mesh liquid-glass reveal";
    w.innerHTML = `
      <div class="empty-state sites-empty" style="padding:48px 24px;text-align:center">
        <div class="sites-empty-mark" aria-hidden="true">
          <svg viewBox="0 0 64 64" width="40" height="40" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="8" y="12" width="48" height="40" rx="10" stroke="currentColor" stroke-width="2"/>
            <path d="M8 24h48" stroke="currentColor" stroke-width="2"/>
            <circle cx="16" cy="18" r="1.8" fill="currentColor"/>
            <circle cx="22.5" cy="18" r="1.8" fill="currentColor"/>
            <circle cx="29" cy="18" r="1.8" fill="currentColor"/>
            <rect x="16" y="32" width="18" height="4" rx="2" fill="currentColor" opacity=".9"/>
            <rect x="16" y="40" width="32" height="3" rx="1.5" fill="currentColor" opacity=".35"/>
            <rect x="16" y="46" width="24" height="3" rx="1.5" fill="currentColor" opacity=".22"/>
          </svg>
        </div>
        <h2 style="font-size:20px;font-weight:600;margin:0 0 8px">No sites yet</h2>
        <p style="font-size:14px;color:var(--color-label-secondary);margin:0 0 24px;max-width:420px;margin-inline:auto">
          Create a blank site or start from a template, then design on the canvas and publish when you are ready.
        </p>
        <div style="display:flex;gap:10px;justify-content:center;flex-wrap:wrap">
          <button class="btn btn-brand" id="sb-new">${lu("plus")} New site</button>
        </div>
      </div>`;
    shell.appendChild(w);
    w.querySelector("#sb-new").onclick = newSiteFlow;
    icons();
    return;
  }

  const staleN = runtimeReady ? staleLiveProjects().length : 0;
  const toolbar = document.createElement("div");
  toolbar.className = "design-toolbar liquid-glass reveal";
  toolbar.innerHTML = `
    <p class="design-toolbar-copy">Open a site to design on the canvas. Console handles stage, promote, and health.</p>
    ${staleN ? `<button type="button" class="btn btn-brand btn-sm sites-republish" id="sites-republish-all">Republish all (${staleN})</button>` : ""}
    <div class="design-stats">
      <span class="design-stat">${lu("layout-grid","lu14")}${used} total</span>
      ${liveN ? `<span class="design-stat is-live">${lu("globe","lu14")}${liveN} live</span>` : ""}
      ${stagedN ? `<span class="design-stat is-staged">${lu("rocket","lu14")}${stagedN} staged</span>` : ""}
    </div>`;
  shell.appendChild(toolbar);
  toolbar.querySelector("#sites-republish-all")?.addEventListener("click", () => {
    const list = staleLiveProjects();
    if (!list.length) return;
    const m = openModal(`<h2>Republish live sites?</h2><p class="sub">${list.length} live site${list.length === 1 ? "" : "s"} will be rebuilt with the current site features, including the cookie banner. Your pages stay as they are.</p>
      <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button><button class="btn btn-brand btn-sm" data-x="ok">Republish all</button></div>`);
    m.querySelector('[data-x="c"]').onclick = closeModal;
    m.querySelector('[data-x="ok"]').onclick = async () => {
      closeModal();
      const btn = toolbar.querySelector("#sites-republish-all");
      if (btn) { btn.disabled = true; btn.textContent = "Republishing…"; }
      try {
        const done = await republishStaleSites(list);
        toast(`Republished ${done.length} live site${done.length === 1 ? "" : "s"}`, { kind: "success" });
      } catch (err) {
        toast(humanErr(err), { kind: "error" });
      }
      go("sites");
    };
  });

  const paintCards = (list, { shared = false } = {}) => {
    const grid = document.createElement("div");
    grid.className = "projects-grid reveal";
    if (!shared) {
      const add = document.createElement("button");
      add.type = "button";
      add.className = "site-card new liquid-glass";
      add.innerHTML = `<span class="plus">${lu("plus")}</span><span>New site</span><span class="note">Blank or from a template</span>`;
      add.onclick = newSiteFlow;
      grid.appendChild(add);
    }
    pinSort(list).forEach(p => {
      const prod = prodOf(p.id), stg = stagingOf(p.id);
      const pinned = isPinned(p.id);
      const pageCount = Array.isArray(p.data?.pages) ? p.data.pages.length : null;
      const pageLabel = pageCount == null ? "" : `${pageCount} page${pageCount === 1 ? "" : "s"} · `;
      const who = shared ? `Shared by ${esc(p.ownerName || "the site owner")} · ` : "";
      const c = document.createElement("article");
      c.className = "site-card liquid-glass" + (shared ? " site-card--shared" : "");
      c.innerHTML = `
      <div class="pc-thumb">
        ${siteThumbMarkup(p)}
        <div class="pc-overlay">
          <button type="button" class="pc-open" data-act="edit">${lu("arrow-up-right","lu16")}<span>Open canvas</span></button>
          <div class="pc-overlay-tools">
            <button type="button" class="pc-tool" data-act="pin" aria-pressed="${pinned}" title="${pinned ? "Unpin" : "Pin"}">${lu(pinned ? "pin-off" : "pin","lu14")}</button>
            ${shared ? "" : `<button type="button" class="pc-tool" data-act="duplicate" title="Duplicate">${lu("copy","lu14")}</button>
            <button type="button" class="pc-tool pc-tool--danger" data-act="delete" title="Delete">${lu("trash-2","lu14")}</button>`}
          </div>
        </div>
        <button type="button" class="pc-console" data-act="dash" title="Site console" aria-label="Open site console" style="--site-mark:${esc(siteIconColorOf(p))};color:var(--site-mark)">${lu(siteIconOf(p),"lu16")}</button>
      </div>
      <div class="pc-body">
        <div class="pc-name">${pinned ? `<span class="pc-pin" title="Pinned">${lu("pin","lu14")}</span>` : ""}${esc(p.name)}${prod ? '<span class="badge live">Live</span>' : stg ? '<span class="badge staging">Staged</span>' : ""}${!shared && sitePublishStale(p.id) ? '<span class="badge update">Update available</span>' : ""}</div>
        <div class="pc-meta">${who}${pageLabel}updated ${fmtWhen(p.updated_at)}</div>
        ${!shared && sitePublishStale(p.id) ? `<div class="pc-update"><button type="button" class="btn btn-brand btn-sm" data-act="republish">Republish</button></div>` : ""}
      </div>`;

      const openEdit = e => { e?.stopPropagation?.(); openBuilderEdit(p.id); };
      const openDash = e => { e?.stopPropagation?.(); activeId = p.id; persistActive(); go("dashboard"); };

      c.querySelector("[data-act=edit]").onclick = openEdit;
      c.querySelector("[data-act=dash]").onclick = openDash;
      c.querySelector("[data-act=pin]").onclick = e => {
        e.stopPropagation();
        const now = togglePin(p.id);
        toast(now ? "Pinned — stays at the top of your lists." : "Unpinned");
        go("sites");
      };
      c.querySelector("[data-act=duplicate]")?.addEventListener("click", async e => {
        e.stopPropagation();
        if (ownProjects().length >= rule.sites) return quotaToast();
        let src = p;
        try { src = await ensureProjectLoaded(p.id) || p; }
        catch (err) { return toast(humanErr(err, "Could not duplicate this site."), { kind: "error" }); }
        if (!src?.data) return toast("Could not duplicate this site.", { kind: "error" });
        const copy = { id: newId(), name: src.name + " copy", data: JSON.parse(JSON.stringify(src.data)), user_id: me?.id || null };
        await api.saveProject(copy); projects.unshift(copy);
        log(copy.id, "created", `Site “${copy.name}” duplicated from “${p.name}”`);
        const hint = slugTakenMessage(slugOf(copy), copy);
        if (hint) toast(hint);
        go("sites");
      });
      c.querySelector("[data-act=delete]")?.addEventListener("click", e => { e.stopPropagation(); deleteSiteFlow(p); });
      c.querySelector("[data-act=republish]")?.addEventListener("click", async e => {
        e.stopPropagation();
        const btn = e.currentTarget;
        btn.disabled = true;
        btn.textContent = "Republishing…";
        try {
          await republishLive(p);
          toast("Live site updated", { kind: "success" });
          go("sites");
        } catch (err) {
          btn.disabled = false;
          btn.textContent = "Republish";
          toast(humanErr(err), { kind: "error" });
        }
      });

      c.addEventListener("click", e => {
        if (e.target.closest("[data-act]")) return;
        if (window.matchMedia("(pointer:coarse)").matches && !c.classList.contains("show-actions")){
          $$(".site-card.show-actions").forEach(x => x.classList.remove("show-actions"));
          c.classList.add("show-actions");
          return;
        }
        openEdit();
      });

      grid.appendChild(c);
      if (!shared) {
        api.getDomain(p.id).then(d => {
          if (d?.status === "verified"){
            const n = c.querySelector(".pc-name");
            n.insertAdjacentHTML("beforeend", `<span class="badge">${esc(d.domain)}</span>`);
          }
        });
      }
    });
    return grid;
  };

  const mineHead = document.createElement("h2");
  mineHead.className = "sites-group";
  mineHead.innerHTML = `My sites <span>${esc(quota)}</span>`;
  shell.appendChild(mineHead);
  shell.appendChild(paintCards(mine));

  if (shared.length) {
    const sharedHead = document.createElement("h2");
    sharedHead.className = "sites-group";
    sharedHead.textContent = "Shared with you";
    shell.appendChild(sharedHead);
    shell.appendChild(paintCards(shared, { shared: true }));
  }
  icons();
}
/* Legacy alias — Design hub merged into Sites */
async function pageSiteBuilderHub(el){ return pageSites(el); }
function quotaToast(){ toast(`The ${PLAN_RULES[plan]?.label || "current"} plan allows ${PLAN_RULES[plan].sites} site${PLAN_RULES[plan].sites===1?"":"s"} — upgrade to Architect for unlimited.`); }
function deleteSiteFlow(p){
  if (!siteIsMine(p)) return toast("Only the site owner can delete this site.");
  confirmModal(`Delete “${esc(p.name)}”?`, "The site, its pages, revisions, staging, and live copy are removed. This can’t be undone.", "Delete site", async () => {
    try { if (prodOf(p.id)) await api.unpublish(p.id, p); } catch {}
    await api.deleteProject(p.id);
    projects = projects.filter(x => x.id !== p.id);
    deploys.staging = deploys.staging.filter(x => x.project_id !== p.id);
    deploys.production = deploys.production.filter(x => x.project_id !== p.id);
    log(p.id, "deleted", `Site “${p.name}” deleted`);
    if (activeId === p.id){ activeId = projects[0]?.id || null; persistActive(); current = null; }
    closeSiteMenu();
    paintSitePick();
    go(projects.length ? "sites" : "dashboard");
    toast("Site deleted");
  });
}

async function openAIRequest(payload){
  const res = await fetch('/api/lumen.php', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': 'Bearer ' + (await api._token())
    },
    body: JSON.stringify(payload)
  });
  const data = await res.json();
  if (!res.ok || data.error) throw new Error(data.error || "The AI builder couldn't finish. Try again");
  return data;
}

function composeAgentBrief(turns){
  return (turns || []).map(turn => {
    const who = turn.role === 'luna' ? 'Luna' : 'User';
    return `${who}: ${String(turn.text || '').trim()}`;
  }).filter(line => !line.endsWith(':')).join('\n\n');
}

async function clarifyAIBrief(prompt, onProgress){
  const profile = lumenNormalizeProfile(lumenPrefs().modelProfile);
  onProgress?.('Understanding your brief...');
  const data = await openAIRequest({
    action: 'generate',
    mode: 'clarify',
    prompt,
    profile
  });
  const out = data.output || {};
  const ready = String(out.ready || '').toLowerCase() === 'yes';
  const message = String(out.message || '').trim();
  const questions = Array.isArray(out.questions)
    ? out.questions.map(q => String(q || '').trim()).filter(Boolean).slice(0, 6)
    : [];
  return { ready, message, questions };
}

async function generateAISitePages(prompt, onProgress){
  const profile = lumenNormalizeProfile(lumenPrefs().modelProfile);
  onProgress?.('Planning pages...', 0, 0);
  const planned = await openAIRequest({ action: 'generate', mode: 'plan', prompt, profile });
  const planPages = planned.output?.pages || [];
  if (!planPages.length) throw new Error('Pulse returned an empty site plan');
  const pages = [];
  for (let i = 0; i < planPages.length; i += 1) {
    const page = planPages[i];
    onProgress?.(`Generating ${page.name}...`, i + 1, planPages.length);
    const generated = await openAIRequest({
      action: 'generate',
      mode: 'page',
      prompt,
      page,
      sitePlan: planPages,
      profile
    });
    if (!generated.output?.page) throw new Error(`Pulse did not return the ${page.name} page`);
    pages.push(generated.output.page);
  }
  return pages;
}

/** Richer design tokens for Luna-generated sites (builder-native only). */
function agentLunaTokens(){
  return Object.assign(defTokens(), {
    accent: "#355cff",
    fontPair: "manrope",
    surface: "cool",
    border: "soft",
    shadow: "deep",
    radius: "soft",
    spacing: "airy",
    fontScale: "lg",
    fontWeight: "medium",
    letterSpacing: "tight",
    motionLevel: "expressive",
    motionReveal: "rise",
    motionTrigger: "scroll",
    motionDuration: "smooth",
    motionHover: "lift",
    pageBgMode: "gradient",
    pageBgA: "#e8eefc",
    pageBgB: "#f7f9ff",
    pageBgAngle: "165",
    buttonStyle: "pill",
  });
}

async function showDashboardAIBuilderFlow(){
  let ready = false;

  try {
    const data = await openAIRequest({ action: 'get-config' });
    ready = !!(data.ready || data.has_key);
    if (data.profile) savePulsePrefs({ modelProfile: data.profile });
  } catch (e) {
    console.error('AI config check failed:', e);
  }

  if (!ready) {
    const m = openModal(`
      <div class="modal-head">
        <h2>Pulse unavailable</h2>
        <button class="modal-close" data-modal-close aria-label="Close">&times;</button>
      </div>
      <div class="modal-body">
        <p class="modal-sub">Pulse is not configured on this server yet. Contact support@brownsdigital.org.</p>
        <div class="modal-actions">
          <button class="btn btn-cta" data-modal-close>OK</button>
        </div>
      </div>
    `);
    m.querySelectorAll('[data-modal-close]').forEach(b => b.onclick = closeModal);
    return;
  }

  const html = `
    <div class="modal-head">
      <h2>Build with Pulse</h2>
      <button class="modal-close" data-modal-close aria-label="Close">&times;</button>
    </div>
    <div class="modal-body">
      <p class="modal-sub">Describe your business and Pulse will create a complete multi-page site. Pulse asks questions when details are missing.</p>

      <div class="field">
        <label for="ai-dash-prompt">What kind of site do you want to build?</label>
        <textarea id="ai-dash-prompt" rows="6" placeholder="Example: A modern SaaS platform for project management software. Include homepage with hero and features, pricing page with 3 tiers (Starter $9, Pro $29, Enterprise custom), about page with team section, and contact page.

Describe:
- Your business/brand
- Pages needed
- Style and colors
- Key content"></textarea>
      </div>

      <div class="field">
        <label for="ai-dash-name">Site name</label>
        <input id="ai-dash-name" type="text" placeholder="My New Site" maxlength="60">
      </div>

      <div id="ai-dash-clarify" hidden>
        <div class="ok-msg" id="ai-dash-questions" role="status"></div>
        <div class="field">
          <label for="ai-dash-answers">Your answers</label>
          <textarea id="ai-dash-answers" rows="4" placeholder="Answer Pulse's questions, or say build with what you have."></textarea>
        </div>
      </div>

      <div class="error-msg" id="ai-dash-error" role="alert"></div>
      <div class="ok-msg" id="ai-dash-progress" role="status" hidden></div>

      <div class="modal-actions">
        <button class="btn" data-modal-close>Cancel</button>
        <button class="btn btn-cta" id="ai-dash-generate">
          ${lu("sparkles")}
          Generate Site
        </button>
      </div>
    </div>
  `;

  const m = openModal(html, true);
  icons();
  const dashTurns = [];

  m.querySelectorAll('[data-modal-close]').forEach(b => b.onclick = closeModal);

  m.querySelector('#ai-dash-generate').onclick = async () => {
    const clarifyWrap = m.querySelector('#ai-dash-clarify');
    const questionsEl = m.querySelector('#ai-dash-questions');
    const answersEl = m.querySelector('#ai-dash-answers');
    const clarifying = !!(clarifyWrap && !clarifyWrap.hidden);
    const prompt = clarifying
      ? (answersEl?.value || '').trim()
      : (m.querySelector('#ai-dash-prompt')?.value || '').trim();
    const siteName = (m.querySelector('#ai-dash-name')?.value || '').trim() || 'Pulse site';
    const err = m.querySelector('#ai-dash-error');
    const progress = m.querySelector('#ai-dash-progress');

    if (!prompt) {
      if (err) {
        err.textContent = clarifying
          ? 'Answer Pulse\'s questions, or say build with what you have'
          : 'Describe what kind of site you want to build';
        err.hidden = false;
      }
      return;
    }

    try {
      if (err) err.hidden = true;
      if (progress) {
        progress.innerHTML = `${lu("loader-circle")} ${clarifying ? 'Checking your answers...' : 'Understanding your brief...'}`;
        progress.hidden = false;
      }

      const btn = m.querySelector('#ai-dash-generate');
      if (btn) {
        btn.disabled = true;
        btn.innerHTML = `${lu("loader-circle")} Generating...`;
        icons();
      }

      if (!clarifying) dashTurns.length = 0;
      dashTurns.push({ role: 'user', text: prompt });
      if (clarifying && answersEl) answersEl.value = '';

      const clarified = await clarifyAIBrief(composeAgentBrief(dashTurns), (message) => {
        if (!progress) return;
        progress.innerHTML = `${lu("loader-circle")} ${esc(message)}`;
        icons();
      });

      if (!clarified.ready) {
        const msg = clarified.message || 'I need a few details before I build.';
        const qHtml = clarified.questions.length
          ? `<ol>${clarified.questions.map(q => `<li>${esc(q)}</li>`).join('')}</ol>`
          : '';
        dashTurns.push({
          role: 'luna',
          text: [msg, ...clarified.questions].filter(Boolean).join('\n')
        });
        if (questionsEl) {
          questionsEl.innerHTML = `<p>${esc(msg)}</p>${qHtml}<p>Answer below, or say build with what you have.</p>`;
        }
        if (clarifyWrap) clarifyWrap.hidden = false;
        if (progress) progress.hidden = true;
        return;
      }

      dashTurns.push({
        role: 'luna',
        text: clarified.message || 'Building your site now.'
      });
      if (clarifyWrap) clarifyWrap.hidden = true;

      const generatedPages = await generateAISitePages(composeAgentBrief(dashTurns), (message, done, total) => {
        if (!progress) return;
        progress.innerHTML = `${lu("loader-circle")} ${esc(message)}${total ? `<br><small>Page ${done} of ${total}</small>` : ''}`;
        icons();
      });

      // Create new project with AI-generated content
      await loadStudioBlocks();
      const newProj = {
        id: newId(),
        name: siteName,
        data: {
          tokens: agentLunaTokens(),
          seo: { title: '', description: '', ogImage: '', favicon: '' },
          shared: { header: null, footer: null },
          pages: generatedPages.map(page => {
            if (!page.id) page.id = newId();
            if (!page.seo) page.seo = { title: '', description: '', ogImage: '' };
            if (!page.type) page.type = 'page';
            if (!page.slug) page.slug = page.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
            page.blocks = (page.blocks || []).map(block => {
              if (!block.id) block.id = newId();
              return block;
            });
            return page;
          }),
          advanced: advancedDefaults()
        }
      };

      await api.saveProject(newProj);
      projects.unshift(newProj);
      log(newProj.id, 'created', `Site "${newProj.name}" created with Pulse (${generatedPages.length} pages)`);

      closeModal();
      toast(`Generated site with ${generatedPages.length} pages`, { kind: 'success' });

      // Open in builder
      activeId = newProj.id;
      persistActive();
      go('dashboard');
      setTimeout(() => openBuilderEdit(newProj.id), 100);
    } catch (e) {
      if (progress) progress.hidden = true;
      if (err) {
        err.textContent = humanErr(e);
        err.hidden = false;
      }
    } finally {
      const btn = m.querySelector('#ai-dash-generate');
      const wrap = m.querySelector('#ai-dash-clarify');
      const stillClarifying = !!(wrap && !wrap.hidden);
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = stillClarifying
          ? `${lu("sparkles")} Continue`
          : `${lu("sparkles")} Generate Site`;
        icons();
      }
    }
  };
}


async function newSiteFlow(){
  try { await loadStudioBlocks(); }
  catch (e) { toast(humanErr(e, "Could not load the builder.")); return; }
  const rule = PLAN_RULES[plan] || PLAN_RULES.free;
  if (ownProjects().length >= rule.sites) return quotaToast();
  const preferred = ["nova", "pulse", "apex", "atelier", "folio"];
  let picked = preferred.find(k => TEMPLATES[k]) || Object.keys(TEMPLATES).find(k => k !== "blank") || "blank";
  let tplCat = "All", tplQuery = "";
  const packCount = Math.max(0, Object.keys(TEMPLATES).filter(k => k !== "blank").length);
  const nameSeed = (me?.name || "").trim().split(/\s+/)[0];
  const defaultName = nameSeed ? `${nameSeed}'s site` : "";
  const m = openModal(`
    <div class="ns-shell ns-shell--split">
      <aside class="ns-left">
        <div class="ns-left-copy">
          <h2 id="modal-title">New site</h2>
          <p class="sub">${packCount
            ? `Name it, pick a template preview, then Create — you will land on the canvas ready to edit.`
            : "Name your site and start from a blank canvas. Starter templates are being rebuilt."}</p>
        </div>
        <div class="field ns-field"><label for="ns-name">Site name</label><input id="ns-name" placeholder="e.g. Browns Digital Landing" value="${esc(defaultName)}" autocomplete="off" required aria-required="true"></div>
        <div class="ns-summary">
          <div class="ns-summary-kicker">Selected template</div>
          <strong id="ns-sum-name"></strong>
          <p id="ns-sum-desc"></p>
          <div class="ns-summary-prev" id="ns-sum-prev" aria-hidden="true"></div>
          <div class="ns-acts">
            <button class="btn ns-btn-ghost btn-sm" data-x="cancel" type="button">Cancel</button>
            <button class="btn ns-btn-solid btn-sm" data-x="go" type="button" disabled aria-disabled="true">Create</button>
          </div>
        </div>
      </aside>
      <section class="ns-right">
        <div class="ns-right-head">
          <input id="ns-search" type="search" placeholder="Search templates…" autocomplete="off" aria-label="Search templates" ${packCount ? "" : "hidden"}>
          ${packCount ? tplCatBar(tplCat, { scroll: true }) : ""}
        </div>
        <div class="tpl-scroll ns-scroll"><div class="tpl-grid tpl-grid-lg" id="ns-tpl-grid">${tplGrid(picked, tplCat, tplQuery)}</div></div>
      </section>
    </div>`, true);
  m.classList.add("ns-picker", "ns-picker--split");
  const gridEl = m.querySelector("#ns-tpl-grid");
  const nameInput = m.querySelector("#ns-name");
  const goBtn = m.querySelector('[data-x="go"]');
  const syncCreateBtn = () => {
    if (!goBtn) return;
    const ok = !!(nameInput?.value || "").trim();
    goBtn.disabled = !ok;
    goBtn.setAttribute("aria-disabled", ok ? "false" : "true");
  };
  const clearNameErr = () => {
    const field = nameInput?.closest(".field");
    if (!field) return;
    field.classList.remove("is-invalid");
    field.querySelector(".ob-field-hint, .ns-field-hint")?.remove();
    nameInput.removeAttribute("aria-invalid");
  };
  const nameFail = (msg) => {
    clearNameErr();
    const field = nameInput?.closest(".field");
    if (!field) return toast(msg);
    field.classList.add("is-invalid");
    const hint = document.createElement("div");
    hint.className = "ns-field-hint";
    hint.textContent = msg;
    field.appendChild(hint);
    nameInput.setAttribute("aria-invalid", "true");
    nameInput.focus();
    syncCreateBtn();
  };
  const paintSummary = () => {
    const nameEl = m.querySelector("#ns-sum-name");
    const descEl = m.querySelector("#ns-sum-desc");
    const prevEl = m.querySelector("#ns-sum-prev");
    if (!nameEl || !descEl || !prevEl) return;
    if (picked === "blank"){
      nameEl.textContent = "Blank canvas";
      descEl.textContent = "Start from nothing — just a page and your ideas.";
      prevEl.innerHTML = `<span class="ns-summary-blank" aria-hidden="true">
        <span class="tpl-blank-glow"></span>
        <span class="tpl-blank-frame">${lu("plus","lu24")}<strong>Blank canvas</strong><em>Start from scratch</em></span>
      </span>`;
      icons();
      return;
    }
    const t = TEMPLATES[picked];
    nameEl.textContent = t?.name || "Template";
    descEl.textContent = t?.desc || "";
    const accent = tplAccent(picked);
    const { html: thumb } = tplThumb(picked);
    prevEl.innerHTML = thumb
      ? `<span class="tpl-prev-live ns-summary-live" style="--tpl-accent:${accent}"><span class="tpl-mini">${thumb}</span></span>`
      : `<span class="ns-summary-wire" style="--tpl-accent:${accent}">${tplWireHTML(t?.bars, accent)}</span>`;
  };
  const create = async (key) => {
    const use = key || picked;
    if (!use || !TEMPLATES[use]) return toast("Pick a template first.");
    const name = (nameInput.value || "").trim();
    if (!name) return nameFail("Enter a site name to continue.");
    const nameConflict = duplicateNameHint(name);
    if (nameConflict) return nameFail(nameConflict);
    clearNameErr();
    closeModal();
    const p = { id: newId(), name, data: TEMPLATES[use].make() };
    await api.saveProject(p);
    projects.unshift(p); activeId = p.id; persistActive();
    log(p.id, "created", `Site “${name}” created`);
    openBuilderEdit(p.id);
  };
  const ensurePickedVisible = () => {
    const q = tplQuery.trim().toLowerCase();
    const matches = (k, t) => {
      if (k === "blank") return tplCat === "All" && !q;
      if (tplCat !== "All" && t.cat !== tplCat) return false;
      if (!q) return true;
      return `${t.name || ""} ${t.desc || ""} ${t.cat || ""}`.toLowerCase().includes(q);
    };
    if (picked === "blank" && matches("blank", null)) return;
    if (picked !== "blank" && TEMPLATES[picked] && matches(picked, TEMPLATES[picked])) return;
    const next = Object.entries(TEMPLATES).find(([k, t]) => k !== "blank" && matches(k, t))?.[0];
    if (next) picked = next;
    else if (matches("blank", null)) picked = "blank";
  };
  const paintTpl = () => {
    ensurePickedVisible();
    gridEl.innerHTML = tplGrid(picked, tplCat, tplQuery);
    bindTplCards(gridEl, k => { picked = k; paintSummary(); }, k => create(k));
    icons();
    paintSummary();
  };
  m.querySelectorAll(".tpl-cat").forEach(b => b.onclick = () => {
    tplCat = b.dataset.cat;
    m.querySelectorAll(".tpl-cat").forEach(x => {
      const on = x === b;
      x.classList.toggle("on", on);
      x.setAttribute("aria-selected", on ? "true" : "false");
    });
    paintTpl();
  });
  m.querySelector("#ns-search").addEventListener("input", e => {
    tplQuery = e.target.value || "";
    paintTpl();
  });
  nameInput.addEventListener("input", () => { clearNameErr(); syncCreateBtn(); });
  nameInput.addEventListener("keydown", e => {
    if (e.key === "Enter"){ e.preventDefault(); create(); }
  });
  bindTplCards(gridEl, k => { picked = k; paintSummary(); }, k => create(k));
  paintSummary();
  syncCreateBtn();
  m.querySelector('[data-x="cancel"]').onclick = closeModal;
  goBtn.onclick = () => create();
  nameInput.focus();
}

/* ============================== PUBLISHING ============================== */
function bdHostOf(p){
  const prod = prodOf(p.id), stg = stagingOf(p.id);
  const slug = prod?.slug || stg?.slug || slugOf(p);
  return slug ? prodSiteHostLabel(slug) : null;
}
/** Prefer www as the public production address whenever the custom domain is usable. */
function domainPreferredPublic(d, apexName){
  if (!d) return null;
  const apex = String(apexName || (d.domain || "").replace(/^www\./, "") || "").toLowerCase();
  if (!apex) return null;
  const hosts = d.hosts || {};
  const wwwHost = String(hosts.www || ("www." + apex)).toLowerCase();
  const apexHost = String(hosts.apex || apex).toLowerCase();
  const wwwLive = !!hosts.www_live;
  const apexLive = !!hosts.apex_live;
  const sslMarked = d.ssl_status === "active" || d.verification_status === "active";
  const verified = d.status === "verified"
    || ["verified", "provisioning_ssl", "active"].includes(String(d.verification_status || ""));
  if (!verified && !wwwLive && !apexLive && !sslMarked) return null;
  /* www is the production address whenever it is possible (verified DNS / live SSL). */
  if (wwwLive || sslMarked || verified) {
    return {
      host: wwwHost,
      url: "https://" + wwwHost + "/",
      kind: "www",
      partial: !apexLive,
      live: wwwLive || sslMarked,
    };
  }
  if (apexLive) {
    return {
      host: apexHost,
      url: "https://" + apexHost + "/",
      kind: "apex",
      partial: !wwwLive,
      live: true,
    };
  }
  return null;
}
function customDomainLabel(d){
  const hosts = d?.hosts || {};
  if (hosts.www_live || hosts.apex_live) {
    return { label: "Connected", tone: "ok", sslActive: true, step: 3 };
  }
  const vs = d.verification_status || (d.status === "verified" ? "verified" : "pending");
  const ssl = d.ssl_status || "none";
  const attach = d.server_attach_status || "n_a";
  if (attach === "pending_manual") return { label: "Finishing setup", tone: "wait", sslActive: false, step: 2 };
  if (attach === "cloudflare" && (vs === "provisioning_ssl" || vs === "verified" || ssl === "pending")) { /* brand-ok */
    if (ssl === "active" && vs === "active") {
      return { label: "Connected", tone: "ok", sslActive: true, step: 3 };
    }
    if (ssl === "active") {
      return { label: "Connected", tone: "ok", sslActive: true, step: 3 };
    }
    return { label: "Provisioning SSL", tone: "wait", sslActive: false, step: 2 };
  }
  if (vs === "active" || (vs === "verified" && (ssl === "active" || attach === "attached" || attach === "cpanel" || attach === "cloudflare"))) { /* brand-ok */
    return { label: "Connected", tone: "ok", sslActive: ssl === "active" || attach === "attached" || attach === "cpanel" || (attach === "cloudflare" && ssl === "active"), step: 3 }; /* brand-ok */
  }
  if (vs === "provisioning_ssl") return { label: "Provisioning SSL", tone: "wait", sslActive: false, step: 2 };
  if (vs === "verified") return { label: "DNS verified", tone: "ok", sslActive: ssl === "active", step: 2 };
  if (vs === "error") return { label: "Needs attention", tone: "warn", sslActive: false, step: 1 };
  if (vs === "dns_required" || vs === "pending" || vs === "verifying") return { label: "Setup required", tone: "wait", sslActive: false, step: 1 };
  return { label: d.status === "verified" ? "DNS verified" : "Setup required", tone: d.status === "verified" ? "ok" : "wait", sslActive: false, step: d.status === "verified" ? 2 : 1 };
}
function domainCard(el, p){
  const allowed = (PLAN_RULES[plan] || {}).customDomain;
  const c = card(el, `<header class="settings-sec-head"><h2>Domains</h2><p class="c-sub">Keep your Browns Digital address, or connect a custom domain with SSL included.</p></header><div id="dom-body">${skelRows(2, { avatar:false })}</div>`);
  paintDomBody(c.querySelector("#dom-body"), p, allowed);
}
function paintDomBody(body, p, allowed){
  if (allowed == null) allowed = (PLAN_RULES[plan] || {}).customDomain;
  const block = (label, inner, extraClass = "") => `<div class="settings-block${extraClass ? " " + extraClass : ""}">${label ? `<p class="settings-block-label">${esc(label)}</p>` : ""}${inner}</div>`;
  const prod = prodOf(p.id);
  const bdHost = bdHostOf(p) || "studio.brownsdigital.org/s/your-site";
  const slug = prodOf(p.id)?.slug || stagingOf(p.id)?.slug || slugOf(p);
  const bdUrl = slug ? prodSiteUrl(slug) : "";

  const renderBdPanel = (opts = {}) => {
    const liveHost = opts.liveHost || bdHost;
    const liveUrl = opts.liveUrl || bdUrl;
    const kicker = opts.kicker || (prod ? "Live address" : "Production address");
    const hint = opts.hint || (prod ? "Always available, even with a custom domain." : "Publish to production to activate this address.");
    const copyId = opts.copyId || "dom-copy-bd";
    return block(opts.label || "Browns Digital", `
    <div class="dom-panel">
      <div class="dom-panel-main">
        <span class="dom-panel-icon" aria-hidden="true">${lu("globe","lu14")}</span>
        <div class="dom-panel-copy">
          <span class="dom-panel-kicker">${esc(kicker)}</span>
          <strong class="dom-panel-host">${esc(liveHost)}</strong>
        </div>
      </div>
      <div class="dom-panel-actions">
        ${liveUrl ? `<a class="dom-icon-btn" href="${esc(liveUrl)}" target="_blank" rel="noopener" aria-label="Open site">${lu("external-link","lu14")}</a>
        <button type="button" class="dom-icon-btn" id="${esc(copyId)}" aria-label="Copy URL">${lu("copy","lu14")}</button>` : ""}
      </div>
    </div>
    <p class="settings-hint">${hint}</p>`);
  };

  const bindCopy = (id, url) => {
    icons();
    const btn = body.querySelector("#" + id);
    if (btn && url) btn.onclick = () => {
      navigator.clipboard?.writeText(url)
        .then(() => toast("URL copied", { kind: "success" }))
        .catch(() => toast("Could not copy", { kind: "error" }));
    };
  };
  const bindBdCopy = () => bindCopy("dom-copy-bd", bdUrl);

  const paintCustom = (d) => {
  if (!allowed){
      body.innerHTML = renderBdPanel() + block("Custom Domain", `
        <div class="dom-empty">
          <p class="dom-empty-title">Available on Architect</p>
          <p class="settings-note">Connect your own domain when you upgrade. Your site stays live on its Browns Digital address either way.</p>
          <div class="settings-footer"><a class="btn btn-brand btn-sm" href="${URLS.pricing}" target="_blank" rel="noopener">View plans</a></div>
        </div>`);
      bindBdCopy();
    return;
  }
    if (!d){
      body.innerHTML = renderBdPanel() + block("Custom Domain", `
        <div class="dom-empty" id="dom-empty-panel">
          <p class="dom-empty-title">Use your own domain</p>
          <p class="settings-note">Enter <strong>yourdomain.com</strong> or <strong>www.yourdomain.com</strong>. You’ll add an ownership TXT and point <strong>www</strong> here — that becomes your live address.</p>
          <div class="settings-footer" id="dom-empty-actions"><button type="button" class="btn btn-brand btn-sm" id="dom-connect">Connect domain</button></div>
        </div>
        <div id="dom-connect-form" class="dom-connect-form" hidden>
          <div class="settings-field">
            <label class="settings-label" for="dom-in">Domain</label>
            <input class="settings-input" id="dom-in" placeholder="yourbusiness.co.za" autocomplete="off" spellcheck="false" aria-label="Your domain">
          </div>
          <div class="settings-footer"><button type="button" class="btn btn-brand btn-sm" id="dom-req">Continue</button></div>
          <p class="settings-hint">SSL is included. Add the ownership TXT and point <strong>www</strong> here. Bare <strong>@</strong> is optional.</p>
        </div>`);
      bindBdCopy();
      const showForm = () => {
        body.querySelector("#dom-empty-panel")?.setAttribute("hidden", "");
        body.querySelector("#dom-connect-form").hidden = false;
        body.querySelector("#dom-in")?.focus();
      };
      body.querySelector("#dom-connect").onclick = showForm;
      body.querySelector("#dom-req").onclick = async () => {
        const btn = body.querySelector("#dom-req");
        btn.disabled = true; btn.textContent = "Connecting…";
        try {
          const rec = await api.requestDomain(p.id, body.querySelector("#dom-in").value);
          log(p.id, "domain", `Domain ${rec.domain} requested for “${p.name}”`);
          toast(rec.normalized_to_www
            ? `Using ${rec.domain} — add the TXT and www CNAME below`
            : "Domain connected — add the TXT and www CNAME below", { kind: "success" });
          paintDomBody(body, p, allowed);
        } catch (e) { toast(humanErr(e), { kind: "error" }); btn.disabled = false; btn.textContent = "Continue"; }
      };
      return;
    }

    const dns = d.dns || {};
    let ingressHost = "brownsdigi.com";
    try {
      if (dns.cname_target) ingressHost = dns.cname_target;
    } catch {}
    const cnameTarget = dns.cname_target || ingressHost;
    const apexName = dns.apex_domain || d.domain.replace(/^www\./, "");
    const wwwName = dns.www_domain || ("www." + apexName);
    const hints = Array.isArray(dns.hints) && dns.hints.length
      ? dns.hints.map(h => `<p class="settings-hint">${esc(h)}</p>`).join("")
      : `<p class="settings-hint">Required: ownership TXT and <strong>www</strong> → <strong>${esc(cnameTarget)}</strong>. Your live address is <strong>${esc(wwwName)}</strong>.</p>`;
    const publishNote = !prodOf(p.id) ? `<p class="settings-hint dom-hint-warn">Publish this site to production before opening the custom domain.</p>` : "";
    const pointRecords = dnsRecordsFromApi(dns, d.token);
    const hosts = d.hosts || {};
    const wwwLive = !!hosts.www_live;
    const apexLive = !!hosts.apex_live;
    const preferred = domainPreferredPublic(d, apexName);
    const meta = customDomainLabel(d);
    const verified = d.status === "verified" || ["verified", "provisioning_ssl", "active"].includes(d.verification_status);
    const pendingManual = d.server_attach_status === "pending_manual" || (d.error_message && /spaceship|document root|cpanel|cloudflare for saas/i.test(String(d.error_message)));
    const attached = d.server_attach_status === "attached" || d.server_attach_status === "cpanel" || d.server_attach_status === "cloudflare"; /* brand-ok */
    /* Any live hostname (usually www first) completes SSL + Live progress. */
    const sslDone = !!(preferred || meta.sslActive || d.ssl_status === "active" || wwwLive || apexLive);
    const sslWait = !sslDone && (
      d.ssl_status === "pending"
      || d.verification_status === "provisioning_ssl"
      || (verified && (attached || pendingManual || d.verification_status === "verified"))
    );
    const sslLabel = sslDone
      ? (wwwLive && apexLive ? "Active" : (wwwLive || apexLive || preferred ? "Active · www" : "Active"))
      : (sslWait ? "Provisioning" : "Pending");
    const serverDone = sslDone && !!prod;
    const serverLabel = serverDone ? "Live" : (!prod ? "Publish first" : (sslWait || pendingManual || attached ? "In progress" : "Pending"));
    const toneClass = meta.tone === "ok" || sslDone ? "is-ok" : (meta.tone === "warn" ? "is-warn" : "is-wait");
    const pillLabel = sslDone ? "Connected" : meta.label;
    const setupFoot = sslDone
      ? (preferred?.partial
        ? `${preferred.host} is your live address. Bare-domain (@) DNS is optional.`
        : "Custom domain is live. Keep DNS pointing here.")
      : (pendingManual
        ? (d.error_message || "DNS is verified. We’re finishing SSL — refresh status in a few minutes.")
        : (!sslDone && (attached || d.verification_status === "provisioning_ssl" || d.verification_status === "verified")
          ? (d.error_message || "We're finishing the connection. This usually takes a few minutes — use Refresh status.")
          : ""));

    const stepClass = (done, active) => done ? "is-done" : (active ? "is-active" : "");
    const statusBlock = verified ? block("Progress", `
      <ol class="dom-steps" aria-label="Domain setup progress">
        <li class="dom-step ${stepClass(true, false)}"><span class="dom-step-dot" aria-hidden="true"></span><span class="dom-step-label">DNS</span><span class="dom-step-meta">Verified${d.demo ? " · demo" : ""}</span></li>
        <li class="dom-step ${stepClass(sslDone, !sslDone && sslWait)}"><span class="dom-step-dot" aria-hidden="true"></span><span class="dom-step-label">SSL</span><span class="dom-step-meta">${esc(sslLabel)}</span></li>
        <li class="dom-step ${stepClass(serverDone, !serverDone && (sslWait || pendingManual || attached || !prod))}"><span class="dom-step-dot" aria-hidden="true"></span><span class="dom-step-label">Live</span><span class="dom-step-meta">${esc(serverLabel)}</span></li>
      </ol>`) : "";

    const wwwHostName = hosts.www || ("www." + apexName);
    const apexHostName = hosts.apex || apexName;
    const wwwUrl = "https://" + wwwHostName.replace(/^https?:\/\//i, "").replace(/\/$/, "") + "/";
    const apexUrl = "https://" + apexHostName.replace(/^https?:\/\//i, "").replace(/\/$/, "") + "/";
    const liveLinks = verified
      ? `<div class="dom-live-links">
          <a class="btn btn-ghost btn-sm" href="${esc(wwwUrl)}" target="_blank" rel="noopener">${wwwLive || sslDone ? "Open www" : "Try www"}</a>
          ${apexLive
            ? `<a class="btn btn-ghost btn-sm" href="${esc(apexUrl)}" target="_blank" rel="noopener">Open ${esc(apexHostName)}</a>`
            : ""}
        </div>`
      : "";

    /* When www is possible, just update the single production / live address. */
    const livePanel = preferred
      ? renderBdPanel({
          label: "Browns Digital",
          kicker: prod ? "Live address" : "Production address",
          liveHost: preferred.host,
          liveUrl: preferred.url,
          copyId: "dom-copy-bd",
          hint: sslDone
            ? `Studio backup: ${bdHost}`
            : `Using www while SSL finishes. Studio backup: ${bdHost}`,
        })
      : renderBdPanel();

    const customBlock = block("Custom Domain", `
      <div class="dom-panel dom-panel--hero">
        <div class="dom-panel-main">
          <span class="dom-panel-icon" aria-hidden="true">${lu("link-2","lu14")}</span>
          <div class="dom-panel-copy">
            <span class="dom-panel-kicker">Connected domain</span>
            <strong class="dom-panel-host">${esc(d.domain)}</strong>
          </div>
        </div>
        <span class="dom-pill ${toneClass}">${esc(pillLabel)}</span>
      </div>
      <p class="settings-hint">Maps to your live Browns Digital site.</p>
      ${setupFoot ? `<p class="settings-hint">${esc(setupFoot)}</p>` : ""}
      ${liveLinks}`);

    const dnsBlock = verified
      ? block("DNS", `
          <div class="dom-dns-list">${pointRecords}</div>
          ${hints}
          <p class="settings-hint">Keep these pointing at <strong>${esc(cnameTarget)}</strong>. If your DNS host proxies records, turn the proxy off for these CNAMEs.</p>
          ${verified && d.id ? `<div class="settings-footer"><button type="button" class="btn btn-ghost btn-sm" id="dom-check">${lu("refresh-cw","lu14")} Refresh status</button></div>` : ""}`)
      : block("DNS records", `
          <p class="settings-note">Add these at your registrar, then verify. Tap copy on each value.</p>
          ${publishNote}
          ${hints}
          <div class="dom-dns-list">
            ${pointRecords}
          </div>
          <p class="settings-hint">Enter only the host shown. Your registrar appends the domain (<code>${esc(dns.txt_host_full || ("_bd-verify." + apexName))}</code>). If your DNS host proxies records, turn the proxy off for these CNAMEs.</p>
          <div class="settings-footer"><button type="button" class="btn btn-brand btn-sm" id="dom-verify">Verify DNS</button></div>`);

    const dangerBlock = `
      <div class="settings-danger-row dom-danger">
        <div><strong>Remove domain</strong><p class="c-sub">The site stays live on its Browns Digital address.</p></div>
        <button type="button" class="btn btn-danger btn-sm" id="dom-rm">Remove</button>
      </div>`;

    body.innerHTML = livePanel + customBlock + statusBlock + dnsBlock + dangerBlock;
    bindCopy("dom-copy-bd", preferred?.url || bdUrl);
    bindDnsCopy(body);
    const v = body.querySelector("#dom-verify");
    if (v) v.onclick = async () => {
      v.disabled = true; v.textContent = "Checking…";
      try {
        const r = await api.verifyDomain(p.id);
        log(p.id, "domain", `Domain ${r.domain || d.domain} verified`);
        if (r.server_attach === "manual" || r.server_attach_status === "pending_manual") {
          toast("DNS verified. We're finishing the connection", { kind: "success" });
        } else if (r.server_attach === "cloudflare" || r.server_attach_status === "cloudflare") { /* brand-ok */
          toast(r.ssl_status === "active" ? "Domain connected with SSL" : "DNS verified. We're finishing the connection", { kind: "success" });
        } else {
          toast(r.demo ? "Verified (demo — live mode checks real DNS records)" : "Domain verified", { kind: "success" });
        }
        paintDomBody(body, p, allowed);
      } catch (e) { toast(humanErr(e), { kind: "error" }); v.disabled = false; v.textContent = "Verify DNS"; }
    };
    const chk = body.querySelector("#dom-check");
    if (chk) chk.onclick = async () => {
      chk.disabled = true;
      try {
        await api.checkSiteDomain(p.id, d.id);
        toast("Domain status updated", { kind: "success" });
        paintDomBody(body, p, allowed);
      } catch (e) { toast(humanErr(e), { kind: "error" }); chk.disabled = false; icons(); }
    };
    body.querySelector("#dom-rm").onclick = () => confirmModal("Remove this domain?", "The site stays live on its Browns Digital address.", "Remove", async () => {
      await api.removeDomain(p.id, d.id || undefined);
      toast("Domain removed");
      paintDomBody(body, p, allowed);
    });
  };

  const domainStatusKey = (row) => {
    if (!row) return "";
    const hosts = row.hosts || {};
    return [
      row.id || "",
      row.domain || "",
      row.status || "",
      row.verification_status || "",
      row.ssl_status || "",
      row.server_attach_status || "",
      row.error_message || "",
      hosts.www_live ? "1" : "0",
      hosts.apex_live ? "1" : "0",
    ].join("|");
  };
  const gen = (body._domPaintGen = (body._domPaintGen || 0) + 1);
  api.getDomain(p.id).then(d => {
    if (body._domPaintGen !== gen) return;
    paintCustom(d);
    /* DNS and Cloudflare stay off the first paint. Refresh the card if status moves. */
    const vs = d?.verification_status || (d?.status === "verified" ? "verified" : "");
    const needsCfSync = d?.id && ["verified", "provisioning_ssl", "active"].includes(vs)
      && (d.ssl_status !== "active" || !d.hosts || (!d.hosts.www_live && !d.hosts.apex_live));
    if (!needsCfSync) return;
    const shown = domainStatusKey(d);
    api.checkSiteDomain(p.id, d.id).then(fresh => {
      if (body._domPaintGen !== gen || !fresh) return;
      if (domainStatusKey(fresh) === shown) return;
      paintCustom(fresh);
    }).catch(() => {});
  }).catch(e => {
    body.innerHTML = renderBdPanel() + block("", `
      <div class="error-msg show">${esc(humanErr(e, "Could not load this domain right now."))}</div>
      <div class="settings-footer"><button type="button" class="btn btn-ghost btn-sm" id="dom-retry">Try again</button></div>`);
    bindBdCopy();
    body.querySelector("#dom-retry").onclick = () => {
      body.innerHTML = skelRows(2, { avatar:false });
      paintDomBody(body, p, allowed);
    };
  });
}

/* publish flows */
/* publish flows — always deploy the freshest in-memory copy of the project */
async function preparePublishSource(p, trigger){
  const src = (current && current.id === p.id) ? current : p;
  /* Fold in a newer crash-recovery draft so unpublished canvas edits ship. */
  const draft = readDraft(src.id);
  if (draft?.data && (!src.updated_at || draft.t > new Date(src.updated_at).getTime() + 500)) {
    src.data = draft.data;
    if (current && current.id === src.id) current.data = draft.data;
  }
  pushRevisionFor(src, trigger || "STAGED", "Before staging deploy");
  if (current && current.id === src.id) await saveNow(true);
  else await api.saveProject(src);
  return (current && current.id === p.id) ? current : (projects.find(x => x.id === p.id) || src);
}
function liveUrlOf(rec, fallbackSlug){
  if (rec?.url) return rec.url;
  if (rec?.hostname) {
    const h = String(rec.hostname);
    if (h.startsWith("http://") || h.startsWith("https://")) return h.endsWith("/") ? h : h + "/";
    return "https://" + h.replace(/\/$/, "") + "/";
  }
  if (fallbackSlug) return prodSiteUrl(fallbackSlug);
  return "";
}
function publishHostLabel(url){
  return String(url || "").replace(/^https?:\/\//i, "").replace(/\/$/, "");
}
function paintPublishPhases(outEl, phases){
  if (!outEl || !Array.isArray(phases) || !phases.length) return;
  outEl.innerHTML = `<ul class="pub-phases">${phases.map(ph => {
    const label = typeof ph === "string" ? ph : (ph.label || "");
    const cls = ph.active ? "active" : (ph.done ? "done" : "");
    return `<li class="${cls}">${esc(label)}</li>`;
  }).join("")}</ul>`;
}
function stageFlow(p){ openPublishModal(p, { intent: "preview" }); }
function promoteFlow(p){ openPublishModal(p, { intent: "live" }); }
function pushLiveFlow(p){ openPublishModal(p, { intent: "live" }); }

/** Unified Publish sheet — Preview then Live. Live always restages before promote. */
function openPublishModal(p, { intent = "preview" } = {}){
  if (!p) return toast("Select a site first.");
  const refreshState = () => ({
    stg: stagingOf(p.id),
    prod: prodOf(p.id),
  });
  let { stg, prod } = refreshState();
  const lockedSlug = (stg || prod)?.slug || "";
  const reachedStep = () => (prod ? "live" : stg ? "preview" : "draft");
  let focus = intent === "live" ? "live" : intent === "draft" ? "draft" : "preview";
  if (!["draft", "preview", "live"].includes(focus)) focus = "preview";

  const m = openModal(`
    <div class="publish-sheet">
      <h2>Publish</h2>
      <p class="sub">Preview first. Live only when you push.</p>
      <p class="publish-site">${esc(p.name)}</p>
      <div class="publish-pipe" role="tablist" aria-label="Publish target" data-reached="${reachedStep()}" data-focus="${focus}">
        <button type="button" role="tab" class="publish-pipe-opt" data-k="draft" aria-selected="${focus === "draft" ? "true" : "false"}"><span>Draft</span></button>
        <button type="button" role="tab" class="publish-pipe-opt" data-k="preview" aria-selected="${focus === "preview" ? "true" : "false"}"><span>Preview</span></button>
        <button type="button" role="tab" class="publish-pipe-opt" data-k="live" aria-selected="${focus === "live" ? "true" : "false"}"><span>Live</span></button>
      </div>
      <div id="pub-address"></div>
      <div id="pub-out"></div>
      <div class="publish-actions" id="pub-actions"></div>
    </div>`);
  m.classList.add("publish-modal");

  const addrEl = m.querySelector("#pub-address");
  const outEl = m.querySelector("#pub-out");
  const actsEl = m.querySelector("#pub-actions");
  const pipeEl = m.querySelector(".publish-pipe");

  const syncPipe = () => {
    ({ stg, prod } = refreshState());
    if (!pipeEl) return;
    pipeEl.dataset.reached = reachedStep();
    pipeEl.dataset.focus = focus;
    pipeEl.querySelectorAll(".publish-pipe-opt").forEach(btn => {
      const on = btn.getAttribute("data-k") === focus;
      btn.setAttribute("aria-selected", on ? "true" : "false");
      btn.classList.toggle("is-selected", on);
    });
  };

  const setFocus = next => {
    if (!["draft", "preview", "live"].includes(next) || next === focus) return;
    focus = next;
    outEl.innerHTML = "";
    syncPipe();
    paintActions();
  };

  pipeEl?.querySelectorAll(".publish-pipe-opt").forEach(btn => {
    btn.onclick = () => setFocus(btn.getAttribute("data-k"));
  });

  const readSlug = () => {
    const inp = m.querySelector("#pub-slug");
    if (inp && !inp.readOnly) return slugify(inp.value.trim());
    return lockedSlug || slugOf(p);
  };

  const paintAddress = () => {
    ({ stg, prod } = refreshState());
    const slug = (stg || prod)?.slug || lockedSlug;
    if (!slug) {
      addrEl.innerHTML = `
        <div class="field publish-address-field">
          <label for="pub-slug">Site address</label>
          <div class="slug-row"><span>${esc(sitesBaseUrl())}</span><input id="pub-slug" value="${esc(slugOf(p))}" autocomplete="off" spellcheck="false"><span>/</span></div>
          <p class="fhint">Letters, numbers, and dashes. Preview and live share this address.</p>
        </div>`;
      const slugIn = m.querySelector("#pub-slug");
      slugIn?.addEventListener("input", () => {
        const msg = slugTakenMessage(slugIn.value.trim(), p);
        if (msg) outEl.innerHTML = `<div class="error-msg show">${esc(msg)}</div>`;
        else if (outEl.querySelector(".error-msg")) outEl.innerHTML = "";
      });
      return;
    }
    const previewUrl = stg?.url || (STAGING_BASE_URL + slug + "/staging/");
    const liveUrl = prod ? liveUrlOf(prod, slug) : prodSiteUrl(slug);
    addrEl.innerHTML = `
      <div class="publish-urls">
        <div class="publish-url-row">
          <em>Preview</em>
          <strong class="publish-url-host">${esc(publishHostLabel(previewUrl))}</strong>
          <span class="publish-url-acts">
            <button type="button" class="btn btn-ghost btn-sm" data-pub-open="preview" ${stg ? "" : "disabled"}>Open</button>
            <button type="button" class="btn btn-ghost btn-sm" data-pub-copy="${esc(previewUrl)}" ${stg ? "" : "disabled"}>Copy</button>
          </span>
        </div>
        <div class="publish-url-row">
          <em>Live</em>
          <strong class="publish-url-host">${esc(publishHostLabel(liveUrl) || "Not live yet")}</strong>
          <span class="publish-url-acts">
            <button type="button" class="btn btn-ghost btn-sm" data-pub-open="live" ${prod ? "" : "disabled"}>Open</button>
            <button type="button" class="btn btn-ghost btn-sm" data-pub-copy="${esc(liveUrl)}" ${prod ? "" : "disabled"}>Copy</button>
          </span>
        </div>
      </div>`;
    addrEl.querySelectorAll("[data-pub-copy]").forEach(btn => {
      btn.onclick = () => {
        const u = btn.getAttribute("data-pub-copy");
        if (!u) return;
        navigator.clipboard?.writeText(u).then(() => toast("Link copied")).catch(() => toast("Could not copy", { kind: "error" }));
      };
    });
    const openPrev = addrEl.querySelector('[data-pub-open="preview"]');
    if (openPrev && stg) openPrev.onclick = () => openDeploy(stg);
    const openLive = addrEl.querySelector('[data-pub-open="live"]');
    if (openLive && prod) openLive.onclick = () => openDeploy(prod);
  };

  const setBusy = busy => {
    actsEl.querySelectorAll("button").forEach(b => { b.disabled = !!busy; });
    const slugIn = m.querySelector("#pub-slug");
    if (slugIn && !slugIn.readOnly) slugIn.disabled = !!busy;
  };

  const showSuccess = ({ kind, url, rec }) => {
    ({ stg, prod } = refreshState());
    focus = kind === "live" ? "live" : "preview";
    syncPipe();
    paintAddress();
    outEl.innerHTML = `
      <div class="publish-success">
        <span class="publish-success-badge">${kind === "live" ? "Live" : "Preview ready"}</span>
        <p class="publish-success-host">${esc(publishHostLabel(url))}</p>
        <div class="publish-success-acts">
          <button type="button" class="btn btn-brand btn-sm" id="pub-open">Open</button>
          <button type="button" class="btn btn-ghost btn-sm" id="pub-copy">Copy link</button>
        </div>
      </div>`;
    outEl.querySelector("#pub-open").onclick = () => openDeploy({ ...rec, url });
    outEl.querySelector("#pub-copy").onclick = () => {
      navigator.clipboard?.writeText(url).then(() => toast("Link copied")).catch(() => toast("Could not copy", { kind: "error" }));
    };
    const liveExtra = kind === "preview"
      ? `<button type="button" class="btn btn-cta btn-sm" id="pub-to-live">Push to live</button>`
      : "";
    actsEl.innerHTML = `
      ${liveExtra}
      <button type="button" class="btn btn-ghost btn-sm" id="pub-done">Done</button>`;
    actsEl.querySelector("#pub-done").onclick = closeModal;
    const toLive = actsEl.querySelector("#pub-to-live");
    if (toLive) toLive.onclick = () => { focus = "live"; syncPipe(); outEl.innerHTML = ""; paintActions(); runLive(); };
    paintLiveInd(); paintSitePick();
    icons();
  };

  const runPreview = async () => {
    const slugVal = readSlug();
    if (!slugVal) {
      outEl.innerHTML = `<div class="error-msg show">Choose a site address first.</div>`;
      return;
    }
    const conflict = slugTakenMessage(slugVal, p);
    if (conflict) {
      outEl.innerHTML = `<div class="error-msg show">${esc(conflict)}</div>`;
      return;
    }
    setBusy(true);
    paintPublishPhases(outEl, [
      { label: "Saving", active: true },
      { label: "Uploading preview", done: false },
    ]);
    try {
      paintPublishPhases(outEl, [
        { label: "Saving", done: true },
        { label: "Uploading preview", active: true },
      ]);
      const src = await preparePublishSource(p, "STAGED");
      const rec = await api.deployStaging(src, slugVal);
      deploys = await api.listDeploys();
      log(p.id, "staged", `\u201c${p.name}\u201d preview updated`);
      toast("Preview updated", { kind: "success" });
      showSuccess({ kind: "preview", url: rec.url, rec });
    } catch (e) {
      outEl.innerHTML = `<div class="error-msg show">${esc(humanErr(e))}</div>`;
      setBusy(false);
      paintActions();
    }
  };

  const runLive = async () => {
    let slugVal = readSlug();
    ({ stg, prod } = refreshState());
    slugVal = (stg || prod)?.slug || slugVal;
    if (!slugVal) {
      outEl.innerHTML = `<div class="error-msg show">Create a preview address first.</div>`;
      return;
    }
    const conflict = slugTakenMessage(slugVal, p);
    if (conflict && !(stg || prod)) {
      outEl.innerHTML = `<div class="error-msg show">${esc(conflict)}</div>`;
      return;
    }
    setBusy(true);
    paintPublishPhases(outEl, [
      { label: "Saving", active: true },
      { label: "Uploading preview", done: false },
      { label: "Pushing live", done: false },
    ]);
    try {
      paintPublishPhases(outEl, [
        { label: "Saving", done: true },
        { label: "Uploading preview", active: true },
        { label: "Pushing live", done: false },
      ]);
      const src = await preparePublishSource(p, "PUBLISHED");
      await api.deployStaging(src, slugVal);
      paintPublishPhases(outEl, [
        { label: "Saving", done: true },
        { label: "Uploading preview", done: true },
        { label: "Pushing live", active: true },
      ]);
      const rec = await api.promote(src);
      deploys = await api.listDeploys();
      const liveUrl = liveUrlOf(rec, slugVal);
      log(p.id, "staged", `\u201c${p.name}\u201d preview updated`);
      log(p.id, "published", `\u201c${p.name}\u201d pushed live`);
      toast(liveUrl ? ("Live at " + publishHostLabel(liveUrl)) : "Site is live", { kind: "success" });
      showSuccess({ kind: "live", url: liveUrl, rec: { ...rec, url: liveUrl } });
    } catch (e) {
      outEl.innerHTML = `<div class="error-msg show">${esc(humanErr(e))}</div>`;
      setBusy(false);
      paintActions();
    }
  };

  const paintActions = () => {
    ({ stg, prod } = refreshState());
    const hasPreview = !!stg;
    const hasLive = !!prod;
    syncPipe();

    if (focus === "draft") {
      actsEl.innerHTML = `
        <button type="button" class="btn btn-ghost btn-sm" data-x="c">Keep editing</button>
        <button type="button" class="btn btn-brand btn-sm" id="pub-go-preview">Continue to preview</button>`;
      actsEl.querySelector('[data-x="c"]').onclick = closeModal;
      actsEl.querySelector("#pub-go-preview").onclick = () => setFocus("preview");
      return;
    }

    if (focus === "preview") {
      if (!hasPreview && !hasLive) {
        actsEl.innerHTML = `
          <button type="button" class="btn btn-ghost btn-sm" data-x="c">Cancel</button>
          <button type="button" class="btn btn-brand btn-sm" id="pub-preview">Create preview</button>`;
        actsEl.querySelector('[data-x="c"]').onclick = closeModal;
        actsEl.querySelector("#pub-preview").onclick = runPreview;
        return;
      }
      actsEl.innerHTML = `
        <button type="button" class="btn btn-ghost btn-sm" data-x="c">Cancel</button>
        ${hasPreview ? `<button type="button" class="btn btn-ghost btn-sm" id="pub-open-preview">Open preview</button>` : ""}
        <button type="button" class="btn btn-brand btn-sm" id="pub-preview">${hasPreview ? "Update preview" : "Create preview"}</button>`;
      actsEl.querySelector('[data-x="c"]').onclick = closeModal;
      const openPrev = actsEl.querySelector("#pub-open-preview");
      if (openPrev) openPrev.onclick = () => openDeploy(stg);
      actsEl.querySelector("#pub-preview").onclick = runPreview;
      return;
    }

    /* focus === "live" */
    if (!hasPreview && !hasLive) {
      actsEl.innerHTML = `
        <button type="button" class="btn btn-ghost btn-sm" data-x="c">Cancel</button>
        <button type="button" class="btn btn-cta btn-sm" id="pub-live">Push to live</button>`;
      actsEl.querySelector('[data-x="c"]').onclick = closeModal;
      actsEl.querySelector("#pub-live").onclick = runLive;
      return;
    }
    if (hasPreview && !hasLive) {
      actsEl.innerHTML = `
        <button type="button" class="btn btn-ghost btn-sm" data-x="c">Cancel</button>
        <button type="button" class="btn btn-ghost btn-sm" id="pub-open-preview">Open preview</button>
        <button type="button" class="btn btn-cta btn-sm publish-cta-pulse" id="pub-live">Push to live</button>`;
      actsEl.querySelector('[data-x="c"]').onclick = closeModal;
      actsEl.querySelector("#pub-open-preview").onclick = () => openDeploy(stg);
      actsEl.querySelector("#pub-live").onclick = runLive;
      return;
    }
    actsEl.innerHTML = `
      <button type="button" class="btn btn-ghost btn-sm" data-x="c">Cancel</button>
      <button type="button" class="btn btn-ghost btn-sm" id="pub-open-live">Open live</button>
      <button type="button" class="btn btn-cta btn-sm" id="pub-live">Update live</button>`;
    actsEl.querySelector('[data-x="c"]').onclick = closeModal;
    actsEl.querySelector("#pub-open-live").onclick = () => openDeploy(prod);
    actsEl.querySelector("#pub-live").onclick = runLive;
  };

  paintAddress();
  paintActions();
  if (focus === "live" && stg && !prod) {
    requestAnimationFrame(() => actsEl.querySelector("#pub-live")?.focus());
  }
  icons();
}
function openQuickPublishModal(){
  const p = active();
  if (!p) return toast("Select a site first.");
  openPublishModal(p, { intent: "preview" });
}
function pushRevisionFor(p, trigger, label){
  const revs = p.data._revisions = p.data._revisions || [];
  revs.unshift({ ts: new Date().toISOString(), trigger: trigger || "SAVE", label: label || "", snap: JSON.parse(JSON.stringify({ tokens: p.data.tokens, seo: p.data.seo, shared: p.data.shared, pages: p.data.pages })) });
  if (revs.length > 12) revs.length = 12;
}

/* ================================ HEALTH ================================ */
function relLum(hex){
  const c = /^#([0-9a-f]{6})$/i.exec(hex || ""); if (!c) return null;
  const [r,g,b] = [0,2,4].map(i => parseInt(c[1].slice(i,i+2),16)/255).map(v => v <= .03928 ? v/12.92 : Math.pow((v+.055)/1.055, 2.4));
  return .2126*r + .7152*g + .0722*b;
}
function contrast(a, b){ const l1 = relLum(a), l2 = relLum(b); if (l1 == null || l2 == null) return 21; const [x,y] = [Math.max(l1,l2), Math.min(l1,l2)]; return (x+.05)/(y+.05); }
function bdAuditRelativeLinks(blocks, where, pageId, add){
  if (typeof lines !== "function" || typeof bdLinkIsRelative !== "function" || typeof parts !== "function") return;
  (blocks || []).forEach(b => {
    if (!b || b.hidden || (b.type !== "nav" && b.type !== "footer")) return;
    const fix = pageId ? { go: "builder-edit", pg: pageId } : { go: "builder-edit" };
    const flag = (label, href) => {
      if (!bdLinkIsRelative(href)) return;
      const name = String(label || "A link").trim() || "A link";
      const hint = typeof siteLinkHref === "function" ? siteLinkHref(href) : ("/" + String(href).replace(/^\/+/, ""));
      add("warn", where, name + " points to “" + href + "” without a leading /. Use " + hint + " so it stays on this site.", fix);
    };
    lines(b.props?.links).forEach(l => {
      const [label, href] = parts(l);
      flag(label, href);
    });
    if (b.props?.ctaHref) flag(b.props.cta || "The button", b.props.ctaHref);
  });
}

function auditSite(p){
  const d = p.data, f = [];
  const add = (sev, area, msg, fix) => f.push({ sev, area, msg, fix });
  const prevPageSlugs = typeof bdRenderPageSlugs === "undefined" ? undefined : bdRenderPageSlugs;
  if (typeof siteLinkHref === "function") {
    bdRenderPageSlugs = new Set((d.pages || []).map(pg => String(pg?.slug || "").replace(/^\/+|\/+$/g, "").toLowerCase()).filter(Boolean));
  }
  const titles = {};
  const slugs = {};
  d.pages.forEach(pg => {
    const where = pg.name;
    const resolved = typeof resolvePageSeo === "function" ? resolvePageSeo(p, pg) : null;
    const title = resolved?.title || pg.seo?.title || d.seo?.title || "";
    const desc = resolved?.description || pg.seo?.description || d.seo?.description || "";
    const explicitTitle = !!(pg.seo?.title || d.seo?.title);
    const explicitDesc = !!(pg.seo?.description || d.seo?.description);
    if (!title) add("crit", where, "No page title — search engines will invent one.", { go:"builder-edit", pg:pg.id });
    else if (!explicitTitle) add("warn", where, "No custom page title — we auto-fill from your content, but a written title usually gets better clicks.", { go:"builder-edit", pg:pg.id });
    else if (title.length > 60) add("warn", where, `Title is ${title.length} characters — Google truncates around 60.`, { go:"builder-edit", pg:pg.id });
    if (!desc) add("warn", where, "No meta description — the share preview and SERP snippet will be empty.", { go:"builder-edit", pg:pg.id });
    else if (!explicitDesc) add("warn", where, "No custom meta description — we auto-fill from your first section at publish time.", { go:"builder-edit", pg:pg.id });
    else if (desc.length > 160) add("warn", where, `Description is ${desc.length} characters — keep it under 160.`, { go:"builder-edit", pg:pg.id });
    if (titles[title] && title) add("warn", where, `Duplicate title — also used on “${titles[title]}”.`, { go:"builder-edit", pg:pg.id });
    titles[title] = pg.name;
    if (slugs[pg.slug] !== undefined) add("crit", where, `Duplicate address “/${pg.slug}” — two pages can’t share a URL.`, { go:"builder-edit", pg:pg.id });
    slugs[pg.slug] = 1;
    if (pg.type !== "notfound"){
      const words = pg.blocks.reduce((n,b) => n + Object.values(b.props).join(" ").split(/\s+/).filter(Boolean).length, 0);
      if (pg.blocks.length === 0) add("crit", where, "Page is empty.", { go:"builder-edit", pg:pg.id });
      else if (words < 30) add("warn", where, `Thin content — only ${words} words. Search engines may ignore it.`, { go:"builder-edit", pg:pg.id });
    }
    const dead = (JSON.stringify(pg.blocks).match(/:"#"/g) || []).length;
    if (dead) add("warn", where, `${dead} button${dead>1?"s":""}/link${dead>1?"s":""} still point to “#”.`, { go:"builder-edit", pg:pg.id });
    bdAuditRelativeLinks(pg.blocks, where, pg.id, add);
    const untouched = typeof BLOCKS === "undefined" ? 0 : pg.blocks.filter(b => JSON.stringify(b.props) === JSON.stringify(BLOCKS[b.type]?.defaults)).length;
    if (untouched) add("warn", where, `${untouched} section${untouched>1?"s use":" uses"} untouched template copy.`, { go:"builder-edit", pg:pg.id });
  });
  bdAuditRelativeLinks(d.shared?.header ? [d.shared.header] : [], "Navigation", null, add);
  bdAuditRelativeLinks(d.shared?.footer ? [d.shared.footer] : [], "Footer", null, add);
  if (typeof siteLinkHref === "function") bdRenderPageSlugs = prevPageSlugs;
  if (!d.seo?.favicon) add("warn", "Site", "No favicon set — browser tabs show a blank icon. Add one under Settings → Site.", { go:"settings", tab:"site" });
  if (!d.seo?.ogImage) add("warn", "Site", "No default share image — links look bare on WhatsApp, X, and LinkedIn.", { go:"settings", tab:"site" });
  if (!d.pages.some(x => x.type === "notfound")) add("warn", "Site", "No 404 page — visitors hitting a dead link get a server default.", { go:"builder-edit" });
  if (d.tokens.mode === "light" && contrast(d.tokens.accent, "#ffffff") < 3)
    add("crit", "Design", `Accent ${d.tokens.accent} has very low contrast on white — buttons may be unreadable (WCAG fail).`, { go:"builder-edit" });
  const crit = f.filter(x => x.sev === "crit").length, warn = f.length - crit;
  return { findings: f, crit, warn, score: Math.max(5, 100 - crit*12 - warn*5) };
}
async function pageHealth(el){
  const shell = document.createElement("div");
  shell.className = "studio-page studio-page--health";
  el.appendChild(shell);

  const p = active();
  if (!p){
    const w = document.createElement("div");
    w.className = "empty-mesh liquid-glass reveal";
    w.innerHTML = emptyStateHtml("heart-pulse", "No site to check",
      "Create a site first, then run a health audit.",
      `<button class="btn btn-brand" id="hl-new">${lu("plus")} New site</button>`);
    shell.appendChild(w);
    w.querySelector("#hl-new").onclick = newSiteFlow;
    icons();
    return;
  }
  if (!Array.isArray(p.data?.pages)) throw new Error("This site has not finished loading. Try again.");
  if (!siteHasContent(p)){
    head(shell, "heart-pulse", "Site health", "Run a full audit once your site has content on the canvas.");
    const empty = document.createElement("div");
    empty.className = "health-empty liquid-glass reveal";
    empty.innerHTML = `
      <span class="health-empty-ico empty-well">${lu("heart-pulse","lu24")}</span>
      <p class="health-empty-title">No site content found</p>
      <p class="health-empty-copy">Add sections on the canvas — health checks need pages with real content.</p>
      <button class="btn btn-brand btn-sm" id="hl-build">${lu("palette","lu14")} Open canvas</button>`;
    shell.appendChild(empty);
    empty.querySelector("#hl-build").onclick = () => openBuilderEdit(activeId);
    icons();
    return;
  }

  await loadStudioBlocks();
  const a = auditSite(p);
  recordHealthHistory(p, a.score);
  const tone = a.score >= 85 ? "good" : a.score >= 60 ? "ok" : "bad";
  const headline = a.score >= 85 ? "Looking sharp" : a.score >= 60 ? "Nearly there" : "Needs attention";
  const countLine = a.findings.length
    ? `<span class="health-count is-crit">${a.crit} critical</span><span class="health-count-sep">·</span><span class="health-count is-warn">${a.warn} warning${a.warn === 1 ? "" : "s"}</span>`
    : `<span class="health-count is-good">All clear</span>`;

  head(
    shell,
    "heart-pulse",
    "Site health",
    "Checked on this site — SEO, content, links, and accessibility."
  );

  const hero = document.createElement("section");
  hero.className = `health-hero liquid-glass reveal is-${tone}`;
  hero.innerHTML = `
    <div class="health-hero-score" aria-label="Health score ${a.score} out of 100">
      ${healthRingSVG(a.score, 96, 7)}
      </div>
    <div class="health-hero-main">
      <p class="health-hero-label">Score</p>
      <h2 class="health-hero-title">${headline}</h2>
      <p class="health-hero-counts">${countLine}</p>
    </div>
    <div class="health-hero-tools" role="group" aria-label="Health actions">
      <button type="button" class="btn btn-ghost btn-sm" id="hl-re" title="Re-scan">${lu("refresh-cw","lu14")} Re-scan</button>
      <button type="button" class="btn btn-brand btn-sm" id="hl-dl" title="Download report">${lu("download","lu14")} Download</button>
    </div>`;
  shell.appendChild(hero);
  hero.querySelector("#hl-re").onclick = () => { recordHealthHistory(p, auditSite(p).score); go("health"); toast("Re-scanned"); };
  hero.querySelector("#hl-dl").onclick = () => downloadHealthReport(p, a);

  const panel = document.createElement("section");
  panel.className = "health-findings liquid-glass reveal";
  panel.innerHTML = `
    <div class="health-findings-head">
      <h2>What to fix <em>${a.findings.length}</em></h2>
      <div class="health-find-filter" role="group" aria-label="Group findings">
        <button type="button" class="on" data-hf="sev">Severity</button>
        <button type="button" data-hf="page">Page</button>
        <button type="button" data-hf="all">List</button>
      </div>
    </div>
    <div class="health-find-list" id="health-find-list"></div>`;
  shell.appendChild(panel);

  const fl = panel.querySelector("#health-find-list");
  const rowHtml = x => {
    const i = a.findings.indexOf(x);
    return `<div class="health-row is-${x.sev === "crit" ? "crit" : "warn"}" data-i="${i}" role="button" tabindex="0">
      <span class="health-row-ico ${x.sev === "crit" ? "crit" : "warn"}" aria-hidden="true">${lu(x.sev === "crit" ? "circle-alert" : "triangle-alert","lu16")}</span>
      <span class="health-row-body">
        <span class="health-row-msg">${esc(x.msg)}</span>
        <span class="health-page-badge">${esc(x.area)}</span>
      </span>
      <button type="button" class="btn btn-brand btn-sm health-fix-btn" data-fix="${i}">
        ${lu("wrench","lu14")} Fix
      </button>
    </div>`;
  };
  const bindFindingRow = r => {
    const run = () => {
        const x = a.findings[+r.dataset.i];
        if (x.fix?.pg) curPageId = x.fix.pg;
        goFix(x.fix);
    };
    r.addEventListener("click", e => {
      if (e.target.closest(".health-fix-btn")) return;
      run();
    });
    r.querySelector(".health-fix-btn").onclick = e => {
      e.stopPropagation();
      run();
    };
    r.addEventListener("keydown", e => {
      if (e.target.closest(".health-fix-btn")) return;
      if (e.key === "Enter" || e.key === " "){
        e.preventDefault();
        run();
      }
    });
  };
  const groupHtml = (title, items, open = true) => {
    const id = "hg-" + title.toLowerCase().replace(/[^a-z0-9]+/g, "-");
    return `<div class="health-group${open ? "" : " is-collapsed"}" data-group="${esc(id)}">
      <button type="button" class="health-group-head" aria-expanded="${open ? "true" : "false"}" aria-controls="${esc(id)}">
        <span class="health-group-chev" aria-hidden="true">${lu("chevron-down","lu14")}</span>
        <strong class="health-group-title">${esc(title)}</strong>
        <span class="health-group-count">${items.length}</span>
      </button>
      <div class="health-group-rows" id="${esc(id)}"${open ? "" : " hidden"}>${items.map(rowHtml).join("")}</div>
    </div>`;
  };

  function bindGroups(){
    fl.querySelectorAll(".health-group-head").forEach(btn => {
      btn.onclick = () => {
        const group = btn.closest(".health-group");
        const rows = group.querySelector(".health-group-rows");
        const open = btn.getAttribute("aria-expanded") !== "true";
        btn.setAttribute("aria-expanded", open ? "true" : "false");
        group.classList.toggle("is-collapsed", !open);
        rows.hidden = !open;
      };
    });
    fl.querySelectorAll(".health-row").forEach(bindFindingRow);
    icons();
  }

  function paintFindings(mode){
    if (!a.findings.length){
      fl.innerHTML = `<div class="health-clear">
        <span class="health-clear-ico">${lu("badge-check","lu24")}</span>
        <div class="big">Nothing to fix</div>
        <p>Every page passed every check.</p>
      </div>`;
      icons();
      return;
    }
    if (mode === "page"){
      const map = new Map();
      a.findings.forEach(x => {
        if (!map.has(x.area)) map.set(x.area, []);
        map.get(x.area).push(x);
      });
      fl.innerHTML = [...map.entries()].map(([area, items]) => groupHtml(area, items, true)).join("");
    } else if (mode === "sev"){
      const crits = a.findings.filter(x => x.sev === "crit");
      const warns = a.findings.filter(x => x.sev !== "crit");
      fl.innerHTML = [
        crits.length ? groupHtml("Critical", crits, true) : "",
        warns.length ? groupHtml("Warnings", warns, true) : "",
      ].join("");
    } else {
      const items = [...a.findings].sort((x, y) => (x.sev === y.sev ? 0 : x.sev === "crit" ? -1 : 1));
      fl.innerHTML = `<div class="health-group-rows health-group-rows--flat">${items.map(rowHtml).join("")}</div>`;
      fl.querySelectorAll(".health-row").forEach(bindFindingRow);
      icons();
      return;
    }
    bindGroups();
  }
  paintFindings("sev");
  panel.querySelectorAll("[data-hf]").forEach(b => b.onclick = () => {
    panel.querySelectorAll("[data-hf]").forEach(x => x.classList.toggle("on", x === b));
    paintFindings(b.dataset.hf);
  });
  icons();
}
function recordHealthHistory(p, score){
  if (!p?.data) return;
  const today = new Date().toISOString().slice(0, 10);
  const prev = Array.isArray(p.data.health_history) ? p.data.health_history : [];
  const current = prev.find(h => h && h.date === today);
  if (current && Number(current.score) === Number(score)) return;
  const hist = prev.slice();
  const row = { date: today, score };
  const idx = hist.findIndex(h => h && h.date === today);
  if (idx >= 0) hist[idx] = row; else hist.push(row);
  p.data.health_history = hist.slice(-30);
  api.saveProject(p, { stamp: false });
}
function renderHealthSparkline(p){
  const hist = (p.data.health_history || []).slice(-7);
  if (hist.length < 2) return "";
  const w = 220, h = 40, pad = 4;
  const scores = hist.map(x => x.score);
  const min = Math.min(...scores, 0), max = Math.max(...scores, 100);
  const pts = scores.map((s, i) => {
    const x = pad + (i / (scores.length - 1)) * (w - pad * 2);
    const y = h - pad - ((s - min) / (max - min || 1)) * (h - pad * 2);
    return `${x},${y}`;
  }).join(" ");
  const area = `${pad},${h - pad} ${pts} ${w - pad},${h - pad}`;
  return `<div class="health-spark"><span class="health-spark-label">7-day trend</span><svg class="health-sparkline" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}" aria-hidden="true"><polygon points="${area}" fill="color-mix(in srgb,var(--brand) 10%,transparent)"/><polyline points="${pts}" fill="none" stroke="var(--brand)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></div>`;
}
function goFix(fix){
  if (fix?.pg) curPageId = fix.pg;
  if (!fix?.go || fix.go === "builder" || fix.go === "builder-edit") openBuilderEdit(activeId);
  else if (fix.go === "settings") goSettings(fix.tab);
  else go(fix.go);
}
/* Standalone, shareable HTML report — opens anywhere, prints cleanly. */
function downloadHealthReport(p, a){
  const when = new Date();
  /* Standalone report has no platform CSS — use hig-tokens label values */
  const col = a.score >= 85 ? "#15803d" : a.score >= 60 ? "#9a5700" : "#c41218";
  const colSoft = a.score >= 85 ? "rgba(52,199,89,.14)" : a.score >= 60 ? "rgba(245,166,35,.16)" : "rgba(255,59,48,.12)";
  const headline = a.score >= 85 ? "Looking sharp" : a.score >= 60 ? "Nearly there" : "Needs attention";
  const crits = a.findings.filter(x => x.sev === "crit");
  const warns = a.findings.filter(x => x.sev !== "crit");
  const findingBlock = (title, items, kind) => !items.length ? "" : `
    <section class="block">
      <div class="block-head"><h2>${title}</h2><span class="pill ${kind}">${items.length}</span></div>
      <ul class="findings">
        ${items.map(x => `<li class="${kind}">
          <span class="dot" aria-hidden="true"></span>
          <div><p class="msg">${esc(x.msg)}</p><p class="area">${esc(x.area)}</p></div>
        </li>`).join("")}
      </ul>
    </section>`;
  const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>Site health · ${esc(p.name)} · Browns Digital</title>
<style>
  :root{--ink:#1d1d1f;--muted:#6e6e73;--line:#e8e8ed;--bg:#f4f5f7;--card:#fff;--brand:#3b6cff}
  *{box-sizing:border-box}
  body{
    margin:0;padding:48px 20px 64px;
    font:15px/1.45 -apple-system,BlinkMacSystemFont,"SF Pro Text","Segoe UI",Helvetica,Arial,sans-serif;
    color:var(--ink);background:
      radial-gradient(900px 420px at 12% -10%,rgba(59,108,255,.08),transparent 60%),
      radial-gradient(700px 360px at 100% 0%,rgba(52,199,89,.06),transparent 55%),
      var(--bg);
    -webkit-font-smoothing:antialiased;
  }
  .wrap{max-width:720px;margin:0 auto}
  .brand{
    display:flex;align-items:center;justify-content:space-between;gap:12px;
    margin-bottom:22px;color:var(--muted);font-size:12.5px;font-weight:600;letter-spacing:.02em;
  }
  .brand strong{color:var(--ink);font-weight:700;letter-spacing:-.02em}
  .hero{
    background:var(--card);border:1px solid var(--line);border-radius:24px;
    padding:28px;margin-bottom:16px;
    box-shadow:0 1px 2px rgba(29,29,31,.04),0 18px 48px rgba(29,29,31,.05);
    display:grid;grid-template-columns:auto 1fr;gap:24px;align-items:center;
  }
  .ring{
    width:104px;height:104px;border-radius:50%;
    background:conic-gradient(${col} ${a.score * 3.6}deg, ${colSoft} 0);
    display:grid;place-items:center;flex:0 0 auto;
  }
  .ring span{
    width:80px;height:80px;border-radius:50%;background:#fff;
    display:grid;place-items:center;
    font-size:28px;font-weight:700;letter-spacing:-.04em;color:var(--ink);
    box-shadow:inset 0 0 0 1px var(--line);
  }
  .kicker{margin:0 0 4px;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--muted)}
  h1{margin:0;font-size:clamp(1.35rem,2.4vw,1.7rem);font-weight:700;letter-spacing:-.035em;line-height:1.15}
  .meta{margin:8px 0 0;color:var(--muted);font-size:13.5px}
  .stats{display:flex;flex-wrap:wrap;gap:8px;margin-top:16px}
  .stat{
    min-width:88px;padding:10px 12px;border-radius:14px;
    background:#f8f8fa;border:1px solid var(--line);
  }
  .stat b{display:block;font-size:18px;font-weight:700;letter-spacing:-.03em;line-height:1.1}
  .stat span{display:block;margin-top:2px;font-size:11px;font-weight:600;color:var(--muted);text-transform:uppercase;letter-spacing:.04em}
  .stat.crit{background:rgba(255,59,48,.08);border-color:rgba(255,59,48,.16)}
  .stat.crit b{color:#c41218}
  .stat.warn{background:rgba(245,166,35,.1);border-color:rgba(245,166,35,.2)}
  .stat.warn b{color:#9a5700}
  .block{
    background:var(--card);border:1px solid var(--line);border-radius:20px;
    padding:20px 22px;margin-bottom:14px;
    box-shadow:0 1px 2px rgba(29,29,31,.03);
  }
  .block-head{display:flex;align-items:center;gap:10px;margin-bottom:12px}
  .block-head h2{margin:0;font-size:14px;font-weight:700;letter-spacing:-.02em}
  .pill{
    margin-left:auto;font-size:11px;font-weight:700;padding:3px 9px;border-radius:999px;
  }
  .pill.crit{background:rgba(255,59,48,.1);color:#c41218}
  .pill.warn{background:rgba(245,166,35,.14);color:#9a5700}
  .findings{list-style:none;margin:0;padding:0}
  .findings li{
    display:grid;grid-template-columns:auto 1fr;gap:12px;align-items:flex-start;
    padding:12px 0;border-top:1px solid var(--line);
  }
  .findings li:first-child{border-top:none;padding-top:2px}
  .dot{width:10px;height:10px;border-radius:50%;margin-top:5px;flex:0 0 auto}
  .findings li.crit .dot{background:#ff3b30}
  .findings li.warn .dot{background:#f5a623}
  .msg{margin:0;font-size:14px;font-weight:500;letter-spacing:-.01em;line-height:1.4}
  .area{margin:3px 0 0;font-size:12px;color:var(--muted)}
  .empty{margin:0;color:var(--muted);font-size:14px}
  .foot{
    margin-top:28px;text-align:center;color:#6e6e73;font-size:12px;line-height:1.5;
  }
  .foot a{color:var(--brand);text-decoration:none;font-weight:600}
  @media (max-width:560px){
    .hero{grid-template-columns:1fr;justify-items:center;text-align:center}
    .stats{justify-content:center}
  }
  @media print{
    body{background:#fff;padding:12px}
    .hero,.block{box-shadow:none}
  }
</style>
</head>
<body>
  <div class="wrap">
    <div class="brand"><strong>Browns Digital</strong><span>Site health report</span></div>
    <header class="hero">
      <div class="ring" aria-hidden="true"><span>${a.score}</span></div>
      <div>
        <p class="kicker">${esc(p.name)}</p>
        <h1>${headline}</h1>
        <p class="meta">Generated ${esc(when.toLocaleString())} · ${p.data.pages.length} page${p.data.pages.length === 1 ? "" : "s"} scanned</p>
        <div class="stats">
          <div class="stat crit"><b>${a.crit}</b><span>Critical</span></div>
          <div class="stat warn"><b>${a.warn}</b><span>Warnings</span></div>
          <div class="stat"><b>${p.data.pages.length}</b><span>Pages</span></div>
          <div class="stat"><b>${a.findings.length}</b><span>Findings</span></div>
        </div>
      </div>
    </header>
    ${a.findings.length
      ? findingBlock("Critical", crits, "crit") + findingBlock("Warnings", warns, "warn")
      : `<section class="block"><p class="empty">No issues found — every page passed every check.</p></section>`}
    <p class="foot">Hosted &amp; checked by Browns Digital Studio<br><a href="https://brownsdigital.org">brownsdigital.org</a></p>
  </div>
</body>
</html>`;
  const aEl = document.createElement("a");
  aEl.href = URL.createObjectURL(new Blob([html], { type: "text/html" }));
  aEl.download = slugOf(p) + "-health-report.html";
  aEl.click(); URL.revokeObjectURL(aEl.href);
  toast("Health report downloaded");
}

/* ============================== SETTINGS ============================== */
function goSettings(tab){
  if (tab){
    const aliases = { general:"site", seo:"site", integrations:"advanced", danger:"advanced" };
    sessionStorage.setItem("bd_settab", aliases[tab] || tab);
  }
  go("settings");
}
function pageSettings(el){
  const p = active();
  if (!p || !Array.isArray(p.data?.pages)) throw new Error("This site has not finished loading. Try again.");
  ensureBrandAccentAligned(p);
  const shell = document.createElement("div");
  shell.className = "studio-page studio-page--settings";
  el.appendChild(shell);

  head(
    shell,
    "settings",
    "Settings",
    "Site name, brand, domain, and publish options."
  );

  const tabs = [
    ["site", "Site"],
    ["brand", "Brand"],
    ["domain", "Domain"],
    ["advanced", "Advanced"],
  ];
  const aliases = { general:"site", seo:"site", integrations:"advanced", danger:"advanced" };
  let cur = sessionStorage.getItem("bd_settab") || "site";
  cur = aliases[cur] || cur;
  if (!tabs.some(([k]) => k === cur)) cur = "site";
  sessionStorage.setItem("bd_settab", cur);

  const nav = document.createElement("nav");
  nav.className = "settings-nav reveal";
  nav.setAttribute("role", "tablist");
  nav.setAttribute("aria-label", "Settings sections");
  nav.innerHTML = `<div class="settings-nav-track">${tabs.map(([k, l]) =>
    `<button type="button" role="tab" data-t="${k}" class="settings-tab${k === cur ? " on" : ""}" aria-selected="${k === cur ? "true" : "false"}">
      <span class="settings-tab-label">${esc(l)}</span>
    </button>`
  ).join("")}</div>`;
  shell.appendChild(nav);

  const body = document.createElement("div");
  body.className = "settings-body";
  shell.appendChild(body);

  const sCard = (html, cls) => {
    const c = document.createElement("section");
    c.className = "settings-card liquid-glass reveal" + (cls ? " " + cls : "");
    c.innerHTML = html;
    body.appendChild(c);
    return c;
  };
  const secHead = (title, sub) =>
    `<header class="settings-sec-head"><h2>${title}</h2>${sub ? `<p class="c-sub">${sub}</p>` : ""}</header>`;
  const block = (label, inner) =>
    `<div class="settings-block">${label ? `<p class="settings-block-label">${esc(label)}</p>` : ""}${inner}</div>`;

  const promoteCards = () => {
    body.querySelectorAll(":scope > .card").forEach(c => {
      c.classList.add("settings-card", "liquid-glass", "reveal");
      c.classList.remove("card");
    });
  };

  const setTab = k => {
    cur = k;
    sessionStorage.setItem("bd_settab", cur);
    if (typeof body._bdIconPopCleanup === "function"){
      body._bdIconPopCleanup();
      body._bdIconPopCleanup = null;
    }
    nav.querySelectorAll(".settings-tab").forEach(x => {
      const on = x.dataset.t === cur;
      x.classList.toggle("on", on);
      x.setAttribute("aria-selected", on ? "true" : "false");
    });
    paint();
  };
  nav.querySelectorAll(".settings-tab").forEach(b => b.onclick = () => setTab(b.dataset.t));

  function paint(){
    body.innerHTML = "";
    body.dataset.tab = cur;

    if (cur === "site"){
      const s = p.data.seo;
      const curIcon = siteIconOf(p);
      const site = sCard(`${secHead("Site", "Display name and search defaults.")}
        ${block("Basics", `
          <div class="settings-field">
            <label class="settings-label" for="st-name">Display name</label>
            <input class="settings-input" id="st-name" value="${esc(p.name)}" aria-label="Site name" autocomplete="organization">
            <div class="settings-inline-save" id="st-name-hint" aria-live="polite"></div>
          </div>
          <div class="settings-field">
            <span class="settings-label" id="st-icon-label">Console icon</span>
            <p class="settings-hint">Shown next to the site name on Console. Colour matches Design accent site-wide.</p>
            <div class="site-icon-field" id="st-icon-field">
              <button type="button" class="site-icon-trigger" id="st-icon-open" aria-haspopup="dialog" aria-expanded="false" aria-labelledby="st-icon-label">
                <span class="site-icon-preview" style="--site-mark:${esc(siteIconColorOf(p))};color:var(--site-mark)">${lu(curIcon)}</span>
                <span class="site-icon-meta">
                  <strong>Customize</strong>
                  <em>Symbol &amp; colour</em>
                </span>
                ${lu("chevron-right","lu14")}
              </button>
              <div class="site-icon-pop" id="st-icon-pop" hidden role="dialog" aria-label="Customize console icon">
                <div class="site-icon-pop-sec">
                  <p class="site-icon-pop-label">Symbol</p>
                  <div class="site-icon-grid" role="listbox" aria-label="Symbol">
                    ${SITE_ICONS.map(name =>
                      `<button type="button" class="site-icon-opt${name === curIcon ? " on" : ""}" role="option" aria-selected="${name === curIcon ? "true" : "false"}" data-icon="${esc(name)}" title="${esc(name)}">${lu(name,"lu16")}</button>`
                    ).join("")}
                  </div>
                </div>
                <div class="site-icon-pop-sec">
                  <p class="site-icon-pop-label">Colour</p>
                  <div class="site-icon-color" id="st-icon-color"></div>
                </div>
              </div>
            </div>
            <div class="settings-inline-save" id="st-icon-hint" aria-live="polite"></div>
          </div>`)}
        ${block("Search", `
          <div class="settings-field-grid">
            <div class="settings-field"><label class="settings-label" for="seo-t">Default title</label><input class="settings-input" id="seo-t" value="${esc(s.title)}" placeholder="${esc(p.name)}"></div>
            <div class="settings-field settings-field--span"><label class="settings-label" for="seo-d">Default description</label><textarea class="settings-input settings-textarea" id="seo-d">${esc(s.description)}</textarea></div>
          </div>`)}
        ${block("Social", `
          <div class="settings-field-grid">
            <div class="settings-field"><label class="settings-label" for="seo-o">Share image URL</label><input class="settings-input" id="seo-o" value="${esc(s.ogImage)}" placeholder="1200×630 recommended"></div>
            <div class="settings-field"><label class="settings-label" for="seo-f">Favicon URL</label><input class="settings-input" id="seo-f" value="${esc(s.favicon)}" placeholder="Square PNG or ICO"></div>
          </div>`)}
        <div class="settings-footer"><button type="button" class="btn btn-brand btn-sm" id="seo-save">Save</button></div>`);
      icons();
      let nameTimer = null;
      const nameHint = site.querySelector("#st-name-hint");
      const nameIn = site.querySelector("#st-name");
      nameIn.oninput = () => {
        clearTimeout(nameTimer);
        nameHint.textContent = "Saving…"; nameHint.classList.remove("saved");
        nameTimer = setTimeout(async () => {
          const v = nameIn.value.trim();
          if (!v || v === p.name){ nameHint.textContent = ""; return; }
          const old = p.name; p.name = v; await api.saveProject(p);
          log(p.id, "renamed", `\u201c${old}\u201d renamed to \u201c${v}\u201d`);
          paintSitePick(); nameHint.textContent = "Saved"; nameHint.classList.add("saved");
        }, 600);
      };
      const iconHint = site.querySelector("#st-icon-hint");
      const iconField = site.querySelector("#st-icon-field");
      const iconOpen = site.querySelector("#st-icon-open");
      const iconPop = site.querySelector("#st-icon-pop");
      const iconPreview = site.querySelector(".site-icon-preview");
      let iconBusy = false;
      const paintIconPreview = () => {
        if (!iconPreview) return;
        const mark = siteIconColorOf(p);
        iconPreview.style.setProperty("--site-mark", mark);
        iconPreview.style.color = mark;
        iconPreview.innerHTML = lu(siteIconOf(p));
        icons();
      };
      const saveIconPrefs = async () => {
        if (iconBusy) return;
        iconBusy = true;
        iconHint.textContent = "Saving…"; iconHint.classList.remove("saved");
        try {
          await api.saveProject(p);
          iconHint.textContent = "Saved"; iconHint.classList.add("saved");
          paintSitePick();
        } catch (e) {
          iconHint.textContent = "";
          toast(humanErr(e));
        } finally {
          iconBusy = false;
        }
      };
      const setIconPopOpen = open => {
        if (!iconPop || !iconOpen) return;
        iconPop.hidden = !open;
        iconOpen.setAttribute("aria-expanded", open ? "true" : "false");
        iconField?.classList.toggle("is-open", open);
      };
      iconOpen.onclick = e => {
        e.stopPropagation();
        setIconPopOpen(iconPop.hidden);
      };
      site.querySelectorAll(".site-icon-opt").forEach(btn => {
        btn.onclick = async () => {
          const name = btn.dataset.icon;
          if (!SITE_ICONS.includes(name) || name === siteIconOf(p)) return;
          p.data.icon = name;
          site.querySelectorAll(".site-icon-opt").forEach(x => {
            const on = x.dataset.icon === name;
            x.classList.toggle("on", on);
            x.setAttribute("aria-selected", on ? "true" : "false");
          });
          paintIconPreview();
          await saveIconPrefs();
        };
      });
      const colorHost = site.querySelector("#st-icon-color");
      if (colorHost && typeof bdColorPicker === "function"){
        colorHost.appendChild(bdColorPicker(
          siteIconColorOf(p),
          v => {
            setBrandAccent(p, v);
            paintIconPreview();
          },
          async v => {
            setBrandAccent(p, v);
            paintIconPreview();
            await saveIconPrefs();
          },
          { portal: true, preferUp: true, compact: true }
        ));
      }
      const onDocClose = e => {
        if (!iconField || iconPop?.hidden) return;
        if (iconField.contains(e.target)) return;
        setIconPopOpen(false);
      };
      if (typeof body._bdIconPopCleanup === "function") body._bdIconPopCleanup();
      document.addEventListener("pointerdown", onDocClose);
      body._bdIconPopCleanup = () => document.removeEventListener("pointerdown", onDocClose);
      site.querySelector("#seo-save").onclick = async () => {
        s.title = site.querySelector("#seo-t").value; s.description = site.querySelector("#seo-d").value;
        s.ogImage = site.querySelector("#seo-o").value.trim(); s.favicon = site.querySelector("#seo-f").value.trim();
        await api.saveProject(p); toast("Saved");
      };
    }

    if (cur === "brand"){
      brandTokensUI(body, p, () => queueBrandTokenSave(p));
      brandAssetsUI(body, p);
      promoteCards();
    }

    if (cur === "domain"){
      domainCard(body, p);
      promoteCards();
    }

    if (cur === "advanced"){
      const a = p.data.advanced;
      if (a.studioAnalytics == null) a.studioAnalytics = false;
      const legal = siteLegalPack(p);
      const ev = discordEventsOf(a);
      const msgs = discordMessagesOf(a);
      let discordState = { connected:false, masked:"" };
      const hookOk = false;
      const liveMsg = discordMsgFor(a, "live");
      const legalStatus = legal.ready
        ? `<p class="settings-hint settings-legal-ok">Privacy, terms, and cookie banner are ready.</p>`
        : `<p class="settings-hint settings-legal-need">Still needed: ${esc(legal.missing.join(", "))}.</p>`;
      const c = sCard(`${secHead("Advanced", "Publish options for this site.")}
        ${block("Privacy & analytics", `
          ${legalStatus}
          ${legal.ready ? "" : `<div class="settings-field"><button type="button" class="btn btn-ghost btn-sm" id="adv-legal-setup">Set up privacy &amp; terms</button><p class="settings-hint">Adds Privacy, Terms, and Legal pages, footer links, and turns on the cookie banner.</p></div>`}
          <div class="settings-field-grid">
            <div class="settings-field"><label class="settings-label">Studio visits</label><div class="seg settings-seg"><button type="button" data-v="0" class="${!a.studioAnalytics ? "on" : ""}">Off</button><button type="button" data-v="1" class="${a.studioAnalytics ? "on" : ""}" id="adv-visits-on" disabled title="Checking custom domain…">On</button></div><p class="settings-hint" id="adv-visits-hint">Cookieless page views in Console. Requires a connected custom domain, plus privacy, terms, and the cookie banner.</p></div>
            <div class="settings-field"><label class="settings-label">Cookie banner</label><div class="seg settings-seg"><button type="button" data-c="0" class="${!a.cookieBanner ? "on" : ""}">Off</button><button type="button" data-c="1" class="${a.cookieBanner ? "on" : ""}">On</button></div></div>
          </div>
          <div class="settings-field"><label class="settings-label" for="adv-an">Plausible domain</label><input class="settings-input" id="adv-an" value="${esc(a.analyticsId)}" placeholder="yourbusiness.co.za" ${legal.ready ? "" : "disabled"}><p class="settings-hint">${legal.ready ? "Optional. Leave blank for no third-party script." : "Enable privacy &amp; terms first. Leave blank for no third-party script."}</p></div>`)}
        ${block("Visibility", `
          <div class="settings-field-grid">
            <div class="settings-field"><label class="settings-label">Search engines</label><div class="seg settings-seg"><button type="button" data-r="index" class="${a.robots !== "noindex" ? "on" : ""}">Index</button><button type="button" data-r="noindex" class="${a.robots === "noindex" ? "on" : ""}">Hide</button></div></div>
            <div class="settings-field"><label class="settings-label" id="adv-lo-label">Language</label><div id="adv-lo-host" class="settings-locale"></div><p class="settings-hint">Sets the site’s HTML language for browsers and search.</p></div>
          </div>`)}
        <div class="settings-footer"><button type="button" class="btn btn-brand btn-sm" id="adv-save">Save</button></div>`);
      let domainReady = false;
      const visitsOnBtn = c.querySelector("#adv-visits-on");
      const visitsHint = c.querySelector("#adv-visits-hint");
      const syncVisitsGate = () => {
        const canOn = domainReady && siteLegalPack(p).ready;
        if (visitsOnBtn) {
          visitsOnBtn.disabled = !canOn;
          visitsOnBtn.title = !domainReady
            ? "Connect a custom domain first"
            : (!siteLegalPack(p).ready ? "Enable privacy & terms first" : "Turn on Studio visits");
          if (!canOn && a.studioAnalytics) {
            a.studioAnalytics = false;
            c.querySelectorAll("[data-v]").forEach(x => x.classList.toggle("on", x.dataset.v === "0"));
          }
        }
        if (visitsHint) {
          visitsHint.textContent = !domainReady
            ? "Studio visits are only available on sites with a connected custom domain. Connect one under Domains, then turn visits on — we’ll add /privacy, /terms, and /legal."
            : "Cookieless page views in Console. Requires privacy, terms, and cookie banner. Enabling creates those legal pages if missing.";
        }
      };
      api.getDomain(p.id).then(d => {
        domainReady = !!(api._domainAllowsVisits && api._domainAllowsVisits(d));
        syncVisitsGate();
      }).catch(() => {
        domainReady = false;
        syncVisitsGate();
      });
      syncVisitsGate();
      c.querySelectorAll("[data-r]").forEach(b => b.onclick = () => { a.robots = b.dataset.r; c.querySelectorAll("[data-r]").forEach(x => x.classList.toggle("on", x === b)); });
      c.querySelectorAll("[data-c]").forEach(b => b.onclick = () => {
        a.cookieBanner = b.dataset.c === "1";
        c.querySelectorAll("[data-c]").forEach(x => x.classList.toggle("on", x === b));
        if (!a.cookieBanner && a.studioAnalytics) {
          a.studioAnalytics = false;
          c.querySelectorAll("[data-v]").forEach(x => x.classList.toggle("on", x.dataset.v === "0"));
          toast("Studio visits need the cookie banner — turned visits off");
        }
        syncVisitsGate();
      });
      c.querySelectorAll("[data-v]").forEach(b => b.onclick = async () => {
        if (b.disabled) return;
        if (b.dataset.v === "1") {
          if (!domainReady) {
            toast("Connect a custom domain before enabling visits");
            return;
          }
          await loadStudioBlocks();
          ensureSiteLegalPack(p);
          a.cookieBanner = true;
          if (!siteLegalPack(p).ready) {
            toast("Set up privacy & terms first");
            return;
          }
          await api.saveProject(p);
        }
        a.studioAnalytics = b.dataset.v === "1";
        c.querySelectorAll("[data-v]").forEach(x => x.classList.toggle("on", x === b));
        syncVisitsGate();
      });
      const legalSetupBtn = c.querySelector("#adv-legal-setup");
      if (legalSetupBtn) {
        legalSetupBtn.onclick = async () => {
          await loadStudioBlocks();
          ensureSiteLegalPack(p);
          a.cookieBanner = true;
          await api.saveProject(p);
          toast("Privacy & terms ready");
          goSettings("advanced");
        };
      }
      let draftLocale = normalizeSiteLocale(a.locale);
      const localeHost = c.querySelector("#adv-lo-host");
      if (localeHost && typeof bdSelect === "function") {
        localeHost.appendChild(bdSelect(
          SITE_LOCALES,
          draftLocale,
          v => { draftLocale = v; },
          { className: "bd-select--settings", portal: true, ariaLabel: "Site language" }
        ));
      }
      c.querySelector("#adv-save").onclick = async () => {
        const pack = siteLegalPack(p);
        let analyticsId = c.querySelector("#adv-an").value.trim();
        if (!domainReady && a.studioAnalytics) {
          a.studioAnalytics = false;
          toast("Studio visits need a custom domain — turned visits off");
        }
        if (!pack.ready) {
          if (a.studioAnalytics || analyticsId) {
            a.studioAnalytics = false;
            toast("Enable privacy & terms before turning on analytics");
          }
          analyticsId = "";
        }
        a.analyticsId = analyticsId;
        a.locale = normalizeSiteLocale(draftLocale);
        await api.saveProject(p);
        if (typeof DEMO !== "undefined" && DEMO && a.studioAnalytics && typeof api.recordDemoVisit === "function") {
          try {
            const existing = await api.getVisitStats(p.id);
            if (!existing?.total) api.recordDemoVisit(p.id, "/");
          } catch {}
        }
        toast("Saved");
      };

      const dc = sCard(`
        <div class="dc-int" data-connected="${hookOk ? "1" : "0"}">
          <header class="dc-int-hero">
            <div class="dc-int-brand">
              <div class="dc-int-logo" aria-hidden="true">${discordBrandMark()}</div>
              <div class="dc-int-titles">
                <div class="dc-int-title-row">
                  <h2>Discord</h2>
                  <span class="dc-int-status ${hookOk ? "is-on" : "is-off"}" id="dc-status">${hookOk ? "Connected" : "Not connected"}</span>
                </div>
                <p class="c-sub">Post branded channel alerts when this site publishes, goes live, goes offline, or verifies a domain. Customise the title and message for each alert.</p>
              </div>
            </div>
          </header>

          <div class="dc-int-grid">
            <div class="dc-int-main">
              <div class="dc-int-panel" id="dc-connect-panel">
                <div class="dc-int-empty" id="dc-empty" ${hookOk ? "hidden" : ""}>
                  <ol class="dc-int-steps">
                    <li><strong>Open Discord</strong><span>Channel settings → Integrations → Webhooks</span></li>
                    <li><strong>Create a webhook</strong><span>Name it Browns Digital, pick a channel</span></li>
                    <li><strong>Paste the URL here</strong><span>Stored for this site only — never on the published page</span></li>
                  </ol>
                  <div class="settings-field">
                    <label class="settings-label" for="int-dc">Webhook URL</label>
                    <div class="discord-hook-row">
                      <input class="settings-input" id="int-dc" type="password" autocomplete="off" spellcheck="false" value="" placeholder="https://discord.com/api/webhooks/…" aria-label="Discord webhook URL">
                      <button type="button" class="btn btn-ghost btn-sm" id="dc-reveal" title="Show webhook">${lu("eye","lu14")}</button>
                    </div>
                  </div>
                  <div class="dc-int-actions">
                    <button type="button" class="btn btn-brand btn-sm" id="dc-connect" ${hookOk ? "" : "disabled"}>Connect Discord</button>
                  </div>
                </div>

                <div class="dc-int-ready" id="dc-ready" ${hookOk ? "" : "hidden"}>
                  <div class="dc-int-ready-row">
                    <div class="dc-int-ready-copy">
                      <strong>Webhook connected</strong>
                      <code class="dc-int-mask" id="dc-mask"></code>
                    </div>
                    <div class="dc-int-ready-btns">
                      <button type="button" class="btn btn-ghost btn-sm" id="dc-edit">${lu("pencil","lu14")} Edit</button>
                      <button type="button" class="btn btn-ghost btn-sm" id="dc-clear">${lu("unlink","lu14")} Disconnect</button>
                    </div>
                  </div>
                  <div class="dc-int-edit" id="dc-edit-wrap" hidden>
                    <div class="settings-field">
                      <label class="settings-label" for="int-dc-edit">Webhook URL</label>
                      <div class="discord-hook-row">
                        <input class="settings-input" id="int-dc-edit" type="password" autocomplete="off" spellcheck="false" value="" placeholder="Paste a replacement webhook URL" aria-label="Discord webhook URL">
                        <button type="button" class="btn btn-ghost btn-sm" id="dc-reveal-edit" title="Show webhook">${lu("eye","lu14")}</button>
                      </div>
                    </div>
                    <div class="dc-int-actions">
                      <button type="button" class="btn btn-brand btn-sm" id="dc-save-hook">Update webhook</button>
                      <button type="button" class="btn btn-ghost btn-sm" id="dc-cancel-edit">Cancel</button>
                    </div>
                  </div>
                  <div class="dc-int-actions dc-int-actions--ready">
                    <button type="button" class="btn btn-brand btn-sm" id="dc-test">${lu("send","lu14")} Send test alert</button>
                  </div>
                </div>
              </div>

              <div class="dc-int-events" role="group" aria-label="Notification events">
                <div class="dc-int-events-head">
                  <p class="settings-block-label">Alerts &amp; messages</p>
                  <p class="settings-hint">Turn events on, then customise how each alert reads in Discord.</p>
                </div>
                <div class="dc-event-grid">${DISCORD_EVENT_DEFS.map(e => `
                  <div class="dc-event-card ${ev[e.id] !== false ? "is-on" : ""}" data-de-card="${e.id}">
                    <label class="dc-event-main">
                      <span class="dc-event-icon" style="--dc-accent:${e.accent}">${lu(e.icon,"lu14")}</span>
                      <span class="dc-event-copy">
                        <strong>${esc(e.label)}</strong>
                        <span>${esc(e.hint)}</span>
                      </span>
                      <span class="cw-switch"><input type="checkbox" role="switch" data-de="${e.id}" aria-label="${esc(e.label)}" ${ev[e.id] !== false ? "checked" : ""}></span>
                    </label>
                    <button type="button" class="dc-event-edit" data-de-edit="${e.id}">Customise message</button>
                  </div>`).join("")}</div>
                <div class="settings-footer"><button type="button" class="btn btn-brand btn-sm" id="dc-save-events">Save alerts</button></div>
              </div>
            </div>

            <aside class="dc-int-aside">
              <p class="dc-int-aside-label">Live preview</p>
              ${discordEmbedPreview(p.name, {
                title: discordFill(liveMsg.title, { site: p.name || "Your site", url: "", env: "Production", domain: "" }),
                description: discordFill(liveMsg.description, { site: p.name || "Your site", url: "", env: "Production", domain: "" }),
                env: "Production",
                accent: liveMsg.color || "#57F287"
              })}
              <p class="settings-hint" id="dc-preview-hint">Preview updates as you edit. Test sends the selected alert’s copy.</p>
            </aside>
          </div>
        </div>`, "settings-card--discord");

      const root = dc.querySelector(".dc-int");
      const statusEl = dc.querySelector("#dc-status");
      const emptyEl = dc.querySelector("#dc-empty");
      const readyEl = dc.querySelector("#dc-ready");
      const editWrap = dc.querySelector("#dc-edit-wrap");
      const maskEl = dc.querySelector("#dc-mask");
      const hookIn = dc.querySelector("#int-dc");
      const hookEdit = dc.querySelector("#int-dc-edit");
      const connectBtn = dc.querySelector("#dc-connect");
      const testBtn = dc.querySelector("#dc-test");
      const clearBtn = dc.querySelector("#dc-clear");
      const editBtn = dc.querySelector("#dc-edit");
      const saveHookBtn = dc.querySelector("#dc-save-hook");
      const cancelEditBtn = dc.querySelector("#dc-cancel-edit");
      const revealBtn = dc.querySelector("#dc-reveal");
      const revealEditBtn = dc.querySelector("#dc-reveal-edit");
      let previewEvent = "live";
      const msgDrafts = {};
      DISCORD_EVENT_DEFS.forEach(e => {
        const cur = discordMsgFor(a, e.id);
        msgDrafts[e.id] = { title: cur.title, description: cur.description, color: cur.color };
      });

      const setReveal = (input, btn) => {
        if (!input || !btn) return;
        btn.onclick = () => {
          const show = input.type === "password";
          input.type = show ? "text" : "password";
          btn.innerHTML = lu(show ? "eye-off" : "eye", "lu14");
          btn.title = show ? "Hide webhook" : "Show webhook";
          icons();
        };
      };
      setReveal(hookIn, revealBtn);
      setReveal(hookEdit, revealEditBtn);

      const readDraftMsg = (id) => {
        const d = msgDrafts[id] || discordMsgFor(a, id);
        const defAccent = (DISCORD_EVENT_DEFS.find(e => e.id === id) || {}).accent || "#57F287";
        const color = /^#[0-9A-Fa-f]{6}$/.test(String(d.color || "")) ? String(d.color).toLowerCase() : defAccent.toLowerCase();
        return { title: d.title || "", description: d.description || "", color };
      };

      const updatePreview = (eventId = previewEvent) => {
        const def = DISCORD_EVENT_DEFS.find(x => x.id === eventId) || DISCORD_EVENT_DEFS[0];
        const draft = readDraftMsg(def.id);
        const vars = {
          site: p.name || "Your site",
          url: "https://yoursite.example/",
          env: def.env || "Production",
          domain: "www.example.com"
        };
        const titleEl = dc.querySelector("#dc-prev-title");
        const descEl = dc.querySelector("#dc-prev-desc");
        const envEl = dc.querySelector("#dc-prev-env");
        const bar = dc.querySelector(".dc-embed-bar");
        if (titleEl) titleEl.textContent = discordFill(draft.title, vars);
        if (descEl) descEl.textContent = discordFill(draft.description, vars);
        if (envEl) envEl.textContent = def.env || "Production";
        if (bar) bar.style.background = draft.color || def.accent;
        const hint = dc.querySelector("#dc-preview-hint");
        if (hint) hint.textContent = `Showing “${def.label}”. Test sends this alert’s copy.`;
      };

      const syncEventCards = () => {
        dc.querySelectorAll("[data-de-card]").forEach(card => {
          const id = card.dataset.deCard;
          const on = !!dc.querySelector(`[data-de="${id}"]`)?.checked;
          card.classList.toggle("is-on", on);
        });
      };
      dc.querySelectorAll("[data-de]").forEach(sw => {
        sw.addEventListener("change", syncEventCards);
      });

      const closeDiscordMsgEditor = () => {
        document.querySelectorAll(".dc-msg-sheet").forEach(el => {
          el.querySelectorAll("input, textarea").forEach(field => field._dcSuggestDestroy?.());
          el.querySelectorAll(".bd-color").forEach(picker => picker._bdCloseColor?.());
          el.remove();
        });
        document.querySelectorAll("body > .dc-suggest").forEach(el => el.remove());
        document.querySelectorAll("body > .bd-color-pop").forEach(el => { el.hidden = true; });
      };

      const openDiscordMsgEditor = (id) => {
        const def = DISCORD_EVENT_DEFS.find(x => x.id === id);
        if (!def) return;
        closeDiscordMsgEditor();
        previewEvent = id;
        updatePreview(id);
        const draft = readDraftMsg(id);
        let draftColor = draft.color || def.accent;
        const sampleVars = {
          site: p.name || "Your site",
          url: "https://yoursite.example/",
          env: def.env || "Production",
          domain: "www.example.com"
        };
        const sheet = document.createElement("div");
        sheet.className = "dc-msg-sheet";
        sheet.innerHTML = `<div class="dc-msg-backdrop" tabindex="-1"></div>
          <div class="dc-msg-panel" role="dialog" aria-modal="true" aria-label="Customise ${esc(def.label)} message" style="--dc-accent:${esc(def.accent || "#5865F2")}">
            <header class="dc-msg-head">
              <div class="dc-msg-head-copy">
                <p class="dc-msg-kicker">Customise message</p>
                <h3>${esc(def.label)}</h3>
                ${def.hint ? `<p class="dc-msg-sub">${esc(def.hint)}</p>` : ""}
              </div>
              <button type="button" class="dc-msg-close" aria-label="Close">${lu("x","lu16")}</button>
            </header>
            <section class="dc-msg-preview" aria-hidden="true">
              <p class="dc-msg-section-label">Preview</p>
              <div class="dc-msg-preview-stage">
                <div class="dc-msg-preview-msg">
                  <span class="dc-msg-preview-avatar" aria-hidden="true">${lu(def.icon || "rocket","lu14")}</span>
                  <div class="dc-msg-preview-col">
                    <div class="dc-msg-preview-meta">
                      <span class="dc-msg-preview-author">Browns Digital Studio</span>
                      <span class="dc-msg-preview-bot">BOT</span>
                      <span class="dc-msg-preview-time">Today at 12:00</span>
                    </div>
                    <div class="dc-msg-preview-card">
                      <div class="dc-msg-preview-bar" id="dc-sheet-bar" style="background:${esc(draftColor)}"></div>
                      <div class="dc-msg-preview-body">
                        <p class="dc-msg-preview-title" id="dc-sheet-prev-title">${esc(discordFill(draft.title, sampleVars))}</p>
                        <p class="dc-msg-preview-desc" id="dc-sheet-prev-desc">${esc(discordFill(draft.description, sampleVars))}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
            <div class="dc-msg-groups">
              <section class="dc-msg-group">
                <div class="settings-field">
                  <label class="settings-label" for="dc-sheet-title">Title</label>
                  <input class="settings-input" id="dc-sheet-title" maxlength="256" value="${esc(draft.title)}" spellcheck="true" autocomplete="off">
                </div>
                <div class="settings-field">
                  <label class="settings-label" for="dc-sheet-body">Message</label>
                  <textarea class="settings-input dc-msg-body" id="dc-sheet-body" rows="3" maxlength="4096" spellcheck="true">${esc(draft.description)}</textarea>
                </div>
              </section>
              <section class="dc-msg-group dc-msg-group--tools">
                <div class="settings-field dc-msg-insert">
                  <span class="settings-label" id="dc-sheet-insert-label">Placeholders</span>
                  <div class="dc-msg-chips" role="group" aria-labelledby="dc-sheet-insert-label">
                    ${DISCORD_PLACEHOLDERS.map(ph =>
                      `<button type="button" class="dc-msg-chip" data-ph="${ph.key}" title="${esc(ph.hint)}"><code>{${ph.key}}</code></button>`
                    ).join("")}
                  </div>
                </div>
                <div class="settings-field dc-msg-color-field">
                  <label class="settings-label" for="dc-sheet-color-hex">Embed colour</label>
                  <div id="dc-sheet-color" class="dc-msg-color"></div>
                </div>
              </section>
            </div>
            <footer class="dc-msg-foot">
              <button type="button" class="dc-msg-reset" id="dc-sheet-reset">Reset to default</button>
              <div class="dc-msg-foot-actions">
                <button type="button" class="btn btn-ghost btn-sm" id="dc-sheet-cancel">Cancel</button>
                <button type="button" class="btn btn-brand btn-sm" id="dc-sheet-done">Done</button>
              </div>
            </footer>
          </div>`;
        document.body.appendChild(sheet);
        icons();
        const titleIn = sheet.querySelector("#dc-sheet-title");
        const bodyIn = sheet.querySelector("#dc-sheet-body");
        const colorHost = sheet.querySelector("#dc-sheet-color");
        const sheetTitle = sheet.querySelector("#dc-sheet-prev-title");
        const sheetDesc = sheet.querySelector("#dc-sheet-prev-desc");
        const sheetBar = sheet.querySelector("#dc-sheet-bar");
        let activeField = bodyIn;
        discordBindPlaceholderSuggest(titleIn);
        discordBindPlaceholderSuggest(bodyIn);
        discordAutoGrow(bodyIn);

        const syncDraftLive = () => {
          msgDrafts[id] = {
            title: titleIn.value,
            description: bodyIn.value,
            color: draftColor
          };
          const panel = sheet.querySelector(".dc-msg-panel");
          if (panel) panel.style.setProperty("--dc-accent", draftColor || def.accent || "#5865F2");
          if (sheetTitle) sheetTitle.textContent = discordFill(titleIn.value, sampleVars);
          if (sheetDesc) sheetDesc.textContent = discordFill(bodyIn.value, sampleVars);
          if (sheetBar) sheetBar.style.background = draftColor || def.accent || "#5865F2";
          updatePreview(id);
          discordAutoGrow(bodyIn);
        };
        let colorPicker = null;
        if (colorHost && typeof bdColorPicker === "function") {
          colorPicker = bdColorPicker(
            draftColor,
            v => { draftColor = v; syncDraftLive(); },
            v => { draftColor = v; syncDraftLive(); },
            { portal: true, preferUp: true, compact: true }
          );
          colorHost.appendChild(colorPicker);
          const hexIn = colorPicker.querySelector(".bd-color-text");
          if (hexIn) hexIn.id = "dc-sheet-color-hex";
        }
        titleIn.addEventListener("focus", () => { activeField = titleIn; });
        bodyIn.addEventListener("focus", () => { activeField = bodyIn; });
        titleIn.addEventListener("input", syncDraftLive);
        bodyIn.addEventListener("input", syncDraftLive);
        sheet.querySelectorAll("[data-ph]").forEach(chip => {
          chip.onclick = () => {
            const target = activeField || bodyIn;
            discordInsertAtCursor(target, "{" + chip.dataset.ph + "}");
            target._dcSuggestHide?.();
          };
        });
        sheet.querySelector("#dc-sheet-reset").onclick = () => {
          const defs = DISCORD_MSG_DEFAULTS[id] || { title: "", description: "" };
          titleIn.value = defs.title;
          bodyIn.value = defs.description;
          draftColor = def.accent;
          colorPicker?._bdSetColor?.(draftColor, false);
          syncDraftLive();
        };
        const finish = (keep) => {
          if (!keep) {
            msgDrafts[id] = { title: draft.title, description: draft.description, color: draft.color };
            updatePreview(id);
          } else {
            msgDrafts[id] = {
              title: titleIn.value.trim() || (DISCORD_MSG_DEFAULTS[id]?.title || ""),
              description: bodyIn.value,
              color: draftColor
            };
            updatePreview(id);
          }
          closeDiscordMsgEditor();
        };
        sheet.querySelector(".dc-msg-backdrop").onclick = () => finish(false);
        sheet.querySelector(".dc-msg-close").onclick = () => finish(false);
        sheet.querySelector("#dc-sheet-cancel").onclick = () => finish(false);
        sheet.querySelector("#dc-sheet-done").onclick = () => finish(true);
        sheet.addEventListener("keydown", e => {
          if (e.key !== "Escape") return;
          if (titleIn._dcSuggestIsOpen?.() || bodyIn._dcSuggestIsOpen?.()) {
            e.preventDefault();
            titleIn._dcSuggestHide?.();
            bodyIn._dcSuggestHide?.();
            return;
          }
          e.preventDefault();
          finish(false);
        });
        bodyIn.focus();
        const len = bodyIn.value.length;
        try { bodyIn.setSelectionRange(len, len); } catch {}
      };

      dc.querySelectorAll("[data-de-edit]").forEach(btn => {
        btn.onclick = () => openDiscordMsgEditor(btn.dataset.deEdit);
      });

      const paintConnected = (ok, masked = discordState.masked) => {
        discordState = { connected:!!ok, masked:masked || "" };
        root.dataset.connected = ok ? "1" : "0";
        statusEl.textContent = ok ? "Connected" : "Not connected";
        statusEl.classList.toggle("is-on", ok);
        statusEl.classList.toggle("is-off", !ok);
        emptyEl.hidden = !!ok;
        readyEl.hidden = !ok;
        if (ok) {
          maskEl.textContent = discordState.masked;
          if (hookEdit) hookEdit.value = "";
          editWrap.hidden = true;
        } else if (hookIn) {
          hookIn.value = "";
        }
        if (connectBtn) connectBtn.disabled = !isDiscordWebhookUrl(hookIn?.value || "");
        if (testBtn) testBtn.disabled = !ok;
        icons();
      };

      if (hookIn) {
        hookIn.addEventListener("input", () => {
          connectBtn.disabled = !isDiscordWebhookUrl(hookIn.value);
        });
        hookIn.addEventListener("paste", () => setTimeout(() => {
          hookIn.value = normalizeDiscordWebhook(hookIn.value);
          connectBtn.disabled = !isDiscordWebhookUrl(hookIn.value);
        }, 0));
      }

      const persistHook = async (raw, { toastOk } = {}) => {
        const v = normalizeDiscordWebhook(raw);
        if (v && !isDiscordWebhookUrl(v)) {
          toast("That doesn’t look like a Discord webhook URL.", { kind: "error" });
          return false;
        }
        const result = await api.saveDiscordIntegration(p.id, { webhook:v, events:ev, messages:msgs });
        delete a.discordWebhook;
        delete a.discord_events;
        delete a.discord_messages;
        paintConnected(result.connected, result.masked);
        if (toastOk) toast(toastOk, { kind: "success" });
        return true;
      };

      if (connectBtn) connectBtn.onclick = async () => {
        connectBtn.disabled = true;
        connectBtn.textContent = "Connecting…";
        try {
          await persistHook(hookIn.value, { toastOk: "Discord connected" });
        } catch (e) {
          toast(humanErr(e, "Could not save webhook."), { kind: "error" });
        } finally {
          connectBtn.textContent = "Connect Discord";
          connectBtn.disabled = !isDiscordWebhookUrl(hookIn?.value || "");
        }
      };

      if (editBtn) editBtn.onclick = () => {
        editWrap.hidden = false;
        hookEdit.value = "";
        hookEdit.focus();
      };
      if (cancelEditBtn) cancelEditBtn.onclick = () => {
        editWrap.hidden = true;
        hookEdit.value = "";
      };
      if (saveHookBtn) saveHookBtn.onclick = async () => {
        saveHookBtn.disabled = true;
        saveHookBtn.textContent = "Saving…";
        try {
          await persistHook(hookEdit.value, { toastOk: "Webhook updated" });
        } catch (e) {
          toast(humanErr(e, "Could not update webhook."), { kind: "error" });
        } finally {
          saveHookBtn.disabled = false;
          saveHookBtn.textContent = "Update webhook";
        }
      };
      if (clearBtn) clearBtn.onclick = () => confirmModal(
        "Disconnect Discord?",
        "Publish alerts will stop until you connect a webhook again.",
        "Disconnect",
        async () => {
          await api.clearDiscordIntegration(p.id);
          delete a.discordWebhook;
          delete a.discord_events;
          delete a.discord_messages;
          paintConnected(false);
          toast("Discord disconnected");
        }
      );

      if (testBtn) testBtn.onclick = async () => {
        if (!discordState.connected) return toast("Connect a Discord webhook first.");
        /* Persist current draft messages so the test matches the preview */
        DISCORD_EVENT_DEFS.forEach(e => {
          const draft = readDraftMsg(e.id);
          msgs[e.id] = { title: draft.title.trim(), description: draft.description, color: draft.color };
        });
        testBtn.disabled = true;
        testBtn.textContent = "Sending…";
        try {
          await api.saveDiscordIntegration(p.id, { events:ev, messages:msgs });
          await api.testDiscord(p, previewEvent || "live");
          toast("Test alert sent — check your Discord channel.", { kind: "success" });
        } catch (e) {
          toast(humanErr(e, "Could not send the test alert."), { kind: "error" });
        } finally {
          testBtn.innerHTML = `${lu("send","lu14")} Send test alert`;
          icons();
          paintConnected(discordState.connected, discordState.masked);
        }
      };

      dc.querySelector("#dc-save-events").onclick = async () => {
        dc.querySelectorAll("[data-de]").forEach(x => { ev[x.dataset.de] = x.checked; });
        DISCORD_EVENT_DEFS.forEach(e => {
          const draft = readDraftMsg(e.id);
          const defs = DISCORD_MSG_DEFAULTS[e.id] || { title: "", description: "" };
          const title = draft.title.trim();
          const description = draft.description;
          const color = draft.color || e.accent;
          const defColor = String(e.accent || "").toLowerCase();
          if (title === defs.title && description === defs.description && String(color).toLowerCase() === defColor) {
            delete msgs[e.id];
          } else {
            msgs[e.id] = { title, description, color };
          }
        });
        await api.saveDiscordIntegration(p.id, { events:ev, messages:msgs });
        delete a.discord_events;
        delete a.discord_messages;
        toast("Alert preferences saved", { kind: "success" });
      };

      paintConnected(hookOk);
      syncEventCards();
      updatePreview("live");
      api.getDiscordIntegration(p.id).then(result => {
        discordState = { connected:!!result.connected, masked:result.masked || "" };
        Object.keys(ev).forEach(key => delete ev[key]);
        Object.assign(ev, result.events || { live:true, stage:true, offline:true, domain:true });
        Object.keys(msgs).forEach(key => delete msgs[key]);
        Object.assign(msgs, result.messages || {});
        DISCORD_EVENT_DEFS.forEach(e => {
          const sw = dc.querySelector(`[data-de="${e.id}"]`);
          if (sw) sw.checked = ev[e.id] !== false;
          const cur = discordMsgFor({ discord_messages:msgs }, e.id);
          msgDrafts[e.id] = { title:cur.title, description:cur.description, color:cur.color };
        });
        paintConnected(discordState.connected, discordState.masked);
        syncEventCards();
        updatePreview("live");
      }).catch(() => {
        paintConnected(false);
      });

      const prod = prodOf(p.id);
      const dz = sCard(`${secHead("Danger zone", "Irreversible actions for this site.")}
        <div class="settings-danger-row">
          <div><strong>Take offline</strong><p class="c-sub">${prod ? "Stops the live address until you promote again." : "Not live — nothing to take down."}</p></div>
          <button type="button" class="btn btn-danger btn-sm" id="dz-down" ${prod ? "" : "disabled"}>Take offline</button>
        </div>
        <div class="settings-danger-row">
          <div><strong>Delete site</strong><p class="c-sub">Removes project, pages, staging, and live copy.</p></div>
          <button type="button" class="btn btn-danger btn-sm" id="dz-del">Delete</button>
        </div>`, "settings-card--danger");
      if (prod) dz.querySelector("#dz-down").onclick = () => confirmModal("Take this site offline?", "The live address stops working until you promote again.", "Take offline", async () => {
        try { await api.unpublish(p.id, p); deploys.production = deploys.production.filter(x => x.project_id !== p.id);
          log(p.id, "unpublished", `\u201c${p.name}\u201d taken offline`); toast("Site taken offline"); paint(); paintLiveInd();
        } catch (e) { toast(humanErr(e)); }
      });
      if (siteIsMine(p)) dz.querySelector("#dz-del").onclick = () => deleteSiteFlow(p);
      else dz.querySelector("#dz-del")?.closest(".settings-danger-row")?.remove();
    }
    icons();
  }
  paint();
  icons();
}
/* ============================== TEAM ============================== */
/* ---------- Crew / collaborators ---------- */
const COLLAB_PERMS = [
  { key:"layout",  label:"Edit layout",           desc:"Change pages, sections, and design." },
  { key:"text",    label:"Edit text",             desc:"Update copy and content across the site." },
  { key:"publish", label:"Publish",               desc:"Push the site live and manage releases." },
  { key:"domain",  label:"Manage domain",         desc:"Connect and manage custom domains." },
  { key:"invite",  label:"Manage who has access", desc:"Invite or remove collaborators." }
];
const COLLAB_PERM_KEYS = COLLAB_PERMS.map(p => p.key);
const COLLAB_ROLES = {
  admin:      { label:"Admin",      desc:"Full site access and invites" },
  developer:  { label:"Developer",  desc:"Edit everything, can publish" },
  editor:     { label:"Editor",     desc:"Edit layout and copy" },
  designer:   { label:"Designer",   desc:"Edit layout, not copy" },
  copywriter: { label:"Copywriter", desc:"Edit text, not layout" },
  viewer:     { label:"Viewer",     desc:"Read-only access" }
};
/** Permission presets per role — selecting a role fills these; users can customize. */
const COLLAB_ROLE_PERMS = {
  admin:      { layout:true,  text:true,  publish:true,  domain:true,  invite:true  },
  developer:  { layout:true,  text:true,  publish:true,  domain:false, invite:false },
  editor:     { layout:true,  text:true,  publish:false, domain:false, invite:false },
  designer:   { layout:true,  text:false, publish:false, domain:false, invite:false },
  copywriter: { layout:false, text:true,  publish:false, domain:false, invite:false },
  viewer:     { layout:false, text:false, publish:false, domain:false, invite:false }
};
function teamRoleOpts(){
  return Object.entries(COLLAB_ROLES).map(([k, x]) => [k, x.label, x.desc]);
}
function normalizeTeamRole(role){
  const key = String(role || "viewer").toLowerCase();
  return COLLAB_ROLES[key] ? key : "viewer";
}
function cloneCollabPerms(src){
  const out = {};
  COLLAB_PERM_KEYS.forEach(k => { out[k] = !!(src && src[k]); });
  return out;
}
function permsForRole(role){
  return cloneCollabPerms(COLLAB_ROLE_PERMS[normalizeTeamRole(role)] || COLLAB_ROLE_PERMS.viewer);
}
function permsEqual(a, b){
  return COLLAB_PERM_KEYS.every(k => !!a?.[k] === !!b?.[k]);
}
function roleMatchingPerms(perms){
  for (const key of Object.keys(COLLAB_ROLE_PERMS)){
    if (permsEqual(COLLAB_ROLE_PERMS[key], perms)) return key;
  }
  return null;
}
/** Map a (possibly customized) permission set to the nearest stored role. */
function roleFromPerms(perms){
  const exact = roleMatchingPerms(perms);
  if (exact) return exact;
  let best = "viewer", bestDist = Infinity;
  for (const [key, preset] of Object.entries(COLLAB_ROLE_PERMS)){
    let dist = 0;
    COLLAB_PERM_KEYS.forEach(k => { if (!!preset[k] !== !!perms?.[k]) dist++; });
    if (dist < bestDist){ bestDist = dist; best = key; }
  }
  return best;
}
function pageTeam(el){
  const p = active();
  const shell = document.createElement("div");
  shell.className = "studio-page studio-page--cowork";
  el.appendChild(shell);
  const initial = (p.name || "S").trim().charAt(0).toUpperCase() || "S";
  head(
    shell,
    "users",
    "Crew",
    `Access applies only to <strong>${esc(p.name)}</strong> and does not affect other sites or Discord permissions.`,
    `<button type="button" class="cw-site-badge" id="cw-site-pick" aria-haspopup="listbox" aria-expanded="false" title="Change site"${projects.length ? "" : " disabled"}>
      <span class="cw-site-badge-dot" aria-hidden="true">${esc(initial)}</span>
      <span class="cw-site-badge-name">${esc(p.name)}</span>
      ${lu("chevron-down","lu14")}
    </button>`
  );
  const sitePick = shell.querySelector("#cw-site-pick");
  if (sitePick){
    sitePick.onclick = e => {
      e.stopPropagation();
      openSiteMenu(sitePick, { align: "end", minW: 248, maxW: 300 });
    };
  }
  teamTab(shell, p);
}
function coworkMemberDisplay(row){
  const pending = String(row?.status || "").toLowerCase() === "pending";
  if (api.isCoworkLinkInvite(row)){
    return { name: "Link invite", avatar: null, link: true, pending };
  }
  const email = String(row.email || "");
  const person = { email, name: row.name || "", username: row.username || "", avatar: row.avatar || "" };
  const name = displayNameOf(person);
  return { name, avatar: avatarUrlOf(person), link: false, pending };
}
function coworkRoleCardsHtml(selectedRole){
  return Object.entries(COLLAB_ROLES).map(([key, meta]) => {
    const on = key === selectedRole;
    return `<button type="button" class="cw-role-card${on ? " is-on" : ""}" data-cw-role="${key}" aria-pressed="${on ? "true" : "false"}">
      <span class="cw-role-card-top">
        <strong>${esc(meta.label)}</strong>
        <span class="cw-role-check" aria-hidden="true"></span>
      </span>
      <span class="cw-role-desc">${esc(meta.desc)}</span>
    </button>`;
  }).join("");
}
function coworkPermRowsHtml(perms){
  return COLLAB_PERMS.map(p => {
    const on = !!perms[p.key];
    return `
    <label class="cw-perm-row${on ? " is-on" : ""}">
      <span class="cw-perm-copy">
        <strong>${esc(p.label)}</strong>
        <em>${esc(p.desc)}</em>
      </span>
      <span class="cw-switch">
        <input type="checkbox" role="switch" data-cw-perm="${p.key}"
          aria-label="${esc(p.label)}" ${on ? "checked" : ""}>
      </span>
    </label>`;
  }).join("");
}
function coworkSeatLabel(used, limit){
  if (limit === Infinity) return `${used} Crew seat${used === 1 ? "" : "s"} used · Unlimited`;
  if (!limit) return "Crew seats unlock on Pro";
  return `${used} of ${limit} Crew seats used`;
}
async function teamTab(body, p){
  const limit = (PLAN_RULES[plan] || PLAN_RULES.free).collaborators ?? 0;
  let selectedRole = "editor";
  let perms = permsForRole(selectedRole);
  const people = card(body, `
    <div class="cw-section-head">
      <div>
        <h2>People with access</h2>
        <p class="c-sub">Invite others to help build or operate this site.</p>
        </div>
      <span class="cw-seats" id="cw-seats">${coworkSeatLabel(0, limit)}</span>
      </div>
    <div class="cw-roster">
      <div class="cw-people-head"><span>Person</span><span>Role</span><span>Actions</span></div>
      <div id="team-list">${skelRows(3)}</div>
          </div>
  `, "cw-card");

  const inviteHtml = limit ? `
    <p class="cw-add-title">${lu("user-plus","lu14")} Add someone</p>
    <div class="cw-preset-block">
      <p class="cw-preset-label">Role preset</p>
      <p class="cw-preset-hint">Choose a role to apply to the invite. You can change individual permissions below.</p>
      <div class="cw-role-grid" id="cw-role-grid" role="group" aria-label="Role presets">${coworkRoleCardsHtml(selectedRole)}</div>
          </div>
    <p class="cw-perms-label">Permissions for this site <span class="cw-perm-custom" id="cw-perm-custom" hidden>Custom</span></p>
    <div class="cw-perm-panel" id="cw-perm-list">${coworkPermRowsHtml(perms)}</div>
    <div class="cw-invite-block">
      <label class="cw-invite-label" for="tm-email">Email</label>
      <div class="cw-invite-row">
        <input id="tm-email" type="email" placeholder="friend@example.com" autocomplete="email">
        <button type="button" class="btn btn-brand" id="tm-invite">${lu("send","lu14")} Send invite</button>
        </div>
      </div>
    <div class="cw-invite-block">
      <label class="cw-invite-label" for="tm-link-url">Share link</label>
      <p class="cw-invite-hint">No email? Generate a one-time link. Expires after 7 days.</p>
      <div class="cw-invite-row">
        <input id="tm-link-url" type="text" readonly placeholder="Generate a link to share" aria-label="Invite link">
        <button type="button" class="btn btn-ghost" id="tm-link-create">${lu("link","lu14")} Generate link</button>
        <button type="button" class="btn btn-ghost" id="tm-link-copy" hidden>${lu("copy","lu14")} Copy</button>
          </div>
        </div>
  ` : `<p class="note cw-upgrade">Crew unlocks on Pro (3 per site) and Studio (unlimited). <a href="${URLS.pricing}" target="_blank" rel="noopener">Upgrade ›</a></p>`;

  const inviteCard = card(body, inviteHtml, "cw-card");

  const syncPresetUi = () => {
    if (!limit) return;
    const match = roleMatchingPerms(perms);
    const customEl = inviteCard.querySelector("#cw-perm-custom");
    if (customEl) customEl.hidden = !!match;
    inviteCard.querySelectorAll("[data-cw-role]").forEach(btn => {
      const key = btn.dataset.cwRole;
      const on = match ? key === match : key === selectedRole;
      btn.classList.toggle("is-on", on);
      btn.classList.toggle("is-custom", !match && key === selectedRole);
      btn.setAttribute("aria-pressed", on ? "true" : "false");
    });
    inviteCard.querySelectorAll("[data-cw-perm]").forEach(sw => {
      const on = !!perms[sw.dataset.cwPerm];
      sw.checked = on;
      sw.closest(".cw-perm-row")?.classList.toggle("is-on", on);
    });
  };
  const resolveInviteRole = () => roleFromPerms(perms);

  const paintList = async () => {
    const rows = await api.listCollaborators(p.id);
    const seats = people.querySelector("#cw-seats");
    if (seats) {
      const used = rows.length + 1; // include owner
      if (limit === Infinity) seats.textContent = `${used} Crew seat${used === 1 ? "" : "s"} used · Unlimited`;
      else if (!limit) seats.textContent = "Upgrade to invite collaborators";
      else seats.textContent = `${used} of ${limit + 1} Crew seats used`;
    }
    const empty = rows.length === 0
      ? `<div class="cw-empty">No collaborators yet. Invite someone to join your site below.</div>`
      : "";
    const ownerName = displayNameOf(me);
    people.querySelector("#team-list").innerHTML = `
      <div class="team-member-row owner">
        <div class="cw-person">${avatarHtml(avatarUrlOf(me), ownerName, 36)}
          <span class="lr-main"><strong>${esc(ownerName)}</strong></span></div>
        <span class="team-role-chip owner">Owner</span>
        <div class="team-member-actions"></div>
      </div>
      ${rows.map(r => {
        const m = coworkMemberDisplay(r);
        const roleKey = normalizeTeamRole(r.role);
        const roleLabel = (COLLAB_ROLES[roleKey] || {}).label || r.role;
        const av = avatarHtml(m.avatar, m.name, 36, m.link ? "link" : "");
        const linkBtn = m.link ? `<button type="button" class="btn btn-ghost btn-sm team-copy-link" data-copy-link="${esc(r.id)}" title="Copy invite link">${lu("copy","lu14")} Copy link</button>` : "";
        if (m.pending && !m.link) {
          return `<div class="team-member-row pending-invite">
            <div class="cw-person">${av}
              <span class="lr-main"><strong>${esc(m.name)}</strong></span></div>
        <span class="team-role-chip pending">Invite pending</span>
            <div class="team-member-actions">
              <button type="button" class="icon-btn team-remove" data-rm="${r.id}" aria-label="Remove collaborator">${lu("user-minus","lu14")}</button>
            </div>
          </div>`;
        }
        const pendingChip = m.pending ? `<span class="team-role-chip pending">Invite pending</span>` : "";
        return `<div class="team-member-row${m.link ? " link-invite" : ""}${m.pending ? " pending-invite" : ""}">
          <div class="cw-person">${av}
            <span class="lr-main"><strong>${esc(m.name)}</strong></span></div>
          ${m.pending
            ? `<span class="team-role-chip pending">${esc(roleLabel)}</span>`
            : `<div class="team-role-sel" data-role="${r.id}" data-val="${esc(roleKey)}"></div>`}
        <div class="team-member-actions">
          ${linkBtn}
            ${m.pending && m.link ? pendingChip : ""}
          <button type="button" class="icon-btn team-remove" data-rm="${r.id}" aria-label="Remove collaborator">${lu("user-minus","lu14")}</button>
          </div>
        </div>`;
      }).join("")}
      ${empty}`;
    people.querySelectorAll(".team-role-sel").forEach(host => {
      const id = host.dataset.role;
      const roleSel = bdSelect(
        teamRoleOpts(),
        normalizeTeamRole(host.dataset.val),
        async v => { await api.updateCollaborator(id, v); toast("Role updated"); paintList(); },
        { className: "bd-select--compact bd-select--roles", portal: true, ariaLabel: "Collaborator role" }
      );
      host.replaceWith(roleSel);
    });
    people.querySelectorAll("[data-rm]").forEach(b => b.onclick = () => confirmModal("Remove from Crew?", "They lose access to this site immediately.", "Remove", async () => {
      await api.removeCollaborator(b.dataset.rm); paintList();
    }));
    people.querySelectorAll("[data-copy-link]").forEach(btn => {
      btn.onclick = async () => {
        const row = rows.find(x => x.id === btn.dataset.copyLink);
        if (!row) return;
        const token = api.coworkInviteToken(row);
        if (!token) return toast("Could not find invite link.");
        copyText(api.coworkInviteUrl(p.id, token));
      };
    });
    icons();
  };
  paintList();

  if (limit){
    inviteCard.querySelector("#cw-role-grid").addEventListener("click", e => {
      const btn = e.target.closest("[data-cw-role]");
      if (!btn) return;
      selectedRole = normalizeTeamRole(btn.dataset.cwRole);
      perms = permsForRole(selectedRole);
      syncPresetUi();
    });
    inviteCard.querySelector("#cw-perm-list").addEventListener("change", e => {
      const sw = e.target.closest("[data-cw-perm]");
      if (!sw) return;
      perms = cloneCollabPerms(perms);
      perms[sw.dataset.cwPerm] = !!sw.checked;
      const match = roleMatchingPerms(perms);
      if (match) selectedRole = match;
      syncPresetUi();
    });
    syncPresetUi();

    inviteCard.querySelector("#tm-invite").onclick = async () => {
      const ev = validEmail(inviteCard.querySelector("#tm-email").value);
      if (!ev.ok) return toast(ev.why);
      const rows = await api.listCollaborators(p.id);
      if (rows.length >= limit) return toast(`Your plan allows ${limit} collaborator${limit===1?"":"s"} per site — upgrade to Architect for unlimited.`);
      try {
        await api.inviteCollaborator(p.id, ev.email, resolveInviteRole());
        inviteCard.querySelector("#tm-email").value = "";
        log(p.id, "saved", `Crew invite sent for “${p.name}”`);
        toast("Invite sent"); paintList();
      } catch (e) { toast(humanErr(e)); }
    };
    inviteCard.querySelector("#tm-link-create").onclick = async () => {
      const rows = await api.listCollaborators(p.id);
      if (rows.length >= limit) return toast(`Your plan allows ${limit} collaborator${limit===1?"":"s"} per site — upgrade to Architect for unlimited.`);
      const btn = inviteCard.querySelector("#tm-link-create");
      const copyBtn = inviteCard.querySelector("#tm-link-copy");
      btn.disabled = true;
      try {
        const r = await api.createCollaboratorLink(p.id, resolveInviteRole());
        const urlIn = inviteCard.querySelector("#tm-link-url");
        urlIn.value = r.link || api.coworkInviteUrl(p.id, api.coworkInviteToken(r));
        copyBtn.hidden = false;
        log(p.id, "saved", `Crew link created for “${p.name}”`);
        toast("Invite link ready — copy and share it"); paintList();
      } catch (e) { toast(humanErr(e)); }
      finally { btn.disabled = false; }
    };
    inviteCard.querySelector("#tm-link-copy")?.addEventListener("click", () => {
      const v = inviteCard.querySelector("#tm-link-url")?.value;
      if (v) copyText(v);
    });
  }
  icons();
}

/* design tokens editor (shared by Settings → Brand and the builder inspector) */
function impactLine(p){
  const els = p.data.pages.reduce((n,pg) => n + pg.blocks.reduce((m,b) => m + (ACCENT_ELS[b.type]||0), 0), 0)
    + (ACCENT_ELS[p.data.shared.header?.type] || 0) + (ACCENT_ELS[p.data.shared.footer?.type] || 0);
  return `Affects ~${els} accent element${els===1?"":"s"} across ${p.data.pages.length} page${p.data.pages.length===1?"":"s"} — change once, updates everywhere.`;
}
function appendDesignTokenFields(host, p, opts = {}){
  const tk = ensureProjectTokens(p);
  const wrapChange = typeof opts.onChange === "function" ? opts.onChange : () => {};
  const beforeSet = typeof opts.beforeSet === "function" ? opts.beforeSet : () => {};
  const set = (key, v, repaint) => {
    beforeSet();
    if (key === "accent") setBrandAccent(p, v);
    else tk[key] = v;
    wrapChange();
    if (repaint) opts.repaint?.();
  };
  function dsField(f, val, onChange){
    const el = fieldEl(f, val, onChange);
    el.classList.add("ds-row");
    el.classList.add("ds-row--" + (f.t || "text"));
    /* 3+ segment options overflow the narrow inspector — stack label above control. */
    if (f.t === "seg" && Array.isArray(f.opts) && f.opts.length >= 3){
      el.classList.add("ds-row--stack");
    }
    return el;
  }
  const buildAppearance = () => {
    const g = document.createElement("div");
    g.className = "ds-group";
    if (!opts.panels) g.innerHTML = `<p class="ds-group-label">Appearance</p>`;
    g.appendChild(dsField({ t:"seg", label:"Theme", opts:["light","dark"] }, tk.mode || "light", v => set("mode", v, true)));
    g.appendChild(dsField({ t:"select", label:"Surface tone", opts: Object.keys(SURFACE_TONES).map(k => [k, k.charAt(0).toUpperCase()+k.slice(1)]) }, tk.surface || "neutral", v => set("surface", v, true)));
    g.appendChild(dsField({ t:"seg", label:"Borders", opts: Object.keys(BORDER_STRENGTH),
      segLabels:{ whisper:"Whisper", soft:"Soft", strong:"Strong" } }, tk.border || "soft", v => set("border", v, true)));
    return g;
  };
  const buildColour = () => {
    const g = document.createElement("div");
    g.className = "ds-group";
    if (!opts.panels) g.innerHTML = `<p class="ds-group-label">Colour</p>`;

    /* ---- Accent (buttons / links stay solid) ---- */
    const colorField = document.createElement("div");
    colorField.className = "field ds-row ds-row--color";
    const colorLab = document.createElement("label");
    colorLab.textContent = "Accent colour";
    colorField.appendChild(colorLab);
    const colorStack = document.createElement("div");
    colorStack.className = "ds-color-stack";
    const sw = document.createElement("div");
    sw.className = "swatches";
    let colorPicker = null;
    let accentSnap = false;
    function syncAccentSwatches(){
      const accentLc = String(tk.accent || "#355cff").toLowerCase();
      sw.querySelectorAll("button").forEach(b => {
        b.classList.toggle("on", String(b.title || "").toLowerCase() === accentLc);
      });
    }
    function paintAccent(v, { snap } = {}){
      if (snap && !accentSnap){ beforeSet(); accentSnap = true; }
      setBrandAccent(p, v);
      wrapChange();
      syncAccentSwatches();
    }
    ACCENT_SWATCHES.forEach(cx => {
      const b = document.createElement("button");
      b.type = "button";
      b.style.background = cx;
      b.title = cx;
      b.onclick = () => {
        accentSnap = false;
        paintAccent(cx, { snap: true });
        if (colorPicker && typeof colorPicker._bdSetColor === "function"){
          colorPicker._bdSetColor(cx, false);
        }
        accentSnap = false;
      };
      sw.appendChild(b);
    });
    syncAccentSwatches();
    colorPicker = bdColorPicker(
      tk.accent || "#355cff",
      v => paintAccent(v, { snap: true }),
      v => { paintAccent(v, { snap: true }); accentSnap = false; },
      { popover: true, compact: true, swatches: false }
    );
    colorStack.appendChild(sw);
    colorStack.appendChild(colorPicker);
    colorField.appendChild(colorStack);
    g.appendChild(colorField);

    /* ---- Page background ---- */
    const bgHead = document.createElement("p");
    bgHead.className = "ds-subhead";
    bgHead.textContent = "Page background";
    g.appendChild(bgHead);

    g.appendChild(dsField({
      t:"seg", label:"Fill", opts:["solid","gradient"],
      segLabels:{ solid:"Solid", gradient:"Gradient" }
    }, tk.pageBgMode === "gradient" ? "gradient" : "solid", v => {
      set("pageBgMode", v === "gradient" ? "gradient" : "solid", true);
      const parent = g.parentNode;
      if (parent){
        parent.replaceChild(buildColour(), g);
        icons();
      }
    }));

    if (tk.pageBgMode !== "gradient"){
      const hint = document.createElement("p");
      hint.className = "settings-hint";
      hint.style.margin = "2px 2px 0";
      hint.textContent = "Solid uses the Surface tone from Look.";
      g.appendChild(hint);
      return g;
    }

    const stack = document.createElement("div");
    stack.className = "ds-page-bg-stack";
    const canvas = document.createElement("div");
    canvas.className = "ds-page-bg";
    canvas.setAttribute("aria-hidden", "true");
    const syncPreview = () => {
      const tone = (SURFACE_TONES[tk.surface] || SURFACE_TONES.neutral)[(tk.mode === "dark") ? "dark" : "light"];
      canvas.style.background = typeof pageBgCss === "function"
        ? pageBgCss(tk, tone.bg)
        : (tk.pageBgA || "#eef3ff");
    };
    syncPreview();

    const stops = document.createElement("div");
    stops.className = "ds-page-bg-stops";
    const mkStop = (key, label) => {
      const wrap = document.createElement("div");
      wrap.className = "ds-page-bg-stop";
      const lab = document.createElement("span");
      lab.textContent = label;
      wrap.appendChild(lab);
      let snap = false;
      const paint = (v, { snap: doSnap } = {}) => {
        const hex = String(v || "").trim();
        if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return;
        if (doSnap && !snap){ beforeSet(); snap = true; }
        tk[key] = hex.toLowerCase();
        wrapChange();
        syncPreview();
        syncPresetOn();
      };
      const picker = bdColorPicker(
        tk[key] || (key === "pageBgA" ? "#eef3ff" : "#ffffff"),
        v => paint(v, { snap: true }),
        v => { paint(v, { snap: true }); snap = false; },
        { popover: true, compact: true, swatches: false }
      );
      wrap.appendChild(picker);
      return wrap;
    };
    stops.appendChild(mkStop("pageBgA", "From"));
    stops.appendChild(mkStop("pageBgB", "To"));
    stack.appendChild(canvas);
    stack.appendChild(stops);
    g.appendChild(stack);

    const presetLab = document.createElement("p");
    presetLab.className = "ds-mini-label";
    presetLab.textContent = "Presets";
    g.appendChild(presetLab);
    const presets = document.createElement("div");
    presets.className = "ds-grad-presets";
    const syncPresetOn = () => {
      const a = String(tk.pageBgA || "").toLowerCase();
      const b = String(tk.pageBgB || "").toLowerCase();
      const ang = String(tk.pageBgAngle || "180");
      presets.querySelectorAll("button").forEach(btn => {
        const on = btn.dataset.a === a && btn.dataset.b === b && btn.dataset.ang === ang;
        btn.classList.toggle("on", on);
      });
    };
    (typeof PAGE_BG_PRESETS !== "undefined" ? PAGE_BG_PRESETS : []).forEach(pr => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "ds-grad-preset";
      btn.title = pr.label;
      btn.dataset.a = pr.a;
      btn.dataset.b = pr.b;
      btn.dataset.ang = pr.ang;
      btn.style.background = `linear-gradient(${pr.ang}deg,${pr.a},${pr.b})`;
      btn.innerHTML = `<span>${esc(pr.label)}</span>`;
      btn.onclick = () => {
        beforeSet();
        tk.pageBgMode = "gradient";
        tk.pageBgA = pr.a;
        tk.pageBgB = pr.b;
        tk.pageBgAngle = pr.ang;
        wrapChange();
        const parent = g.parentNode;
        if (parent){
          parent.replaceChild(buildColour(), g);
          icons();
        }
      };
      presets.appendChild(btn);
    });
    syncPresetOn();
    g.appendChild(presets);

    const dirLab = document.createElement("p");
    dirLab.className = "ds-mini-label";
    dirLab.textContent = "Direction";
    g.appendChild(dirLab);
    const dirs = document.createElement("div");
    dirs.className = "ds-grad-dirs";
    const DIR_OPTS = [
      ["180", "Down"],
      ["90", "Right"],
      ["135", "Diagonal"],
      ["45", "Up-right"],
      ["0", "Up"]
    ];
    DIR_OPTS.forEach(([ang, label]) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "ds-grad-dir" + (String(tk.pageBgAngle || "180") === ang ? " on" : "");
      btn.title = label;
      const a = tk.pageBgA || "#eef3ff";
      const b = tk.pageBgB || "#ffffff";
      btn.style.background = `linear-gradient(${ang}deg,${a},${b})`;
      btn.innerHTML = `<span>${esc(label)}</span>`;
      btn.onclick = () => {
        set("pageBgAngle", ang, true);
        dirs.querySelectorAll(".ds-grad-dir").forEach(el => el.classList.toggle("on", el === btn));
        syncPreview();
        syncPresetOn();
      };
      dirs.appendChild(btn);
    });
    g.appendChild(dirs);

    return g;
  };
  const buildType = () => {
    const g = document.createElement("div");
    g.className = "ds-group";
    if (!opts.panels) g.innerHTML = `<p class="ds-group-label">Typography</p>`;
    g.appendChild(dsField({ t:"select", label:"Type pairing", opts: Object.entries(FONT_PAIRS).map(([k,v]) => [k, v.label]) }, tk.fontPair, v => set("fontPair", v, true)));
    g.appendChild(dsField({ t:"select", label:"Type scale", opts: [["xs","Extra small"],["sm","Small"],["md","Regular"],["lg","Large"],["xl","Extra large"],["xxl","Display"]] }, tk.fontScale || "md", v => set("fontScale", v)));
    g.appendChild(dsField({ t:"select", label:"Heading weight", opts: [["regular","Regular"],["medium","Medium"],["semibold","Semibold"],["bold","Bold"],["heavy","Heavy"]] }, tk.fontWeight || "medium", v => set("fontWeight", v)));
    g.appendChild(dsField({ t:"select", label:"Letter spacing", opts: [["tighter","Tighter"],["tight","Tight"],["normal","Normal"],["wide","Wide"]] }, tk.letterSpacing || "normal", v => set("letterSpacing", v)));
    g.appendChild(dsField({ t:"seg", label:"Body line height", opts: Object.keys(BODY_LINES) }, tk.bodyLine || "normal", v => set("bodyLine", v)));
    g.appendChild(dsField({ t:"text", label:"Custom font URL", placeholder:"https://fonts.googleapis.com/css2?family=Inter:wght@400;600&display=swap" }, tk.customFontUrl || "", v => set("customFontUrl", v)));
    return g;
  };
  const buildShape = () => {
    const g = document.createElement("div");
    g.className = "ds-group";
    if (!opts.panels) g.innerHTML = `<p class="ds-group-label">Shape & density</p>`;
    g.appendChild(dsField({ t:"select", label:"Corners", opts: Object.keys(RADII).map(k => [k, k.charAt(0).toUpperCase()+k.slice(1)]) }, tk.radius, v => set("radius", v, true)));
    g.appendChild(dsField({ t:"select", label:"Buttons", opts: Object.keys(BUTTON_RADII).map(k => [k, k.charAt(0).toUpperCase()+k.slice(1)]) }, tk.buttonStyle || "pill", v => set("buttonStyle", v, true)));
    g.appendChild(dsField({ t:"select", label:"Spacing", opts: Object.keys(SPACING).map(k => [k, k.charAt(0).toUpperCase()+k.slice(1)]) }, tk.spacing, v => set("spacing", v)));
    g.appendChild(dsField({ t:"select", label:"Elevation", opts: Object.keys(SHADOWS).map(k => [k, k.charAt(0).toUpperCase()+k.slice(1)]) }, tk.shadow, v => set("shadow", v, true)));
    return g;
  };
  const buildMotion = () => {
    const g = document.createElement("div");
    g.className = "ds-group";
    if (!opts.panels) g.innerHTML = `<p class="ds-group-label">Motion</p>`;
    g.appendChild(dsField({ t:"seg", label:"Intensity", opts:["off","subtle","medium","expressive"],
      segLabels:{ off:"Off", subtle:"Subtle", medium:"Medium", expressive:"Bold" } }, tk.motionLevel || "medium", v => set("motionLevel", v, true)));
    g.appendChild(dsField({ t:"select", label:"Default enter", opts:[
      ["none","None"],["fade","Fade"],["rise","Rise"],["slideL","Slide left"],["slideR","Slide right"],["zoom","Zoom"],["blur","Blur"],["flip","Flip"]
    ] }, tk.motionReveal || "rise", v => set("motionReveal", v)));
    g.appendChild(dsField({ t:"seg", label:"Trigger", opts:["scroll","load"],
      segLabels:{ scroll:"On scroll", load:"On load" } }, tk.motionTrigger || "scroll", v => set("motionTrigger", v)));
    g.appendChild(dsField({ t:"seg", label:"Duration", opts:["snappy","normal","smooth"],
      segLabels:{ snappy:"Snappy", normal:"Normal", smooth:"Smooth" } }, tk.motionDuration || "normal", v => set("motionDuration", v)));
    g.appendChild(dsField({ t:"select", label:"Default hover", opts:[
      ["none","None"],["lift","Lift"],["glow","Glow"],["zoom","Zoom"],["tilt","Tilt"]
    ] }, tk.motionHover || "none", v => set("motionHover", v)));
    const hint = document.createElement("p");
    hint.className = "settings-hint";
    hint.style.margin = "4px 2px 0";
    hint.textContent = "Sections can override these in the inspector → Motion. Use Enter = Site to inherit.";
    g.appendChild(hint);
    return g;
  };

  if (opts.panels){
    let panel = "appearance";
    try { panel = sessionStorage.getItem("bd_ds_panel") || "appearance"; } catch {}
    const panels = [
      ["appearance", "Look", buildAppearance],
      ["colour", "Colour", buildColour],
      ["type", "Type", buildType],
      ["shape", "Shape", buildShape],
      ["motion", "Motion", buildMotion],
    ];
    if (!panels.some(([k]) => k === panel)) panel = "appearance";
    const bar = document.createElement("div");
    bar.className = "ds-panels";
    bar.setAttribute("role", "tablist");
    bar.innerHTML = panels.map(([k, label]) =>
      `<button type="button" class="ds-panel-btn${k === panel ? " on" : ""}" role="tab" aria-selected="${k === panel ? "true" : "false"}" data-dsp="${k}">${esc(label)}</button>`
    ).join("");
    const body = document.createElement("div");
    body.className = "ds-panel-body";
    const paint = () => {
      body.innerHTML = "";
      const hit = panels.find(([k]) => k === panel) || panels[0];
      body.appendChild(hit[2]());
      icons();
    };
    bar.querySelectorAll("[data-dsp]").forEach(btn => {
      btn.onclick = () => {
        /* Commit in-progress hex/text so the closed-over tokens stay current. */
        try {
          const ae = document.activeElement;
          if (ae && typeof ae.blur === "function" && body.contains(ae)) ae.blur();
        } catch {}
        panel = btn.dataset.dsp;
        try { sessionStorage.setItem("bd_ds_panel", panel); } catch {}
        bar.querySelectorAll("[data-dsp]").forEach(b => {
          const on = b.dataset.dsp === panel;
          b.classList.toggle("on", on);
          b.setAttribute("aria-selected", on ? "true" : "false");
        });
        paint();
      };
    });
    host.appendChild(bar);
    host.appendChild(body);
    paint();
    return;
  }

  host.appendChild(buildAppearance());
  host.appendChild(buildColour());
  host.appendChild(buildType());
  host.appendChild(buildShape());
  host.appendChild(buildMotion());
}
function brandTokensUI(elOrBody, p, onChange){
  const c = card(elOrBody, `<header class="settings-sec-head"><h2>Design system</h2><p class="c-sub">Same tokens as the builder Design tab — edit once, the whole site updates.</p></header><div id="tk-body" class="ds-token-body ds-token-body--panels"></div>`);
  const bodyEl = c.querySelector("#tk-body");
  const wrapChange = () => onChange();
  const repaint = () => {
    bodyEl.innerHTML = "";
    appendDesignTokenFields(bodyEl, p, { panels: true, onChange: wrapChange, repaint: () => {} });
  };
  repaint();
  return c;
}

/* ---------- Brand assets ----------
   Logos and images uploaded once, reusable across the site. Stored on
   the project (data URLs in demo; same shape syncs to Supabase live). */
function brandAssetsUI(body, p){
  const c = card(body, `<header class="settings-sec-head settings-sec-head--flush"><h2>Brand assets</h2><p class="c-sub">Upload once — paste into any image field.</p></header><div id="ba-body"></div>`);
  const bodyEl = c.querySelector("#ba-body");
  p.data.assets = p.data.assets || [];
  const MAX_KB = 400, MAX_N = 24;
  const paint = () => {
    bodyEl.innerHTML = `
      <div class="asset-grid">${p.data.assets.map(a =>
        `<div class="asset-card"><div class="asset-img" style="background-image:url('${a.url}')"></div>
          <div class="asset-meta"><strong title="${esc(a.name)}">${esc(a.name)}</strong><em>${Math.round(a.size/1024)} KB</em></div>
          <div class="asset-actions"><button data-copy="${a.id}" title="Copy URL">${lu("copy","lu14")}</button><button data-del="${a.id}" title="Delete">${lu("trash-2","lu14")}</button></div></div>`).join("")}
        <label class="asset-card asset-add" for="ba-file">${lu("image-plus")}<span>Upload</span><em>PNG · JPG · SVG · WebP, ≤ ${MAX_KB} KB</em></label>
      </div>
      <input type="file" id="ba-file" accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif" multiple hidden>
      <p class="hint">${p.data.assets.length} of ${MAX_N} assets used. Copy an asset's URL and paste it into any Image field in the builder inspector.</p>`;
    icons();
    bodyEl.querySelector("#ba-file").onchange = async e => {
      for (const f of [...e.target.files]){
        if (p.data.assets.length >= MAX_N){ toast(`Asset library is full (${MAX_N} max).`); break; }
        if (f.size > MAX_KB * 1024){ toast(`“${f.name}” is over ${MAX_KB} KB — compress it first.`); continue; }
        const url = await new Promise(res => { const r = new FileReader(); r.onload = () => res(r.result); r.readAsDataURL(f); });
        p.data.assets.push({ id: newId(), name: f.name, size: f.size, url });
      }
      try { await api.saveProject(p); paint(); toast("Assets saved"); }
      catch (err){ p.data.assets.pop(); paint(); toast(humanErr(err, "Couldn’t store that asset — storage on this device may be full."), { kind: "error" }); }
    };
    bodyEl.querySelectorAll("[data-copy]").forEach(b => b.onclick = () => {
      const a = p.data.assets.find(x => x.id === b.dataset.copy);
      navigator.clipboard?.writeText(a.url); toast("Asset URL copied — paste into any image field");
    });
    bodyEl.querySelectorAll("[data-del]").forEach(b => b.onclick = () => confirmModal("Delete this asset?", "Sections already using it keep working until you replace the URL.", "Delete", async () => {
      p.data.assets = p.data.assets.filter(x => x.id !== b.dataset.del);
      await api.saveProject(p); paint();
    }));
  };
  paint();
}

/* ============================== ACCOUNT ============================== */
function pageAccount(el){
  /* helper — inline card div (not appended to el, used in template literals) */
  function card_html(cls, inner){ return `<div class="card liquid-glass${cls?" "+cls:""}" style="margin-bottom:16px">${inner}</div>`; }

  /* ── shell: two-column layout inside the existing #page scroll container ── */
  const _acctWrap = document.createElement("div");
  _acctWrap.innerHTML = `
  <style>
  .acct-wrap{display:flex;min-height:calc(100dvh - 120px);gap:0}
  .acct-side-nav{width:210px;flex:0 0 210px;padding:4px 8px 24px;display:flex;flex-direction:column;gap:1px;border-right:1px solid var(--color-separator);position:sticky;top:0;align-self:flex-start;max-height:calc(100dvh - 120px);overflow-y:auto}
  .acct-side-label{font-size:10.5px;font-weight:600;letter-spacing:.06em;text-transform:uppercase;color:var(--color-label-tertiary);padding:10px 10px 4px;margin-top:4px}
  .acct-side-label:first-child{margin-top:0}
  .acct-side-btn{display:flex;align-items:center;gap:8px;width:100%;padding:0 10px;height:33px;border-radius:8px;border:none;background:transparent;color:var(--color-label-primary);font-size:13px;font-weight:450;cursor:pointer;text-align:left;font-family:inherit;transition:background .12s}
  .acct-side-btn:hover{background:var(--color-fill-secondary)}
  .acct-side-btn.on{background:color-mix(in srgb,var(--color-tint) 12%,transparent);color:var(--color-tint);font-weight:500}
  .acct-side-btn.disabled{pointer-events:none;opacity:.5}
  .acct-side-btn svg{width:14px;height:14px;flex:0 0 auto;stroke-width:2;stroke:currentColor;fill:none}
  .soon-tag{font-size:9px;font-weight:600;letter-spacing:.04em;background:var(--color-fill-secondary);color:var(--color-label-tertiary);border-radius:4px;padding:2px 5px;margin-left:auto;text-transform:uppercase}
  .acct-panels{flex:1;min-width:0;padding:0 0 0 32px}
  .acct-panel{display:none}.acct-panel.on{display:block}
  .acct-panel-title{font-size:22px;font-weight:700;letter-spacing:-.022em;margin-bottom:4px}
  .acct-panel-sub{font-size:13px;color:var(--color-label-secondary);margin-bottom:24px;line-height:1.5}
  /* avatar */
  .acct-avatar-circle{width:64px;height:64px;border-radius:50%;background:var(--color-tint);color:#fff;font-size:22px;font-weight:600;display:flex;align-items:center;justify-content:center;position:relative;overflow:hidden;flex:0 0 auto}
  .acct-avatar-circle img{width:100%;height:100%;object-fit:cover;position:absolute;inset:0;display:none}
  .acct-avatar-circle img.loaded{display:block}
  .acct-avatar-upload{background:transparent;border:none;cursor:pointer;color:var(--color-tint);font-size:12.5px;font-weight:500;padding:0;font-family:inherit;text-decoration:underline;text-underline-offset:2px}
  #acct-avatar-file{display:none}
  /* field grid */
  .acct-field-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:16px}
  .acct-field-grid.single{grid-template-columns:1fr}
  .acct-hint{font-size:11.5px;color:var(--color-label-tertiary);margin-top:5px;line-height:1.4}
  .acct-save-hint{font-size:11.5px;color:var(--color-label-tertiary);margin-top:4px;min-height:16px}
  .acct-save-hint.ok{color:var(--color-success,#34c759)}
  /* at-prefix input — pad must beat .field input padding or @ overlaps the handle */
  .acct-at-wrap{position:relative;display:block}
  .acct-at-wrap .at-pre{
    position:absolute;left:14px;top:50%;transform:translateY(-50%);
    font-size:14px;font-weight:500;color:var(--color-label-tertiary);
    pointer-events:none;line-height:1;z-index:1;
  }
  .field .acct-at-wrap > input{padding-left:30px}
  /* toggle rows */
  .acct-toggle-row{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;padding:13px 0;border-bottom:1px solid var(--color-separator)}
  .acct-toggle-row:last-of-type{border-bottom:none}
  .acct-toggle-info strong{display:block;font-size:13.5px;font-weight:500;margin-bottom:2px}
  .acct-toggle-info span{font-size:12px;color:var(--color-label-secondary);line-height:1.45}
  .acct-sw{position:relative;flex:0 0 44px;height:26px;margin-top:2px}
  .acct-sw input{opacity:0;position:absolute;width:0;height:0}
  .acct-sw-track{position:absolute;inset:0;border-radius:999px;background:var(--color-fill-tertiary,rgba(120,120,128,.2));transition:background .2s;cursor:pointer}
  .acct-sw input:checked+.acct-sw-track{background:var(--color-tint)}
  .acct-sw-thumb{position:absolute;left:3px;top:3px;width:20px;height:20px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(0,0,0,.2);transition:transform .2s;pointer-events:none}
  .acct-sw input:checked~.acct-sw-thumb{transform:translateX(18px)}
  /* method rows */
  .acct-method-row{display:flex;align-items:center;gap:12px;padding:12px 0;border-bottom:1px solid var(--color-separator)}
  .acct-method-row:last-child{border-bottom:none}
  .acct-method-ico{width:34px;height:34px;border-radius:10px;background:var(--color-fill-secondary);display:flex;align-items:center;justify-content:center;flex:0 0 auto}
  .acct-method-ico svg{width:15px;height:15px;stroke:var(--color-label-secondary);fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
  .acct-method-info{flex:1;min-width:0}
  .acct-method-info strong{display:block;font-size:13px;font-weight:500;margin-bottom:1px}
  .acct-method-info span{font-size:11.5px;color:var(--color-label-secondary)}
  .acct-status{font-size:11px;font-weight:600;padding:3px 8px;border-radius:5px;white-space:nowrap}
  .acct-status.on{background:color-mix(in srgb,var(--color-success,#34c759) 14%,transparent);color:var(--color-success,#34c759)}
  .acct-status.off{background:var(--color-fill-secondary);color:var(--color-label-tertiary)}
  /* data rows */
  .acct-data-row{display:flex;align-items:center;gap:12px;padding:10px 0;border-bottom:1px solid var(--color-separator)}
  .acct-data-row:last-child{border-bottom:none}
  .acct-data-row-info{flex:1}.acct-data-row-info strong{display:block;font-size:13px;font-weight:500}
  .acct-data-row-info span{font-size:11.5px;color:var(--color-label-secondary)}
  /* pref group heads */
  .acct-pref-head{font-size:11px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;color:var(--color-label-tertiary);margin:18px 0 6px}
  .acct-pref-head:first-child{margin-top:0}
  /* danger */
  .acct-danger-item{padding:16px 0;border-bottom:1px solid var(--color-separator)}
  .acct-danger-item:last-child{border-bottom:none}
  .acct-danger-item h3{font-size:14px;font-weight:600;margin-bottom:4px}
  .acct-danger-item p{font-size:13px;color:var(--color-label-secondary);line-height:1.5;margin-bottom:10px}
  .acct-danger-note{font-size:12px;color:var(--color-label-tertiary);margin-top:8px;font-style:italic}
  /* PIN */
  .acct-pin-wrap{margin-top:12px;display:none}
  @media(max-width:820px){.acct-side-nav{display:none}.acct-panels{padding:0}}
  </style>
  <div class="acct-wrap">
    <nav class="acct-side-nav" id="acct-nav">
      <div class="acct-side-label">Account</div>
      <button class="acct-side-btn on" data-acct="profile">
        <svg viewBox="0 0 24 24"><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 1 0-16 0"/></svg>Account
      </button>
      <button class="acct-side-btn" data-acct="subscription">
        <svg viewBox="0 0 24 24"><rect x="1" y="4" width="22" height="16" rx="2"/><path d="M1 10h22"/></svg>Subscription
      </button>
      <div class="acct-side-label">Integrations</div>
      <button class="acct-side-btn" data-acct="ai">
        <svg viewBox="0 0 24 24"><path d="M12 8V4H8"/><rect x="8" y="12" width="16" height="12" rx="2"/><path d="m9 16 .5-.5a2.5 2.5 0 1 1 3.5 3.5l-.5.5-.5.5"/><circle cx="17" cy="15" r="1"/></svg>Pulse
      </button>
      <div class="acct-side-label">Account</div>
      <button class="acct-side-btn" data-acct="privacy">
        <svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>Privacy &amp; Security
      </button>
      <button class="acct-side-btn" data-acct="prefs">
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14"/></svg>Preferences
      </button>
      <button class="acct-side-btn" data-acct="customize">
        <svg viewBox="0 0 24 24"><circle cx="13.5" cy="6.5" r="3.5"/><circle cx="8" cy="16" r="4"/><circle cx="19" cy="16" r="3"/></svg>Customization
      </button>
      <button class="acct-side-btn" data-acct="notifications">
        <svg viewBox="0 0 24 24"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>Notifications
      </button>
      <button class="acct-side-btn" data-acct="shortcuts">
        <svg viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M6 8h.01M10 8h.01M14 8h.01M18 8h.01M8 12h.01M12 12h.01M16 12h.01M7 16h10"/></svg>Keyboard shortcuts
      </button>
      <div class="acct-side-label">Danger</div>
      <button class="acct-side-btn" data-acct="danger">
        <svg viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>Danger Zone
      </button>
    </nav>
    <div class="acct-panels" id="acct-panels">

      <!-- ── Account / Profile ── -->
      <div class="acct-panel on" id="acct-panel-profile">
        <div class="page-head" style="margin-bottom:20px">
          <div class="ph-icon">${lu("circle-user")}</div>
          <div><h1 class="acct-panel-title">Account</h1><p class="acct-panel-sub">Manage your profile, display name, and username.</p></div>
        </div>
        ${card_html("", `
          <h2>Profile picture</h2><p class="c-sub">Appears in the dashboard, on collaborator lists, and in Crew. Google and GitHub photos are used by default — change anytime.</p>
          <div style="display:flex;align-items:center;gap:16px;margin-bottom:4px">
            <div class="acct-avatar-circle" id="acct-avatar-circle">
              <span id="acct-avatar-initial">${esc((me.name||me.email||"?")[0].toUpperCase())}</span>
              <img id="acct-avatar-img" alt="" referrerpolicy="no-referrer">
            </div>
            <div>
              <button class="acct-avatar-upload" id="acct-avatar-btn">${lu("pencil","lu14")} Change photo</button>
              <input type="file" id="acct-avatar-file" accept="image/jpeg,image/png,image/webp,image/gif">
              <p class="acct-hint" style="margin-top:5px">JPEG, PNG, WebP or GIF · max 2 MB · optional</p>
            </div>
          </div>
        `)}
        ${card_html("", `
          <h2>Identity</h2><p class="c-sub">Your display name is shown publicly. Your @handle lets others find and mention you.</p>
          <div class="acct-field-grid">
            <div class="field">
              <label for="acct-displayname">Display name</label>
              <input id="acct-displayname" type="text" maxlength="64" autocomplete="name" value="${esc(me.name||"")}" placeholder="Full name">
              <div class="acct-save-hint" id="acct-name-hint"></div>
            </div>
            <div class="field">
              <label for="acct-username">Username</label>
              <div class="acct-at-wrap"><span class="at-pre">@</span><input id="acct-username" type="text" maxlength="32" autocomplete="off" value="${esc(me.username||me.email.split("@")[0]||"")}" placeholder="handle"></div>
              <div class="acct-hint">Letters, numbers, underscores only.</div>
              <div class="acct-save-hint" id="acct-un-hint"></div>
            </div>
          </div>
          <div class="acct-field-grid single">
            <div class="field">
              <label>Email address</label>
              <input value="${esc(me.email)}" readonly>
              <div class="acct-hint">Email changes require verification — contact support.</div>
            </div>
          </div>
        `)}
        <div id="acct-membership-summary"></div>
      </div>

      <!-- ── Subscription ── -->
      <div class="acct-panel" id="acct-panel-subscription">
        <div class="page-head" style="margin-bottom:20px">
          <div class="ph-icon">${lu("credit-card")}</div>
          <div><h1 class="acct-panel-title">Subscription</h1><p class="acct-panel-sub">Manage your membership and billing.</p></div>
        </div>
        <div id="acct-subscription-body"></div>
      </div>

      <!-- ── AI Site Builder ── -->
      <div class="acct-panel" id="acct-panel-ai">
        <div class="page-head" style="margin-bottom:20px">
          <div class="ph-icon">${lu("bot")}</div>
          <div><h1 class="acct-panel-title">Pulse</h1><p class="acct-panel-sub">Pulse builds production-ready sites with Studio blocks. Available on Build, Forge, and Architect.</p></div>
        </div>
        <div id="acct-ai-body"></div>
      </div>

      <!-- ── Privacy & Security ── -->
      <div class="acct-panel" id="acct-panel-privacy">
        <div class="page-head" style="margin-bottom:20px">
          <div class="ph-icon">${lu("shield")}</div>
          <div><h1 class="acct-panel-title">Privacy &amp; Security</h1><p class="acct-panel-sub">Control your visibility, sign-in methods, and account access.</p></div>
        </div>
        ${card_html("", `
          <h2>Privacy</h2>
          <div class="acct-toggle-row">
            <div class="acct-toggle-info"><strong>Private profile</strong><span>When on, your profile is hidden from user search on the site. You will not appear when others search by display name or @handle, including in Crew.</span></div>
            <label class="acct-sw"><input type="checkbox" id="acct-pref-private"><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label>
          </div>
        `)}
        ${card_html("", `
          <h2>Data takeout</h2><p class="c-sub">Download a copy of your account metadata as JSON. Site HTML, blocks, and secrets are never included. Limited to 3 exports per day.</p>
          <div class="acct-data-row"><div class="acct-data-row-info"><strong>Account data</strong><span>Profile, preferences, and settings</span></div><button class="btn btn-ghost btn-sm" data-export="account">Download</button></div>
          <div class="acct-data-row"><div class="acct-data-row-info"><strong>Sites &amp; pages</strong><span>Site names, page slugs, and counts — no block content</span></div><button class="btn btn-ghost btn-sm" data-export="sites">Download</button></div>
          <div class="acct-data-row"><div class="acct-data-row-info"><strong>Storage &amp; variables</strong><span>Flows, Storage variables, and connected application metadata</span></div><button class="btn btn-ghost btn-sm" data-export="storage">Download</button></div>
          <div class="acct-data-row"><div class="acct-data-row-info"><strong>Full export (all of the above)</strong><span>Combined metadata JSON</span></div><button class="btn btn-brand btn-sm" data-export="all">Download all</button></div>
        `)}
        ${card_html("", `
          <h2>Sign-in methods</h2><p class="c-sub">How you authenticate with Browns Digital Studio.</p>
          <div class="acct-method-row">
            <div class="acct-method-ico"><svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></div>
            <div class="acct-method-info"><strong>Password</strong><span>Standard email &amp; password sign-in</span></div>
            <span class="acct-status on">Active</span>
            <button class="btn btn-ghost btn-sm" id="acct-chpass-btn" style="margin-left:8px">Change</button>
          </div>
          <div class="acct-method-row">
            <div class="acct-method-ico"><svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg></div>
            <div class="acct-method-info"><strong>Passkey</strong><span>Biometric or device PIN — phishing-resistant</span></div>
            <span class="acct-status off" id="acct-passkey-status">Not set up</span>
            <button class="btn btn-ghost btn-sm" id="acct-passkey-btn" style="margin-left:8px">Set up</button>
          </div>
          <div class="acct-method-row">
            <div class="acct-method-ico"><svg viewBox="0 0 24 24"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></div>
            <div class="acct-method-info"><strong>Two-factor authentication (TOTP)</strong><span>Google Authenticator, Authy, 1Password</span></div>
            <span class="acct-status off" id="acct-totp-status">Not set up</span>
            <button class="btn btn-ghost btn-sm" id="acct-totp-btn" style="margin-left:8px">Set up</button>
          </div>
        `)}
        <div id="acct-chpass-section" style="display:none">
          ${card_html("", `
            <h2>Change password</h2>
            <div class="acct-field-grid">
              <div class="field"><label for="acct-curpass">Current password</label><input id="acct-curpass" type="password" autocomplete="current-password" placeholder="••••••••"></div>
              <div class="field"><label for="acct-newpass">New password</label><input id="acct-newpass" type="password" autocomplete="new-password" placeholder="At least 8 characters">
                <div class="password-meter" id="acct-pass-meter" data-strength="0">
                  <div class="password-meter-head"><span>Password strength</span><strong id="acct-pass-strength">Not entered</strong></div>
                  <span class="password-meter-track" role="meter" aria-label="New password strength" aria-valuemin="0" aria-valuemax="4" aria-valuenow="0"><i id="acct-pass-meter-bar"></i></span>
                </div>
              </div>
            </div>
            <div class="field" id="acct-chpass-mfa" style="display:none;max-width:280px">
              <label for="acct-chpass-mfa-code">Authenticator code</label>
              <input id="acct-chpass-mfa-code" inputmode="numeric" autocomplete="one-time-code" maxlength="6" placeholder="123456">
              <p class="note">Enter the current 6-digit code from your authenticator app.</p>
            </div>
            <button class="btn btn-brand btn-sm" id="acct-savepass-btn">Update password</button>
            <button class="btn btn-ghost btn-sm" id="acct-cancelpass-btn" style="margin-left:8px">Cancel</button>
          `)}
        </div>
        ${card_html("", `
          <h2>Session PIN</h2><p class="c-sub">Optional 4-digit local PIN required every time Studio opens — after sign-in or a refresh. Contact support if you forget your PIN.</p>
          <div class="acct-toggle-row">
            <div class="acct-toggle-info"><strong>Enable session PIN</strong><span>You'll set your PIN after toggling on.</span></div>
            <label class="acct-sw"><input type="checkbox" id="acct-pin-toggle"><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label>
          </div>
          <div class="acct-pin-wrap" id="acct-pin-wrap">
            <div class="field" style="max-width:160px;margin-top:4px"><label for="acct-pin-input">4-digit PIN</label><input id="acct-pin-input" type="password" inputmode="numeric" maxlength="4" pattern="[0-9]{4}" placeholder="••••"></div>
            <button class="btn btn-brand btn-sm" id="acct-pin-save" style="margin-top:10px">Save PIN</button>
          </div>
        `)}
      </div>

      <!-- ── Preferences ── -->
      <div class="acct-panel" id="acct-panel-prefs">
        <div class="page-head" style="margin-bottom:20px">
          <div class="ph-icon">${lu("sliders-horizontal")}</div>
          <div><h1 class="acct-panel-title">Preferences</h1><p class="acct-panel-sub">Optional behaviours you can turn on or off.</p></div>
        </div>
        ${card_html("", `
          <div class="acct-pref-head">Console</div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Studio animations</strong><span>Motion in Browns Digital Studio — page opens, staggered cards, and hover. Off is instant and still. Does not change customer sites.</span></div><label class="acct-sw"><input type="checkbox" id="apref-animations" checked><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Show metadata in site cards</strong><span>Displays page count, last updated, and publish status on each card.</span></div><label class="acct-sw"><input type="checkbox" id="apref-meta" checked><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Compact sidebar</strong><span>Collapses the sidebar to icon-only by default.</span></div><label class="acct-sw"><input type="checkbox" id="apref-compact"><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Confirm before deleting sections</strong><span>Ask for confirmation when deleting a builder block.</span></div><label class="acct-sw"><input type="checkbox" id="apref-confirm-del" checked><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
          <div class="acct-pref-head" style="margin-top:18px">Builder</div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Snap to grid</strong><span>Sections snap to an 8px grid when reordering.</span></div><label class="acct-sw"><input type="checkbox" id="apref-snap" checked><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Show section outlines</strong><span>Draws a faint border around each section in the builder canvas.</span></div><label class="acct-sw"><input type="checkbox" id="apref-outlines"><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Autosave every 30 seconds</strong><span>Silently saves your work in the background while building.</span></div><label class="acct-sw"><input type="checkbox" id="apref-autosave" checked><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
          <div class="acct-pref-head" style="margin-top:18px">Notifications</div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Email me on live publish</strong><span>Confirmation email when a site is promoted to production.</span></div><label class="acct-sw"><input type="checkbox" id="apref-email-pub" checked><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Email me on health issues</strong><span>Get notified when site health score drops below 60.</span></div><label class="acct-sw"><input type="checkbox" id="apref-email-health"><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Team activity emails</strong><span>Email when a collaborator edits or publishes.</span></div><label class="acct-sw"><input type="checkbox" id="apref-email-team"><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
        `)}
        <button class="btn btn-brand btn-sm" id="acct-prefs-save" style="margin-top:4px">Save preferences</button>
      </div>

      <!-- ── Customization ── -->
      <div class="acct-panel" id="acct-panel-customize">
        <div class="page-head" style="margin-bottom:20px">
          <div class="ph-icon">${lu("palette")}</div>
          <div><h1 class="acct-panel-title">Customization</h1><p class="acct-panel-sub">Theme and display settings for your Studio dashboard.</p></div>
        </div>
        ${card_html("", `
          <p class="c-sub" style="margin-bottom:14px">Theme customization is available via the <strong>Customize</strong> option in the bottom-left user menu, or press <kbd style="font-family:var(--font-mono,monospace);background:var(--color-fill-secondary);padding:1px 6px;border-radius:5px;font-size:12px">Ctrl K</kbd> → Open Customize.</p>
          <button class="btn btn-brand btn-sm" id="acct-open-cust">Open Customize</button>
        `)}
      </div>

      <!-- ── Notifications ── -->
      <div class="acct-panel" id="acct-panel-notifications">
        <div class="page-head" style="margin-bottom:20px">
          <div class="ph-icon">${lu("bell")}</div>
          <div><h1 class="acct-panel-title">Notifications</h1><p class="acct-panel-sub">Control when and how Browns Digital contacts you.</p></div>
        </div>
        ${card_html("", `
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Publishing confirmations</strong><span>Email when a site goes live or is promoted.</span></div><label class="acct-sw"><input type="checkbox" id="anotif-pub" checked><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Health alerts</strong><span>Email when your site health score drops significantly.</span></div><label class="acct-sw"><input type="checkbox" id="anotif-health"><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Team activity</strong><span>Email when a collaborator edits or publishes.</span></div><label class="acct-sw"><input type="checkbox" id="anotif-team"><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
          <div class="acct-toggle-row"><div class="acct-toggle-info"><strong>Product updates</strong><span>Occasional emails about new features and improvements.</span></div><label class="acct-sw"><input type="checkbox" id="anotif-product" checked><div class="acct-sw-track"></div><div class="acct-sw-thumb"></div></label></div>
        `)}
        <button class="btn btn-brand btn-sm" id="acct-notif-save" style="margin-top:4px">Save</button>
      </div>

      <!-- ── Shortcuts ── -->
      <div class="acct-panel" id="acct-panel-shortcuts">
        <div class="page-head" style="margin-bottom:20px">
          <div class="ph-icon">${lu("keyboard")}</div>
          <div><h1 class="acct-panel-title">Keyboard shortcuts</h1><p class="acct-panel-sub">Configure builder shortcuts — synced to this browser.</p></div>
        </div>
        <div id="acct-shortcuts-mount"></div>
      </div>

      <!-- ── Danger Zone ── -->
      <div class="acct-panel" id="acct-panel-danger">
        <div class="page-head" style="margin-bottom:20px">
          <div class="ph-icon">${lu("triangle-alert")}</div>
          <div><h1 class="acct-panel-title">Danger Zone</h1><p class="acct-panel-sub">Irreversible actions. Read carefully before proceeding.</p></div>
        </div>
        ${card_html("", `
          <div class="acct-danger-item">
            <h3>Active sign-ins</h3>
            <p>Sign out of Studio on every browser and device. This includes your current session — you will need to sign in again.</p>
            <button class="btn btn-danger btn-sm" id="acct-signout-all">Sign out all sessions</button>
          </div>
          <div class="acct-danger-item">
            <h3>Delete account</h3>
            <p>Permanently deletes your account and all associated data — sites, pages, revisions, domains, and collaborator records — after a 7-day grace period. You can cancel the request from the dashboard until deletion runs.</p>
            <p>Staff accounts cannot be deleted through this page. Contact an executive if you need your account removed.</p>
            <button class="btn btn-danger btn-sm" id="acct-delete-btn">Request account deletion</button>
            <p class="acct-danger-note" id="acct-delete-note" style="display:none">${lu("check","lu14")} Deletion request submitted. Your account will be removed in 7 days unless cancelled.</p>
          </div>
        `)}
      </div>

    </div>
  </div>`;
  el.appendChild(_acctWrap);

  /* helper: generate a card div without going through the card() fn (which appends to el) */
  function card_html(cls, inner){ return `<div class="card liquid-glass${cls?" "+cls:""}" style="margin-bottom:16px">${inner}</div>`; }

  icons();

  /* ── Nav switching ── */
  el.querySelectorAll(".acct-side-btn[data-acct]").forEach(btn => {
    btn.addEventListener("click", () => {
      if(btn.classList.contains("disabled")) return;
      el.querySelectorAll(".acct-side-btn").forEach(b => b.classList.remove("on"));
      el.querySelectorAll(".acct-panel").forEach(p => p.classList.remove("on"));
      btn.classList.add("on");
      el.querySelector(`#acct-panel-${btn.dataset.acct}`)?.classList.add("on");
      if(btn.dataset.acct === "shortcuts"){
        const host = el.querySelector("#acct-shortcuts-mount");
        if(host && !host.querySelector(".card")) mountAccountShortcuts(host, { head: false });
        icons();
      }
      if(btn.dataset.acct === "ai"){
        const host = el.querySelector("#acct-ai-body");
        if(host && !host.querySelector(".card")) mountAISettings(host);
      }
      el.closest("#page")?.scrollTo(0,0);
    });
  });
  /* handle goAccountShortcuts() scroll target */
  const acctScroll = sessionStorage.getItem("bd_acct_scroll");
  sessionStorage.removeItem("bd_acct_scroll");
  if(acctScroll === "shortcuts"){
    const btn = el.querySelector('[data-acct="shortcuts"]');
    if(btn) btn.click();
  }
  if(acctScroll === "subscription"){
    el.querySelector('[data-acct="subscription"]')?.click();
  }

  /* recommended tier — highlighted as the most popular pick */
  const SUB_RECOMMENDED = "forge";

  let subInterval = "monthly";
  let subSelected = SUB_RECOMMENDED;
  let subPlansExpanded = false;

  /* absolute date, e.g. "12 Aug 2026" */
  const fmtDate = iso => { if (!iso) return "—"; try { return new Date(iso).toLocaleDateString(undefined, { day:"numeric", month:"short", year:"numeric" }); } catch { return "—"; } };

  /* assign to the OUTER binding (declared near the top) so checkout flows can
     repaint this panel after a successful payment. */
  paintSubscriptionPanel = function(){
    const host = el.querySelector("#acct-subscription-body");
    if (!host) return;
    const bal = creditBalance;
    const hasSub = bdHasActiveSubscription(bal);
    if (hasSub && bal?.subscription?.interval) {
      subInterval = bal.subscription.interval === "annual" ? "annual" : "monthly";
    }
    const isDraft = !hasSub;
    const memberTier = hasSub ? bdNormalizePlan(plan) : "free";
    const currentLabel = hasSub ? bdPlanDisplayLabel(plan) : "Draft";
    const sub = bal?.subscription || null;
    const subStatus = sub?.status || "none";
    const subActive = subStatus === "active";
    const subCancelling = subStatus === "non-renewing";
    const cardAttention = subStatus === "attention" || sub?.last_charge_failed === true;

    const resetWord = bal?.reset_at ? fmtForward(bal.reset_at) : "";
    const subInt   = sub?.interval || subInterval;
    const subPrice = sub?.amount_zar || bdSubPricing(memberTier, subInt).zar;
    const subPer   = sub?.per || (subInt === "annual" ? "year" : "month");

    /* ── plan management card (subscribed) OR upgrade hero (draft) ── */
    let leadHTML;
    if (hasSub){
      leadHTML = `
      <section class="plan-manage reveal">
        <div class="plan-manage-head">
          <div class="plan-manage-id">
            <p class="sub-hero-kicker">Your plan</p>
            <div class="plan-manage-title">
              <h2>${esc(currentLabel)}</h2>
              <span class="plan-status ${subActive ? "active" : "ending"}">${subActive ? "Active" : "Cancels " + fmtDate(sub?.ends_at)}</span>
            </div>
            <p class="plan-manage-price">${bdFormatZARFromRands(subPrice)} / ${subPer} · ${subInt === "annual" ? "billed yearly" : "billed monthly"}</p>
          </div>
          <div class="plan-manage-icon">${lu(subActive ? "badge-check" : "clock")}</div>
        </div>
        ${subCancelling ? `
        <div class="plan-notice">
          ${lu("info","lu14")}
          <span>Auto-renew is off. You'll keep ${esc(currentLabel)} until <strong>${fmtDate(sub?.ends_at)}</strong>, then move to Draft.</span>
        </div>` : `
        <p class="plan-manage-renew">${resetWord ? "Next charge " + esc(resetWord.replace(/^renews\s*/i, "")) : "Renews automatically each cycle."}</p>`}
        <div class="plan-manage-actions">
          <button type="button" class="btn btn-ghost btn-sm" id="plan-compare-toggle">${lu("layers","lu14")} ${subPlansExpanded ? "Hide plans" : "Compare plans"}</button>
          ${subActive
            ? `<button type="button" class="btn btn-ghost btn-sm plan-cancel" id="plan-cancel">Cancel plan</button>`
            : `<button type="button" class="btn btn-brand btn-sm" id="plan-resume">${lu("rotate-cw","lu14")} Resume plan</button>`}
        </div>
      </section>`;
    } else {
      leadHTML = `
      <div class="sub-hero draft">
        <div class="sub-hero-icon">${lu("sparkles")}</div>
        <div class="sub-hero-text">
          <p class="sub-hero-kicker">You're on Draft</p>
          <h2 class="sub-hero-title">Draft</h2>
          <p class="sub-hero-sub">Choose a plan below to unlock more sites, collaborators, and custom CSS.</p>
        </div>
      </div>`;
    }

    const activeBillingInterval = hasSub ? ((sub?.interval === "annual") ? "annual" : "monthly") : null;

    /* ── plan picker tiles ── */
    const planTiles = ["build", "forge", "architect"].map(key => {
      const meta = CREDIT_PLANS[key];
      const price = bdSubPricing(key, subInterval);
      const isCurrent = hasSub && memberTier === key && subInterval === activeBillingInterval;
      const isDowngrade = hasSub && subPlanRank(key) < subPlanRank(memberTier);
      const isSel = !isDowngrade && subSelected === key;
      const isRec = key === SUB_RECOMMENDED;
      const moEquiv = subInterval === "annual" ? Math.round(price.zar / 12) : null;
      const feats = (SUBSCRIPTION_FEATURES[key] || []).map(f => `<li>${esc(f)}</li>`).join("");
      return `<button type="button" class="sub-plan-tile${isSel ? " sel" : ""}${isCurrent ? " current" : ""}${isDowngrade ? " locked" : ""}${isRec ? " rec" : ""}"${isDowngrade ? ` data-sub-downgrade="${key}"` : ` data-sub-select="${key}"`} aria-pressed="${isSel}"${isDowngrade ? ' aria-disabled="true"' : ""}>
        <span class="sub-plan-check" aria-hidden="true">${lu("check","lu14")}</span>
        <div class="sub-plan-row">
          ${isRec && !isCurrent && !isDowngrade ? `<span class="sub-plan-flag">Most popular</span>` : ""}
          ${isCurrent ? `<span class="sub-plan-flag current">Current plan</span>` : ""}
          ${isDowngrade ? `<span class="sub-plan-flag locked">Contact support</span>` : ""}
        </div>
        <strong class="sub-plan-name">${esc(meta.label)}</strong>
        <div class="sub-plan-price-row">
          <span class="sub-plan-price">${bdFormatZARFromRands(price.zar)}</span>
          <em>/${price.per}</em>
        </div>
        ${moEquiv ? `<span class="sub-plan-mo">≈ ${bdFormatZARFromRands(moEquiv)}/mo${price.save ? " · " + esc(price.save) : ""}</span>` : ""}
        <ul class="sub-plan-feats">${feats}</ul>
      </button>`;
    }).join("");

    const ledger = Array.isArray(bal?.ledger) ? bal.ledger : [];
    const billSeen = new Set();
    const billRows = ledger.filter(e => {
      const isTopup = e.kind === "topup";
      const isCharge = e.kind === "charge" || e.kind === "subscription";
      const isPaidReset = e.kind === "reset" && (e.ref || /subscription|credits reset/i.test(String(e.note || "")));
      if (!isTopup && !isCharge && !isPaidReset) return false;
      if (!(e.delta || e.amount_zar || e.ref || isPaidReset)) return false;
      const key = e.ref || `${e.kind}|${String(e.note || "").slice(0, 40)}|${String(e.at || "").slice(0, 10)}`;
      if (billSeen.has(key)) return false;
      billSeen.add(key);
      return true;
    }).slice(0, 10).map(e => {
      const label = e.kind === "topup"
        ? (e.note || "Credit top-up")
        : `${currentLabel} subscription`;
      const amount = e.amount_zar
        ? bdFormatZARFromRands(e.amount_zar)
        : (e.kind === "topup" ? `${e.delta || 0} credits` : bdFormatZARFromRands(subPrice));
      return `
      <div class="bill-row">
        <span class="bill-ico sub">${lu(e.kind === "topup" ? "coins" : "refresh-cw","lu14")}</span>
        <div class="bill-info"><strong>${esc(label)}</strong><span>${esc(fmtDate(e.at))}</span></div>
        <span class="bill-amount">${esc(amount)}</span>
      </div>`;
    }).join("");
    const billHTML = billRows ? `
        <div class="bill-sec bill-sec-nested">
          <div class="sub-sec-head"><h3>Billing history</h3></div>
          <div class="bill-list">${billRows}</div>
        </div>` : "";

    /* ── billing & payment ── */
    const card = sub?.card;
    const nextBillDate = sub?.next_charge_at || (subCancelling ? sub?.ends_at : null) || bal?.reset_at;
    let billingHTML;
    if (hasSub){
      billingHTML = `
      <section class="bill-manage-sec" id="sub-billing">
        <div class="sub-sec-head">
          <div>
            <h3>Billing &amp; payment</h3>
            <p class="sub-sec-sub">Update your card if it expired or was replaced. Receipts are emailed after each charge.</p>
          </div>
        </div>
        <div class="bill-manage-grid">
          <div class="pay-card bill-manage-pay">
            <span class="pay-card-ico">${lu("credit-card")}</span>
            <div class="pay-card-info">
              ${card ? `<strong>${esc(card.brand || "Card")} ending ${esc(card.last4 || "••••")}</strong><span>Charged for ${esc(currentLabel)}</span>`
                     : `<strong>No card on file</strong><span>Add a card to keep ${esc(currentLabel)} active</span>`}
            </div>
            <button type="button" class="btn btn-brand btn-sm" id="pay-update">${lu("pencil","lu14")} Update card</button>
          </div>
          <div class="bill-manage-meta">
            <div class="bill-meta-row"><span>Plan</span><strong>${esc(currentLabel)}</strong></div>
            <div class="bill-meta-row"><span>Amount</span><strong>${bdFormatZARFromRands(subPrice)} / ${subPer}</strong></div>
            <div class="bill-meta-row"><span>Billing</span><strong>${subInt === "annual" ? "Annual" : "Monthly"}</strong></div>
            ${nextBillDate ? `<div class="bill-meta-row"><span>${subCancelling ? "Access until" : "Next charge"}</span><strong>${fmtDate(nextBillDate)}</strong></div>` : ""}
          </div>
        </div>
        ${cardAttention ? `<div class="bill-manage-help">
          <button type="button" class="bill-manage-help-btn" id="bill-help-card">
            ${lu("info","lu14")}
            <span>Card declined or expired? <strong>Update card</strong> to keep your plan active.</span>
          </button>
        </div>` : ""}
        ${billHTML}
      </section>`;
    } else {
      billingHTML = `
      <section class="bill-manage-sec draft" id="sub-billing">
        <div class="sub-sec-head"><h3>Billing</h3></div>
        <button type="button" class="bill-info-card bill-info-action" id="sub-billing-cta">
          <strong>Subscribe to unlock billing</strong>
          <p>Choose a plan above. After your first payment you can update your card, see renewal dates, and view receipts here.</p>
          <span class="bill-info-link">View plans ${lu("chevron-right","lu14")}</span>
        </button>
        <p class="bill-info-support">Questions? Email <a href="mailto:support@brownsdigital.org">support@brownsdigital.org</a></p>
      </section>`;
    }

    const selPrice = bdSubPricing(subSelected, subInterval);
    const selMeta = CREDIT_PLANS[subSelected];
    const isCurrentPlan = hasSub && memberTier === subSelected && subInterval === activeBillingInterval;
    const isUpgrade = hasSub && subPlanRank(subSelected) > subPlanRank(memberTier);
    const canSubscribe = hasSub ? isUpgrade : true;
    const selMoEquiv = subInterval === "annual" ? Math.round(selPrice.zar / 12) : null;
    const switching = hasSub && isUpgrade;
    const plansVisible = isDraft || subPlansExpanded;

    host.innerHTML = `
      ${leadHTML}

      <section class="sub-plan-section${plansVisible ? "" : " collapsed"}" id="sub-plan-section">
        <div class="sub-plan-intro">
          <div class="sub-sec-head sub-sec-head--plans">
            <div>
              <h3>${isDraft ? "Choose a plan" : "Upgrade plan"}</h3>
              ${hasSub
                ? `<p class="sub-sec-sub">Only upgrades are available here. To downgrade or change billing interval, contact support or cancel auto-renew and choose again when your cycle ends.</p>`
                : `<p class="sub-sec-sub">Pick the tier that fits your workflow. Switch or cancel anytime.</p>`}
            </div>
            <div class="sub-seg" role="tablist" aria-label="Billing interval">
              <button type="button" class="sub-seg-btn${subInterval === "monthly" ? " on" : ""}" data-sub-interval="monthly" role="tab" aria-selected="${subInterval === "monthly"}">Monthly</button>
              <button type="button" class="sub-seg-btn${subInterval === "annual" ? " on" : ""}" data-sub-interval="annual" role="tab" aria-selected="${subInterval === "annual"}">Annual</button>
            </div>
          </div>
          ${subInterval === "annual" ? `<p class="sub-plan-save-note">Save 17% with annual billing</p>` : ""}
        </div>
        <div class="sub-plan-grid">${planTiles}</div>
      </section>

      ${billingHTML}

      <div class="sub-checkout-bar"${canSubscribe ? "" : ' hidden style="display:none"'}>
        <div class="sub-checkout-copy">
          <strong>${switching ? "Upgrade to " : isDraft ? "Subscribe to " : ""}${esc(selMeta.label)} · ${subInterval === "annual" ? "Annual" : "Monthly"}</strong>
          <span>${bdFormatZARFromRands(selPrice.zar)} / ${selPrice.per}${selMoEquiv ? ` · ≈ ${bdFormatZARFromRands(selMoEquiv)}/mo` : ""}</span>
          ${switching ? `<span class="sub-checkout-note">Upgrades take effect after payment</span>` : ""}
        </div>
        <button type="button" class="btn btn-brand sub-checkout-btn" id="sub-checkout-go">
          ${switching ? "Upgrade plan" : "Continue"} · ${bdFormatZARFromRands(selPrice.zar)}
        </button>
      </div>
    `;

    host.querySelectorAll("[data-sub-interval]").forEach(btn => {
      btn.onclick = () => { subInterval = btn.dataset.subInterval; paintSubscriptionPanel(); };
    });
    host.querySelectorAll("[data-sub-select]").forEach(btn => {
      btn.onclick = () => { subSelected = btn.dataset.subSelect; paintSubscriptionPanel(); };
    });
    host.querySelectorAll("[data-sub-downgrade]").forEach(btn => {
      btn.onclick = () => showPlanChangeBlockedModal(currentLabel, "Downgrading to a lower tier isn't self-serve.");
    });
    host.querySelector("#sub-checkout-go")?.addEventListener("click", () => {
      if (isCurrentPlan || !canSubscribe) return;
      startSubscribeCheckout(subSelected, subInterval, { switching: hasSub });
    });
    host.querySelector("#plan-compare-toggle")?.addEventListener("click", () => {
      subPlansExpanded = !subPlansExpanded;
      paintSubscriptionPanel();
    });
    host.querySelector("#plan-cancel")?.addEventListener("click", () => {
      confirmModal(
        "Turn off auto-renew?",
        `You'll keep ${esc(currentLabel)} until the end of the current billing period, then move to Draft. You can resume anytime before then.`,
        "Turn off auto-renew",
        async () => {
          try {
            const r = await api.cancelSubscription();
            if (r?.balance) creditBalance = r.balance;
            paintCreditsUI();
            paintSubscriptionPanel();
            toast("Auto-renew turned off.", { kind: "success" });
          } catch (e){ toast(humanErr(e, "Could not cancel."), { kind: "error" }); }
        }
      );
    });
    host.querySelector("#plan-resume")?.addEventListener("click", async () => {
      const btn = host.querySelector("#plan-resume");
      btn.disabled = true;
      try {
        const r = await api.resumeSubscription();
        if (r?.balance) creditBalance = r.balance;
        paintCreditsUI();
        paintSubscriptionPanel();
        toast("Auto-renew turned back on.", { kind: "success" });
      } catch (e){ btn.disabled = false; toast(humanErr(e, "Could not resume."), { kind: "error" }); }
    });
    host.querySelector("#bill-help-card")?.addEventListener("click", () => {
      host.querySelector("#pay-update")?.click();
    });
    host.querySelector("#sub-billing-cta")?.addEventListener("click", () => {
      document.getElementById("sub-plan-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
      subPlansExpanded = true;
      paintSubscriptionPanel();
    });
    host.querySelector("#pay-update")?.addEventListener("click", async () => {
      const btn = host.querySelector("#pay-update");
      const label = btn.textContent;
      btn.disabled = true; btn.textContent = "Opening…";
      try {
        const r = await api.updatePaymentMethod();
        if (r?.demo){ toast("In production, this opens your secure card-update page.", { kind: "default" }); }
        else if (r?.link){
          bdOpenPaystackIframeSheet({
            title: "Update payment method",
            subtitle: "Secure card update",
            url: r.link
          });
        }
      } catch (e){ toast(humanErr(e, "Could not open card management."), { kind: "error" }); }
      finally { btn.disabled = false; btn.textContent = label; }
    });
    icons();
  };
  paintSubscriptionPanel();
  paintAccountMembershipSummary();

  /* Pulse settings */
  async function mountAISettings(host) {
    let config = { ready: false, has_key: false, profile: lumenPrefs().modelProfile, model: '', model_label: 'Pulse', profiles: LUMEN_PROFILES, is_admin: false };
    let training = { examples: [], is_admin: false, count: 0 };
    let loading = true;

    const paintTraining = () => {
      if (!config.is_admin && !training.is_admin) {
        return card_html("", `
          <h2>Training library</h2>
          <p class="c-sub">Pulse is trained by Browns Digital on curated Studio pages (${training.count || 0} examples). Production-ready compositions are injected as few-shots when Pulse generates.</p>
        `);
      }
      const rows = (training.examples || []).map(ex => `
        <div class="acct-data-row" data-train-id="${esc(ex.id)}">
          <div class="acct-data-row-info">
            <strong>${esc(ex.title || ex.id)}</strong>
            <span>${esc(ex.mode || "page")} · ${(ex.tags || []).join(", ") || "no tags"}</span>
          </div>
          <button type="button" class="btn btn-ghost btn-sm" data-train-del="${esc(ex.id)}">Delete</button>
        </div>`).join("") || `<p class="c-sub">No examples yet. Paste a gold page JSON below.</p>`;
      return card_html("", `
        <h2>Training library</h2>
        <p class="c-sub">Curated few-shot examples for Pulse. Admins only. Examples must use Studio block schema.</p>
        ${rows}
        <div class="acct-field-grid single" style="margin-top:16px">
          <div class="field">
            <label for="ai-train-json">Add / update example (JSON)</label>
            <textarea id="ai-train-json" rows="8" class="settings-input" placeholder='{"id":"saas-home-v2","title":"...","tags":["saas"],"brief":"...","mode":"page","output":{"name":"Home","slug":"","blocks":[]}}'></textarea>
            <div class="acct-save-hint" id="ai-train-hint"></div>
          </div>
        </div>
        <button type="button" class="btn btn-brand" id="ai-train-save" style="margin-top:10px">Save example</button>
      `);
    };

    const paint = () => {
      if (loading) {
        host.innerHTML = card_html("", `<p style="text-align:center;padding:40px 0;color:var(--color-label-secondary)">Loading...</p>`);
        return;
      }

      const ready = !!(config.ready || config.has_key);
      const profiles = config.profiles || LUMEN_PROFILES;
      const profileId = lumenNormalizeProfile(config.profile || config.model || lumenPrefs().modelProfile);
      const profileLabel = profiles.find(p => p.id === profileId)?.label
        || LUMEN_PROFILES.find(p => p.id === profileId)?.label
        || "Pulse 1 Flash";

      host.innerHTML = card_html("", `
        <h2>Pulse</h2>
        <p class="c-sub">Browns Digital provides Pulse. Choose a model profile — no API key required on your side.</p>
        <div class="lumen-card" style="margin-top:14px">
          <div class="lumen-row">
            <div>
              <strong>Model profile</strong>
              <p>Pick Pulse 1 Flash for nearly instant answers or Pulse 1 for maximum craft.</p>
            </div>
            ${lumenPickerHtml("ai-profile", profiles, profileId, "Model profile")}
          </div>
        </div>
        <div class="acct-save-hint" id="ai-profile-hint" style="margin-top:10px"></div>
        <div style="display:flex;gap:10px;margin-top:12px;flex-wrap:wrap">
          ${config.is_admin ? '<button class="btn btn-ghost" id="ai-test-btn">Test platform connection</button>' : ''}
        </div>
      `) + card_html("", `
        <h2>Status</h2>
        <div class="acct-data-row">
          <div class="acct-data-row-info">
            <strong>Platform</strong>
            <span>${ready ? "Pulse ready on Studio" : "Not configured on this server"}</span>
          </div>
          <span class="acct-status ${ready ? "on" : ""}">${ready ? "Active" : "Offline"}</span>
        </div>
        <div class="acct-data-row">
          <div class="acct-data-row-info">
            <strong>Your model</strong>
            <span>${esc(profileLabel)}</span>
          </div>
        </div>
      `) + paintTraining();

      icons();

      const hint = host.querySelector("#ai-profile-hint");
      bindLumenPicker(host, "ai-profile", {
        onChange: async (id) => {
          try {
            if (hint) {
              hint.textContent = "Saving...";
              hint.className = "acct-save-hint";
            }
            await persistLumenProfile(id);
            config.profile = id;
            config.model = id;
            if (hint) {
              hint.textContent = "Saved";
              hint.className = "acct-save-hint ok";
            }
            toast("Pulse profile updated", { kind: "success" });
            paint();
          } catch (err) {
            if (hint) {
              hint.textContent = humanErr(err);
              hint.className = "acct-save-hint";
            }
            toast(humanErr(err), { kind: "error" });
          }
        }
      });

      host.querySelector("#ai-test-btn")?.addEventListener("click", async () => {
        const testBtn = host.querySelector("#ai-test-btn");
        try {
          testBtn.disabled = true;
          testBtn.textContent = "Testing...";
          await openAIRequest({ action: "test-connection" });
          toast("Platform connection OK", { kind: "success" });
        } catch (e) {
          toast(humanErr(e, "Connection test failed"), { kind: "error" });
        } finally {
          testBtn.disabled = false;
          testBtn.textContent = "Test platform connection";
        }
      });

      host.querySelector("#ai-train-save")?.addEventListener("click", async () => {
        const ta = host.querySelector("#ai-train-json");
        const trainHint = host.querySelector("#ai-train-hint");
        try {
          const example = JSON.parse((ta?.value || "").trim());
          const data = await openAIRequest({ action: "training-save", example });
          if (trainHint) {
            trainHint.textContent = "Saved " + (data.example?.id || "");
            trainHint.className = "acct-save-hint ok";
          }
          toast("Training example saved", { kind: "success" });
          training = await openAIRequest({ action: "training-list" });
          paint();
        } catch (e) {
          if (trainHint) {
            trainHint.textContent = humanErr(e, "Invalid JSON");
            trainHint.className = "acct-save-hint";
          }
          toast(humanErr(e, "Could not save example"), { kind: "error" });
        }
      });

      host.querySelectorAll("[data-train-del]").forEach(btn => {
        btn.addEventListener("click", () => {
          const id = btn.getAttribute("data-train-del");
          confirmModal("Delete training example?", `Remove "${id}" from Pulse's few-shot library.`, "Delete", async () => {
            try {
              await openAIRequest({ action: "training-delete", id });
              toast("Example deleted", { kind: "success" });
              training = await openAIRequest({ action: "training-list" });
              paint();
            } catch (e) {
              toast(humanErr(e, "Could not delete"), { kind: "error" });
            }
          });
        });
      });
    };

    try {
      const data = await openAIRequest({ action: 'get-config' });
      config = Object.assign(config, data || {});
    } catch (e) {
      console.error('Failed to load AI config:', e);
    }
    try {
      training = await openAIRequest({ action: 'training-list' });
    } catch (e) {
      training = { examples: [], is_admin: !!config.is_admin, count: 0 };
    } finally {
      loading = false;
      paint();
    }
  }

  /* ── Avatar ── */
  const savedAvatar = avatarUrlOf(me);
  if(savedAvatar){ const img = el.querySelector("#acct-avatar-img"); img.src = savedAvatar; img.setAttribute("referrerpolicy", "no-referrer"); img.classList.add("loaded"); el.querySelector("#acct-avatar-initial").style.display = "none"; }
  const initialEl = el.querySelector("#acct-avatar-initial");
  if (initialEl && !savedAvatar) initialEl.textContent = displayNameOf(me).charAt(0).toUpperCase() || "?";
  const nameInput = el.querySelector("#acct-displayname");
  if (nameInput) nameInput.value = me.name || "";
  el.querySelector("#acct-avatar-btn").onclick = () => el.querySelector("#acct-avatar-file").click();
  el.querySelector("#acct-avatar-file").addEventListener("change", e => {
    const file = e.target.files[0]; if(!file) return;
    if(file.size > 2*1024*1024){ toast("Image must be under 2 MB"); return; }
    if(!/^image\/(jpeg|png|webp|gif)$/.test(file.type)){ toast("Only JPEG, PNG, WebP or GIF allowed"); return; }
    const reader = new FileReader();
    reader.onload = ev => {
      const src = ev.target.result;
      const testImg = new Image();
      testImg.onload = async () => {
        localStorage.setItem("bd_avatar_" + me.email, src);
        me.avatar = src;
        try { await api.syncAvatar(src); } catch {}
        const img = el.querySelector("#acct-avatar-img"); img.src = src; img.classList.add("loaded");
        el.querySelector("#acct-avatar-initial").style.display = "none";
        paintSideUser();
        toast("Photo updated");
      };
      testImg.onerror = () => toast("Could not read image — try another file.");
      testImg.src = src;
    };
    reader.readAsDataURL(file);
  });

  /* ── Display name ── */
  let nameT;
  const nameHint = el.querySelector("#acct-name-hint");
  el.querySelector("#acct-displayname").addEventListener("input", e => {
    clearTimeout(nameT); nameHint.textContent = "Saving…"; nameHint.className = "acct-save-hint";
    nameT = setTimeout(async () => {
      const v = e.target.value.trim().replace(/<[^>]*>/g,"").slice(0,64);
      if(!v){ nameHint.textContent = "Name cannot be empty"; return; }
      await api.updateName(v); me.name = v; paintSideUser();
      el.querySelector("#acct-avatar-initial").textContent = v[0].toUpperCase();
      nameHint.textContent = "Saved"; nameHint.className = "acct-save-hint ok";
    }, 700);
  });

  /* ── Username ── */
  let unT;
  const unHint = el.querySelector("#acct-un-hint");
  el.querySelector("#acct-username").addEventListener("input", e => {
    let v = e.target.value.replace(/[^a-z0-9_]/gi,"").slice(0,32); e.target.value = v;
    clearTimeout(unT); unHint.textContent = "Saving…"; unHint.className = "acct-save-hint"; if(!v) return;
    unT = setTimeout(async () => {
      if(v.length < 3){ unHint.textContent = "At least 3 characters"; unHint.className = "acct-save-hint"; return; }
      try {
        const saved = await api.updateUsername(v);
        me.username = saved;
      unHint.textContent = "Saved"; unHint.className = "acct-save-hint ok";
      } catch (err) {
        unHint.textContent = humanErr(err, "Could not save username");
        unHint.className = "acct-save-hint";
      }
    }, 700);
  });

  /* ── Private profile ── */
  const priv = JSON.parse(localStorage.getItem("bd_prefs_" + me.email)||"{}");
  el.querySelector("#acct-pref-private").checked = !!priv.privateProfile;
  el.querySelector("#acct-pref-private").addEventListener("change", e => {
    const p = JSON.parse(localStorage.getItem("bd_prefs_" + me.email)||"{}");
    p.privateProfile = e.target.checked; localStorage.setItem("bd_prefs_" + me.email, JSON.stringify(p));
    syncAccountPrefsNow();
    toast(e.target.checked ? "Profile hidden from search" : "Profile visible in search");
  });

  /* ── Data export ── */
  el.querySelectorAll("[data-export]").forEach(btn => {
    btn.onclick = async () => {
      const scope = btn.dataset.export;
      const label = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Preparing…";
      try {
        const file = await api.requestDataExport(scope);
        api.downloadExportFile(file);
        toast("Download started.", { kind: "success" });
      } catch (e) {
        toast(humanErr(e, "Export failed"), { kind: "error" });
      } finally {
        btn.disabled = false;
        btn.textContent = label;
      }
    };
  });

  /* ── Password change ── */
  const chPassSec = el.querySelector("#acct-chpass-section");
  const chPassMfa = el.querySelector("#acct-chpass-mfa");
  const chPassMfaCode = el.querySelector("#acct-chpass-mfa-code");
  const revealPasswordMfa = () => {
    chPassMfa.style.display = "block";
    chPassMfaCode.focus();
  };
  el.querySelector("#acct-chpass-btn").onclick = async () => {
    const opening = chPassSec.style.display === "none";
    chPassSec.style.display = opening ? "block" : "none";
    if (!opening) return;
    try {
      const state = await api.getMfaState();
      chPassMfa.style.display = state.totpEnabled ? "block" : "none";
    } catch {}
  };
  el.querySelector("#acct-cancelpass-btn").onclick = () => {
    chPassSec.style.display = "none";
    chPassMfaCode.value = "";
  };
  const accountNewPassword = el.querySelector("#acct-newpass");
  const paintAccountPassword = () => paintPasswordStrength(
    accountNewPassword, el.querySelector("#acct-pass-meter"),
    el.querySelector("#acct-pass-meter-bar"), el.querySelector("#acct-pass-strength")
  );
  accountNewPassword.addEventListener("input", paintAccountPassword);
  paintAccountPassword();
  el.querySelector("#acct-savepass-btn").onclick = async () => {
    const cur = el.querySelector("#acct-curpass").value, nw = el.querySelector("#acct-newpass").value;
    if(!cur){ toast("Enter your current password"); return; }
    if(nw.length < 8){ toast("New password must be at least 8 characters"); return; }
    const saveBtn = el.querySelector("#acct-savepass-btn");
    saveBtn.disabled = true; saveBtn.textContent = "Updating…";
    try {
      /* Validate the current password, then restore AAL2 before changing it. */
      await api.signIn(me.email, cur);
      const aal = await api.getAalState();
      if (aal.needsMfa){
        const code = String(chPassMfaCode.value || "").trim();
        if (!/^\d{6}$/.test(code)){
          revealPasswordMfa();
          toast("Enter the 6-digit code from your authenticator app.");
          return;
        }
        await api.mfaElevateTotp(code);
        if (typeof BD_AUTH !== "undefined" && api.getSupabase()){
          try { await BD_AUTH.establish(api.getSupabase()); } catch {}
        }
      }
      await api.setNewPassword(nw);
      el.querySelector("#acct-curpass").value = "";
      el.querySelector("#acct-newpass").value = "";
      chPassMfaCode.value = "";
      paintAccountPassword();
      chPassSec.style.display = "none";
      toast("Password updated", { kind:"success" });
    }
    catch(e){
      if (isMfaRequiredError(e)) revealPasswordMfa();
      toast(humanErr(e), { kind:"error" });
    }
    finally {
      saveBtn.disabled = false; saveBtn.textContent = "Update password";
    }
  };

  /* ── Passkey / TOTP ── */
  const passkeyBtn = el.querySelector("#acct-passkey-btn");
  const totpBtn = el.querySelector("#acct-totp-btn");
  const passkeyStatus = el.querySelector("#acct-passkey-status");
  const totpStatus = el.querySelector("#acct-totp-status");
  let mfaState = { enabled: false, totpEnabled: false, factors: { all: [], totp: [], webauthn: [] } };
  let passkeyState = { enabled: false, passkeys: [] };
  function paintMfaUi(){
    const hasPasskey = !!passkeyState.enabled;
    const hasTotp = !!mfaState.totpEnabled;
    passkeyStatus.textContent = hasPasskey ? "Enabled" : "Not set up";
    passkeyStatus.className = "acct-status " + (hasPasskey ? "on" : "off");
    passkeyBtn.textContent = hasPasskey ? "Remove" : "Set up";
    totpStatus.textContent = hasTotp ? "Enabled" : "Not set up";
    totpStatus.className = "acct-status " + (hasTotp ? "on" : "off");
    totpBtn.textContent = hasTotp ? "Remove" : "Set up";
  }
  async function refreshMfaState(){
    if (DEMO){
      passkeyBtn.disabled = true;
      totpBtn.disabled = true;
      passkeyStatus.textContent = "Live only";
      totpStatus.textContent = "Live only";
      return;
    }
    try {
      mfaState = await api.getMfaState();
      passkeyState = await api.getPasskeyState();
    }
    catch (e) { toast(humanErr(e, "Could not read MFA status."), { kind: "error" }); }
    paintMfaUi();
  }
  async function setupTotpFlow(){
    const setup = await api.mfaEnrollTotp();
    if (!setup.factorId) throw new Error("Could not start authenticator setup. Try again.");
    const qrHtml = setup.qr
      ? `<img src="${esc(setup.qr)}" alt="Scan this QR with your authenticator app" style="width:220px;height:220px;border-radius:12px;border:1px solid var(--line);background:#fff;padding:8px">`
      : `<p class="sub">QR preview unavailable. Use the manual secret below.</p>`;
    const m = openModal(`<h2>Set up authenticator</h2>
      <p class="sub">Scan with Google Authenticator, Authy, or 1Password. It appears as <strong>Browns Digital</strong>, then enter the 6-digit code to finish.</p>
      <div style="display:flex;justify-content:center;margin:10px 0 14px">${qrHtml}</div>
      ${setup.secret ? `<div class="field"><label>Manual secret</label><input readonly value="${esc(setup.secret)}"></div>` : ""}
      <div class="field"><label>6-digit code</label><input id="mfa-totp-code" inputmode="numeric" autocomplete="one-time-code" placeholder="123456" autofocus></div>
      <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button><button class="btn btn-brand btn-sm" data-x="ok">Verify and enable</button></div>`);
    const codeIn = m.querySelector("#mfa-totp-code");
    const close = async () => {
      const factors = await api.getMfaState();
      const pending = (factors.factors?.totp || []).find(f => String(f.status || "").toLowerCase() === "unverified");
      if (pending?.id) await api.mfaDisableFactor(pending.id, "totp").catch(() => {});
    };
    m.querySelector('[data-x="c"]').onclick = async () => { await close(); closeModal(); };
    m.querySelector('[data-x="ok"]').onclick = async () => {
      const code = String(codeIn.value || "").trim();
      if (!/^\d{6}$/.test(code)) { toast("Enter the 6-digit code from your authenticator app."); return; }
      try {
        await api.mfaVerifyTotp(setup.factorId, code);
        closeModal();
        toast("Two-factor authentication enabled.", { kind: "success" });
        await refreshMfaState();
      } catch (e) {
        toast(humanErr(e, "Could not verify TOTP code."), { kind: "error" });
      }
    };
  }
  async function disableTotpFlow(){
    const aal = await api.getAalState();
    if (aal.needsMfa){
      const code = await promptAuthenticatorCode({
        title: "Enter authenticator code",
        product: "Browns Digital",
        sub: "Enter the current 6-digit code from your authenticator app before disabling two-factor authentication.",
        showCancel: true,
        verify: (c) => api.mfaElevateTotp(c)
      });
      if (!code) return;
      if (typeof BD_AUTH !== "undefined" && api.getSupabase()){
        try { await BD_AUTH.establish(api.getSupabase()); } catch {}
      }
    }
    await api.mfaDisableTotpAll();
    const state = await api.getMfaState();
    if (state.totpEnabled) throw new Error("Two-factor authentication could not be disabled.");
    await refreshMfaState();
    toast("Two-factor authentication disabled.", { kind:"success" });
  }
  passkeyBtn.onclick = async () => {
    try {
      passkeyBtn.disabled = true;
      if (passkeyState.enabled){
        confirmModal("Remove passkey?", "This removes all passkeys on your account. You can still sign in with password and set up passkeys again anytime.", "Remove passkeys", async () => {
          try {
            await api.passkeyRemoveAll();
            await refreshMfaState();
            toast("Passkeys removed.");
          } catch (e) { toast(humanErr(e, "Could not remove passkey."), { kind: "error" }); }
        });
      } else {
        const aal = await api.getAalState();
        if (aal.needsMfa){
          const code = await promptAuthenticatorCode({
            title: "Enter authenticator code",
            product: "Browns Digital",
            sub: "Enter the 6-digit code from your authenticator app before adding a passkey.",
            showCancel: true,
            verify: (c) => api.mfaElevateTotp(c)
          });
          if (!code) return;
        }
        await api.mfaEnrollPasskey();
        toast("Passkey enabled.", { kind: "success" });
        await refreshMfaState();
      }
    } catch (e) {
      await refreshMfaState();
      if (passkeyState.enabled){
        toast("Passkey enabled on this device.", { kind: "success" });
      } else {
      toast(humanErr(e, "Could not set up passkey."), { kind: "error" });
      }
    } finally {
      passkeyBtn.disabled = false;
    }
  };
  totpBtn.onclick = async () => {
    try {
      totpBtn.disabled = true;
      if (mfaState.totpEnabled){
        confirmModal("Disable two-factor authentication?", "You’ll verify your authenticator code before its factors are removed.", "Disable 2FA", async () => {
          try {
            await disableTotpFlow();
          } catch (e) { toast(humanErr(e, "Could not disable 2FA."), { kind: "error" }); }
        });
      } else {
        await setupTotpFlow();
      }
    } catch (e) {
      toast(humanErr(e, "Could not set up two-factor authentication."), { kind: "error" });
    } finally {
      totpBtn.disabled = false;
    }
  };
  refreshMfaState();

  /* ── Session PIN ── */
  const pinWrap = el.querySelector("#acct-pin-wrap");
  const pinToggle = el.querySelector("#acct-pin-toggle");
  if(readSessionPin(me.email)){ pinToggle.checked = true; pinWrap.style.display = "block"; }
  pinToggle.addEventListener("change", () => {
    pinWrap.style.display = pinToggle.checked ? "block" : "none";
    if(!pinToggle.checked){
      clearSessionPin(me.email);
      toast("Session PIN removed");
    }
  });
  el.querySelector("#acct-pin-save").onclick = () => {
    const v = el.querySelector("#acct-pin-input").value;
    if(!/^\d{4}$/.test(v)){ toast("PIN must be exactly 4 digits"); return; }
    /* UX obfuscation only — not encryption; Supabase session is the real auth boundary. */
    writeSessionPin(me.email, btoa(v));
    toast("Session PIN saved — required on every sign-in and refresh");
  };

  /* ── Preferences ── */
  const PREF_IDS = ["animations","meta","compact","confirm-del","snap","outlines","autosave","email-pub","email-health","email-team"];
  const savedP = JSON.parse(localStorage.getItem("bd_uiprefs_" + me.email)||"{}");
  PREF_IDS.forEach(k => { const el2 = el.querySelector("#apref-" + k); if(el2 && k in savedP) el2.checked = savedP[k]; });
  el.querySelector("#acct-prefs-save").onclick = () => {
    const out = { ...savedP }; PREF_IDS.forEach(k => { const el2 = el.querySelector("#apref-" + k); if(el2) out[k] = el2.checked; });
    localStorage.setItem("bd_uiprefs_" + me.email, JSON.stringify(out)); syncAccountPrefsNow(); applyMotionPref(); toast("Preferences saved");
  };

  /* ── Customization ── */
  el.querySelector("#acct-open-cust").onclick = () => openCustomizeModal();

  /* ── Notifications ── */
  const NOTIF_IDS = ["pub","health","team","product"];
  const savedN = JSON.parse(localStorage.getItem("bd_notif_" + me.email)||"{}");
  NOTIF_IDS.forEach(k => { const el2 = el.querySelector("#anotif-" + k); if(el2 && k in savedN) el2.checked = savedN[k]; });
  el.querySelector("#acct-notif-save").onclick = () => {
    const out = {}; NOTIF_IDS.forEach(k => { const el2 = el.querySelector("#anotif-" + k); if(el2) out[k] = el2.checked; });
    localStorage.setItem("bd_notif_" + me.email, JSON.stringify(out)); syncAccountPrefsNow(); toast("Notification preferences saved");
  };

  /* ── Danger Zone ── */
  el.querySelector("#acct-signout-all").onclick = () => {
    confirmModal("Sign out of all devices?", "This includes your current session. You will need to sign in again.", "Sign out everywhere", () => {
      localStorage.removeItem("bd_session"); window.location.href = "/";
    });
  };
  el.querySelector("#acct-delete-btn").onclick = async () => {
    const input = await promptModal("Delete account?", "This is irreversible after the grace period. Type <strong>DELETE</strong> to confirm the deletion request.", { placeholder: "DELETE", confirmLabel: "Request deletion", danger: true });
    if(input?.trim().toUpperCase() !== "DELETE"){ toast("Deletion cancelled"); return; }
    try{
      const u = JSON.parse(localStorage.getItem("bd_users")||"{}");
      if(u[me.email]?.plan === "staff"){ toast("Staff accounts cannot be deleted here — contact an executive."); return; }
      u[me.email] = {...(u[me.email]||{}), deletionRequested: new Date().toISOString()};
      localStorage.setItem("bd_users", JSON.stringify(u));
    }catch(e){}
    el.querySelector("#acct-delete-btn").disabled = true;
    el.querySelector("#acct-delete-note").style.display = "block";
    toast("Deletion request submitted");
  };

  /* ── card_html helper declared at top of function ── */
}

/* ============================== ACTIVITY LOG ============================== */
async function pageActivity(el){
  head(el, "activity", "Activity log", "Everything that happened across your sites — publishes, saves, domains, and more.");
  const filters = document.createElement("div");
  filters.className = "activity-page-filters reveal";
  filters.innerHTML = `<div class="tabs" role="group" aria-label="Filter activity"><button class="on" data-af="all">All</button><button data-af="pub">Publishing</button><button data-af="health">Health</button><button data-af="domain">Domains</button></div>`;
  el.appendChild(filters);
  const listCard = card(el, `<div class="activity-log-list dash-activity-list" id="act-full-list"></div><div class="activity-load-more" id="act-load-wrap" hidden><button type="button" class="btn btn-ghost btn-sm" id="act-load-more">Load more</button></div>`);
  const list = listCard.querySelector("#act-full-list");
  const loadWrap = listCard.querySelector("#act-load-wrap");
  const actMap = { pub:["published","staged","unpublished","restored"], health:["saved"], domain:["domain"] };
  let offset = 0, filter = "all", rows = [], loading = false, done = false;
  async function fetchMore(){
    if (loading || done) return;
    loading = true;
    if (!rows.length) list.innerHTML = skelRows(4);
    const batch = await api.listActivity(null, 25, offset);
    if (batch.length < 25) done = true;
    rows = rows.concat(batch);
    offset += batch.length;
    loading = false;
    paintRows();
    loadWrap.hidden = done;
  }
  function paintRows(){
    const shown = rows.filter(a => filter === "all" || (actMap[filter] || []).includes(a.kind));
    const kindClass = k =>
      k === "published" ? "g" :
      k === "created" || k === "saved" || k === "restored" ? "b" :
      k === "staged" ? "w" :
      k === "deleted" || k === "unpublished" ? "r" : "";
    list.innerHTML = shown.length ? shown.map(a =>
      `<div class="lr" role="listitem"><span class="ic ${kindClass(a.kind)}" aria-hidden="true">${lu(ACT_ICON[a.kind] || "dot")}</span><span class="lr-main"><strong>${esc(a.message)}</strong></span><time datetime="${esc(a.created_at)}">${fmtWhen(a.created_at)}</time></div>`).join("")
      : `<p class="dash-empty-note" style="padding:16px 4px">No activity yet — edits, saves, and publishes will show up here.</p>`;
    icons();
  }
  filters.querySelectorAll("[data-af]").forEach(b => b.onclick = () => {
    filters.querySelectorAll("[data-af]").forEach(x => x.classList.toggle("on", x === b));
    filter = b.dataset.af; paintRows();
  });
  listCard.querySelector("#act-load-more").onclick = () => fetchMore();
  await fetchMore();
}

/* ============================== DEVELOPER ============================== */
function crc32(buf){
  let t = crc32.t;
  if (!t){ t = crc32.t = new Int32Array(256); for (let n=0;n<256;n++){ let c=n; for (let k=0;k<8;k++) c = c&1 ? 0xEDB88320 ^ (c>>>1) : c>>>1; t[n]=c; } }
  let c = -1;
  for (let i=0;i<buf.length;i++) c = (c>>>8) ^ t[(c ^ buf[i]) & 255];
  return (c ^ -1) >>> 0;
}
function makeZip(files){ /* store-only zip, no compression — fine for HTML */
  const enc = new TextEncoder(); const chunks = []; const central = []; let offset = 0;
  const u16 = n => new Uint8Array([n&255, (n>>8)&255]);
  const u32 = n => new Uint8Array([n&255, (n>>8)&255, (n>>16)&255, (n>>24)&255]);
  files.forEach(f => {
    const name = enc.encode(f.path), data = enc.encode(f.content), crc = crc32(data);
    const head = [u32(0x04034b50), u16(20), u16(0), u16(0), u16(0), u16(0), u32(crc), u32(data.length), u32(data.length), u16(name.length), u16(0)];
    central.push({ name, data, crc, offset });
    head.forEach(h => chunks.push(h)); chunks.push(name, data);
    offset += 30 + name.length + data.length;
  });
  const cdStart = offset; let cdLen = 0;
  central.forEach(c => {
    const rec = [u32(0x02014b50), u16(20), u16(20), u16(0), u16(0), u16(0), u16(0), u32(c.crc), u32(c.data.length), u32(c.data.length), u16(c.name.length), u16(0), u16(0), u16(0), u16(0), u32(0), u32(c.offset)];
    rec.forEach(r => chunks.push(r)); chunks.push(c.name);
    cdLen += 46 + c.name.length;
  });
  [u32(0x06054b50), u16(0), u16(0), u16(central.length), u16(central.length), u32(cdLen), u32(cdStart), u16(0)].forEach(e => chunks.push(e));
  return new Blob(chunks, { type: "application/zip" });
}

/* =============================== BUILDER ===============================
   Immersive mode: the app sidebar + topbar disappear; all chrome lives
   in a floating dark pill toolbar above the canvas.
   Preview mode renders the full site; breakpoints adjust the canvas width. */
const BPS = [1920,1440,1280,1024,768,480];

/* ---------- builder preferences (separate from app settings) ---------- */
const BPREFS_DEFAULT = { autosave:"60", confirmDelete:"yes", outlines:"yes", hoverLabels:"no", showIds:"no", verbose:"no", chrome:"match" };
function bprefs(){
  try {
    const p = Object.assign({}, BPREFS_DEFAULT, JSON.parse(localStorage.getItem("bd_builder_prefs")) || {});
    /* One-time: prior default light chrome → match Studio theme */
    if (!localStorage.getItem("bd_builder_chrome_v3")){
      if (p.chrome === "light"){
        p.chrome = "match";
        localStorage.setItem("bd_builder_prefs", JSON.stringify(p));
      }
      localStorage.setItem("bd_builder_chrome_v3", "1");
    }
    return p;
  } catch { return Object.assign({}, BPREFS_DEFAULT); }
}
function setBpref(k, v){ const p = bprefs(); p[k] = v; localStorage.setItem("bd_builder_prefs", JSON.stringify(p)); api.syncBuilderPrefs(p); applyBprefs(); }
function applyBprefs(){
  const b = document.querySelector(".builder"); if (!b) return;
  const p = bprefs();
  b.classList.toggle("no-outlines", p.outlines === "no");
  b.classList.toggle("hover-labels", p.hoverLabels === "yes");
  const pill = $("#b-pill");
  if (pill){
    const appDark = document.documentElement.dataset.theme === "dark"
      || String(document.documentElement.dataset.bdTheme || "").includes("dark");
    /* match → follow Studio; light/dark → force that chrome */
    const wantLight = p.chrome === "light" || (p.chrome !== "dark" && !appDark);
    pill.classList.toggle("light", wantLight);
    pill.classList.toggle("dark", !wantLight);
  }
}
/* ---------- configurable keybinds ---------- */
const KEY_DEFAULTS = { save:"Ctrl+S", undo:"Ctrl+Z", redo:"Ctrl+Shift+Z", delete:"Backspace", duplicate:"Ctrl+D", preview:"Ctrl+P", live:"Ctrl+E", search:"Ctrl+F", exit:"Escape" };
const KEY_LABELS = { save:"Save revision", undo:"Undo", redo:"Redo", delete:"Delete selected section", duplicate:"Duplicate selected section", preview:"Open interactive preview", live:"Toggle live edit", search:"Find on canvas", exit:"Exit / deselect" };
function keybinds(){
  try { return Object.assign({}, KEY_DEFAULTS, JSON.parse(localStorage.getItem("bd_keybinds")) || {}); }
  catch { return Object.assign({}, KEY_DEFAULTS); }
}
function goAccountShortcuts(){
  if (!DEMO && !QA_MODE && typeof BD_ACCOUNT !== "undefined") BD_ACCOUNT.go("shortcuts");
  else { sessionStorage.setItem("bd_acct_scroll", "shortcuts"); go("account"); }
}
function kbdChips(combo){ return combo.split("+").map(k => `<kbd>${esc(k)}</kbd>`).join(""); }
function validateKeyCombo(combo, keyId){
  if (combo === "Ctrl+K") return "Ctrl K is reserved for the command menu.";
  const parts = combo.split("+");
  const key = parts[parts.length - 1];
  const needsMod = !["Backspace","Delete","Escape"].includes(key);
  if (needsMod && !parts.some(p => ["Ctrl","Meta","Alt","Shift"].includes(p))) return "Include Ctrl, Meta, Alt, or Shift.";
  const clash = Object.entries(keybinds()).find(([k, v]) => v === combo && k !== keyId);
  if (clash) return `Already bound to ${KEY_LABELS[clash[0]]}.`;
  return "";
}
function mountAccountShortcuts(host, opts){
  host.id = "acct-shortcuts";
  host.className = "card acct-shortcuts reveal";
  const kb = keybinds();
  const head = opts && opts.head === false
    ? ""
    : `<h2>Keyboard shortcuts</h2><p class="c-sub">Configure builder shortcuts — synced to this browser via local storage.</p>`;
  host.innerHTML = `${head}
    <table class="sc-table acct-kb-table"><tbody id="acct-kb-rows"></tbody></table>
    <div class="row acct-kb-foot"><button type="button" class="btn btn-ghost btn-sm" id="acct-kb-reset">Reset all to defaults</button>
    <button type="button" class="link-btn" id="acct-kb-export">Export shortcuts</button>
    <button type="button" class="link-btn" id="acct-kb-import">Import shortcuts</button></div>`;
  const tbody = host.querySelector("#acct-kb-rows");
  let captureId = null;
  function paintRows(){
    const cur = keybinds();
    tbody.innerHTML = Object.entries(KEY_LABELS).map(([id, label]) => {
      const editing = captureId === id;
      return `<tr data-kb-id="${id}"><td>${esc(label)}</td><td class="acct-kb-val">${editing
        ? `<input class="acct-kb-capture" aria-label="Press a key combination" placeholder="Press a key combination…" readonly>`
        : kbdChips(cur[id])}</td><td>${editing ? `<button type="button" class="btn btn-ghost btn-sm acct-kb-cancel">Cancel</button>` : `<button type="button" class="btn btn-ghost btn-sm acct-kb-edit">Edit</button>`}</td></tr>`;
    }).join("");
    if (captureId){
      const row = tbody.querySelector(`tr[data-kb-id="${captureId}"]`);
      const inp = row.querySelector(".acct-kb-capture");
      inp.focus();
      const onKey = ev => {
        ev.preventDefault(); ev.stopPropagation();
        if (["Control","Shift","Alt","Meta"].includes(ev.key)) return;
        const combo = comboOf(ev);
        const err = validateKeyCombo(combo, captureId);
        document.removeEventListener("keydown", onKey, true);
        if (err){ row.insertAdjacentHTML("afterend", `<tr class="acct-kb-warn"><td colspan="3">${esc(err)}</td></tr>`); setTimeout(() => tbody.querySelector(".acct-kb-warn")?.remove(), 3000); }
        else { const k = keybinds(); k[captureId] = combo; localStorage.setItem("bd_keybinds", JSON.stringify(k)); syncAccountPrefsNow(); toast("Shortcut updated"); }
        captureId = null; paintRows();
      };
      document.addEventListener("keydown", onKey, true);
      row.querySelector(".acct-kb-cancel").onclick = () => { captureId = null; paintRows(); };
    }
    tbody.querySelectorAll(".acct-kb-edit").forEach(b => b.onclick = () => { captureId = b.closest("tr").dataset.kbId; paintRows(); });
  }
  paintRows();
  host.querySelector("#acct-kb-reset").onclick = () => { localStorage.removeItem("bd_keybinds"); syncAccountPrefsNow(); toast("Shortcuts reset"); paintRows(); };
  host.querySelector("#acct-kb-export").onclick = () => { navigator.clipboard?.writeText(JSON.stringify(keybinds(), null, 2)); toast("Copied to clipboard"); };
  host.querySelector("#acct-kb-import").onclick = async () => {
    const raw = await promptModal("Import shortcuts.", "Paste the exported shortcuts JSON below.", { placeholder: "{\u2026}", confirmLabel: "Import" });
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (!Object.keys(KEY_DEFAULTS).every(k => parsed[k])) return toast("Invalid shortcuts file — missing keys.");
      localStorage.setItem("bd_keybinds", JSON.stringify(parsed)); syncAccountPrefsNow(); toast("Shortcuts imported"); paintRows();
    } catch { toast("Could not parse JSON."); }
  };
}
async function showAIBuildModal(mode = 'full'){
  if (!current) return;
  const editTarget = mode === 'edit' ? selBlock() : null;
  if (mode === 'edit' && !editTarget) {
    toast('Select one section to edit with Pulse.');
    return;
  }

  const titles = {
    full: 'Pulse',
    section: 'Generate Section',
    edit: 'Edit with AI'
  };

  const placeholders = {
    full: 'Describe your business and what pages you need.\n\nExample: "A modern SaaS company for project management software with pricing, features, and testimonials pages"',
    section: 'Describe the section you want.\n\nExample: "A hero section for a fitness app with a bold headline and app store buttons"',
    edit: 'Describe what you want to change.\n\nExample: "Make the headline more exciting" or "Add a third pricing tier"'
  };

  const html = `
    <div class="modal-head">
      <h2>${titles[mode] || 'AI Builder'}</h2>
      <button class="modal-close" data-modal-close aria-label="Close">&times;</button>
    </div>
    <div class="modal-body">
      <p class="modal-sub">Available on Build, Forge, and Architect plans. Pulse asks questions when details are missing.</p>

      <div class="field">
        <label for="ai-prompt">What do you want to build?</label>
        <textarea id="ai-prompt" rows="4" placeholder="${esc(placeholders[mode] || '')}"></textarea>
      </div>

      <div id="ai-brief-clarify" hidden>
        <div class="ok-msg" id="ai-brief-questions" role="status"></div>
        <div class="field">
          <label for="ai-brief-answers">Your answers</label>
          <textarea id="ai-brief-answers" rows="4" placeholder="Answer Pulse's questions, or say build with what you have."></textarea>
        </div>
      </div>

      <div class="error-msg" id="ai-build-error" role="alert"></div>
      <div class="ok-msg" id="ai-build-progress" role="status" hidden></div>

      <div class="modal-actions">
        <button class="btn" data-modal-close>Cancel</button>
        <button class="btn btn-cta" id="ai-build-generate">
          ${lu("sparkles")}
          Generate
        </button>
      </div>
    </div>
  `;

  const m = openModal(html);
  icons();
  const briefTurns = [];

  m.querySelector('[data-modal-close]').onclick = closeModal;
  m.querySelectorAll('[data-modal-close]').forEach(b => b.onclick = closeModal);

  m.querySelector('#ai-build-generate').onclick = async () => {
    const clarifyWrap = m.querySelector('#ai-brief-clarify');
    const questionsEl = m.querySelector('#ai-brief-questions');
    const answersEl = m.querySelector('#ai-brief-answers');
    const clarifying = !!(clarifyWrap && !clarifyWrap.hidden);
    const prompt = clarifying
      ? (answersEl?.value || '').trim()
      : (m.querySelector('#ai-prompt')?.value || '').trim();
    const err = m.querySelector('#ai-build-error');
    const progress = m.querySelector('#ai-build-progress');

    if (!prompt) {
      if (err) {
        err.textContent = clarifying
          ? 'Answer Pulse\'s questions, or say build with what you have'
          : 'Enter a description of what you want to build';
        err.hidden = false;
      }
      return;
    }

    try {
      if (err) err.hidden = true;
      if (progress) {
        progress.textContent = clarifying ? 'Checking your answers...' : 'Understanding your brief...';
        progress.hidden = false;
      }

      const btn = m.querySelector('#ai-build-generate');
      if (btn) {
        btn.disabled = true;
        btn.innerHTML = `${lu("loader-circle")} Generating...`;
        icons();
      }

      if (!clarifying) briefTurns.length = 0;
      briefTurns.push({ role: 'user', text: prompt });
      if (clarifying && answersEl) answersEl.value = '';

      const clarified = await clarifyAIBrief(composeAgentBrief(briefTurns), (message) => {
        if (progress) progress.textContent = message;
      });

      if (!clarified.ready) {
        const msg = clarified.message || 'I need a few details before I build.';
        const qHtml = clarified.questions.length
          ? `<ol>${clarified.questions.map(q => `<li>${esc(q)}</li>`).join('')}</ol>`
          : '';
        briefTurns.push({
          role: 'luna',
          text: [msg, ...clarified.questions].filter(Boolean).join('\n')
        });
        if (questionsEl) {
          questionsEl.innerHTML = `<p>${esc(msg)}</p>${qHtml}<p>Answer below, or say build with what you have.</p>`;
        }
        if (clarifyWrap) clarifyWrap.hidden = false;
        if (progress) progress.hidden = true;
        return;
      }

      briefTurns.push({
        role: 'luna',
        text: clarified.message || 'Building your site now.'
      });
      if (clarifyWrap) clarifyWrap.hidden = true;

      const builtPrompt = composeAgentBrief(briefTurns);
      let data;
      if (mode === 'full') {
        if (progress) progress.textContent = 'Planning pages...';
        const pages = await generateAISitePages(builtPrompt, (message, done, total) => {
          if (progress) progress.textContent = total ? `${message} Page ${done} of ${total}.` : message;
        });
        data = { output: { pages } };
      } else {
        if (progress) progress.textContent = 'Generating section...';
        const payload = {
          action: 'generate',
          mode,
          prompt: builtPrompt,
          profile: lumenNormalizeProfile(lumenPrefs().modelProfile)
        };
        if (mode === 'edit') {
          payload.existing_block = {
            type: editTarget.type,
            props: Object.fromEntries(
              Object.entries(editTarget.props || {}).filter(([key]) => !key.startsWith('_'))
            )
          };
        }
        data = await openAIRequest(payload);
      }

      closeModal();

      // Handle the result based on mode
      if (mode === 'full' && data.output.pages) {
        // Replace all pages
        await loadStudioBlocks();
        current.data.tokens = agentLunaTokens();
        current.data.pages = data.output.pages.map(page => {
          if (!page.id) page.id = newId();
          if (!page.seo) page.seo = { title: '', description: '', ogImage: '' };
          if (!page.type) page.type = 'page';
          return page;
        });

        markDirty();
        await api.saveProject(current);
        paint();
        renderCanvas();
        renderPagesList();
        toast(`Generated ${data.output.pages.length} page${data.output.pages.length === 1 ? '' : 's'}`, { kind: 'success' });
      } else if ((mode === 'section' || mode === 'edit') && data.output.block) {
        const page = activePage();
        if (!page) return;

        const block = data.output.block;
        if (!block.id) block.id = newId();
        if (mode === 'edit') {
          const index = page.blocks.findIndex(item => item.id === editTarget.id);
          if (index < 0) throw new Error('The selected section is no longer available.');
          block.id = editTarget.id;
          page.blocks.splice(index, 1, block);
          selected = { k:'b', id:block.id };
        } else {
          page.blocks.push(block);
        }
        markDirty();
        await api.saveProject(current);
        renderCanvas();
        toast(mode === 'edit' ? 'Section updated' : 'Section generated', { kind: 'success' });
      }
    } catch (e) {
      if (progress) progress.hidden = true;
      if (err) {
        err.textContent = humanErr(e);
        err.hidden = false;
      }
    } finally {
      const btn = m.querySelector('#ai-build-generate');
      const wrap = m.querySelector('#ai-brief-clarify');
      const stillClarifying = !!(wrap && !wrap.hidden);
      if (btn) {
        btn.disabled = false;
        btn.innerHTML = stillClarifying
          ? `${lu("sparkles")} Continue`
          : `${lu("sparkles")} Generate`;
        icons();
      }
    }
  };
}

function comboOf(e){
  const ps = [];
  if (e.ctrlKey || e.metaKey) ps.push("Ctrl");
  if (e.shiftKey) ps.push("Shift");
  if (e.altKey) ps.push("Alt");
  let k = e.key;
  if (k === " ") k = "Space";
  if (k.length === 1) k = k.toUpperCase();
  ps.push(k);
  return ps.join("+");
}

function syncCurrent(){
  if (activeId) current = projects.find(p => p.id === activeId) || current;
}
async function pageBuilder(el){
  await loadStudioBlocks();
  syncCurrent();
  if (!current || !Array.isArray(current.data?.pages)){ go("sites"); return; }
  current.data = migrateData(current.data, current.name);
  ensureBrandAccentAligned(current);
  if (!current.data.pages.length) current.data.pages.push(PAGE_TEMPLATES.blank.make("Home"));
  if (!current.data.pages.some(x => x.id === curPageId)) curPageId = (current.data.pages.find(x => !x.slug) || current.data.pages[0]).id;
  selected = null; clearMultiSel(); dirty = false; past = []; future = [];
  previewMode = false;
  liveEdit = false;
  try { sessionStorage.setItem("bd_builder_live", "0"); } catch {}
  document.body.classList.remove("builder-preview", "builder-live");
  builderPanel = sessionStorage.getItem("bd_builder_panel") || "sections";
  if (!["sections","layers","pages"].includes(builderPanel)) builderPanel = "sections";
  lastSavedSnap = JSON.stringify({ tokens: current.data.tokens, seo: current.data.seo, shared: current.data.shared, pages: current.data.pages });
  el.innerHTML = `
  <div class="builder immersive">
    <div class="b-pill liquid-glass light" id="b-pill">
      <div class="bp-scroll">
        <div class="bp-group">
          <button type="button" class="bp-ic" id="pb-exit" title="Exit builder">${lu("x")}</button>
          <button type="button" class="bp-crumb" id="pb-crumb" title="Switch page"><span id="pb-site">${esc(current.name)}</span><em>/</em><span id="pb-page">Home</span>${lu("chevron-down","lu14")}</button>
        </div>
        <span class="bp-div" aria-hidden="true"></span>
        <div class="bp-group">
          <button type="button" class="bp-ic" id="pb-search" title="Find on canvas (${esc(keybinds().search)})">${lu("search")}</button>
          <button type="button" class="bp-ic bp-sm-hide" id="pb-history" title="History & revisions">${lu("history")}</button>
          <button type="button" class="bp-ic" id="btn-undo" title="Undo (${esc(keybinds().undo)})" disabled>${lu("undo-2")}</button>
          <button type="button" class="bp-ic bp-xs-hide" id="btn-redo" title="Redo (${esc(keybinds().redo)})" disabled>${lu("redo-2")}</button>
        </div>
        <span class="bp-div bp-sm-hide" aria-hidden="true"></span>
        <div class="bp-group">
          <button type="button" class="bp-ic" id="pb-add" title="Add section">${lu("plus")}</button>
          <button type="button" class="bp-ic bp-sm-hide" id="pb-ai-build" title="AI site builder">${lu("bot")}</button>
          <button type="button" class="bp-ic${liveEdit ? " on" : ""}" id="pb-live" title="Live edit (${esc(keybinds().live)}) — produced site look">${lu("sparkles")}</button>
          <button type="button" class="bp-ic bp-sm-hide" id="pb-preview" title="Open interactive preview (${esc(keybinds().preview)})">${lu("play")}</button>
        </div>
      </div>
      <div class="bp-trail">
        <span class="bp-div bp-div--meta bp-md-hide" aria-hidden="true"></span>
        <div class="bp-group bp-group--meta">
          <button type="button" class="bp-label bp-sm-hide" id="pb-share" title="Share">${lu("share")}<span>Share</span></button>
          <button type="button" class="bp-save" id="pb-save" title="Save (${esc(keybinds().save)})">${lu("save")}<span class="save-state" id="save-state">Saved</span></button>
          <button type="button" class="bp-ic bp-sm-hide" id="pb-bset" title="Builder settings">${lu("settings-2")}</button>
          <button type="button" class="bp-ic bp-more" id="pb-more" title="More tools" aria-label="More tools" aria-haspopup="menu">${lu("ellipsis")}</button>
        </div>
        <span class="bp-div bp-div--pub bp-xs-hide" aria-hidden="true"></span>
        <button type="button" class="bp-publish" id="btn-publish" title="Publish">${lu("upload","lu14")}<span class="bp-publish-label">Publish</span>${lu("chevron-down","lu14")}</button>
      </div>
    </div>
    <div class="b-body">
      <aside class="panel panel-left liquid-glass" id="panel-left">
        <nav class="lp-tabs" role="tablist">
          <button type="button" class="lp-tab" id="lp-tab-sections" data-lp="sections" role="tab">Library</button>
          <button type="button" class="lp-tab" id="lp-tab-layers" data-lp="layers" role="tab">Layers</button>
          <button type="button" class="lp-tab" id="lp-tab-pages" data-lp="pages" role="tab">Pages</button>
        </nav>
        <div class="lp-panels">
          <div class="lp-panel" id="ltab-pages"><div id="pages-list"></div><button class="btn btn-ghost btn-sm btn-block" id="pg-add2" style="margin-top:8px">${lu("plus")} Add page</button><p class="panel-tip">The house icon marks your home page. Dots open page options.</p></div>
          <div class="lp-panel" id="ltab-sections" hidden>
            <div class="lib-filter">
              <div class="lib-search-wrap">
                <span class="lib-search-ico" aria-hidden="true">${lu("search","lu14")}</span>
                <input id="lib-search" type="search" placeholder="Find a section…" autocomplete="off" aria-label="Find a section" aria-autocomplete="list" aria-controls="lib-suggest">
                <div id="lib-suggest" class="lib-suggest" role="listbox" hidden></div>
              </div>
              <div id="lib-chips" class="lib-chips" role="tablist" aria-label="Filter by category"></div>
            </div>
            <div id="block-library"></div>
            <p class="panel-tip">Star favorites, reuse Custom sections you make, or right‑click the canvas to save one. Drag onto the canvas or click to add.</p>
          </div>
          <div class="lp-panel" id="ltab-layers" hidden>
            <div id="layers-list" class="layers-list" aria-label="Page layers"></div>
            <p class="panel-tip">Select, reorder, hide, or inspect every section on this page.</p>
          </div>
        </div>
      </aside>
      <main class="canvas-wrap" id="canvas-wrap">
        <div class="page-frame" id="page-frame">
          <div class="page-chrome" id="page-chrome"><i></i><i></i><i></i><span id="chrome-url"></span></div>
          <div class="page-sheet" id="page-sheet"></div>
        </div>
      </main>
      <aside class="panel panel-right liquid-glass" id="inspector"></aside>
      <aside class="debug-panel" id="debug-panel"></aside>
    </div>
    <button type="button" class="bp-exit-preview" id="pb-exit-preview" hidden>
      <span class="bp-exit-label">${lu("pencil-ruler")} Exit preview</span>
    </button>
    <div class="live-chrome" id="live-chrome" hidden>
      <div class="live-chrome-inner">
        <button type="button" class="lc-done" id="lc-done" title="Exit live edit">${lu("check","lu14")} Done</button>
        <button type="button" class="lc-crumb" id="lc-page" title="Switch page"><span id="lc-page-label">Home</span>${lu("chevron-down","lu14")}</button>
        <span class="lc-div" aria-hidden="true"></span>
        <button type="button" class="lc-ic" id="lc-undo" title="Undo" disabled>${lu("undo-2")}</button>
        <button type="button" class="lc-ic" id="lc-redo" title="Redo" disabled>${lu("redo-2")}</button>
        <button type="button" class="lc-ic" id="lc-save" title="Save">${lu("save")}</button>
        <span class="lc-div" aria-hidden="true"></span>
        <div class="lc-bp" id="lc-bp" role="group" aria-label="Device width">
          <button type="button" class="lc-ic" data-lbp="1440" title="Desktop">${lu("monitor")}</button>
          <button type="button" class="lc-ic" data-lbp="768" title="Tablet">${lu("tablet")}</button>
          <button type="button" class="lc-ic" data-lbp="480" title="Phone">${lu("smartphone")}</button>
        </div>
        <span class="lc-div" aria-hidden="true"></span>
        <button type="button" class="lc-add" id="lc-add" title="Add section">${lu("plus","lu14")} Section</button>

      </div>
    </div>
    <div class="live-sel-bar" id="live-sel-bar" hidden></div>
    <div class="mb-veil" id="mb-veil" hidden></div>
    <nav class="mb-bar" id="mb-bar" aria-label="Builder tools">
      <button data-mb="pages">${lu("files")}<span>Pages</span></button>
      <button data-mb="library">${lu("blocks")}<span>Library</span></button>
      <button class="mb-add" id="mb-add" aria-label="Insert section">${lu("plus")}</button>
      <button data-mb="inspector">${lu("sliders-horizontal")}<span>Edit</span></button>
    </nav>
  </div>`;
  bindBuilder();
  switchBuilderPanel(builderPanel);
  syncBuilderPreviewShell();
  paintPageSwitch(); renderLibrary(); renderCanvas(); renderInspector(); renderLayers(); renderPagesList(); applyFrame(); applyBprefs();
  icons();
  offerDraftRestore();
  if (!localStorage.getItem("bd_tour_done") && !sessionStorage.getItem("bd_tour_offered")){
    sessionStorage.setItem("bd_tour_offered", "1");
    setTimeout(startTour, 500);
  }
}
const setSaveState = s => {
  const el = $("#save-state");
  if (!el) return;
  el.className = "save-state" + (s === "saved" ? "" : " " + s);
  el.textContent = s === "saved" ? "Saved" : s === "saving" ? "Saving…" : "Unsaved";
};
/* ---------- crash recovery drafts ----------
   Every edit shadows the working copy into localStorage (debounced).
   A clean save clears it; a crashed tab leaves it behind, and the next
   builder session offers to restore it. Oversized sites skip silently. */
const draftKey = id => "bd_draft_" + id;
let _draftT = null;
function writeDraft(){
  if (!current) return;
  clearTimeout(_draftT);
  _draftT = setTimeout(() => {
    try {
      const body = JSON.stringify({ t: Date.now(), data: current.data });
      if (body.length < 2.5e6) localStorage.setItem(draftKey(current.id), body);
    } catch { /* quota — recovery is best-effort */ }
  }, 800);
}
function clearDraft(id){ try { localStorage.removeItem(draftKey(id)); } catch {} }
function readDraft(id){
  try { const d = JSON.parse(localStorage.getItem(draftKey(id)) || "null"); return d && d.data ? d : null; }
  catch { return null; }
}
function offerDraftRestore(){
  if (!current) return;
  const d = readDraft(current.id);
  if (!d) return;
  const newer = !current.updated_at || d.t > new Date(current.updated_at).getTime() + 1500;
  if (!newer){ clearDraft(current.id); return; }
  const m = openModal(`<h2>Unsaved work recovered.</h2>
    <p class="sub">This browser has edits from ${fmtWhen(new Date(d.t).toISOString())} that were never saved to your account — likely from a closed or crashed tab. Nothing has been lost.</p>
    <div class="row">
      <button class="btn btn-ghost btn-sm" data-x="discard">Discard draft</button>
      <button class="btn btn-cta btn-sm" data-x="restore" autofocus>Restore my work</button>
    </div>`);
  m.querySelector('[data-x="discard"]').onclick = () => { clearDraft(current.id); closeModal(); toast("Draft discarded — the saved version stays as-is."); };
  m.querySelector('[data-x="restore"]').onclick = () => {
    current.data = d.data;
    clearDraft(current.id);
    dirty = true; setSaveState("dirty");
    closeModal();
    paintPageSwitch(); renderCanvas(); renderInspector(); renderLayers(); renderPagesList(); icons();
    toast("Restored — press Save to keep it.", { kind: "success" });
  };
}
function markDirty(){
  dirty = true; setSaveState("dirty");
  writeDraft();
  if (bprefs().verbose === "yes") blog("edit", "Change on “" + (activePage()?.name || "?") + "”");
  clearTimeout(autosaveT);
  const iv = bprefs().autosave;
  if (iv !== "off") autosaveT = setTimeout(() => saveNow(true), (parseInt(iv, 10) || 60) * 1000);
}
const snapshot = () => JSON.stringify({ tokens: current.data.tokens, seo: current.data.seo, shared: current.data.shared, pages: current.data.pages });
function restoreSnap(json){
  const s = JSON.parse(json);
  Object.assign(current.data, s);
  if (!current.data.pages.some(x => x.id === curPageId)) curPageId = current.data.pages[0]?.id;
  selected = null;
}
function commit(){ past.push(snapshot()); if (past.length > 60) past.shift(); future = []; syncUndo(); }
function undo(){ if (!past.length) return; future.push(snapshot()); restoreSnap(past.pop()); afterHistory(); }
function redo(){ if (!future.length) return; past.push(snapshot()); restoreSnap(future.pop()); afterHistory(); }
function afterHistory(){ syncUndo(); markDirty(); paintPageSwitch(); renderCanvas(); renderInspector(); renderLayers(); renderPagesList(); icons(); }
function syncUndo(){
  const disU = !past.length, disR = !future.length;
  [["#btn-undo","#btn-redo"],["#lc-undo","#lc-redo"]].forEach(([uu, rr]) => {
    const u = $(uu), r = $(rr);
    if (u) u.disabled = disU;
    if (r) r.disabled = disR;
  });
}
async function saveNow(auto){
  if (!current) return;
  setSaveState("saving");
  try {
    if (!auto){ pushRevisionFor(current, "SAVE"); log(current.id, "saved", `Revision saved for “${current.name}”`); }
    await api.saveProject(current);
    dirty = false; setSaveState("saved");
    clearTimeout(_draftT); clearDraft(current.id);
    lastSavedSnap = snapshot();
    if (!auto) toast("Saved");
  } catch (e) { setSaveState("dirty"); if (!auto) toast(humanErr(e, "Save failed — your changes are still on this device."), { kind: "error" }); }
}
/* ---------- exit (with unsaved-changes prompt) ---------- */
function exitBuilder(dest){
  dest = dest || "dashboard";
  if (!dirty) return go(dest);
  const m = openModal(`<button class="modal-x" data-x="c" title="Stay in the builder">${lu("x")}</button>
    <h2>You have unsaved changes.</h2>
    <p class="sub">Your latest edits haven’t been saved to your account.</p>
    <div class="row">
      <button class="btn btn-danger btn-sm" data-x="discard">Exit without saving</button>
      <button class="btn btn-cta btn-sm" data-x="save">Save and exit</button>
    </div>`);
  m.querySelector('[data-x="c"]').onclick = closeModal;
  m.querySelector('[data-x="save"]').onclick = async () => { closeModal(); await saveNow(false); go(dest); };
  m.querySelector('[data-x="discard"]').onclick = () => {
    closeModal();
    clearTimeout(autosaveT);
    if (lastSavedSnap) restoreSnap(lastSavedSnap);
    dirty = false;
    go(dest);
  };
}
/* ---------- frame: breakpoints + preview mode ---------- */
function applyFrame(){
  const frame = $("#page-frame"), wrap = $("#canvas-wrap");
  if (!frame) return;
  const chrome = $("#page-chrome");
  if (chrome) chrome.style.display = "";
  const sheet = $("#page-sheet");
  if (sheet){ sheet.style.height = ""; sheet.style.overflowY = ""; }

  const safeBp = Number.isFinite(+bp) && +bp > 0 ? +bp : 1440;
  const avail = wrap ? Math.max(320, wrap.clientWidth - 40) : safeBp;
  /* True-width device preview — never scale (scale blurs type).
     Live/preview use the exact breakpoint; structure mode fits the canvas. */
  const frameW = (liveEdit || previewMode) ? safeBp : Math.min(safeBp, avail);

  frame.style.width = Math.round(frameW) + "px";
  frame.className = "page-frame";
  frame.style.transform = "";
  frame.style.transformOrigin = "";
  frame.style.marginBottom = "";
  frame.style.maxWidth = "";
  if (wrap){
    wrap.style.overflowX = frameW > wrap.clientWidth + 4 ? "auto" : "";
  }
}
window.addEventListener("resize", () => { if (page === "builder-edit") applyFrame(); });
function syncBuilderPreviewShell(){
  document.body.classList.toggle("builder-preview", previewMode);
  document.body.classList.toggle("builder-live", liveEdit && page === "builder-edit" && !previewMode);
  updatePreviewChrome();
}
function setLiveEdit(on){
  const next = !!on;
  if (next && previewMode){
    previewMode = false;
    const pb = $("#pb-preview"); if (pb) pb.classList.remove("on");
  }
  liveEdit = next;
  try { sessionStorage.setItem("bd_builder_live", liveEdit ? "1" : "0"); } catch {}
  selected = null;
  clearMultiSel();
  paintSelection();
  if (liveEdit){
    bp = 1440;
  }
  syncBuilderPreviewShell();
  const b = $("#pb-live"); if (b) b.classList.toggle("on", liveEdit);
  renderCanvas();
  applyFrame();
  icons();
  toast(liveEdit
    ? "Live edit — produced site look. Click text to change it; Edit opens the inspector"
    : "Block editor — rearrange sections; open Live edit for the produced look");
}
function updatePreviewChrome(){
  const btn = $("#pb-exit-preview");
  const liveBar = $("#live-chrome");
  if (previewMode){
    if (btn){
      btn.hidden = false;
      btn.dataset.mode = "preview";
      const label = btn.querySelector(".bp-exit-label") || btn;
      label.innerHTML = `${lu("pencil-ruler")} Exit preview`;
    }
    if (liveBar) liveBar.hidden = true;
    const bar = $("#live-sel-bar"); if (bar) bar.hidden = true;
    $("#inspector")?.classList.remove("live-open");
  } else if (liveEdit){
    if (btn){ btn.hidden = true; btn.dataset.mode = ""; }
    if (liveBar) liveBar.hidden = false;
    syncLiveChrome();
    syncLiveSelBar();
    syncLiveInspector();
  } else {
    if (btn){ btn.hidden = true; btn.dataset.mode = ""; }
    if (liveBar) liveBar.hidden = true;
    $("#inspector")?.classList.remove("live-open");
    syncLiveSelBar();
  }
  icons();
}
function syncLiveChrome(){
  const label = $("#lc-page-label");
  if (label) label.textContent = activePage()?.name || "—";
  syncUndo();
  syncLiveBp();
}
function syncLiveBp(){
  $$("#lc-bp [data-lbp]").forEach(b => b.classList.toggle("on", +b.dataset.lbp === bp));
}
function syncLiveInspector(){
  const insp = $("#inspector");
  if (!insp) return;
  if (!liveEdit || previewMode || !selected || !selBlock()){
    insp.classList.remove("live-open");
  }
  const editBtn = $("#live-sel-bar [data-la=insp]");
  if (editBtn) editBtn.classList.toggle("on", insp.classList.contains("live-open"));
}
function syncLiveSelBar(){
  const bar = $("#live-sel-bar");
  if (!bar) return;
  const multi = selectedPageBlocks();
  /* Inspector owns the chrome when open — avoid a second floating HUD over the page. */
  if (liveEdit && !previewMode && $("#inspector")?.classList.contains("live-open")){
    bar.hidden = true;
    return;
  }
  if (previewMode || !selected){
    bar.hidden = true;
    return;
  }
  if (multi.length > 1){
    bar.hidden = false;
    bar.innerHTML = `
      <span class="lsb-name">${multi.length} sections</span>
      <button type="button" class="lsb-btn" data-la="save" title="Save as custom section">${lu("puzzle","lu14")} Save as custom</button>
      <button type="button" class="lsb-ic" data-la="clear" title="Clear selection">${lu("x")}</button>`;
    const first = document.querySelector(`.blk[data-bid="${multi[0].id}"]`);
    const place = () => {
      if (!first){ bar.hidden = true; return; }
      const r = first.getBoundingClientRect();
      const bw = bar.offsetWidth || 280;
      const chromeBottom = ($("#live-chrome")?.getBoundingClientRect().bottom || 0) + 10;
      const minTop = liveEdit ? Math.max(72, chromeBottom) : 64;
      bar.style.left = Math.max(12, Math.min(r.left + r.width / 2 - bw / 2, innerWidth - bw - 12)) + "px";
      let top = r.top - 52;
      if (top < minTop) top = Math.min(innerHeight - 56, r.bottom + 10);
      if (top < minTop) top = minTop;
      bar.style.top = top + "px";
    };
    place();
    requestAnimationFrame(place);
    bar.querySelectorAll("[data-la]").forEach(btn => {
      btn.onclick = e => {
        e.stopPropagation();
        if (btn.dataset.la === "clear"){
          selected = null; clearMultiSel();
          paintSelection(); renderInspector(); renderLayers(); icons();
          return;
        }
        if (btn.dataset.la === "save") createCustomSectionFlow({ fromBlocks: selectedPageBlocks() });
      };
    });
    icons();
    return;
  }
  const el = selected.k === "s"
    ? document.querySelector(`.blk[data-shared="${selected.slot}"]`)
    : document.querySelector(`.blk[data-bid="${selected.id}"]`);
  const blk = selBlock();
  if (!el || !blk){ bar.hidden = true; return; }
  const def = BLOCKS[blk.type];
  const name = def?.name || "Section";
  const canMove = selected.k === "b";
  const shared = selected.k === "s";
  bar.hidden = false;
  bar.classList.toggle("live-sel-bar--shared", shared);
  bar.innerHTML = `
    <span class="lsb-ico" aria-hidden="true">${lu(def?.icon || "box","lu14")}</span>
    <span class="lsb-name">${esc(name)}</span>
    ${shared ? `<span class="lsb-badge">Shared</span>` : ""}
    ${canMove ? `<span class="lsb-div"></span>
      <button type="button" class="lsb-ic" data-la="up" title="Move up">${lu("arrow-up")}</button>
      <button type="button" class="lsb-ic" data-la="down" title="Move down">${lu("arrow-down")}</button>
      <button type="button" class="lsb-ic" data-la="dup" title="Duplicate">${lu("copy")}</button>
      <button type="button" class="lsb-ic" data-la="hide" title="${blk.hidden ? "Show" : "Hide"}">${lu(blk.hidden ? "eye" : "eye-off")}</button>
      <button type="button" class="lsb-ic lsb-danger" data-la="del" title="Delete">${lu("trash-2")}</button>
      <span class="lsb-div"></span>
      ${liveEdit ? `<button type="button" class="lsb-btn" data-la="insp" title="Edit properties">${lu("sliders-horizontal","lu14")} Edit</button>` : ""}
      <button type="button" class="lsb-btn" data-la="save" title="Save as custom section">${lu("puzzle","lu14")} Save</button>` : ""}
    ${shared ? `<span class="lsb-div"></span>
      <button type="button" class="lsb-btn" data-la="insp" title="Edit ${esc(name)}">${lu("sliders-horizontal","lu14")} Edit</button>
      <button type="button" class="lsb-ic lsb-danger" data-la="unshare" title="Remove shared ${esc(selected.slot)}">${lu("x")}</button>` : ""}`;
  const place = () => {
    const r = el.getBoundingClientRect();
    const bw = bar.offsetWidth || 280;
    const chromeBottom = ($("#live-chrome")?.getBoundingClientRect().bottom || 0) + 10;
    const minTop = liveEdit ? Math.max(72, chromeBottom) : 64;
    bar.style.left = Math.max(12, Math.min(r.left + r.width / 2 - bw / 2, innerWidth - bw - 12)) + "px";
    let top = r.top - 52;
    if (top < minTop) top = Math.min(innerHeight - 56, r.bottom + 10);
    if (top < minTop) top = minTop;
    bar.style.top = top + "px";
  };
  place();
  requestAnimationFrame(place);
  bar.querySelectorAll("[data-la]").forEach(btn => {
    btn.onclick = e => {
      e.stopPropagation();
      const a = btn.dataset.la;
      if (a === "insp"){
        openLiveInspector("content");
        return;
      }
      if (a === "save"){
        const blocks = selectedPageBlocks();
        if (blocks.length > 1) createCustomSectionFlow({ fromBlocks: blocks });
        else if (selected?.k === "b") createCustomSectionFlow({ fromBlock: selBlock() });
        return;
      }
      if (a === "unshare" && selected?.k === "s"){
        commit(); current.data.shared[selected.slot] = null; selected = null; clearMultiSel();
        markDirty(); renderCanvas(); renderInspector(); renderLayers(); icons();
        return;
      }
      if (selected?.k !== "b") return;
      const i = activePage().blocks.findIndex(b => b.id === selected.id);
      if (a === "del" && bprefs().confirmDelete === "yes"){
        confirmModal("Delete this section?", "You can undo until you leave the builder.", "Delete", () => blockAction("del", i));
      } else blockAction(a, i);
    };
  });
  icons();
}
function switchBuilderPanel(which){
  if (which === "structure") which = "layers";
  if (which === "library") which = "sections";
  builderPanel = which;
  sessionStorage.setItem("bd_builder_panel", which);
  ["pages", "sections", "layers"].forEach(k => {
    $(`#lp-tab-${k}`)?.classList.toggle("on", k === which);
    const panel = $(`#ltab-${k}`);
    if (panel) panel.hidden = k !== which;
  });
  if (which === "pages") renderPagesList();
  if (which === "sections") renderLibrary();
  if (which === "layers") renderLayers();
  icons();
}
function setPreviewMode(on){
  const next = !!on;
  if (next && liveEdit){
    liveEdit = false;
    try { sessionStorage.setItem("bd_builder_live", "0"); } catch {}
    const lb = $("#pb-live"); if (lb) lb.classList.remove("on");
  }
  previewMode = next;
  syncBuilderPreviewShell();
  const pb = $("#pb-preview"); if (pb) pb.classList.toggle("on", next);
  selected = null; clearMultiSel(); paintSelection();
  renderCanvas();
  applyFrame();
  icons();
  toast(next ? "Preview — read-only produced look. Exit to keep editing." : (liveEdit ? "Live edit" : "Block editor — select a section to edit"));
}
/* ---------- pill popovers ---------- */
function pillPop(btn, html, wide){
  const m = $("#ctx-menu");
  /* Same trigger again → close (stopPropagation otherwise traps the menu open) */
  if (btn && m && !m.hidden && _pillPopBtn === btn){
    closeCtx();
    return null;
  }
  openCtx(0, 0, html);
  m.classList.toggle("wide", !!wide);
  _pillPopBtn = btn || null;
  const r = btn.getBoundingClientRect();
  const w = wide ? 360 : 230;
  m.style.left = Math.max(8, Math.min(r.left + r.width / 2 - w / 2, innerWidth - w - 10)) + "px";
  m.style.top  = (r.bottom + 10) + "px";
  icons();
  return m;
}
function pagePopover(anchor){
  const btn = anchor || $("#pb-crumb");
  if (!btn) return;
  const items = current.data.pages.map(x =>
    `<button data-pg="${x.id}">${lu(x.slug ? "file" : "house")} ${esc(x.name)}<span class="pp-slug">/${esc(x.slug)}</span>${x.id===curPageId?lu("check","lu14"):""}</button>`).join("");
  const m = pillPop(btn, `<div class="pp-head">Pages</div>${items}
    <button data-add="1" class="sm-new">${lu("plus","lu14")} Add page</button>
    <button data-rename="1">${lu("pencil","lu14")} Rename site</button>`);
  if (!m) return;
  m.querySelectorAll("[data-pg]").forEach(b => b.onclick = () => { closeCtx(); switchPage(b.dataset.pg); });
  m.querySelector("[data-add]").onclick = () => { closeCtx(); addPageFlow(); };
  m.querySelector("[data-rename]").onclick = () => {
    closeCtx();
    const md = openModal(`<h2>Rename site.</h2><div class="field"><label>Site name</label><input id="rn-in" value="${esc(current.name)}"></div>
      <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button><button class="btn btn-cta btn-sm" data-x="ok">Save</button></div>`);
    md.querySelector('[data-x="c"]').onclick = closeModal;
    md.querySelector('[data-x="ok"]').onclick = () => {
      const v = md.querySelector("#rn-in").value.trim();
      if (v){ current.name = v; markDirty(); paintSitePick(); paintPageSwitch(); }
      closeModal();
    };
  };
}
function publishPopover(){
  const stg = stagingOf(current.id), prod = prodOf(current.id);
  const m = pillPop($("#btn-publish"), `
    <button data-a="publish">${lu("upload","lu14")} Publish…</button>
    <button data-a="prevstg" ${stg?"":"disabled style='opacity:.4'"}>${lu("eye","lu14")} Open preview</button>
    <button data-a="live" ${stg||prod?"":"disabled style='opacity:.4'"}>${lu("rocket","lu14")} ${prod ? "Update live…" : "Push to live…"}</button>
    <button data-a="prod" ${prod?"":"disabled style='opacity:.4'"}>${lu("globe","lu14")} Open live URL</button>`);
  if (!m) return;
  m.querySelector('[data-a="publish"]').onclick = () => { closeCtx(); openPublishModal(current, { intent: "preview" }); };
  if (stg) m.querySelector('[data-a="prevstg"]').onclick = () => { closeCtx(); openDeploy(stg); };
  const liveBtn = m.querySelector('[data-a="live"]');
  if (liveBtn && !liveBtn.disabled) liveBtn.onclick = () => { closeCtx(); openPublishModal(current, { intent: "live" }); };
  if (prod) m.querySelector('[data-a="prod"]').onclick = () => { closeCtx(); openDeploy(prod); };
}
function sharePopover(anchor){
  const stg = stagingOf(current.id), prod = prodOf(current.id);
  const btn = anchor || $("#pb-share") || $("#pb-more");
  if (!btn) return;
  const m = pillPop(btn, `
    <div class="pp-head">Share “${esc(current.name)}”</div>
    <button data-a="copystg" ${stg?"":"disabled style='opacity:.4'"}>${lu("link","lu14")} Copy staging URL</button>
    <button data-a="copyprod" ${prod?"":"disabled style='opacity:.4'"}>${lu("globe","lu14")} Copy live URL</button>
    <button data-a="preview">${lu("eye","lu14")} Open preview in new tab</button>
    <button data-a="invite">${lu("user-plus","lu14")} Invite to Crew…</button>`);
  if (!m) return;
  if (stg) m.querySelector('[data-a="copystg"]').onclick = () => { closeCtx(); navigator.clipboard?.writeText(stg.url); toast("Staging URL copied"); };
  if (prod) m.querySelector('[data-a="copyprod"]').onclick = () => { closeCtx(); navigator.clipboard?.writeText(prod.url); toast("Live URL copied"); };
  m.querySelector('[data-a="preview"]').onclick = () => { closeCtx(); previewSite(current, activePage()?.slug); };
  m.querySelector('[data-a="invite"]').onclick = () => { closeCtx(); go("team"); };
}
function searchPopover(){
  const m = pillPop($("#pb-search"), `<input class="pp-search" id="cs-in" placeholder="Find a section on this page…"><div id="cs-out"></div>`, true);
  if (!m) return;
  const inEl = m.querySelector("#cs-in"), out = m.querySelector("#cs-out");
  const all = [];
  if (current.data.shared.header) all.push({ k:"s", slot:"header", b: current.data.shared.header });
  activePage().blocks.forEach(b => all.push({ k:"b", b }));
  if (current.data.shared.footer) all.push({ k:"s", slot:"footer", b: current.data.shared.footer });
  const paint = q => {
    const hits = all.filter(x => {
      if (!q) return true;
      const hay = (BLOCKS[x.b.type]?.name + " " + x.b.type + " " + Object.values(x.b.props || {}).join(" ")).toLowerCase();
      return hay.includes(q);
    });
    out.innerHTML = hits.length ? hits.map((x, i) => {
      const txt = x.b.props.heading || x.b.props.brand || x.b.props.quote || x.b.props.text || "";
      return `<button data-i="${i}">${lu(BLOCKS[x.b.type]?.icon || "square")} ${esc(BLOCKS[x.b.type]?.name || x.b.type)}<span class="pp-slug">${esc(String(txt).slice(0, 26))}</span></button>`;
    }).join("") : `<p class="note" style="padding:8px 10px">No sections match.</p>`;
    icons();
    out.querySelectorAll("[data-i]").forEach(btn => btn.onclick = () => {
      const x = hits[+btn.dataset.i];
      closeCtx();
      selected = x.k === "s" ? { k:"s", slot:x.slot } : { k:"b", id:x.b.id };
      paintSelection(); renderInspector(); renderLayers(); icons(); scrollToSelected();
      const el2 = x.k === "s" ? document.querySelector(`.blk[data-shared="${x.slot}"]`) : document.querySelector(`.blk[data-bid="${x.b.id}"]`);
      el2?.scrollIntoView({ behavior:"smooth", block:"center" });
    });
  };
  inEl.addEventListener("input", () => paint(inEl.value.trim().toLowerCase()));
  inEl.addEventListener("click", e => e.stopPropagation());
  paint("");
  inEl.focus();
}

function bindBuilder(){
  $("#pb-exit").onclick = () => exitBuilder();
  $("#pb-crumb").onclick = e => { e.stopPropagation(); pagePopover(); };
  $("#pb-search").onclick = e => { e.stopPropagation(); searchPopover(); };
  $("#pb-history").onclick = historyModal;
  $("#btn-undo").onclick = undo;
  $("#btn-redo").onclick = redo;
  $("#pb-live").onclick = () => setLiveEdit(!liveEdit);
  $("#pb-preview").onclick = () => {
    if (!current) return toast("Open a site first.");
    previewSite(current, activePage()?.slug || "");
  };
  $("#pb-exit-preview").onclick = () => {
    if (previewMode) setPreviewMode(false);
    else if (liveEdit) setLiveEdit(false);
  };
  $("#pb-add")?.addEventListener("pointerdown", e => e.stopPropagation());
  $("#pb-add") && ($("#pb-add").onclick = e => {
    e.preventDefault(); e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    toggleInsertPalette(e.currentTarget, Math.min(r.left, innerWidth - 280), r.bottom + 8, activePage()?.blocks.length || 0);
  });
  const lcDone = $("#lc-done");
  if (lcDone) lcDone.onclick = () => setLiveEdit(false);
  const lcPage = $("#lc-page");
  if (lcPage) lcPage.onclick = e => { e.stopPropagation(); pagePopover(lcPage); };
  const lcUndo = $("#lc-undo"); if (lcUndo) lcUndo.onclick = undo;
  const lcRedo = $("#lc-redo"); if (lcRedo) lcRedo.onclick = redo;
  const lcSave = $("#lc-save"); if (lcSave) lcSave.onclick = () => saveNow(false);
  const lcAdd = $("#lc-add");
  if (lcAdd){
    lcAdd.addEventListener("pointerdown", e => e.stopPropagation());
    lcAdd.onclick = e => {
      e.preventDefault(); e.stopPropagation();
      const r = e.currentTarget.getBoundingClientRect();
      toggleInsertPalette(e.currentTarget, Math.min(r.left, innerWidth - 280), r.bottom + 8, activePage()?.blocks.length || 0);
    };
  }
  $$("#lc-bp [data-lbp]").forEach(b => b.onclick = () => {
    bp = +b.dataset.lbp;
    builderBreakpoint = bp >= 1200 ? "desktop" : bp >= 700 ? "tablet" : "mobile";
    syncLiveBp();
    renderCanvas();
    renderInspector();
    applyFrame();
    icons();
    const wrap = $("#canvas-wrap");
    if (wrap) wrap.scrollTop = 0;
  });
  $("#pb-share").onclick = e => { e.stopPropagation(); sharePopover(); };
  $("#pb-save").onclick = () => saveNow(false);
  $("#pb-bset").onclick = () => builderSettingsModal();
  const openAiBuildMenu = (anchor) => {
    const m = pillPop(anchor || $("#pb-ai-build") || $("#pb-more"), `
      <div class="pp-head">Pulse</div>
      <button data-a="full">${lu("sparkles","lu14")} Replace all pages</button>
      <button data-a="section">${lu("layout-grid","lu14")} Generate section</button>
      ${selBlock() ? `<button data-a="edit">${lu("wand-sparkles","lu14")} Edit selected section</button>` : ''}`);
    if (!m) return;
    m.querySelector('[data-a="full"]').onclick = () => { closeCtx(); showAIBuildModal("full"); };
    m.querySelector('[data-a="section"]').onclick = () => { closeCtx(); showAIBuildModal("section"); };
    m.querySelector('[data-a="edit"]')?.addEventListener('click', () => { closeCtx(); showAIBuildModal("edit"); });
  };
  $("#pb-ai-build") && ($("#pb-ai-build").onclick = e => {
    e.stopPropagation();
    openAiBuildMenu(e.currentTarget);
  });
  $("#pb-more") && ($("#pb-more").onclick = e => {
    e.stopPropagation();
    const m = pillPop(e.currentTarget, `
      <div class="pp-head">More</div>
      <button data-a="history">${lu("history","lu14")} History</button>
      <button data-a="ai">${lu("bot","lu14")} Pulse</button>
      <button data-a="preview">${lu("play","lu14")} Preview</button>
      <button data-a="share">${lu("share","lu14")} Share</button>
      <button data-a="settings">${lu("settings-2","lu14")} Builder settings</button>`);
    if (!m) return;
    m.querySelector('[data-a="history"]').onclick = () => { closeCtx(); historyModal(); };
    m.querySelector('[data-a="ai"]').onclick = () => { closeCtx(); openAiBuildMenu($("#pb-more")); };
    m.querySelector('[data-a="preview"]').onclick = () => {
      closeCtx();
      if (!current) return toast("Open a site first.");
      previewSite(current, activePage()?.slug || "");
    };
    m.querySelector('[data-a="share"]').onclick = () => { closeCtx(); sharePopover($("#pb-more")); };
    m.querySelector('[data-a="settings"]').onclick = () => { closeCtx(); builderSettingsModal(); };
  });
  $("#btn-publish").onclick = e => { e.stopPropagation(); publishPopover(); };
  $("#pg-add2").onclick = addPageFlow;
  $("#lib-search").addEventListener("input", e => {
    libQuery = e.target.value.trim().toLowerCase();
    paintLibSuggest();
    renderLibrary();
  });
  $("#lib-search").addEventListener("focus", () => paintLibSuggest());
  $("#lib-search").addEventListener("keydown", e => {
    if (e.key === "Escape"){
      const box = $("#lib-suggest");
      if (box && !box.hidden){ e.stopPropagation(); box.hidden = true; return; }
    }
    if (e.key === "Enter"){
      const first = $("#lib-suggest [data-sug]");
      if (first && !$("#lib-suggest")?.hidden){ e.preventDefault(); first.click(); }
    }
  });
  if (!window.__bdLibSugDismiss){
    window.__bdLibSugDismiss = true;
    document.addEventListener("pointerdown", e => {
      const box = $("#lib-suggest");
      if (!box || box.hidden) return;
      if (e.target.closest("#lib-suggest") || e.target.closest(".lib-search-wrap")) return;
      box.hidden = true;
    }, true);
  }
  $$("#panel-left .lp-tab").forEach(b => b.onclick = () => switchBuilderPanel(b.dataset.lp));
  /* ---------- mobile bottom sheets ---------- */
  const mbSet = (which) => {
    document.body.classList.toggle("mb-left-open", which === "left");
    document.body.classList.toggle("mb-right-open", which === "right");
    const veil = $("#mb-veil"); if (veil) veil.hidden = !which;
    $$("#mb-bar [data-mb]").forEach(b => b.classList.remove("on"));
  };
  window.closeMobileSheets = () => mbSet(null);
  window.openMobileSheet = (side, tab) => {
    if (tab) switchBuilderPanel(tab);
    mbSet(side);
    if (side){
      const mbKey = side === "right" ? "inspector" : (tab === "pages" ? "pages" : "library");
      const b = $(`#mb-bar [data-mb="${mbKey}"]`);
      if (b) b.classList.add("on");
    }
  };
  $$("#mb-bar [data-mb]").forEach(b => b.onclick = () => {
    const t = b.dataset.mb === "library" ? "sections" : b.dataset.mb;
    const side = t === "inspector" ? "right" : "left";
    const open = document.body.classList.contains(side === "right" ? "mb-right-open" : "mb-left-open");
    const sameTab = side === "right" || builderPanel === t;
    if (open && sameTab) mbSet(null);
    else openMobileSheet(side, side === "left" ? t : null);
  });
  const mbAdd = $("#mb-add");
  if (mbAdd) mbAdd.onclick = () => { mbSet(null); insertPalette(innerWidth / 2, innerHeight * 0.35, activePage().blocks.length); };
  const mbVeil = $("#mb-veil");
  if (mbVeil) mbVeil.onclick = () => mbSet(null);
  /* tapping a section on a phone slides the inspector up */
  $("#page-sheet").addEventListener("click", e => {
    if (window.innerWidth > 760) return;
    if (previewMode || liveEdit) return;
    if (!e.target.closest(".blk")) return;
    if (e.target.closest(".blk-tools") || e.target.closest("[contenteditable]")) return;
    requestAnimationFrame(() => { if (selected) openMobileSheet("right"); });
  });
  $("#canvas-wrap").addEventListener("click", e => {
    if (previewMode) return;
    if (e.target === e.currentTarget || e.target.id === "page-frame" || e.target.id === "page-sheet"){
      if (e.target.closest?.(".blk")) return;
      selected = null; paintSelection(); renderInspector(); renderLayers();
      $("#inspector")?.classList.remove("open");
      icons();
    }
  });
  $("#canvas-wrap")?.addEventListener("scroll", () => { if (!previewMode) syncLiveSelBar(); }, { passive: true });
  window.addEventListener("scroll", () => { if (!previewMode) syncLiveSelBar(); }, true);
  const sheet = $("#page-sheet");
  sheet.addEventListener("dragover", e => {
    if (previewMode) return;
    const types = e.dataTransfer.types;
    if (!types.includes("text/bd-block") && !types.includes("text/bd-move") && !types.includes("text/bd-saved")) return;
    e.preventDefault();
    const linesEls = $$("#page-sheet .drop-line");
    let best = null, bd = Infinity;
    linesEls.forEach(l => { const r = l.getBoundingClientRect(); const d = Math.abs(e.clientY - r.top); if (d < bd){ bd = d; best = l; } });
    linesEls.forEach(l => l.classList.toggle("on", l === best));
  });
  sheet.addEventListener("dragleave", e => { if (e.target === sheet) $$("#page-sheet .drop-line").forEach(l => l.classList.remove("on")); });
  sheet.addEventListener("drop", e => {
    if (previewMode) return;
    const add = e.dataTransfer.getData("text/bd-block");
    const move = e.dataTransfer.getData("text/bd-move");
    const saved = e.dataTransfer.getData("text/bd-saved");
    if (!add && !move && !saved) return;
    e.preventDefault();
    const on = $("#page-sheet .drop-line.on");
    const index = on ? parseInt(on.dataset.index) : activePage().blocks.length;
    $$("#page-sheet .drop-line").forEach(l => l.classList.remove("on"));
    if (add) addBlock(add, index);
    else if (saved) addSavedComponent(saved, index);
    else moveBlockTo(move, index);
  });
  sheet.addEventListener("click", handleCanvasClick);
  sheet.addEventListener("click", guardCanvasNavigation, true);
  sheet.addEventListener("auxclick", guardCanvasNavigation, true);
  sheet.addEventListener("dblclick", inlineEdit);
  sheet.addEventListener("contextmenu", e => { if (!previewMode) blockContext(e); });
  /* Keep inspector interactions from falling through to the canvas underneath. */
  const inspEl = $("#inspector");
  if (inspEl && !inspEl.dataset.bdInspGuard){
    inspEl.dataset.bdInspGuard = "1";
    ["pointerdown", "pointerup", "click", "mousedown", "mouseup"].forEach(ev => {
      inspEl.addEventListener(ev, e => e.stopPropagation());
    });
  }
}
function paintPageSwitch(){
  const siteEl = $("#pb-site"); if (!siteEl) return;
  siteEl.textContent = current.name;
  $("#pb-page").textContent = activePage()?.name || "—";
  const lc = $("#lc-page-label"); if (lc) lc.textContent = activePage()?.name || "—";
  const slug = stagingOf(current.id)?.slug || prodOf(current.id)?.slug || slugOf(current);
  const page = activePage()?.slug ? activePage().slug + "/" : "";
  $("#chrome-url").textContent = (slug ? prodSiteHostLabel(slug) + "/" : "") + page;
}
async function switchPage(id){
  if (dirty) await saveNow(true);
  curPageId = id; selected = null; clearMultiSel();
  try { localStorage.setItem("bd_last_page_" + activeId, id); } catch {}
  paintPageSwitch(); renderCanvas(); renderInspector(); renderLayers(); renderPagesList(); icons();
}
/* ---------- pages panel ---------- */
function renderPagesList(){
  const list = $("#pages-list"); if (!list || !current) return;
  list.innerHTML = current.data.pages.map(pg => `
    <div class="pg-row${pg.id===curPageId?" on":""}" data-id="${pg.id}">
      ${pg.slug ? lu("file") : lu("house","home-star")}
      <span class="ln">${esc(pg.name)}</span>
      <span class="lt">/${esc(pg.slug)}</span>
      <button class="pg-menu-btn" data-menu="${pg.id}">${lu("ellipsis")}</button>
    </div>`).join("");
  icons();
  list.querySelectorAll(".pg-row").forEach(r => {
    r.onclick = e => {
      if (e.target.closest("[data-menu]")){ e.stopPropagation(); pageMenu(e, r.dataset.id); return; }
      switchPage(r.dataset.id);
    };
  });
}
function pageMenu(e, id){
  e.preventDefault();
  const pg = current.data.pages.find(x => x.id === id);
  const isHome = !pg.slug;
  const m = openCtx(e.clientX, e.clientY, `
    <button data-a="rename">${lu("pencil")} Rename & address</button>
    <button data-a="dup">${lu("copy")} Duplicate page</button>
    ${isHome ? "" : `<button data-a="home">${lu("house")} Make home page</button>`}
    ${isHome ? "" : `<button data-a="del" class="danger">${lu("trash-2")} Delete page</button>`}`);
  m.querySelectorAll("button").forEach(b => b.onclick = () => {
    closeCtx();
    const a = b.dataset.a;
    if (a === "rename"){
      const md = openModal(`<h2>Page settings.</h2>
        <div class="field"><label>Page name</label><input id="pgs-n" value="${esc(pg.name)}"></div>
        <div class="field"><label>Address</label><div class="slug-row"><span>/</span><input id="pgs-s" value="${esc(pg.slug)}" ${isHome?"readonly":""}></div>${isHome?`<p class="fhint">The home page always lives at /.</p>`:""}</div>
        <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button><button class="btn btn-cta btn-sm" data-x="ok">Save</button></div>`);
      md.querySelector('[data-x="c"]').onclick = closeModal;
      md.querySelector('[data-x="ok"]').onclick = () => {
        commit();
        pg.name = md.querySelector("#pgs-n").value.trim() || pg.name;
        if (!isHome){
          let s = slugify(md.querySelector("#pgs-s").value) || slugify(pg.name);
          while (current.data.pages.some(x => x !== pg && x.slug === s)) s += "-2";
          pg.slug = s;
        }
        closeModal(); markDirty(); paintPageSwitch(); renderPagesList(); renderCanvas(); icons();
      };
    }
    if (a === "dup"){
      commit();
      const copy = JSON.parse(JSON.stringify(pg));
      copy.id = uid(); copy.name += " copy";
      copy.slug = slugify(copy.name);
      while (current.data.pages.some(x => x.slug === copy.slug)) copy.slug += "-2";
      copy.blocks.forEach(b => b.id = uid());
      current.data.pages.push(copy);
      markDirty(); paintPageSwitch(); renderPagesList(); toast("Page duplicated");
    }
    if (a === "home"){
      commit();
      const oldHome = current.data.pages.find(x => !x.slug);
      if (oldHome){ oldHome.slug = slugify(oldHome.name) || "home"; while (current.data.pages.some(x => x !== oldHome && x.slug === oldHome.slug)) oldHome.slug += "-2"; }
      pg.slug = "";
      markDirty(); paintPageSwitch(); renderPagesList(); renderCanvas(); icons(); toast(`“${pg.name}” is now the home page`);
    }
    if (a === "del"){
      confirmModal(`Delete “${esc(pg.name)}”?`, "The page and its sections are removed (undo works until you leave the builder).", "Delete page", () => {
        commit();
        current.data.pages = current.data.pages.filter(x => x.id !== pg.id);
        if (curPageId === pg.id) curPageId = current.data.pages[0].id;
        markDirty(); paintPageSwitch(); renderPagesList(); renderCanvas(); renderLayers(); icons();
      });
    }
  });
}
function addPageFlow(){
  let picked = "landing";
  const grid = () => Object.entries(PAGE_TEMPLATES).map(([k,t]) =>
    `<button class="tpl-card${k===picked?" sel":""}" data-tpl="${k}"><span class="tpl-name" style="padding-top:11px">${t.name}</span><span class="tpl-desc">${t.desc}</span></button>`).join("");
  const m = openModal(`<h2>Add a page.</h2><p class="sub">Pick a page type — each comes with sensible starter sections. The shared header and footer appear automatically.</p>
    <div class="field"><label>Page name</label><input id="np-name" placeholder="e.g. Pricing"></div>
    <div class="tpl-grid">${grid()}</div>
    <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button><button class="btn btn-cta btn-sm" data-x="ok">Add page</button></div>`, true);
  bindTplCards(m, k => { picked = k; });
  m.querySelector('[data-x="c"]').onclick = closeModal;
  m.querySelector('[data-x="ok"]').onclick = () => {
    const name = m.querySelector("#np-name").value.trim() || PAGE_TEMPLATES[picked].name;
    closeModal(); commit();
    const pg = PAGE_TEMPLATES[picked].make(name);
    pg.slug = slugify(name) || pg.slug || "page";
    while (current.data.pages.some(x => x.slug === pg.slug)) pg.slug += "-2";
    current.data.pages.push(pg);
    markDirty(); switchPage(pg.id); toast(`“${name}” added`);
  };
  m.querySelector("#np-name").focus();
}
/* ---------- library / favorites / recent / custom ---------- */
const favKey = () => "bd_fav_blocks_" + (me?.email || "anon");
function favBlocks(){
  try {
    const raw = JSON.parse(localStorage.getItem(favKey()) || "[]");
    return (Array.isArray(raw) ? raw : []).filter(t => BLOCKS[t]);
  } catch { return []; }
}
function setFavBlocks(arr){
  try { localStorage.setItem(favKey(), JSON.stringify(arr.slice(0, 40))); } catch {}
}
function isFav(type){ return favBlocks().includes(type); }
function toggleFav(type){
  if (!BLOCKS[type]) return;
  const cur = favBlocks();
  const i = cur.indexOf(type);
  if (i >= 0) cur.splice(i, 1);
  else cur.unshift(type);
  setFavBlocks(cur);
  renderLibrary(); icons();
  toast(i >= 0 ? "Removed from favorites" : "Added to favorites");
}
const recentKey = () => "bd_recent_blocks_" + (me?.email || "anon");
function recentBlocks(){
  try {
    const raw = JSON.parse(localStorage.getItem(recentKey()) || "[]");
    return (Array.isArray(raw) ? raw : []).filter(t => BLOCKS[t]);
  } catch { return []; }
}
function pushRecentBlock(type){
  if (!BLOCKS[type]) return;
  const cur = recentBlocks().filter(t => t !== type);
  cur.unshift(type);
  try { localStorage.setItem(recentKey(), JSON.stringify(cur.slice(0, 12))); } catch {}
}
/* Custom sections — user-made reusable blocks (stored per account in this browser) */
const compKey = () => "bd_components_" + (me?.email || "");
function savedComponents(){ try { return JSON.parse(localStorage.getItem(compKey())) || []; } catch { return []; } }
function setSavedComponents(arr){
  try { localStorage.setItem(compKey(), JSON.stringify(arr.slice(0, 40))); } catch {}
}
/** Normalize legacy single-block customs and multi-block customs into a blocks array. */
function customSectionBlocks(c){
  if (!c) return [];
  if (Array.isArray(c.blocks) && c.blocks.length){
    return c.blocks
      .filter(b => b && BLOCKS[b.type])
      .map(b => ({ type: b.type, props: JSON.parse(JSON.stringify(b.props || BLOCKS[b.type].defaults || {})) }));
  }
  if (c.type && BLOCKS[c.type]){
    return [{ type: c.type, props: JSON.parse(JSON.stringify(c.props || BLOCKS[c.type].defaults || {})) }];
  }
  return [];
}
function customSectionIcon(c){
  const blocks = customSectionBlocks(c);
  if (blocks.length > 1) return "layers";
  return BLOCKS[blocks[0]?.type]?.icon || "puzzle";
}
function customSectionMeta(c){
  const blocks = customSectionBlocks(c);
  if (!blocks.length) return "Empty";
  if (blocks.length === 1) return BLOCKS[blocks[0].type]?.name || blocks[0].type;
  return blocks.length + " sections";
}
function saveCustomSection(blk, name){
  if (!blk || !BLOCKS[blk.type]) return null;
  return saveCustomSectionBlocks([{ type: blk.type, props: blk.props || {} }], name || blk.props?.heading);
}
function saveCustomSectionBlocks(parts, name){
  const blocks = (parts || [])
    .filter(b => b && BLOCKS[b.type])
    .map(b => ({
      type: b.type,
      props: JSON.parse(JSON.stringify(b.props != null ? b.props : (BLOCKS[b.type].defaults || {}))),
    }));
  if (!blocks.length) return null;
  const entry = {
    id: uid(),
    name: (name || BLOCKS[blocks[0].type].name || "Custom section").trim().slice(0, 60),
    blocks,
    /* Keep legacy fields for older readers */
    type: blocks[0].type,
    props: JSON.parse(JSON.stringify(blocks[0].props)),
    createdAt: Date.now(),
  };
  const all = savedComponents();
  all.unshift(entry);
  setSavedComponents(all);
  return entry;
}
function updateCustomSection(id, patch){
  const all = savedComponents();
  const i = all.findIndex(c => c.id === id);
  if (i < 0) return null;
  all[i] = Object.assign({}, all[i], patch);
  setSavedComponents(all);
  return all[i];
}
function removeCustomSection(id){
  setSavedComponents(savedComponents().filter(c => c.id !== id));
}
function createCustomSectionFlow(opts){
  const fromBlocks = Array.isArray(opts?.fromBlocks) ? opts.fromBlocks.filter(b => b && BLOCKS[b.type]) : null;
  openCustomSectionComposer({
    fromBlock: opts?.fromBlock || null,
    fromBlocks: fromBlocks?.length ? fromBlocks : null,
    seedTypes: (opts?.fromBlock || fromBlocks?.length) ? null : [],
  });
}
function editCustomSectionFlow(id){
  openCustomSectionComposer({ id });
}
/** Create or edit a multi-block custom section — pick layouts and customize their fields. */
function openCustomSectionComposer(opts = {}){
  const existing = opts.id ? savedComponents().find(x => x.id === opts.id) : null;
  const fromBlk = opts.fromBlock || null;
  const fromBlocks = Array.isArray(opts.fromBlocks) ? opts.fromBlocks.filter(b => b && BLOCKS[b.type]) : [];
  const isEdit = !!existing;
  const clonePart = (type, props) => {
    if (!BLOCKS[type]) return null;
    return {
      type,
      props: JSON.parse(JSON.stringify(props != null ? props : (BLOCKS[type].defaults || {}))),
    };
  };
  let parts = [];
  if (existing){
    parts = customSectionBlocks(existing).map(b => clonePart(b.type, b.props)).filter(Boolean);
  } else if (fromBlocks.length){
    parts = fromBlocks.map(b => clonePart(b.type, b.props)).filter(Boolean);
  } else if (fromBlk){
    const p = clonePart(fromBlk.type, fromBlk.props);
    if (p) parts = [p];
  } else {
    const seeds = Array.isArray(opts.seedTypes) ? opts.seedTypes : [];
    parts = seeds.map(t => clonePart(t)).filter(Boolean);
  }
  let openIdx = parts.length ? 0 : -1;
  let showPicker = !isEdit && !fromBlk && !fromBlocks.length;
  /* Pending multi-select in the layout picker (types), applied with “Add selected”. */
  let draftPick = [];

  const types = BLOCK_ORDER.filter(t => BLOCKS[t] && !/coming soon/i.test(BLOCKS[t].group || ""));
  const defaultName = existing?.name
    || (fromBlocks.length > 1
      ? `${fromBlocks.length} sections`
      : fromBlk || fromBlocks[0]
        ? ((fromBlk || fromBlocks[0]).props?.heading || BLOCKS[(fromBlk || fromBlocks[0]).type]?.name || "Custom section")
        : "My custom section");

  const md = openModal(`
    <h2 id="modal-title">${isEdit ? "Customize custom section" : (fromBlk || fromBlocks.length) ? "Save as custom section" : "New custom section"}</h2>
    <p class="sub">${isEdit
      ? "Rename it, reorder sections, and edit content for each layout. Changes apply next time you add it to a page."
      : (fromBlk || fromBlocks.length)
        ? (fromBlocks.length > 1
          ? `Saving ${fromBlocks.length} selected sections together. Reorder or add more layouts, then keep them in Custom.`
          : "Tweak this section’s content, then keep it in Custom to reuse on any page.")
        : "Multi-select layouts below, customize their content, and save them together as one reusable custom."}</p>
    <div class="field"><label for="cmp-n">Name</label><input id="cmp-n" value="${esc(String(defaultName).slice(0, 60))}" maxlength="60" autocomplete="off"></div>
    <div class="field cmp-field">
      <label>Sections <span class="cmp-count" id="cmp-count"></span></label>
      <div class="cmp-parts" id="cmp-parts"></div>
      <button type="button" class="cmp-add-btn" id="cmp-add">${lu("plus","lu14")} Add section</button>
      <div class="cmp-picker" id="cmp-picker" ${showPicker ? "" : "hidden"}>
        <input type="search" class="cmp-search" id="cmp-search" placeholder="Search layouts…" autocomplete="off">
        <div class="cmp-list" id="cmp-list" role="listbox" aria-multiselectable="true" aria-label="Layouts"></div>
        <div class="cmp-pick-bar" id="cmp-pick-bar" hidden>
          <button type="button" class="btn btn-cta btn-sm" id="cmp-pick-add">Add selected</button>
          <button type="button" class="btn btn-ghost btn-sm" id="cmp-pick-clear">Clear</button>
        </div>
      </div>
    </div>
    <div class="row" style="gap:8px;flex-wrap:wrap">
      <button type="button" class="btn btn-ghost btn-sm" data-x="c">Cancel</button>
      ${isEdit
        ? `<button type="button" class="btn btn-cta btn-sm" data-x="save">Save changes</button>`
        : (fromBlk || fromBlocks.length)
          ? `<button type="button" class="btn btn-cta btn-sm" data-x="ok">Save to Custom</button>`
          : `<button type="button" class="btn btn-ghost btn-sm" data-x="lib">Save to library only</button>
             <button type="button" class="btn btn-cta btn-sm" data-x="add">Create &amp; add to page</button>`}
    </div>`, true);
  md.classList.add("cmp-modal");
  md.querySelector('[data-x="c"]').onclick = closeModal;

  const tipPreview = (p) => {
    const tip = p.props?.heading || p.props?.brand || p.props?.text || p.props?.quote || "";
    const s = String(tip).replace(/\s+/g, " ").trim();
    if (!s) return "";
    return s.length > 42 ? s.slice(0, 42) + "…" : s;
  };

  const paintParts = () => {
    const host = md.querySelector("#cmp-parts");
    const count = md.querySelector("#cmp-count");
    if (!host) return;
    if (!parts.length){
      host.innerHTML = `<span class="cmp-picked-empty">Add at least one section below</span>`;
    } else {
      host.innerHTML = "";
      parts.forEach((part, i) => {
        const def = BLOCKS[part.type];
        if (!def) return;
        const open = openIdx === i;
        const cat = libCatKey(def.group);
        const preview = tipPreview(part);
        const hint = open ? "Collapse" : "Click to customize";
        const row = document.createElement("div");
        row.className = "cmp-part" + (open ? " open" : "");
        row.dataset.i = String(i);
        row.innerHTML = `
          <div class="cmp-part-bar">
            <button type="button" class="cmp-part-toggle" data-toggle="${i}" aria-expanded="${open ? "true" : "false"}" title="${esc(hint)}" aria-label="${esc(hint)} — ${esc(def.name)}">
              <span class="bi-icon" data-cat="${cat}">${lu(def.icon,"lu14")}</span>
              <span class="cmp-part-meta">
                <strong>${esc(def.name)}</strong>
                ${preview ? `<em>${esc(preview)}</em>` : (open ? `<em class="muted">Editing fields</em>` : "")}
              </span>
              <span class="cmp-part-chev" aria-hidden="true">${lu(open ? "chevron-up" : "chevron-right","lu14")}</span>
            </button>
            <div class="cmp-part-acts">
              <button type="button" class="cmp-ico" data-up="${i}" title="Move up" ${i === 0 ? "disabled" : ""}>${lu("chevron-up","lu14")}</button>
              <button type="button" class="cmp-ico" data-dn="${i}" title="Move down" ${i === parts.length - 1 ? "disabled" : ""}>${lu("chevron-down","lu14")}</button>
              <button type="button" class="cmp-ico danger" data-rm="${i}" title="Remove">${lu("trash-2","lu14")}</button>
            </div>
            <span class="cmp-part-hint" aria-hidden="true">${esc(hint)}</span>
          </div>
          <div class="cmp-part-body" ${open ? "" : "hidden"}></div>`;
        host.appendChild(row);
        if (open){
          const body = row.querySelector(".cmp-part-body");
          const fields = def.fields || [];
          if (!fields.length){
            const tip = document.createElement("p");
            tip.className = "cmp-part-empty";
            tip.textContent = "This layout has no editable fields.";
            body.appendChild(tip);
          } else {
            fields.forEach(f => {
              appendBlockContentField(body, part.props, f, (key, v) => {
                part.props[key] = v;
                const em = row.querySelector(".cmp-part-meta em");
                if (em){
                  const tip = tipPreview(part);
                  em.textContent = tip || "Editing fields";
                  em.classList.toggle("muted", !tip);
                }
              }, { fields });
            });
          }
          const done = document.createElement("button");
          done.type = "button";
          done.className = "cmp-part-done";
          done.innerHTML = `${lu("chevron-up","lu14")} Collapse`;
          done.title = "Collapse";
          done.onclick = e => {
            e.preventDefault();
            openIdx = -1;
            paintParts();
          };
          body.appendChild(done);
        }
      });
      host.querySelectorAll("[data-toggle]").forEach(btn => {
        btn.onclick = e => {
          e.preventDefault();
          const i = +btn.dataset.toggle;
          openIdx = openIdx === i ? -1 : i;
          paintParts();
        };
      });
      host.querySelectorAll("[data-up]").forEach(btn => {
        btn.onclick = e => {
          e.preventDefault(); e.stopPropagation();
          const i = +btn.dataset.up;
          if (i <= 0) return;
          const [row] = parts.splice(i, 1);
          parts.splice(i - 1, 0, row);
          if (openIdx === i) openIdx = i - 1;
          else if (openIdx === i - 1) openIdx = i;
          paintParts();
        };
      });
      host.querySelectorAll("[data-dn]").forEach(btn => {
        btn.onclick = e => {
          e.preventDefault(); e.stopPropagation();
          const i = +btn.dataset.dn;
          if (i >= parts.length - 1) return;
          const [row] = parts.splice(i, 1);
          parts.splice(i + 1, 0, row);
          if (openIdx === i) openIdx = i + 1;
          else if (openIdx === i + 1) openIdx = i;
          paintParts();
        };
      });
      host.querySelectorAll("[data-rm]").forEach(btn => {
        btn.onclick = e => {
          e.preventDefault(); e.stopPropagation();
          const i = +btn.dataset.rm;
          parts.splice(i, 1);
          if (openIdx === i) openIdx = parts.length ? Math.min(i, parts.length - 1) : -1;
          else if (openIdx > i) openIdx -= 1;
          paintParts();
          paintList();
        };
      });
    }
    if (count) count.textContent = parts.length === 1 ? "1 section" : `${parts.length} sections`;
    icons();
  };

  const paintList = () => {
    const list = md.querySelector("#cmp-list");
    const picker = md.querySelector("#cmp-picker");
    const bar = md.querySelector("#cmp-pick-bar");
    const addSel = md.querySelector("#cmp-pick-add");
    if (!list || !picker || picker.hidden) return;
    const q = (md.querySelector("#cmp-search")?.value || "").trim().toLowerCase();
    const groups = [];
    BLOCK_GROUPS.forEach(g => {
      if (/coming soon/i.test(g)) return;
      const items = types.filter(t => {
        if (BLOCKS[t].group !== g) return false;
        if (!q && typeof isLibraryMainBlock === "function" && !isLibraryMainBlock(t)) return false;
        if (!q) return true;
        return BLOCKS[t].name.toLowerCase().includes(q) || t.includes(q) || g.toLowerCase().includes(q);
      });
      if (items.length) groups.push({ g, items });
    });
    if (!groups.length){
      list.innerHTML = `<p class="cmp-empty">No layouts match.</p>`;
      if (bar) bar.hidden = true;
      return;
    }
    list.innerHTML = groups.map(({ g, items }) => `
      <div class="cmp-group">
        <div class="cmp-g-head">${esc(libCatLabel(g))}</div>
        <div class="cmp-g-body">
          ${items.map(t => {
            const inParts = parts.filter(p => p.type === t).length;
            const draft = draftPick.includes(t);
            const on = draft || inParts > 0;
            const cat = libCatKey(BLOCKS[t].group);
            const mark = draft
              ? lu("check","lu14")
              : (inParts > 1 ? `<em>${inParts}</em>` : inParts ? lu("check","lu14") : "");
            return `<button type="button" class="cmp-opt${on ? " on" : ""}${draft ? " draft" : ""}" role="option" aria-selected="${draft ? "true" : "false"}" data-t="${esc(t)}" title="${draft ? "Selected — click to deselect" : inParts ? "Already in section — click to add another" : "Click to select"}">
              <span class="bi-icon" data-cat="${cat}">${lu(BLOCKS[t].icon,"lu14")}</span>
              <span class="cmp-opt-name">${esc(BLOCKS[t].name)}</span>
              <span class="cmp-opt-check">${mark}</span>
            </button>`;
          }).join("")}
        </div>
      </div>`).join("");
    list.querySelectorAll("[data-t]").forEach(btn => {
      btn.onclick = e => {
        const t = btn.dataset.t;
        if (!t || !BLOCKS[t]) return;
        /* Shift/Ctrl = add another instance immediately; plain click toggles multi-select draft. */
        if (e.shiftKey || e.metaKey || e.ctrlKey){
          const p = clonePart(t);
          if (!p) return;
          parts.push(p);
          openIdx = -1;
          draftPick = draftPick.filter(x => x !== t);
          paintParts();
          paintList();
          return;
        }
        if (draftPick.includes(t)) draftPick = draftPick.filter(x => x !== t);
        else draftPick.push(t);
        paintList();
      };
    });
    if (bar){
      bar.hidden = draftPick.length === 0;
      if (addSel) addSel.textContent = draftPick.length <= 1
        ? "Add selected"
        : `Add selected (${draftPick.length})`;
    }
    icons();
  };

  const applyDraftPick = () => {
    if (!draftPick.length) return;
    draftPick.forEach(t => {
      const p = clonePart(t);
      if (p) parts.push(p);
    });
    draftPick = [];
    openIdx = parts.length ? parts.length - 1 : -1;
    paintParts();
    paintList();
  };

  const setPicker = (on) => {
    showPicker = on;
    const picker = md.querySelector("#cmp-picker");
    const addBtn = md.querySelector("#cmp-add");
    if (picker) picker.hidden = !on;
    if (addBtn) addBtn.classList.toggle("on", on);
    if (!on) draftPick = [];
    if (on) paintList();
  };

  md.querySelector("#cmp-add").onclick = () => setPicker(!showPicker);
  md.querySelector("#cmp-search")?.addEventListener("input", paintList);
  md.querySelector("#cmp-pick-add")?.addEventListener("click", applyDraftPick);
  md.querySelector("#cmp-pick-clear")?.addEventListener("click", () => { draftPick = []; paintList(); });

  paintParts();
  if (showPicker) paintList();
  else setPicker(false);

  const finish = (alsoAdd) => {
    const name = md.querySelector("#cmp-n").value.trim() || "Custom section";
    if (!parts.length) return toast("Add at least one section.");
    const payload = {
      name,
      blocks: parts.map(p => ({
        type: p.type,
        props: JSON.parse(JSON.stringify(p.props || {})),
      })),
      type: parts[0].type,
      props: JSON.parse(JSON.stringify(parts[0].props || {})),
    };
    let entry;
    if (isEdit){
      entry = updateCustomSection(existing.id, payload);
      closeModal();
      renderLibrary(); icons();
      toast(`“${entry.name}” updated`);
      return;
    }
    entry = saveCustomSectionBlocks(payload.blocks, name);
    if (!entry) return toast("Could not save custom section.");
    closeModal();
    if (alsoAdd) addSavedComponent(entry.id, activePage()?.blocks.length || 0);
    else { renderLibrary(); icons(); toast(`“${entry.name}” saved to Custom`); }
  };
  md.querySelector('[data-x="ok"]')?.addEventListener("click", () => finish(false));
  md.querySelector('[data-x="lib"]')?.addEventListener("click", () => finish(false));
  md.querySelector('[data-x="add"]')?.addEventListener("click", () => finish(true));
  md.querySelector('[data-x="save"]')?.addEventListener("click", () => finish(false));
  md.querySelector("#cmp-n")?.focus();
  icons();
}
function renameCustomSectionFlow(id){
  const c = savedComponents().find(x => x.id === id); if (!c) return;
  const md = openModal(`
    <h2>Rename custom section</h2>
    <div class="field"><label for="cmp-rn">Name</label><input id="cmp-rn" value="${esc(c.name)}" maxlength="60"></div>
    <div class="row"><button type="button" class="btn btn-ghost btn-sm" data-x="c">Cancel</button>
      <button type="button" class="btn btn-cta btn-sm" data-x="ok">Save</button></div>`);
  md.querySelector('[data-x="c"]').onclick = closeModal;
  md.querySelector('[data-x="ok"]').onclick = () => {
    const name = md.querySelector("#cmp-rn").value.trim() || c.name;
    updateCustomSection(id, { name });
    closeModal(); renderLibrary(); icons(); toast("Renamed");
  };
  md.querySelector("#cmp-rn").focus();
}

/* Intent aliases — type a job (“price”, “email”) and land on the right section */
const LIB_ALIASES = {
  nav: ["menu","header","navigation","navbar","top bar"],
  sticky: ["announcement","banner","promo bar","marquee"],
  hero: ["banner","headline","splash","landing","masthead","intro"],
  columns: ["grid","layout","multi column"],
  divider: ["line","rule","separator","hr"],
  spacer: ["space","gap","padding","whitespace"],
  footer: ["bottom","site footer"],
  text: ["copy","paragraph","rich text","body","article"],
  features: ["feature","benefits","grid","cards"],
  split: ["image text","media text","two column"],
  list: ["checklist","bullets","todo","points"],
  table: ["spreadsheet","comparison table","data"],
  code: ["snippet","embed code","pre"],
  faq: ["questions","accordion","help","q&a","q and a"],
  tabs: ["tabbed","panels"],
  timeline: ["history","milestones","roadmap"],
  steps: ["process","how it works","numbered"],
  compare: ["comparison","versus","vs"],
  image: ["photo","picture","img"],
  gallery: ["photos","images","album"],
  carousel: ["slider","slideshow"],
  beforeafter: ["before after","compare photos","slider"],
  video: ["youtube","vimeo","film","embed"],
  logos: ["clients","brands","partners","logo strip"],
  stats: ["numbers","metrics","counters","kpi"],
  testimonial: ["review","quote","customer","social proof"],
  quote: ["pull quote","citation","blockquote"],
  team: ["people","staff","about us","crew"],
  awards: ["press","badges","recognition","as seen"],
  casestudy: ["case study","portfolio","project"],
  pricing: ["price","plan","cost","packages","tiers","money"],
  contact: ["email","form","reach","get in touch","message"],
  newsletter: ["subscribe","email list","signup","mailing"],
  countdown: ["timer","deadline","launch"],
  cta: ["call to action","convert","sign up","button","promo"],
  social: ["share","links","instagram","twitter","icons"],
  infobar: ["info","notice","alert bar"],
};
const LIB_CAT_INTENTS = {
  structure: "Structure",
  content: "Content",
  media: "Media",
  "social proof": "Social proof",
  conversion: "Conversion",
  convert: "Conversion",
  favorites: "__fav__",
  favourites: "__fav__",
  favorite: "__fav__",
  favourite: "__fav__",
  recent: "__recent__",
  recently: "__recent__",
  "recently used": "__recent__",
  custom: "__custom__",
  customs: "__custom__",
  "my sections": "__custom__",
  saved: "__custom__",
  components: "__custom__",
};

function libActiveCats(){
  return BLOCK_GROUPS.filter(g => !/coming soon/i.test(g));
}
function libCatLabel(g){
  if (g === "__fav__") return "Favorites";
  if (g === "__recent__") return "Recently used";
  if (g === "__custom__" || g === "__saved__") return "Custom";
  return String(g || "").replace(/\s*—\s*coming soon/i, "");
}
function libCatKey(g){
  const s = String(g || "");
  if (s === "__fav__") return "favorites";
  if (s === "__recent__") return "recent";
  if (s === "__custom__" || s === "__saved__") return "custom";
  if (/blog/i.test(s)) return "blog";
  if (/browns/i.test(s)) return "brand";
  if (/social/i.test(s)) return "social";
  if (/structure/i.test(s)) return "structure";
  if (/content/i.test(s)) return "content";
  if (/media/i.test(s)) return "media";
  if (/conversion/i.test(s)) return "conversion";
  return "content";
}
function paintLibChips(){
  const host = $("#lib-chips"); if (!host) return;
  const cats = [
    { id: "", label: "All" },
    { id: "__fav__", label: "Favorites" },
    { id: "__recent__", label: "Recent" },
    { id: "__custom__", label: "Custom" },
    ...libActiveCats().map(g => ({ id: g, label: libCatLabel(g) })),
  ];
  host.innerHTML = cats.map(c =>
    `<button type="button" class="chip-btn${libGroup === c.id ? " on" : ""}" role="tab" aria-selected="${libGroup === c.id ? "true" : "false"}" data-lg="${esc(c.id)}">${esc(c.label)}</button>`
  ).join("");
  host.querySelectorAll("[data-lg]").forEach(btn => {
    btn.onclick = () => {
      libGroup = btn.dataset.lg || "";
      renderLibrary();
      icons();
    };
  });
}
function blockMatchScore(type, q){
  if (!q) return 0;
  const b = BLOCKS[type]; if (!b) return 0;
  const name = b.name.toLowerCase();
  const group = (b.group || "").toLowerCase();
  const aliases = (LIB_ALIASES[type] || []).map(a => a.toLowerCase());
  if (name === q || type === q) return 100;
  if (name.startsWith(q) || type.startsWith(q)) return 90;
  if (aliases.some(a => a === q)) return 85;
  if (name.includes(q) || type.includes(q)) return 70;
  if (aliases.some(a => a.startsWith(q) || a.includes(q))) return 65;
  if (group.includes(q)) return 40;
  const tokens = q.split(/\s+/).filter(Boolean);
  if (tokens.length > 1 && tokens.every(t => name.includes(t) || type.includes(t) || aliases.some(a => a.includes(t)))) return 55;
  return 0;
}
function libSuggestList(q){
  const query = (q || "").trim().toLowerCase();
  if (!query){
    const starter = ["hero","features","cta","pricing","faq","contact"];
    const seen = new Set();
    const out = [];
    favBlocks().forEach(t => { if (!seen.has(t) && BLOCKS[t]){ seen.add(t); out.push({ type:t, why:"Favorite" }); } });
    starter.forEach(t => { if (!seen.has(t) && BLOCKS[t]){ seen.add(t); out.push({ type:t, why:"Popular" }); } });
    return out.slice(0, 6);
  }
  let catHit = LIB_CAT_INTENTS[query] || null;
  if (!catHit){
    const hit = Object.entries(LIB_CAT_INTENTS).find(([k]) => query === k || query.startsWith(k + " "));
    if (hit) catHit = hit[1];
  }
  const scored = BLOCK_ORDER.map(t => ({ type: t, score: blockMatchScore(t, query) }))
    .filter(x => x.score > 0 && (typeof isLibraryMainBlock !== "function" || isLibraryMainBlock(x.type) || x.score >= 70))
    .sort((a, b) => b.score - a.score || BLOCKS[a.type].name.localeCompare(BLOCKS[b.type].name));
  const out = scored.slice(0, 6).map(x => ({
    type: x.type,
    why: x.score >= 85 ? "Best match" : ((LIB_ALIASES[x.type] || []).some(a => a.toLowerCase().includes(query)) ? "Related" : "Match"),
  }));
  if (catHit) out.unshift({ _cat: catHit, why: "Category" });
  return out.slice(0, 7);
}
function paintLibSuggest(){
  const box = $("#lib-suggest");
  const input = $("#lib-search");
  if (!box || !input) return;
  if (document.activeElement !== input && !libQuery){ box.hidden = true; return; }
  const items = libSuggestList(libQuery);
  if (!items.length){ box.hidden = true; box.innerHTML = ""; return; }
  box.hidden = false;
  box.innerHTML = items.map(it => {
    if (it._cat){
      const label = libCatLabel(it._cat);
      const cat = libCatKey(it._cat);
      return `<button type="button" class="lib-sug-row" role="option" data-sug-cat="${esc(it._cat)}" data-cat="${cat}">
        <span class="bi-icon" data-cat="${cat}">${lu("layers","lu14")}</span>
        <span class="lib-sug-meta"><strong>Filter: ${esc(label)}</strong><em>${esc(it.why)}</em></span>
      </button>`;
    }
    const b = BLOCKS[it.type];
    const cat = libCatKey(b.group);
    return `<button type="button" class="lib-sug-row" role="option" data-sug="${esc(it.type)}" data-cat="${cat}">
      <span class="bi-icon" data-cat="${cat}">${lu(b.icon,"lu14")}</span>
      <span class="lib-sug-meta"><strong>${esc(b.name)}</strong><em>${esc(it.why)} · ${esc(libCatLabel(b.group))}</em></span>
      <span class="lib-sug-add">${lu("plus","lu14")}</span>
    </button>`;
  }).join("");
  box.querySelectorAll("[data-sug-cat]").forEach(btn => {
    btn.onclick = e => {
      e.preventDefault(); e.stopPropagation();
      libGroup = btn.dataset.sugCat || "";
      libQuery = "";
      if (input) input.value = "";
      box.hidden = true;
      renderLibrary(); icons();
    };
  });
  box.querySelectorAll("[data-sug]").forEach(btn => {
    btn.onclick = e => {
      e.preventDefault(); e.stopPropagation();
      const t = btn.dataset.sug;
      box.hidden = true;
      addBlock(t, activePage()?.blocks.length || 0);
    };
  });
  icons();
}
function libBlockItem(type, opts){
  const b = BLOCKS[type]; if (!b) return null;
  const fav = isFav(type);
  const cat = libCatKey(b.group);
  const subs = (typeof subBlocksOf === "function" ? subBlocksOf(type) : []).filter(t => BLOCKS[t]);
  const isSub = opts?.asSub || (typeof isLibraryMainBlock === "function" && !isLibraryMainBlock(type));
  const nestKey = "lib-sub-" + type;
  const el = document.createElement("div");
  el.className = "block-item" + (fav ? " is-fav" : "") + (isSub ? " block-item--sub" : "") + (subs.length && !isSub ? " block-item--host" : "");
  el.dataset.cat = cat;
  el.draggable = true;
  el.innerHTML = `<span class="bi-icon" data-cat="${cat}">${lu(b.icon)}</span><span class="bi-name">${esc(b.name)}${isSub ? `<em class="bi-subtag">Related</em>` : ""}</span>
    ${subs.length && !isSub ? `<button type="button" class="bi-expand${lpOpen(nestKey) ? " on" : ""}" title="Related layouts" aria-label="Related layouts" aria-expanded="${lpOpen(nestKey) ? "true" : "false"}">${lu("chevron-right","lu14")}</button>` : ""}
    <button type="button" class="bi-fav${fav ? " on" : ""}" title="${fav ? "Remove from favorites" : "Add to favorites"}" aria-label="${fav ? "Remove from favorites" : "Add to favorites"}">${lu("star","lu14")}</button>
    <span class="bi-add">${lu("plus")}</span>`;
  el.addEventListener("dragstart", e => { e.dataTransfer.setData("text/bd-block", type); e.dataTransfer.effectAllowed = "copy"; });
  el.onclick = () => addBlock(type, activePage().blocks.length);
  const star = el.querySelector(".bi-fav");
  star.onclick = e => { e.preventDefault(); e.stopPropagation(); toggleFav(type); };
  star.onmousedown = e => e.stopPropagation();
  const exp = el.querySelector(".bi-expand");
  if (exp){
    exp.onclick = e => {
      e.preventDefault(); e.stopPropagation();
      setLpOpen(nestKey, !lpOpen(nestKey));
      renderLibrary();
    };
    exp.onmousedown = e => e.stopPropagation();
  }
  if (opts?.fromFav){
    el.oncontextmenu = e => {
      e.preventDefault();
      openCtx(e.clientX, e.clientY, `<button data-a="unfav">${lu("star")} Remove from favorites</button>`)
        .querySelector("[data-a=unfav]").onclick = () => { closeCtx(); toggleFav(type); };
    };
  } else {
    el.oncontextmenu = e => {
      e.preventDefault();
      const label = fav ? "Remove from favorites" : "Add to favorites";
      openCtx(e.clientX, e.clientY, `<button data-a="fav">${lu("star")} ${label}</button>`)
        .querySelector("[data-a=fav]").onclick = () => { closeCtx(); toggleFav(type); };
    };
  }
  if (subs.length && !isSub && (lpOpen(nestKey) || opts?.forceSubs)){
    const wrap = document.createElement("div");
    wrap.className = "lib-sub-nest";
    wrap.appendChild(el);
    subs.forEach(t => {
      const child = libBlockItem(t, { asSub: true });
      if (child) wrap.appendChild(child);
    });
    return wrap;
  }
  return el;
}
function renderLibrary(){
  const lib = $("#block-library"); if (!lib) return;
  paintLibChips();
  lib.innerHTML = "";
  const showFav = !libGroup || libGroup === "__fav__";
  const showRecent = !libGroup || libGroup === "__recent__";
  const showCustom = !libGroup || libGroup === "__custom__" || libGroup === "__saved__";
  const showGroups = !libGroup || (libGroup !== "__fav__" && libGroup !== "__recent__" && libGroup !== "__custom__" && libGroup !== "__saved__");

  if (showFav){
    const favs = favBlocks().filter(t => {
      if (libGroup && libGroup !== "__fav__") return false;
      if (typeof isLibraryMainBlock === "function" && !isLibraryMainBlock(t) && !libQuery) return false;
      if (libQuery && blockMatchScore(t, libQuery) <= 0) return false;
      return true;
    });
    const sec = document.createElement("div");
    sec.className = "lib-sec lib-sec--fav" + (lpOpen("lib-favorites") || libGroup === "__fav__" || !!libQuery ? " open" : "");
    sec.innerHTML = `<button type="button" class="lib-head">${lu("chevron-right","lu14")}<span>Favorites</span><em>${favs.length}</em></button><div class="lib-body"></div>`;
    const body = sec.querySelector(".lib-body");
    if (favs.length){
      favs.forEach(t => {
        const el = libBlockItem(t, { fromFav: true });
        if (el) body.appendChild(el);
      });
    } else if (!libQuery){
      const tip = document.createElement("p");
      tip.className = "lib-fav-empty";
      tip.textContent = "Star any section below to pin it here.";
      body.appendChild(tip);
    } else {
      const tip = document.createElement("p");
      tip.className = "lib-fav-empty";
      tip.textContent = "No favorites match.";
      body.appendChild(tip);
    }
    sec.querySelector(".lib-head").onclick = () => { setLpOpen("lib-favorites", !lpOpen("lib-favorites")); renderLibrary(); };
    lib.appendChild(sec);
  }

  if (showRecent){
    const recents = recentBlocks().filter(t => {
      if (typeof isLibraryMainBlock === "function" && !isLibraryMainBlock(t) && !libQuery) return false;
      if (libQuery && blockMatchScore(t, libQuery) <= 0) return false;
      return true;
    });
    if (recents.length || libGroup === "__recent__"){
      const sec = document.createElement("div");
      sec.className = "lib-sec lib-sec--recent" + (lpOpen("lib-recent") || libGroup === "__recent__" || !!libQuery ? " open" : "");
      sec.innerHTML = `<button type="button" class="lib-head">${lu("chevron-right","lu14")}<span>Recently used</span><em>${recents.length}</em></button><div class="lib-body"></div>`;
      const body = sec.querySelector(".lib-body");
      if (recents.length){
        recents.forEach(t => {
          const el = libBlockItem(t);
          if (el) body.appendChild(el);
        });
      } else {
        const tip = document.createElement("p");
        tip.className = "lib-fav-empty";
        tip.textContent = "Sections you add will show up here.";
        body.appendChild(tip);
      }
      sec.querySelector(".lib-head").onclick = () => { setLpOpen("lib-recent", !lpOpen("lib-recent")); renderLibrary(); };
      lib.appendChild(sec);
    }
  }

  if (showCustom){
    const customs = savedComponents().filter(c => {
      if (libQuery && !c.name.toLowerCase().includes(libQuery) && blockMatchScore(c.type, libQuery) <= 0) return false;
      return true;
    });
    const sec = document.createElement("div");
    sec.className = "lib-sec lib-sec--custom" + (lpOpen("lib-custom") || libGroup === "__custom__" || libGroup === "__saved__" || !!libQuery ? " open" : "");
    sec.innerHTML = `<button type="button" class="lib-head">${lu("chevron-right","lu14")}<span>Custom</span><em>${customs.length}</em></button><div class="lib-body"></div>`;
    const body = sec.querySelector(".lib-body");
    const newBtn = document.createElement("button");
    newBtn.type = "button";
    newBtn.className = "lib-new-custom";
    newBtn.innerHTML = `${lu("plus","lu14")} New custom section`;
    newBtn.onclick = e => { e.stopPropagation(); createCustomSectionFlow(); };
    body.appendChild(newBtn);
    if (customs.length){
      customs.forEach(c => {
        const el = document.createElement("div");
        el.className = "block-item"; el.draggable = true;
        el.dataset.cat = "custom";
        el.innerHTML = `<span class="bi-icon" data-cat="custom">${lu(customSectionIcon(c),"lu14")}</span><span class="bi-name">${esc(c.name)}</span><span class="bi-meta">${esc(customSectionMeta(c))}</span><span class="bi-add">${lu("plus")}</span>`;
        el.addEventListener("dragstart", e => e.dataTransfer.setData("text/bd-saved", c.id));
        el.onclick = () => addSavedComponent(c.id, activePage().blocks.length);
        el.oncontextmenu = e => {
          e.preventDefault();
          openCtx(e.clientX, e.clientY, `
            <button data-a="add">${lu("plus")} Add to page</button>
            <button data-a="edit">${lu("sliders-horizontal")} Customize</button>
            <button data-a="ren">${lu("pencil")} Rename</button>
            <button data-a="rm" class="danger">${lu("trash-2")} Delete</button>`)
            .querySelectorAll("button").forEach(btn => {
              btn.onclick = () => {
                closeCtx();
                if (btn.dataset.a === "add") addSavedComponent(c.id, activePage().blocks.length);
                if (btn.dataset.a === "edit") editCustomSectionFlow(c.id);
                if (btn.dataset.a === "ren") renameCustomSectionFlow(c.id);
                if (btn.dataset.a === "rm"){
                  removeCustomSection(c.id);
                  renderLibrary(); icons();
                  toast("Custom section deleted");
                }
              };
            });
        };
        body.appendChild(el);
      });
    } else if (!libQuery){
      const tip = document.createElement("p");
      tip.className = "lib-fav-empty";
      tip.textContent = "Create a custom section, or Shift-click several blocks on the canvas → Save as custom.";
      body.appendChild(tip);
    } else {
      const tip = document.createElement("p");
      tip.className = "lib-fav-empty";
      tip.textContent = "No custom sections match.";
      body.appendChild(tip);
    }
    sec.querySelector(".lib-head").onclick = () => { setLpOpen("lib-custom", !lpOpen("lib-custom")); renderLibrary(); };
    lib.appendChild(sec);
  }

  if (showGroups){
    const groups = libGroup && libGroup !== "__fav__" && libGroup !== "__recent__" && libGroup !== "__custom__" && libGroup !== "__saved__"
      ? BLOCK_GROUPS.filter(g => g === libGroup)
      : BLOCK_GROUPS;
    let any = false;
    groups.forEach(g => {
      const types = BLOCK_ORDER.filter(t => {
        const b = BLOCKS[t];
        if (b.group !== g) return false;
        /* Sub-blocks stay nested under hosts unless the user is searching. */
        if (typeof isLibraryMainBlock === "function" && !isLibraryMainBlock(t) && !libQuery) return false;
        if (libQuery && blockMatchScore(t, libQuery) <= 0) return false;
        return true;
      });
      if (!types.length) return;
      any = true;
      const key = "lib-" + g;
      const forceOpen = !!libQuery || !!libGroup;
      const sec = document.createElement("div");
      sec.className = "lib-sec" + (forceOpen || lpOpen(key) ? " open" : "");
      sec.innerHTML = `<button type="button" class="lib-head">${lu("chevron-right","lu14")}<span>${esc(libCatLabel(g))}</span><em>${types.length}</em></button><div class="lib-body"></div>`;
      const body = sec.querySelector(".lib-body");
      const ordered = libQuery
        ? types.slice().sort((a, b) => blockMatchScore(b, libQuery) - blockMatchScore(a, libQuery))
        : types;
      ordered.forEach(t => {
        const el = libBlockItem(t, { asSub: typeof isLibraryMainBlock === "function" && !isLibraryMainBlock(t) });
        if (el) body.appendChild(el);
      });
      sec.querySelector(".lib-head").onclick = () => { setLpOpen(key, !lpOpen(key)); renderLibrary(); };
      lib.appendChild(sec);
    });
    if (!any && (libQuery || libGroup)){
      const empty = document.createElement("div");
      empty.className = "lib-empty";
      empty.innerHTML = `<p>No sections match${libQuery ? ` “${esc(libQuery)}”` : ""}.</p>
        <button type="button" class="btn btn-ghost btn-sm" id="lib-clear">Clear filters</button>`;
      empty.querySelector("#lib-clear").onclick = () => {
        libQuery = ""; libGroup = "";
        const inp = $("#lib-search"); if (inp) inp.value = "";
        paintLibSuggest(); renderLibrary(); icons();
      };
      lib.appendChild(empty);
    }
  }
  icons();
}
function addSavedComponent(id, index){
  const c = savedComponents().find(x => x.id === id); if (!c) return;
  const parts = customSectionBlocks(c);
  if (!parts.length) return toast("That custom section is empty.");
  commit();
  const arr = activePage().blocks;
  const inserted = parts.map(p => ({ id: uid(), type: p.type, props: JSON.parse(JSON.stringify(p.props)) }));
  arr.splice(index, 0, ...inserted);
  selected = { k:"b", id: inserted[0].id };
  markDirty();
  inserted.forEach(b => pushRecentBlock(b.type));
  renderCanvas(); renderInspector(); renderLayers(); renderLibrary(); icons();
  toast(inserted.length === 1
    ? `“${c.name}” added`
    : `“${c.name}” added · ${inserted.length} sections`);
}
/* ---------- layers ---------- */
function renderLayers(){
  const list = $("#layers-list");
  if (!list || !current) return;
  const pg = activePage();
  const row = (b, i, shared = "") => {
    const def = BLOCKS[b.type] || { name:b.type, icon:"box" };
    const on = shared ? selected?.k === "s" && selected.slot === shared : selected?.k === "b" && selected.id === b.id;
    return `<div class="layer-row${on ? " on" : ""}${b.hidden ? " is-hidden" : ""}" ${shared ? `data-layer-shared="${shared}"` : `data-layer-id="${b.id}" data-layer-index="${i}" draggable="true"`}>
      <span class="layer-grip" aria-hidden="true">${shared ? lu("link","lu14") : lu("grip-vertical","lu14")}</span>
      <span class="layer-icon">${lu(def.icon || "box","lu14")}</span>
      <span class="layer-name">${esc(def.name)}${shared ? `<em>Shared ${shared}</em>` : ""}</span>
      ${shared ? "" : `<button type="button" data-layer-act="hide" title="${b.hidden ? "Show" : "Hide"}">${lu(b.hidden ? "eye" : "eye-off","lu14")}</button>
      <button type="button" data-layer-act="up" title="Move up">${lu("arrow-up","lu14")}</button>
      <button type="button" data-layer-act="down" title="Move down">${lu("arrow-down","lu14")}</button>`}
    </div>`;
  };
  const rows = [];
  if (current.data.shared.header) rows.push(row(current.data.shared.header, -1, "header"));
  (pg?.blocks || []).forEach((b, i) => rows.push(row(b, i)));
  if (current.data.shared.footer) rows.push(row(current.data.shared.footer, -1, "footer"));
  list.innerHTML = rows.join("") || `<p class="layers-empty">No layers on this page.</p>`;
  list.querySelectorAll(".layer-row").forEach(el => {
    el.onclick = e => {
      const act = e.target.closest("[data-layer-act]")?.dataset.layerAct;
      if (act){
        e.stopPropagation();
        blockAction(act, +el.dataset.layerIndex);
        return;
      }
      selected = el.dataset.layerShared
        ? { k:"s", slot:el.dataset.layerShared }
        : { k:"b", id:el.dataset.layerId };
      clearMultiSel();
      paintSelection();
      renderInspector();
      renderLayers();
      scrollToSelected();
      icons();
    };
    if (!el.dataset.layerId) return;
    el.addEventListener("dragstart", e => {
      e.dataTransfer.setData("text/bd-layer", el.dataset.layerId);
      e.dataTransfer.effectAllowed = "move";
    });
    el.addEventListener("dragover", e => { e.preventDefault(); el.classList.add("drop-on"); });
    el.addEventListener("dragleave", () => el.classList.remove("drop-on"));
    el.addEventListener("drop", e => {
      e.preventDefault();
      el.classList.remove("drop-on");
      const id = e.dataTransfer.getData("text/bd-layer");
      if (id && id !== el.dataset.layerId) moveBlockTo(id, +el.dataset.layerIndex);
    });
  });
  icons();
}
function scrollToSelected(){
  if (selected?.k !== "b") return;
  /* Live edit keeps the viewport still — jumping the canvas feels broken. */
  if (liveEdit && !previewMode) return;
  const el = document.querySelector(`.blk[data-bid="${selected.id}"]`);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
}
function withCanvasScrollLocked(fn){
  const wrap = $("#canvas-wrap");
  const pageEl = $("#page");
  const y = wrap ? wrap.scrollTop : 0;
  const x = wrap ? wrap.scrollLeft : 0;
  const pageY = pageEl ? pageEl.scrollTop : 0;
  const winX = window.scrollX || 0;
  const winY = window.scrollY || 0;
  try { return fn(); }
  finally {
    const restore = () => {
      if (wrap){
        wrap.scrollTop = y;
        wrap.scrollLeft = x;
      }
      if (pageEl) pageEl.scrollTop = pageY;
      if (winX || winY) window.scrollTo(winX, winY);
    };
    restore();
    requestAnimationFrame(restore);
  }
}
/* ---------- canvas ---------- */
function addBlock(type, index){
  syncCurrent();
  if (!current) return;
  commit();
  const blk = mkBlock(type);
  if ((type === "nav" && !current.data.shared.header) || (type === "footer" && !current.data.shared.footer)){
    const slot = type === "nav" ? "header" : "footer";
    current.data.shared[slot] = blk;
    selected = { k:"s", slot };
    toast(BLOCKS[type].name + " added as shared " + slot + " — it repeats on every page");
  } else {
    activePage().blocks.splice(index, 0, blk);
    selected = { k:"b", id: blk.id };
    toast(BLOCKS[type].name + " added");
  }
  markDirty();
  pushRecentBlock(type);
  renderCanvas(); renderInspector(); renderLayers(); renderLibrary(); icons();
  if (type === "footer") queueFooterLocationFill(blk, { silent: true });
}
/** Swap the selected page block to a related layout, keeping overlapping copy/styles. */
function swapBlockType(id, toType){
  syncCurrent();
  if (!current || !BLOCKS[toType]) return;
  const arr = activePage()?.blocks;
  if (!arr) return;
  const i = arr.findIndex(b => b.id === id);
  if (i < 0) return;
  const prev = arr[i];
  if (prev.type === toType) return;
  commit();
  const props = typeof mergeBlockPropsForSwap === "function"
    ? mergeBlockPropsForSwap(prev.type, toType, prev.props)
    : Object.assign(JSON.parse(JSON.stringify(BLOCKS[toType].defaults || {})), prev.props || {});
  arr[i] = { id: prev.id, type: toType, props, hidden: prev.hidden };
  selected = { k: "b", id: prev.id };
  markDirty();
  pushRecentBlock(toType);
  renderCanvas(); renderInspector(); renderLayers(); renderLibrary(); icons();
  toast("Switched to " + (BLOCKS[toType].name || toType));
}
function appendRelatedLayoutsCard(mount, blk){
  if (!blk || typeof relatedLayoutBlocks !== "function") return;
  const related = relatedLayoutBlocks(blk.type);
  if (!related.length) return;
  const card = document.createElement("section");
  card.className = "insp-style-card insp-related-card";
  card.innerHTML = `<header class="insp-style-card-head">${lu("layers","lu14")}<span>Related layouts</span></header>
    <div class="insp-style-card-body">
      <p class="insp-related-lead">Variants for this section — swap in place or add below.</p>
      <div class="insp-related-grid"></div>
    </div>`;
  const grid = card.querySelector(".insp-related-grid");
  related.forEach(t => {
    const def = BLOCKS[t]; if (!def) return;
    const cat = libCatKey(def.group);
    const row = document.createElement("div");
    row.className = "insp-related-row";
    row.innerHTML = `<span class="bi-icon" data-cat="${cat}">${lu(def.icon || "box","lu14")}</span>
      <span class="insp-related-name">${esc(def.name)}</span>
      <button type="button" class="btn btn-ghost btn-sm" data-x="swap" title="Replace this section">${lu("refresh-cw","lu14")} Swap</button>
      <button type="button" class="btn btn-ghost btn-sm" data-x="add" title="Add below">${lu("plus","lu14")}</button>`;
    row.querySelector('[data-x="swap"]').onclick = () => {
      if (selected?.k === "s") return toast("Shared sections can't swap layouts here");
      swapBlockType(blk.id, t);
    };
    row.querySelector('[data-x="add"]').onclick = () => {
      const arr = activePage()?.blocks || [];
      const idx = arr.findIndex(b => b.id === blk.id);
      addBlock(t, idx >= 0 ? idx + 1 : arr.length);
    };
    grid.appendChild(row);
  });
  mount.appendChild(card);
  icons();
}
function moveBlockTo(id, index){
  commit();
  const arr = activePage().blocks;
  const from = arr.findIndex(b => b.id === id); if (from < 0) return;
  const [b] = arr.splice(from, 1);
  if (from < index) index--;
  arr.splice(index, 0, b);
  markDirty(); renderCanvas(); renderLayers(); icons();
}
function dropLine(index){
  const el = document.createElement("div");
  el.className = "drop-line";
  el.dataset.index = index;
  el.setAttribute("aria-hidden", "true");
  /* Drag target only — no between-section + button (Library / toolbar add instead) */
  return el;
}
/* Searchable, categorised section picker for inline inserts — the same
   library, one keystroke away from any gap between sections. */
function toggleInsertPalette(btn, x, y, index){
  const m = $("#ctx-menu");
  if (btn && m && !m.hidden && _pillPopBtn === btn){
    closeCtx();
    return;
  }
  _pillPopBtn = btn || null;
  insertPalette(x, y, index);
}
function insertPalette(x, y, index){
  const groups = BLOCK_GROUPS.filter(g => g !== "Blog \u2014 coming soon");
  const paint = (q = "") => {
    const ql = q.trim().toLowerCase();
    const parts = groups.map(g => {
      let items = BLOCK_ORDER.filter(t => {
        const b = BLOCKS[t];
        if (b.group !== g) return false;
        if (!ql) return typeof isLibraryMainBlock !== "function" || isLibraryMainBlock(t);
        return blockMatchScore(t, ql) > 0;
      });
      if (!items.length) return "";
      if (ql) items = items.slice().sort((a, b) => blockMatchScore(b, ql) - blockMatchScore(a, ql));
      return `<div class="ip-group">${esc(libCatLabel(g))}</div>` + items.map(t => {
        const b = BLOCKS[t];
        const cat = libCatKey(b.group);
        const sub = typeof isLibraryMainBlock === "function" && !isLibraryMainBlock(t);
        return `<button type="button" class="ip-item${sub ? " ip-item--sub" : ""}" data-t="${t}" data-cat="${cat}">
          <span class="bi-icon" data-cat="${cat}">${lu(b.icon,"lu14")}</span>
          <span class="ip-name">${esc(b.name)}${sub ? `<em>Related</em>` : ""}</span>
        </button>`;
      }).join("");
    }).filter(Boolean);
    return parts.join("") || `<p class="ip-none">Nothing matches \u201C${esc(q)}\u201D</p>`;
  };
  const m = openCtx(x, y, `<div class="mini-pal ins-pal">
    <div class="ip-search">
      <span class="ip-search-ico" aria-hidden="true">${lu("search","lu14")}</span>
      <input id="ip-q" type="search" placeholder="Search sections\u2026" autocomplete="off" aria-label="Search sections">
    </div>
    <div class="ip-list" id="ip-list">${paint()}</div></div>`);
  m.classList.add("ins-pal-menu");
  /* Reposition after layout so tall palette stays on-screen below the live chrome */
  const pad = 12;
  const mh = Math.min(m.offsetHeight || 400, innerHeight - pad * 2);
  let top = y;
  if (top + mh > innerHeight - pad) top = Math.max(pad, innerHeight - mh - pad);
  m.style.top = top + "px";
  m.style.left = Math.max(pad, Math.min(x, innerWidth - Math.min(m.offsetWidth || 280, 300) - pad)) + "px";
  m.style.maxHeight = mh + "px";
  m.style.overflowY = "hidden";
  const list = m.querySelector("#ip-list");
  const bind = () => list.querySelectorAll("[data-t]").forEach(b => b.onclick = () => { closeCtx(); if (window.closeMobileSheets) closeMobileSheets(); addBlock(b.dataset.t, index); });
  bind();
  const q = m.querySelector("#ip-q");
  q.oninput = () => { list.innerHTML = paint(q.value); bind(); icons(); };
  q.onkeydown = e => {
    if (e.key === "Enter"){ const first = list.querySelector("[data-t]"); if (first){ closeCtx(); addBlock(first.dataset.t, index); } }
    e.stopPropagation();
  };
  requestAnimationFrame(() => q.focus());
}
function breakpointProps(props){
  if (builderBreakpoint === "desktop" || !props?._styleResponsive?.[builderBreakpoint]) return props;
  return Object.assign({}, props, {
    _style: Object.assign({}, props._style || {}, props._styleResponsive[builderBreakpoint])
  });
}
function blockWrap(b, opts){
  const T = themeOf(current.data.tokens);
  const wrap = document.createElement("div");
  wrap.className = "blk" + (opts.shared ? " shared-blk" : "");
  if (opts.shared) wrap.dataset.shared = opts.shared; else wrap.dataset.bid = b.id;
  const isSel = opts.shared ? (selected?.k === "s" && selected.slot === opts.shared) : (selected?.k === "b" && selected.id === b.id);
  if (isSel) wrap.classList.add("selected");
  wrap.innerHTML = renderBlockHTML(b.type, breakpointProps(b.props), T) +
    `<span class="blk-label">${opts.shared ? "Shared " + opts.shared : BLOCKS[b.type]?.name || b.type}${!opts.shared && bprefs().showIds === "yes" ? ` <code>${esc(b.id)}</code>` : ""}</span>
     ${opts.shared ? `<div class="blk-tools"><button data-act="unshare" title="Remove shared ${opts.shared}">${lu("x")}</button></div>` :
     `<div class="blk-tools">
       <button class="grip" draggable="true" title="Drag to reorder">${lu("grip-vertical")}</button>
       <button data-act="up" title="Move up">${lu("arrow-up")}</button>
       <button data-act="down" title="Move down">${lu("arrow-down")}</button>
       <button data-act="dup" title="Duplicate">${lu("copy")}</button>
       <button data-act="hide" title="Hide section">${lu("eye-off")}</button>
       <button data-act="del" title="Delete">${lu("x")}</button>
     </div>`}`;
  const grip = wrap.querySelector(".grip");
  if (grip){
    grip.addEventListener("dragstart", e => {
      e.stopPropagation();
      e.dataTransfer.setData("text/bd-move", b.id);
      e.dataTransfer.effectAllowed = "move";
      wrap.classList.add("dragging");
    });
    grip.addEventListener("dragend", () => wrap.classList.remove("dragging"));
    grip.addEventListener("click", e => e.stopPropagation());
  }
  wireBlkTools(wrap);
  return wrap;
}
function blockWireframe(b, opts){
  const def = BLOCKS[b.type] || { name: b.type, icon: "box" };
  const wrap = document.createElement("div");
  wrap.className = "blk bw-card" + (opts.shared ? " shared-blk" : "");
  if (opts.shared) wrap.dataset.shared = opts.shared; else wrap.dataset.bid = b.id;
  const isSel = opts.shared ? (selected?.k === "s" && selected.slot === opts.shared) : (selected?.k === "b" && selected.id === b.id);
  if (isSel) wrap.classList.add("selected");
  const preview = b.props.heading || b.props.brand || b.props.quote || b.props.label || b.props.title || "";
  /* Card only — actions live on the floating selection bar (no duplicate HUD) */
  wrap.innerHTML = `${opts.shared
      ? `<span class="bw-grip bw-grip--static" aria-hidden="true"></span>`
      : `<button type="button" class="bw-grip grip" draggable="true" title="Drag to reorder">${lu("grip-vertical")}</button>`}
    <div class="bw-inner">
      <span class="bw-icon">${lu(def.icon)}</span>
      <div class="bw-meta"><strong>${opts.shared ? "Shared " + opts.shared : def.name}</strong><em>${esc(preview || "Customize in the inspector →")}</em></div>
    </div>`;
  const grip = wrap.querySelector(".grip");
  if (grip){
    grip.addEventListener("dragstart", e => {
      e.stopPropagation();
      e.dataTransfer.setData("text/bd-move", b.id);
      e.dataTransfer.effectAllowed = "move";
      wrap.classList.add("dragging");
    });
    grip.addEventListener("dragend", () => wrap.classList.remove("dragging"));
    grip.addEventListener("click", e => e.stopPropagation());
  }
  return wrap;
}
function ensureBuilderFonts(){
  /* the app shell never loads Google fonts; the canvas only pulls them
     in when the customer's site actually uses a non-system pairing */
  if (!(FONT_PAIRS[current?.data.tokens.fontPair] || {}).ext) return;
  if (document.getElementById("bd-ext-fonts")) return;
  const l = document.createElement("link");
  l.id = "bd-ext-fonts"; l.rel = "stylesheet"; l.href = GOOGLE_FONT_URL;
  document.head.appendChild(l);
}
function hiddenStub(b){
  const el = document.createElement("div");
  el.className = "blk-hidden-stub";
  el.dataset.bid = b.id;
  el.innerHTML = `${lu("eye-off","lu14")} ${esc(BLOCKS[b.type]?.name || b.type)} — hidden <button>Show</button>`;
  el.querySelector("button").onclick = e => {
    e.stopPropagation();
    commit(); b.hidden = false; markDirty();
    renderCanvas(); renderLayers(); icons();
  };
  return el;
}
function guardCanvasNavigation(e){
  /* Never let the Studio tab follow canvas links — Preview opens the site in a new tab. */
  if (e.target.closest?.("[contenteditable=true]")) return;
  const a = e.target.closest?.("a, area");
  if (!a) return;
  const href = a.getAttribute("href");
  if (!href || href === "#" || href.startsWith("#") || /^(mailto:|tel:|sms:|javascript:)/i.test(href)) return;
  e.preventDefault();
  e.stopPropagation();
}
function handleCanvasClick(e){
  if (e.target.closest("a, area") && !e.target.closest("[contenteditable=true]")) {
    guardCanvasNavigation(e);
  }
  if (previewMode) return;
  if (e.target.closest("[contenteditable=true]")) return;
  if (e.target.closest(".live-sel-bar") || e.target.closest(".live-chrome") || e.target.closest("#inspector")) return;
  const wrap = e.target.closest(".blk");
  if (!wrap || !current) return;
  const act = e.target.closest("[data-act]")?.dataset.act;
  if (act){
    e.stopPropagation();
    if (act === "unshare" && wrap.dataset.shared){
      commit(); current.data.shared[wrap.dataset.shared] = null; selected = null; clearMultiSel();
      markDirty(); renderCanvas(); renderInspector(); renderLayers(); icons();
      return;
    }
    if (wrap.dataset.bid){
      blockAction(act, activePage().blocks.findIndex(x => x.id === wrap.dataset.bid));
    }
    return;
  }
  const additive = !!(e.shiftKey || e.metaKey || e.ctrlKey);
  if (wrap.dataset.shared){
    selected = { k:"s", slot: wrap.dataset.shared };
    clearMultiSel();
  } else if (wrap.dataset.bid){
    setBlockSelection(wrap.dataset.bid, { additive });
  } else {
    selected = null;
    clearMultiSel();
  }
  paintSelection(); renderInspector(); renderLayers(); icons();
  if (additive || selectedBlockIdSet().size > 1){
    /* Live edit: keep inspector closed until Edit / double-click */
    if (!liveEdit && window.innerWidth <= 1080) $("#inspector")?.classList.add("open");
    syncLiveSelBar();
    return;
  }
  /* API action buttons fire a request — don't open inline edit on the label. */
  if (e.target.closest("[data-bd-api]")) {
    syncLiveSelBar();
    return;
  }
  const te = e.target.closest("[data-e]");
  if (te){
    if (liveEdit){
      const insp = $("#inspector");
      /* Keep the panel open on Content while typing — don't yank it away mid-edit. */
      if (insp?.classList.contains("live-open")){
        inspTabSet("content");
        inspEditSubSet("content");
        withCanvasScrollLocked(() => { renderInspector(); icons(); });
      }
    } else if (window.innerWidth <= 1080){
      $("#inspector")?.classList.add("open");
    }
    queueMicrotask(() => startInlineEdit(te));
    syncLiveSelBar();
    return;
  }
  /* Live edit: select only — inspector opens from Edit / double-click */
  if (!liveEdit && window.innerWidth <= 1080) $("#inspector")?.classList.add("open");
  syncLiveSelBar();
}
function openLiveInspector(tab){
  if (previewMode || !liveEdit) return;
  withCanvasScrollLocked(() => {
    $("#inspector")?.classList.add("live-open");
    if (tab === "style"){ inspTabSet("style"); inspEditSubSet("style"); }
    else { inspTabSet("content"); inspEditSubSet("content"); }
    renderInspector();
    icons();
  });
  syncLiveSelBar();
}
function renderCanvas(){
  const sheet = $("#page-sheet"); if (!sheet || !current) return;
  ensureBuilderFonts();
  const d = current.data, T = themeOf(d.tokens), pg = activePage();
  if (!pg) return;
  if (!Array.isArray(pg.blocks)) pg.blocks = [];
  /* Block editor = cards; Live edit + Preview = produced site render */
  const produced = !!(liveEdit || previewMode);
  const wrapBlk = produced ? blockWrap : blockWireframe;
  if (produced){
    sheet.style.background = T.pageBg || T.bg;
    sheet.style.color = T.ink;
    sheet.style.fontFamily = T.body;
  } else {
    sheet.style.background = "";
    sheet.style.color = "";
    sheet.style.fontFamily = "";
  }
  const wrapEl = $("#canvas-wrap");
  if (wrapEl) wrapEl.style.background = "";
  sheet.innerHTML = "";
  if (produced){
    const base = document.createElement("style");
    base.textContent = BLOCK_BASE_CSS + "\n" + (typeof motionSiteCss === "function" ? motionSiteCss(d.tokens) : "");
    sheet.appendChild(base);
    const mClass = typeof motionHtmlClass === "function" ? motionHtmlClass(d.tokens) : "";
    document.documentElement.classList.remove("bd-m-off","bd-m-subtle","bd-m-medium","bd-m-expressive");
    if (mClass) document.documentElement.classList.add(...mClass.split(/\s+/).filter(Boolean));
  }
  if (d.shared.header) sheet.appendChild(wrapBlk(d.shared.header, { shared:"header" }));
  sheet.appendChild(dropLine(0));
  if (!pg.blocks.length){
    const empty = document.createElement("div");
    empty.className = "canvas-empty canvas-empty--coach";
    const starters = [
      { type: "graphicHero", label: "Graphic hero", icon: "sparkles" },
      { type: "features", label: "Features", icon: "layout-grid" },
      { type: "cta", label: "Call to action", icon: "mouse-pointer-click" }
    ].filter(s => BLOCKS[s.type]);
    empty.innerHTML = produced
      ? `${lu("layout-template","lu24")}<h3>This page is empty.</h3>
      <p>Add a section, then edit text in place — or leave Live edit to rearrange blocks.</p>
      <button type="button" class="btn btn-cta btn-sm" id="ce-add">${lu("plus","lu14")} Add a section</button>`
      : `${lu("layout-template","lu24")}<h3>Start with a section</h3>
      <p>Drop a block on the canvas, or pick a quick start below. Open Live edit anytime for the produced look.</p>
      <div class="canvas-empty-starters">${starters.map(s =>
        `<button type="button" class="canvas-empty-chip" data-ce-type="${esc(s.type)}">${lu(s.icon,"lu14")} ${esc(s.label)}</button>`
      ).join("")}</div>
      <button type="button" class="btn btn-cta btn-sm" id="ce-add">${lu("plus","lu14")} Browse library</button>`;
    sheet.appendChild(empty);
    empty.querySelector("#ce-add").onclick = e => {
      e.stopPropagation();
      insertPalette(e.clientX, e.clientY, 0);
    };
    empty.querySelectorAll("[data-ce-type]").forEach(btn => {
      btn.onclick = e => {
        e.stopPropagation();
        addBlock(btn.dataset.ceType, 0);
        toast(`Added ${BLOCKS[btn.dataset.ceType]?.name || "section"}`, { kind: "success" });
      };
    });
  }
  pg.blocks.forEach((b, i) => {
    sheet.appendChild(b.hidden ? hiddenStub(b) : wrapBlk(b, {}));
    sheet.appendChild(dropLine(i + 1));
  });
  if (d.shared.footer) sheet.appendChild(wrapBlk(d.shared.footer, { shared:"footer" }));
  paintPageSwitch();
  paintSelection();
  icons();
  applyFrame();
  if (produced && typeof motionScrollBoot === "function"){
    requestAnimationFrame(() => motionScrollBoot(sheet));
  }
  if (produced && typeof bdApiBoot === "function"){
    requestAnimationFrame(() => bdApiBoot(sheet));
  }
}
function paintSelection(){
  const ids = selectedBlockIdSet();
  $$(".blk").forEach(el => {
    let on = false;
    if (el.dataset.shared) on = selected?.k === "s" && selected.slot === el.dataset.shared;
    else if (el.dataset.bid) on = ids.has(el.dataset.bid);
    el.classList.toggle("selected", on);
    el.classList.toggle("multi-selected", !!(el.dataset.bid && ids.size > 1 && ids.has(el.dataset.bid)));
  });
  $("#block-float-bar")?.remove();
  syncLiveSelBar();
  syncLiveInspector();
}
function wireBlkTools(wrap){
  wrap.querySelectorAll(".blk-tools button").forEach(btn => {
    btn.type = "button";
    btn.addEventListener("mousedown", e => e.stopPropagation());
  });
}
function blockAction(act, i){
  if (i < 0) return;
  commit();
  const arr = activePage().blocks;
  if (act === "up"   && i > 0)            [arr[i-1], arr[i]] = [arr[i], arr[i-1]];
  if (act === "down" && i < arr.length-1) [arr[i+1], arr[i]] = [arr[i], arr[i+1]];
  if (act === "dup"){ const c = JSON.parse(JSON.stringify(arr[i])); c.id = uid(); arr.splice(i+1, 0, c); selected = { k:"b", id:c.id }; clearMultiSel(); }
  if (act === "hide"){ arr[i].hidden = !arr[i].hidden; toast(arr[i].hidden ? "Section hidden — it won’t publish until shown again" : "Section visible again"); }
  if (act === "del"){
    const id = arr[i].id;
    arr.splice(i, 1);
    if (selected?.id === id) selected = null;
    multiSel = multiSel.filter(x => x !== id);
  }
  markDirty(); renderCanvas(); renderInspector(); renderLayers(); icons();
}
function blockContext(e){
  const wrap = e.target.closest(".blk");
  if (!wrap || wrap.dataset.shared) return;
  e.preventDefault();
  const id = wrap.dataset.bid;
  const i = activePage().blocks.findIndex(b => b.id === id);
  const blk = activePage().blocks[i];
  if (!isBlockIdSelected(id)) setBlockSelection(id);
  const multi = selectedPageBlocks();
  const saveLabel = multi.length > 1
    ? `Save ${multi.length} as custom section`
    : "Save as custom section";
  const m = openCtx(e.clientX, e.clientY, `
    <button data-a="dup">${lu("copy")} Duplicate</button>
    <button data-a="save">${lu("puzzle")} ${esc(saveLabel)}</button>
    <button data-a="up">${lu("arrow-up")} Move up</button>
    <button data-a="down">${lu("arrow-down")} Move down</button>
    <button data-a="hide">${lu(blk?.hidden ? "eye" : "eye-off")} ${blk?.hidden ? "Show section" : "Hide section"}</button>
    <button data-a="del" class="danger">${lu("trash-2")} Delete</button>`);
  m.querySelectorAll("button").forEach(b => b.onclick = () => {
    closeCtx();
    if (b.dataset.a === "save"){
      const blocks = selectedPageBlocks();
      if (blocks.length > 1) createCustomSectionFlow({ fromBlocks: blocks });
      else createCustomSectionFlow({ fromBlock: activePage().blocks[i] });
      return;
    }
    blockAction(b.dataset.a, i);
  });
}
/* ---------- inline edit ---------- */
function startInlineEdit(t){
  if (!t || previewMode) return;
  const wrap = t.closest(".blk"); if (!wrap) return;
  const blk = wrap.dataset.shared ? current.data.shared[wrap.dataset.shared] : activePage().blocks.find(b => b.id === wrap.dataset.bid);
  if (!blk) return;
  if (t.getAttribute("contenteditable") === "true") return;
  const key = t.dataset.e;
  const before = blk.props[key] != null ? String(blk.props[key]) : (t.innerText || "");
  const multiline = /\n/.test(before) || /pre-wrap|pre-line/.test(getComputedStyle(t).whiteSpace || "");
  /* Prefer prop text so newlines survive; DOM may already be escaped as text nodes */
  if (multiline && blk.props[key] != null) t.textContent = before;
  t.setAttribute("contenteditable", "true");
  t.classList.add("is-editing");
  try { t.focus({ preventScroll: true }); } catch { t.focus(); }
  try { document.execCommand && document.execCommand("selectAll", false, null); } catch {}
  const finish = (cancel) => {
    t.removeAttribute("contenteditable");
    t.classList.remove("is-editing");
    t.removeEventListener("blur", onBlur); t.removeEventListener("keydown", onKey);
    if (cancel){
      t.textContent = before;
      return;
    }
    /* Keep intentional line breaks; collapse only runaway blank runs */
    const v = (t.innerText || "").replace(/\u00a0/g, " ").replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n").trimEnd();
    if (v === before) return;
    commit(); blk.props[key] = v; markDirty();
    /* Patch in place — avoid full canvas re-render so selection / scroll stay put */
    if (!multiline) t.textContent = v;
    renderInspector();
    syncLiveSelBar();
    icons();
  };
  const onBlur = () => finish(false);
  const onKey = ev => {
    if (ev.key === "Escape"){ ev.preventDefault(); finish(true); }
    /* Single-line fields: Enter commits. Multiline / pre-wrap: Enter inserts a line. */
    if (ev.key === "Enter" && !ev.shiftKey && !multiline){ ev.preventDefault(); t.blur(); }
  };
  t.addEventListener("blur", onBlur); t.addEventListener("keydown", onKey);
}
function inlineEdit(e){
  if (previewMode) return;
  const t = e.target.closest("[data-e]");
  if (t){
    e.preventDefault();
    startInlineEdit(t);
    return;
  }
  /* Double-click a section (not a text node) opens the inspector on demand */
  const wrap = e.target.closest(".blk");
  if (wrap && liveEdit){
    e.preventDefault();
    openLiveInspector("content");
  }
}
/* ---------- inspector ---------- */
function clearMultiSel(){ multiSel = []; }
function selectedBlockIdSet(){
  const ids = new Set(multiSel.filter(Boolean));
  if (selected?.k === "b" && selected.id) ids.add(selected.id);
  return ids;
}
/** Page blocks in canvas order for the current multi/single selection. */
function selectedPageBlocks(){
  const ids = selectedBlockIdSet();
  if (!ids.size) return [];
  return (activePage()?.blocks || []).filter(b => ids.has(b.id));
}
function isBlockIdSelected(id){
  if (!id) return false;
  return (selected?.k === "b" && selected.id === id) || multiSel.includes(id);
}
function setBlockSelection(id, { additive = false } = {}){
  if (!id){
    selected = null;
    clearMultiSel();
    return;
  }
  if (!additive){
    selected = { k:"b", id };
    clearMultiSel();
    return;
  }
  if (selected?.k === "s"){
    selected = { k:"b", id };
    clearMultiSel();
    return;
  }
  if (!selected || selected.k !== "b"){
    selected = { k:"b", id };
    clearMultiSel();
    return;
  }
  if (selected.id === id){
    if (multiSel.length){
      selected = { k:"b", id: multiSel[0] };
      multiSel = multiSel.slice(1);
    } else {
      selected = null;
    }
    return;
  }
  if (multiSel.includes(id)) multiSel = multiSel.filter(x => x !== id);
  else multiSel.push(id);
}
function selBlock(){
  if (!selected) return null;
  if (selected.k === "s") return current.data.shared[selected.slot];
  return activePage().blocks.find(b => b.id === selected.id) || null;
}
/* collapsible inspector groups with remembered open/closed state */
function inspOpenMap(){ try { return JSON.parse(localStorage.getItem("bd_insp_open")) || {}; } catch { return {}; } }
function inspGroup(parent, key, title, defOpen){
  const open = inspOpenMap()[key] ?? defOpen;
  const g = document.createElement("div");
  g.className = "insp-group" + (open ? " open" : "");
  g.innerHTML = `<button class="ig-head">${lu("chevron-right","lu14")}<span>${esc(title)}</span></button><div class="ig-body"></div>`;
  g.querySelector(".ig-head").onclick = () => {
    g.classList.toggle("open");
    const s = inspOpenMap(); s[key] = g.classList.contains("open");
    localStorage.setItem("bd_insp_open", JSON.stringify(s));
  };
  parent.appendChild(g);
  return g.querySelector(".ig-body");
}
/* Style field defaults for sections - extend existing system */
const BUILDER_STYLE_DEFAULTS = {
  /* Existing layout fields */
  pad: "default",
  maxW: "default",
  align: "inherit",
  bg: "default",
  ink: "",
  bgColor: "",
  bgColor2: "",
  bgAngle: "135",
  bgImage: "",
  bgOverlay: "",
  bgPos: "center",
  bgAttach: "scroll",
  bgPattern: "none",
  /* NEW: Layout modes */
  layoutMode: "default",
  flexDir: "column",
  flexAlign: "center",
  flexJustify: "start",
  gridCols: "",
  gridGap: "",
  /* NEW: Position */
  position: "",
  stickyTop: "",
  zIndex: "",
  top: "",
  right: "",
  bottom: "",
  left: "",
  width: "",
  height: "",
  /* NEW: Background video */
  bgVideo: "",
  bgVideoOverlay: "",
  bgSvg: "",
  /* NEW: Visual effects */
  backdropBlur: "",
  blur: "",
  shadow: "",
  blendMode: "",
  borderRadius: "",
  opacity: "",
  /* Existing effects */
  reveal: "site",
  revealWhen: "site",
  motion: "none",
  hover: "none",
  /* NEW: Ticker */
  ticker: "off",
  tickerSpeed: "",
  /* Advanced */
  hide: "",
  anchor: ""
};

/* Style field definitions for the inspector Style tab */
const BUILDER_STYLE_FIELDS = {
  layout: [
    { k:"layoutMode", t:"seg", label:"Mode", opts:[
      ["default","Default"],
      ["flex","Stack"],
      ["grid","Grid"]
    ]},
    { k:"flexDir", t:"seg", label:"Direction", opts:[["column","Vertical"],["row","Horizontal"]], showIf:{k:"layoutMode",v:"flex"} },
    { k:"flexAlign", t:"seg", label:"Align", opts:[["start","Start"],["center","Center"],["end","End"]], showIf:{k:"layoutMode",v:"flex"} },
    { k:"flexJustify", t:"seg", label:"Justify", opts:[["start","Start"],["center","Center"],["end","End"],["between","Space between"]], showIf:{k:"layoutMode",v:"flex"} },
    { k:"gridCols", t:"text", label:"Columns", placeholder:"e.g. repeat(3, 1fr)", showIf:{k:"layoutMode",v:"grid"} },
    { k:"gridGap", t:"text", label:"Gap", placeholder:"e.g. 2rem", showIf:{k:"layoutMode",v:"grid"} },
    { k:"pad", t:"seg", label:"Padding", opts:[["none","None"],["compact","Compact"],["default","Default"],["spacious","Spacious"],["huge","Huge"]] },
    { k:"maxW", t:"seg", label:"Max width", opts:[["default","Default"],["narrow","Narrow"],["wide","Wide"],["full","Full"]] },
    { k:"align", t:"seg", label:"Text align", opts:[["inherit","Auto"],["left","Left"],["center","Center"],["right","Right"]] },
    { k:"position", t:"seg", label:"Position", opts:[["flow","Flow"],["sticky","Sticky"],["free","Free"]] },
    { k:"stickyTop", t:"text", label:"Sticky top", placeholder:"e.g. 4rem", showIf:{k:"position",v:"sticky"} },
    { k:"top", t:"text", label:"Top", placeholder:"e.g. 40px", showIf:{k:"position",v:"free"} },
    { k:"right", t:"text", label:"Right", placeholder:"e.g. 5vw", showIf:{k:"position",v:"free"} },
    { k:"bottom", t:"text", label:"Bottom", placeholder:"auto", showIf:{k:"position",v:"free"} },
    { k:"left", t:"text", label:"Left", placeholder:"e.g. 10%", showIf:{k:"position",v:"free"} },
    { k:"width", t:"text", label:"Width", placeholder:"e.g. min(600px, 80vw)", showIf:{k:"position",v:"free"} },
    { k:"height", t:"text", label:"Height", placeholder:"e.g. 400px", showIf:{k:"position",v:"free"} },
    { k:"zIndex", t:"text", label:"Z-index", placeholder:"e.g. 10" }
  ],
  background: [
    { k:"bg", t:"seg", label:"Fill", opts:[["default","Inherit"],["alt","Alt"],["tint","Tint"],["dark","Dark"],["custom","Custom"],["glass","Glass"],["gradient","Gradient"],["image","Image"]] },
    { k:"bgColor", t:"color", label:"Color", showIf:{k:"bg",v:["custom","tint"]} },
    { k:"bgColor", t:"color", label:"Start color", showIf:{k:"bg",v:"gradient"} },
    { k:"bgColor2", t:"color", label:"End color", showIf:{k:"bg",v:"gradient"} },
    { k:"bgAngle", t:"seg", label:"Angle", opts:[["0","0°"],["45","45°"],["90","90°"],["135","135°"],["180","180°"]], showIf:{k:"bg",v:"gradient"} },
    { k:"bgImage", t:"text", label:"Image URL", placeholder:"Paste from Assets library", showIf:{k:"bg",v:"image"} },
    { k:"bgOverlay", t:"seg", label:"Overlay", opts:[["none","None"],["light","Light"],["dark","Dark"],["accent","Accent"],["heavy","Heavy"]], showIf:{k:"bg",v:"image"} },
    { k:"bgPos", t:"seg", label:"Position", opts:[["center","Center"],["top","Top"],["bottom","Bottom"],["left","Left"],["right","Right"]], showIf:{k:"bg",v:"image"} },
    { k:"bgAttach", t:"seg", label:"Attachment", opts:[["scroll","Scroll"],["fixed","Fixed"]], showIf:{k:"bg",v:"image"} },
    { k:"bgVideo", t:"text", label:"Video URL", placeholder:"MP4 or WebM URL" },
    { k:"bgVideoOverlay", t:"seg", label:"Video overlay", opts:[["none","None"],["light","Light"],["dark","Dark"],["heavy","Heavy"]], showIf:{k:"bgVideo"} },
    { k:"bgSvg", t:"text", label:"SVG artwork URL", placeholder:"Paste an SVG asset URL" },
    { k:"backdropBlur", t:"text", label:"Backdrop blur", placeholder:"e.g. 12px" },
    { k:"blur", t:"text", label:"Blur", placeholder:"e.g. 4px" },
    { k:"shadow", t:"text", label:"Shadow", placeholder:"e.g. 0 4px 12px rgba(0,0,0,0.1)" },
    { k:"blendMode", t:"seg", label:"Blend mode", opts:[["normal","Normal"],["multiply","Multiply"],["screen","Screen"],["overlay","Overlay"],["darken","Darken"],["lighten","Lighten"]] },
    { k:"opacity", t:"text", label:"Opacity", placeholder:"0 to 1, e.g. 0.9" }
  ],
  effects: [
    { k:"reveal", t:"seg", label:"Appear", opts:[["site","Site default"],["none","Off"],["fade","Fade"],["rise","Rise"],["scale","Scale"],["blur","Blur"]] },
    { k:"revealWhen", t:"seg", label:"Trigger", opts:[["site","Site default"],["instant","Instant"],["scroll","On scroll"]], showIf:{k:"reveal",v:["fade","rise","scale","blur"]} },
    { k:"motion", t:"seg", label:"Loop", opts:[["none","Off"],["float","Float"],["pulse","Pulse"],["rotate","Rotate"]] },
    { k:"hover", t:"seg", label:"Hover", opts:[["none","Off"],["lift","Lift"],["glow","Glow"],["zoom","Zoom"]] },
    { k:"ticker", t:"seg", label:"Ticker", opts:[["off","Off"],["left","Scroll left"],["right","Scroll right"]] },
    { k:"tickerSpeed", t:"text", label:"Ticker speed", placeholder:"e.g. 40s", showIf:{k:"ticker",v:["left","right"]} }
  ],
  advanced: [
    { k:"hide", t:"seg", label:"Visibility", opts:[["","Always visible"],["mobile","Hide mobile"],["desktop","Hide desktop"]] },
    { k:"anchor", t:"text", label:"Anchor ID", placeholder:"my-section" }
  ]
};

function ensureBlockStyle(blk){
  if (!blk.props) blk.props = {};
  /* Always mutate the live object on the block — never leave callbacks holding a stale clone. */
  blk.props._style = Object.assign({}, BUILDER_STYLE_DEFAULTS, blk.props._style || {});
  return blk.props._style;
}
function styleField(parent, blk, f){
  const base = ensureBlockStyle(blk);
  const overrides = builderBreakpoint === "desktop" ? null : (blk.props._styleResponsive?.[builderBreakpoint] || {});
  const st = Object.assign({}, base, overrides || {});
  if (!st.position) st.position = "flow";
  if (f.showIf){
    const cur = st[f.showIf.k];
    const ok = Object.prototype.hasOwnProperty.call(f.showIf, "v")
      ? (Array.isArray(f.showIf.v) ? f.showIf.v.includes(cur) : cur === f.showIf.v)
      : !!cur;
    if (!ok) return;
  }
  const live = !!(liveEdit && !previewMode);
  parent.appendChild(fieldEl(f, st[f.k], (v, final) => {
    if (final) commit();
    if (builderBreakpoint === "desktop"){
      ensureBlockStyle(blk)[f.k] = v;
    } else {
      if (!blk.props._styleResponsive) blk.props._styleResponsive = {};
      if (!blk.props._styleResponsive[builderBreakpoint]) blk.props._styleResponsive[builderBreakpoint] = {};
      if (!blk.props._responsiveId) blk.props._responsiveId = "r-" + uid();
      blk.props._styleResponsive[builderBreakpoint][f.k] = v;
    }
    markDirty();
    /* Colour drag: skip canvas until commit. Other fields update live. */
    if (!(final === false && f.t === "color")) renderCanvas();
    /* Rebuild when dependent fields appear/disappear (e.g. Image overlay after Fill → Image). */
    if (!(final === false && f.t === "color") && ["bg", "reveal", "bgPattern", "motion", "hover", "revealWhen", "position", "bgVideo"].includes(f.k)){
      const pane = $("#inspector .insp-pane");
      const scroll = pane?.scrollTop || 0;
      const keepOpen = $("#inspector")?.classList.contains("live-open");
      requestAnimationFrame(() => {
        if (live){ inspTabSet("style"); inspEditSubSet("style"); }
        else { inspTabSet("edit"); inspEditSubSet("style"); }
        renderInspector();
        if (keepOpen) $("#inspector")?.classList.add("live-open");
        const p2 = $("#inspector .insp-pane");
        if (p2) p2.scrollTop = scroll;
        syncLiveSelBar();
        icons();
      });
    }
  }, { live }));
}
function effectOf(blk, type){
  return (Array.isArray(blk.props?._fx) ? blk.props._fx : []).find(f => f?.type === type) || null;
}
function setEffectValue(blk, type, path, value){
  if (!Array.isArray(blk.props._fx)) blk.props._fx = [];
  let fx = effectOf(blk, type);
  if (path === "preset" && value === "none"){
    blk.props._fx = blk.props._fx.filter(f => f?.type !== type);
    return;
  }
  if (!fx){
    fx = {
      id: "fx-" + uid(),
      type,
      preset:value === "none" ? (type === "appear" ? "rise" : type === "scroll" ? "parallax-up" : "lift") : value,
      transition:{ duration:type === "hover" ? 300 : 650, ease:"outExpo" }
    };
    if (type === "appear") fx.trigger = { kind:"inView", amount:.15, once:true };
    blk.props._fx.push(fx);
  }
  if (path === "preset") fx.preset = value;
  else if (path === "trigger.kind"){
    fx.trigger = Object.assign({ amount:.15, once:true }, fx.trigger || {}, { kind:value });
  } else if (path === "delay") fx.delay = Math.max(0, parseInt(value, 10) || 0);
  else if (path.startsWith("transition.")){
    const key = path.split(".")[1];
    fx.transition = Object.assign({}, fx.transition || {});
    fx.transition[key] = key === "duration" ? Math.max(0, parseInt(value, 10) || 0) : value;
  }
}
function appendEffectField(parent, blk, type, path, def, fallback){
  const fx = effectOf(blk, type);
  let value = fallback;
  if (path === "preset") value = fx?.preset || "none";
  else if (path === "trigger.kind") value = fx?.trigger?.kind || fallback;
  else if (path === "delay") value = fx?.delay ?? fallback;
  else if (path.startsWith("transition.")) value = fx?.transition?.[path.split(".")[1]] ?? fallback;
  parent.appendChild(fieldEl(def, String(value ?? ""), (v, final) => {
    if (final) commit();
    setEffectValue(blk, type, path, v);
    markDirty();
    renderCanvas();
    if (path === "preset"){
      requestAnimationFrame(() => {
        inspTabSet(liveEdit && !previewMode ? "style" : "edit");
        inspEditSubSet("style");
        renderInspector();
        icons();
      });
    }
  }, { live:!!(liveEdit && !previewMode) }));
}
function renderEffectsControls(parent, blk){
  const section = (title, type, presetOptions) => {
    const group = document.createElement("div");
    group.className = "fx-control-group";
    group.innerHTML = `<p class="fx-control-title">${esc(title)}</p>`;
    appendEffectField(group, blk, type, "preset", { t:"seg", label:"Animation", opts:presetOptions }, "none");
    const fx = effectOf(blk, type);
    if (fx){
      if (type === "appear"){
        appendEffectField(group, blk, type, "trigger.kind", { t:"seg", label:"Trigger", opts:[["inView","On scroll"],["load","On load"]] }, "inView");
      }
      appendEffectField(group, blk, type, "delay", { t:"text", label:"Delay (ms)", placeholder:"0" }, "0");
      appendEffectField(group, blk, type, "transition.duration", { t:"text", label:"Duration (ms)", placeholder:type === "hover" ? "300" : "650" }, type === "hover" ? "300" : "650");
      appendEffectField(group, blk, type, "transition.ease", { t:"seg", label:"Easing", opts:[["outExpo","Expo"],["easeOut","Out"],["easeInOut","In/out"],["linear","Linear"]] }, "outExpo");
    }
    parent.appendChild(group);
  };
  section("Entrance", "appear", [["none","Off"],["fade","Fade"],["rise","Rise"],["scale","Scale"],["blur","Blur"],["slide-l","Left"],["slide-r","Right"],["mask","Mask"]]);
  section("Scroll", "scroll", [["none","Off"],["fade","Fade"],["scale","Scale"],["parallax-up","Parallax"],["parallax-left","Left"],["parallax-right","Right"]]);
  section("Hover", "hover", [["none","Off"],["lift","Lift"],["glow","Glow"],["zoom","Zoom"],["tilt","Tilt"]]);
}
let _inspSelSig = null;
function inspTabGet(){ try { return sessionStorage.getItem("bd_insp_tab") || ""; } catch { return ""; } }
function inspTabSet(tab){ try { sessionStorage.setItem("bd_insp_tab", tab); } catch {} }
function inspEditSubGet(){ try { return sessionStorage.getItem("bd_insp_edit") || "content"; } catch { return "content"; } }
function inspEditSubSet(sub){ try { sessionStorage.setItem("bd_insp_edit", sub); } catch {} }
function renderInspector(){
  const insp = $("#inspector"); if (!insp || !current) return;
  document.querySelectorAll("body > .bd-color-pop").forEach(p => p.remove());
  const prevScroll = insp.querySelector(".insp-pane")?.scrollTop || 0;
  insp.innerHTML = "";
  insp.classList.add("insp-tabbed");
  insp.classList.toggle("insp-live", !!liveEdit && !previewMode);
  const multi = selectedPageBlocks();
  if (multi.length > 1){
    _inspSelSig = "multi:" + multi.map(b => b.id).join(",");
    const chrome = document.createElement("div");
    chrome.className = "insp-chrome" + (liveEdit && !previewMode ? " insp-chrome--live" : "");
    chrome.innerHTML = `<div class="insp-head">
      <div class="insp-head-main">
        <span class="insp-eyebrow">Selection</span>
        <span class="t">${multi.length} sections selected</span>
        <span class="insp-head-sub">Shift-click to add or remove · Save as one custom section</span>
      </div>
      <div class="insp-head-acts">
        <button type="button" class="insp-ico" data-x="clear" title="Clear selection">${lu("x","lu14")}</button>
      </div>
    </div>`;
    chrome.querySelector('[data-x="clear"]').onclick = () => {
      selected = null; clearMultiSel();
      paintSelection(); renderInspector(); renderLayers(); icons();
    };
    insp.appendChild(chrome);
    const pane = document.createElement("div");
    pane.className = "insp-pane";
    const names = multi.map(b => BLOCKS[b.type]?.name || b.type).join(", ");
    pane.innerHTML = `<div class="insp-card">
      <p class="insp-card-kicker">Custom section</p>
      <p class="c-sub" style="margin:0 0 14px">${esc(names)}</p>
      <button type="button" class="btn btn-cta btn-sm" data-x="save">${lu("puzzle","lu14")} Save ${multi.length} as custom</button>
    </div>`;
    pane.querySelector('[data-x="save"]').onclick = () => createCustomSectionFlow({ fromBlocks: selectedPageBlocks() });
    insp.appendChild(pane);
    icons();
    return;
  }
  const blk = selBlock();
  const selSig = blk
    ? (selected.k === "s" ? `s:${selected.slot}` : `b:${selected.id}`)
    : "";
  const selChanged = selSig !== _inspSelSig;
  if (selChanged){
    const had = _inspSelSig;
    _inspSelSig = selSig;
    if (blk && selSig !== had) inspTabSet(liveEdit ? "content" : "edit");
    else if (!blk && (inspTabGet() === "edit" || inspTabGet() === "content" || inspTabGet() === "style")) inspTabSet("design");
  }
  let tab = inspTabGet();
  const liveMode = !!(liveEdit && !previewMode);

  if (liveMode){
    /* Flattened component editor: Content | Style | Design | SEO */
    if (tab === "edit") tab = inspEditSubGet() === "style" ? "style" : "content";
    if (tab === "more") tab = "style";
    if (!["content", "style", "design", "seo"].includes(tab)) tab = blk ? "content" : "design";
    if ((tab === "content" || tab === "style") && !blk) tab = "design";
    /* Structural blocks have no section styles — land on Content instead of an empty Style pane. */
    if (blk && BLOCKS[blk.type]?.styleable === false && tab === "style") tab = "content";
  } else {
    if (tab !== "edit" && tab !== "design" && tab !== "seo") tab = blk ? "edit" : "design";
    if (tab === "edit" && !blk) tab = "design";
  }

  const chrome = document.createElement("div");
  chrome.className = "insp-chrome" + (liveMode ? " insp-chrome--live" : "");

  const headEl = document.createElement("div");
  headEl.className = "insp-head" + (blk ? "" : " insp-head--idle");
  if (blk){
    const def = BLOCKS[blk.type];
    headEl.innerHTML = liveMode
      ? `<div class="insp-head-ico" aria-hidden="true">${lu(def.icon || "box")}</div>
         <div class="insp-head-main">
           <span class="insp-eyebrow">${selected.k==="s" ? "Shared · " + esc(selected.slot) : "Component"}</span>
           <span class="t">${esc(def.name)}${selected.k==="s"?' <span class="badge">Shared</span>':""}</span>
           <span class="insp-head-sub">${esc(libCatLabel(def.group || ""))}${selected.k==="s" ? " · on every page" : " · edits apply live"}</span>
         </div>
         <div class="insp-head-acts">
           <button type="button" class="insp-ico" data-x="done" title="Done">${lu("check","lu14")}</button>
           <button type="button" class="insp-ico danger" data-x="rm" title="${selected.k==="s" ? "Remove shared " + esc(selected.slot) : "Remove section"}">${lu("trash-2","lu14")}</button>
         </div>`
      : `<div class="insp-head-main">
        <span class="t">${esc(def.name)}${selected.k==="s"?' <span class="badge">Shared</span>':""}</span>
        <span class="insp-head-sub">${esc(libCatLabel(def.group || ""))}</span>
      </div>
      <div class="insp-head-acts">
        <button type="button" class="insp-ico" data-x="clear" title="Deselect">${lu("minimize-2","lu14")}</button>
        <button type="button" class="insp-ico danger" data-x="rm" title="Remove section">${lu("trash-2","lu14")}</button>
      </div>`;
    const doneBtn = headEl.querySelector('[data-x="done"]') || headEl.querySelector('[data-x="clear"]');
    if (doneBtn) doneBtn.onclick = () => {
      if (liveMode){
        $("#inspector")?.classList.remove("live-open");
        syncLiveInspector();
        syncLiveSelBar();
        icons();
        return;
      }
      selected = null; clearMultiSel(); renderCanvas(); renderInspector(); icons();
    };
    headEl.querySelector('[data-x="rm"]').onclick = () => {
      if (selected.k === "s"){ commit(); current.data.shared[selected.slot] = null; selected = null; clearMultiSel(); markDirty(); renderCanvas(); renderInspector(); renderLayers(); icons(); }
      else blockAction("del", activePage().blocks.findIndex(b => b.id === blk.id));
    };
  } else {
    const titles = { design: "Design system", seo: "Page SEO", edit: "Inspector", content: "Inspector", style: "Style" };
    headEl.innerHTML = liveMode
      ? `<div class="insp-head-main"><span class="insp-eyebrow">Site</span><span class="t">${esc(titles[tab] || "Inspector")}</span><span class="insp-head-sub">Select a section to edit its content</span></div>`
      : `<div class="insp-head-main"><span class="t">${esc(titles[tab] || "Inspector")}</span><span class="insp-head-sub">Site-wide tokens and page meta</span></div>`;
  }
  chrome.appendChild(headEl);

  const tabs = document.createElement("nav");
  tabs.className = "insp-tabs" + (liveMode ? " insp-tabs--live" : "");
  tabs.setAttribute("role", "tablist");
  let tabDefs = [];
  if (liveMode){
    if (blk){
      const canStyle = BLOCKS[blk.type]?.styleable !== false;
      tabDefs.push(["content", "Content"]);
      if (canStyle) tabDefs.push(["style", "Style"]);
    }
    tabDefs.push(["design", "Design"], ["seo", "SEO"]);
  } else {
    if (blk) tabDefs.push(["edit", "Section"]);
    tabDefs.push(["design", "Design"], ["seo", "SEO"]);
  }
  tabs.innerHTML = tabDefs.map(([k, label]) =>
    `<button type="button" class="insp-tab${k === tab ? " on" : ""}" role="tab" aria-selected="${k === tab ? "true" : "false"}" data-itab="${k}">${esc(label)}</button>`
  ).join("");
  tabs.querySelectorAll("[data-itab]").forEach(btn => {
    btn.onclick = () => {
      const next = btn.dataset.itab;
      inspTabSet(next);
      if (next === "content" || next === "style") inspEditSubSet(next);
      if (next === "edit") inspEditSubSet("content");
      renderInspector();
      icons();
    };
  });
  chrome.appendChild(tabs);

  let editSub = inspEditSubGet();
  if (editSub !== "content" && editSub !== "style" && editSub !== "more") editSub = "content";
  if (!liveMode && tab === "edit" && blk){
    const sub = document.createElement("nav");
    sub.className = "insp-subtabs";
    sub.setAttribute("role", "tablist");
    const subs = [["content", "Content"], ["style", "Style"]];
    if (BLOCKS[blk.type].styleable !== false) subs.push(["more", "Visibility"]);
    if (!subs.some(([k]) => k === editSub)) editSub = "content";
    sub.innerHTML = subs.map(([k, label]) =>
      `<button type="button" class="insp-sub${k === editSub ? " on" : ""}" role="tab" aria-selected="${k === editSub ? "true" : "false"}" data-esub="${k}">${esc(label)}</button>`
    ).join("");
    sub.querySelectorAll("[data-esub]").forEach(btn => {
      btn.onclick = () => {
        inspEditSubSet(btn.dataset.esub);
        renderInspector();
        icons();
      };
    });
    chrome.appendChild(sub);
  }

  insp.appendChild(chrome);

  const pane = document.createElement("div");
  pane.className = "insp-pane" + (liveMode ? " insp-pane--live" : "");
  pane.setAttribute("role", "tabpanel");

  const editMode = liveMode
    ? (tab === "content" || tab === "style")
    : (tab === "edit");
  const activeEditSub = liveMode
    ? (tab === "style" ? "style" : "content")
    : editSub;

  if (editMode && blk){
    const def = BLOCKS[blk.type];
    if (selected.k === "s"){
      const tip = document.createElement("p"); tip.className = "hint insp-banner insp-banner--shared";
      tip.innerHTML = `${lu("link","lu14")} <span>Shared on every page — edit once, updates everywhere.</span>`;
      pane.appendChild(tip);
    }
    if (liveMode && activeEditSub === "content"){
      /* Quiet — selection chrome + fields are enough; no tutorial tip. */
    }
    const mount = document.createElement("div");
    mount.className = "insp-stack" + (liveMode ? " insp-stack--live" : "");
    if (activeEditSub === "content"){
      if (!(def.fields || []).length){
        mount.innerHTML = `<p class="insp-empty-fields">This section has no content fields.</p>`;
      } else {
        mount.classList.add("insp-stack--content");
        const card = document.createElement("section");
        card.className = "insp-style-card";
        card.innerHTML = `<header class="insp-style-card-head">${lu(def.icon || "pencil","lu14")}<span>Content</span></header>`;
        const body = document.createElement("div");
        body.className = "insp-style-card-body";
        def.fields.forEach(f => appendBlockContentField(body, blk.props, f, (key, v, final) => {
          if (final) commit();
          blk.props[key] = v; markDirty();
          const liveKey = selected?.k === "s"
            ? `.blk[data-shared="${selected.slot}"] [data-e="${key}"]`
            : `.blk[data-bid="${blk.id}"] [data-e="${key}"]`;
          const liveEl = document.querySelector(liveKey);
          if (liveEl && !liveEl.isContentEditable && (f.t === "text" || f.t === "area" || !f.t) && key === f.k){
            liveEl.textContent = v ?? "";
          } else {
            renderCanvas();
          }
        }, { live: liveMode, fields: def.fields }));
        if (blk.type === "footer") appendFooterLocationControls(body, blk);
        card.appendChild(body);
        mount.appendChild(card);
      }
      appendRelatedLayoutsCard(mount, blk);
      icons();
    } else if (activeEditSub === "style"){
      if (def.styleable === false){
        mount.classList.add("insp-stack--style");
        const card = document.createElement("section");
        card.className = "insp-style-card";
        card.innerHTML = `<header class="insp-style-card-head">${lu("info","lu14")}<span>Built-in layout</span></header>
          <div class="insp-style-card-body insp-struct-body">
            <p class="insp-struct-lead">${esc(def.name)} manages its own layout — spacing and structure stay consistent site-wide.</p>
            <p class="insp-struct-sub">Change brand, links, and labels under <strong>Content</strong>. Site colours, type, and radius live under <strong>Design</strong>.</p>
            <div class="insp-struct-acts">
              <button type="button" class="btn btn-cta btn-sm" data-x="to-content">${lu("pencil","lu14")} Edit content</button>
              <button type="button" class="btn btn-ghost btn-sm" data-x="to-design">${lu("palette","lu14")} Design tokens</button>
            </div>
          </div>`;
        card.querySelector('[data-x="to-content"]').onclick = () => {
          inspTabSet(liveMode ? "content" : "edit"); inspEditSubSet("content"); renderInspector(); icons();
        };
        card.querySelector('[data-x="to-design"]').onclick = () => {
          inspTabSet("design"); renderInspector(); icons();
        };
        mount.appendChild(card);
        icons();
      } else {
        mount.classList.add("insp-stack--style");
        const addGroup = (title, icon, fields) => {
          const card = document.createElement("section");
          card.className = "insp-style-card";
          card.innerHTML = `<header class="insp-style-card-head">${lu(icon,"lu14")}<span>${esc(title)}</span></header>`;
          const body = document.createElement("div");
          body.className = "insp-style-card-body";
          fields.forEach(f => styleField(body, blk, f));
          card.appendChild(body);
          mount.appendChild(card);
          icons();
        };
        const bpCard = document.createElement("div");
        bpCard.className = "responsive-edit-banner";
        bpCard.innerHTML = `<span>${lu(builderBreakpoint === "desktop" ? "monitor" : builderBreakpoint === "tablet" ? "tablet" : "smartphone","lu14")} Editing <strong>${esc(builderBreakpoint)}</strong> ${builderBreakpoint === "desktop" ? "defaults" : "overrides"}</span>${builderBreakpoint === "desktop" ? "" : `<button type="button">Reset overrides</button>`}`;
        if (builderBreakpoint !== "desktop"){
          bpCard.querySelector("button").onclick = () => {
            commit();
            if (blk.props._styleResponsive) delete blk.props._styleResponsive[builderBreakpoint];
            markDirty();
            renderCanvas();
            renderInspector();
            icons();
          };
        }
        mount.appendChild(bpCard);
        if (builderBreakpoint === "desktop"){
          addGroup("Layout", "layout-template", BUILDER_STYLE_FIELDS.layout);
          addGroup("Background", "paintbrush", BUILDER_STYLE_FIELDS.background);
          const fxCard = document.createElement("section");
          fxCard.className = "insp-style-card";
          fxCard.innerHTML = `<header class="insp-style-card-head">${lu("sparkles","lu14")}<span>Effects</span></header>`;
          const fxBody = document.createElement("div");
          fxBody.className = "insp-style-card-body";
          renderEffectsControls(fxBody, blk);
          fxCard.appendChild(fxBody);
          mount.appendChild(fxCard);
        } else {
          const responsiveKeys = new Set(["pad","maxW","align","position","stickyTop","top","right","bottom","left","width","height","zIndex"]);
          addGroup(`${builderBreakpoint[0].toUpperCase() + builderBreakpoint.slice(1)} layout`, "panels-top-left", BUILDER_STYLE_FIELDS.layout.filter(f => responsiveKeys.has(f.k)));
          addGroup("Visibility", "eye", [{ k:"hide", t:"seg", label:"At this breakpoint", opts:[["","Visible"],["yes","Hidden"]] }]);
        }
        if (liveMode && builderBreakpoint === "desktop") addGroup("Visibility", "eye", BUILDER_STYLE_FIELDS.advanced);
        const tip = document.createElement("p"); tip.className = "hint insp-style-hint";
        tip.textContent = "Changes apply live. Published effects use a same-origin external runtime under strict CSP.";
        mount.appendChild(tip);
      }
    } else {
      if (def.styleable === false){
        mount.innerHTML = `<p class="insp-empty-fields">No advanced style options for this block.</p>`;
      } else {
        mount.classList.add("insp-stack--style");
        const card = document.createElement("section");
        card.className = "insp-style-card";
        card.innerHTML = `<header class="insp-style-card-head">${lu("eye","lu14")}<span>Visibility & anchors</span></header>`;
        const body = document.createElement("div");
        body.className = "insp-style-card-body";
        BUILDER_STYLE_FIELDS.advanced.forEach(f => styleField(body, blk, f));
        card.appendChild(body);
        mount.appendChild(card);
        const tip2 = document.createElement("p"); tip2.className = "hint insp-style-hint";
        tip2.textContent = "Backgrounds and animations live under Style.";
        mount.appendChild(tip2);
        icons();
      }
    }
    pane.appendChild(mount);
  } else if (tab === "design"){
    const ds = document.createElement("div");
    ds.className = "ds-token-body ds-token-body--panels";
    const onTk = () => { markDirty(); renderCanvas(); };
    appendDesignTokenFields(ds, current, {
      panels: true,
      beforeSet: () => commit(),
      onChange: onTk,
      repaint: () => { renderCanvas(); }
    });
    pane.appendChild(ds);
  } else {
    const pg = activePage();
    const card = document.createElement("div");
    card.className = "insp-seo-card";
    const seoHead = document.createElement("p");
    seoHead.className = "insp-stack-label";
    seoHead.textContent = pg.name;
    card.appendChild(seoHead);
    card.appendChild(fieldEl({ t:"text", label:"Page title" }, pg.seo.title, v => { pg.seo.title = v; markDirty(); }, { live: liveMode }));
    card.appendChild(fieldEl({ t:"area", label:"Meta description" }, pg.seo.description, v => { pg.seo.description = v; markDirty(); }, { live: liveMode }));
    card.appendChild(fieldEl({ t:"text", label:"Share image URL", placeholder:"Overrides site default" }, pg.seo.ogImage, v => { pg.seo.ogImage = v; markDirty(); }, { live: liveMode }));
    const tip = document.createElement("p"); tip.className = "hint";
    tip.textContent = "Blank fields fall back to Settings → Site.";
    card.appendChild(tip);
    pane.appendChild(card);
  }

  insp.appendChild(pane);

  if (liveMode && blk){
    const foot = document.createElement("div");
    foot.className = "insp-live-foot";
    foot.innerHTML = `
      <button type="button" class="insp-live-done" data-x="foot-done">${lu("check","lu14")} Done</button>
      <button type="button" class="insp-live-save" data-x="foot-save">${lu("puzzle","lu14")} Save as custom</button>`;
    foot.querySelector('[data-x="foot-done"]').onclick = () => {
      $("#inspector")?.classList.remove("live-open");
      syncLiveInspector();
      syncLiveSelBar();
      icons();
    };
    foot.querySelector('[data-x="foot-save"]').onclick = () => {
      const blocks = selectedPageBlocks();
      if (blocks.length > 1) createCustomSectionFlow({ fromBlocks: blocks });
      else if (selected?.k === "b") createCustomSectionFlow({ fromBlock: selBlock() });
    };
    insp.appendChild(foot);
  }

  if (!selChanged && prevScroll) pane.scrollTop = prevScroll;
  else pane.scrollTop = 0;
  syncLiveInspector();
  syncLiveSelBar();
  if (liveMode && insp.classList.contains("live-open")){
    /* Keep canvas + window scroll locked after DOM swap (flex/height paint can nudge it). */
    const wrap = $("#canvas-wrap");
    const pageEl = $("#page");
    const y = wrap ? wrap.scrollTop : 0;
    const pageY = pageEl ? pageEl.scrollTop : 0;
    const winX = window.scrollX || 0;
    const winY = window.scrollY || 0;
    const restore = () => {
      if (wrap) wrap.scrollTop = y;
      if (pageEl) pageEl.scrollTop = pageY;
      if (winX || winY) window.scrollTo(winX, winY);
    };
    restore();
    requestAnimationFrame(restore);
  }
}

/* ============================ LIST EDITOR ============================
   Structured editing for every pipe-separated list field. Column names
   come from the field label ("Cells \u2014 Title|Body per line" \u2192 Title, Body).
   Each item is its own card with per-column inputs, reorder and delete —
   with a raw-text toggle for power editing. Data stays the same pipe
   format underneath, so nothing about rendering or templates changes. */
function listSpec(label){
  const m = /\u2014\s*(.+?)\s+per line/i.exec(label || "");
  if (!m) return { cols: ["Item"], single: true };
  const part = m[1].trim();
  if (/^one$/i.test(part)){
    const noun = (label.split("\u2014")[0] || "Item").trim().replace(/s$/i, "") || "Item";
    return { cols: [noun], single: true };
  }
  const nice = { "/slug/": "Link", "Feat,Feat": "Features (comma-separated)" };
  return { cols: part.split("|").map(c => nice[c.trim()] || c.trim() || "Value"), single: false };
}
function listEditor(f, val, onChange){
  const spec = listSpec(f.label);
  const host = document.createElement("div");
  host.className = "list-ed";
  let raw = false;
  let rows = String(val ?? "").split("\n").filter(l => l.trim() !== "").map(l => {
    const cells = l.split("|");
    while (cells.length < spec.cols.length) cells.push("");
    return cells;
  });
  const serialize = () => rows.map(r => r.slice(0, Math.max(spec.cols.length, r.length)).join("|")).join("\n");
  const push = (final) => onChange(serialize(), final);
  const longCol = i => /body|answer|description|content|quote|bio/i.test(spec.cols[i] || "");
  function paint(){
    host.innerHTML = "";
    if (raw){
      const ta = document.createElement("textarea");
      ta.value = serialize();
      ta.style.resize = "none"; ta.style.minHeight = "96px"; ta.style.overflow = "hidden";
      const autoH = () => { ta.style.height = "auto"; ta.style.height = ta.scrollHeight + "px"; };
      requestAnimationFrame(autoH);
      let armed = false;
      ta.addEventListener("focus", () => armed = true);
      ta.addEventListener("input", () => {
        autoH();
        rows = ta.value.split("\n").filter(l => l.trim() !== "").map(l => l.split("|"));
        onChange(ta.value, armed); armed = false;
      });
      host.appendChild(ta);
    } else {
      rows.forEach((r, i) => {
        const card = document.createElement("div");
        card.className = "list-ed-item";
        const head = document.createElement("div");
        head.className = "list-ed-head";
        head.innerHTML = `<span class="list-ed-n">${i + 1}</span><span class="list-ed-sp"></span>
          <button type="button" class="list-ed-ic" data-a="up" title="Move up" ${i === 0 ? "disabled" : ""}>${lu("chevron-up","lu14")}</button>
          <button type="button" class="list-ed-ic" data-a="down" title="Move down" ${i === rows.length - 1 ? "disabled" : ""}>${lu("chevron-down","lu14")}</button>
          <button type="button" class="list-ed-ic list-ed-del" data-a="del" title="Remove">${lu("x","lu14")}</button>`;
        head.addEventListener("click", e => {
          const a = e.target.closest("[data-a]")?.dataset.a;
          if (!a) return;
          if (a === "del") rows.splice(i, 1);
          if (a === "up" && i > 0) [rows[i - 1], rows[i]] = [rows[i], rows[i - 1]];
          if (a === "down" && i < rows.length - 1) [rows[i + 1], rows[i]] = [rows[i], rows[i + 1]];
          push(true); paint(); icons();
        });
        card.appendChild(head);
        spec.cols.forEach((col, ci) => {
          const cw = document.createElement("div");
          cw.className = "list-ed-cell";
          if (!spec.single){
            const lab = document.createElement("label"); lab.textContent = col; cw.appendChild(lab);
          }
          const inp = document.createElement(longCol(ci) ? "textarea" : "input");
          if (inp.tagName === "TEXTAREA"){
            inp.rows = 1; inp.style.resize = "none"; inp.style.overflow = "hidden";
            const autoH = () => { inp.style.height = "auto"; inp.style.height = inp.scrollHeight + "px"; };
            inp.addEventListener("input", autoH);
            requestAnimationFrame(autoH);
          } else inp.type = "text";
          inp.value = r[ci] ?? "";
          const isAltCol = /alt\s*text|^alt$/i.test(col || "");
          const isLinkCol = /^link$/i.test(col || "");
          inp.placeholder = isAltCol ? "Who or what is shown, and why" : col;
          if (isAltCol) inp.title = "Good alt text names the subject. Skip \"image of...\".";
          let armed = false;
          inp.addEventListener("focus", () => armed = true);
          inp.addEventListener("input", () => { r[ci] = inp.value; push(armed); armed = false; });
          cw.appendChild(inp);
          if (isLinkCol && inp.tagName === "INPUT") {
            attachBareWebHint(cw, inp, next => { r[ci] = next; push(true); });
          }
          card.appendChild(cw);
        });
        host.appendChild(card);
      });
      if (spec.cols.some(c => /alt\s*text|^alt$/i.test(c || ""))) {
        const tip = document.createElement("p");
        tip.className = "insp-alt-tip list-ed-alt-tip";
        tip.textContent = "Alt tip: describe the subject in a short phrase. Leave blank only if the image is decorative.";
        host.appendChild(tip);
      }
      const foot = document.createElement("div");
      foot.className = "list-ed-foot";
      foot.innerHTML = `<button type="button" class="list-ed-add">${lu("plus","lu14")} Add ${esc((spec.single ? spec.cols[0] : "item").toLowerCase())}</button>
        <button type="button" class="list-ed-raw" title="Edit as raw text">${lu("code","lu14")} Text</button>`;
      foot.querySelector(".list-ed-add").onclick = () => {
        rows.push(spec.cols.map(() => ""));
        push(true); paint(); icons();
        const last = host.querySelectorAll(".list-ed-item");
        last[last.length - 1]?.querySelector("input,textarea")?.focus();
      };
      foot.querySelector(".list-ed-raw").onclick = () => { raw = true; paint(); icons(); };
      host.appendChild(foot);
    }
    if (raw){
      const back = document.createElement("button");
      back.type = "button"; back.className = "list-ed-raw list-ed-back";
      back.innerHTML = `${lu("list","lu14")} Back to items`;
      back.onclick = () => { raw = false; paint(); icons(); };
      host.appendChild(back);
    }
  }
  paint();
  requestAnimationFrame(icons);
  return host;
}
function imgUrlAltKey(f){
  if (f?.altK) return f.altK;
  const k = String(f?.k || "");
  if (k === "img" || k === "image") return "alt";
  if (k === "before") return "beforeAlt";
  if (k === "after") return "afterAlt";
  return k + "Alt";
}
function isImgUrlField(f){
  if (!f || f.alt === false || f.t === "list" || f.t === "seg" || f.t === "pick") return false;
  const k = String(f.k || "");
  if (/^(img|image|before|after)$/i.test(k)) return true;
  if (/Url$/i.test(k) && /img|image|photo|poster|mockup|banner/i.test(k)) return true;
  const lab = String(f.label || "").toLowerCase();
  return /image\s*url|poster image|mockup url|screen image|map\s*\/\s*place|background image/.test(lab);
}
function isAltTextField(f){
  if (!f || f.t === "list" || f.t === "seg" || f.t === "pick") return false;
  const k = String(f.k || "");
  if (/^(alt|imgAlt|beforeAlt|afterAlt)$/i.test(k) || /Alt$/i.test(k)) return true;
  return /alt\s*text/i.test(String(f.label || ""));
}
function cleanAltSeed(s){
  return String(s || "").replace(/\s+/g, " ").trim().replace(/^["'\u201c\u201d]+|["'\u201c\u201d]+$/g, "");
}
function imageUrlForAltProps(props = {}, imgField = null){
  const k = String(imgField?.k || "");
  // Only treat imgField as an image URL source when it is actually a URL field.
  if (k && !isAltTextField(imgField) && props[k]) {
    const direct = String(props[k] || "").trim();
    if (direct) return direct;
  }
  return String(
    props.img || props.image || props.imgUrl || props.before || props.after
    || props.leftUrl || props.rightUrl || ""
  ).trim();
}
function canDescribeImageUrl(url){
  const u = String(url || "").trim();
  if (!u) return false;
  if (/^data:image\/svg/i.test(u)) return false;
  if (/via\.placeholder|placehold\.co|dummyimage|picsum\.photos/i.test(u)) return false;
  if (/^https?:\/\//i.test(u)) return true;
  if (/^data:image\/(jpeg|jpg|png|webp|gif);base64,/i.test(u) && u.length < 1400000) return true;
  return false;
}
function refreshAltSuggestionsIn(host){
  if (!host) return;
  host.querySelectorAll(".insp-alt-suggest").forEach(el => {
    try { el._bdAltRefresh?.(); } catch (_) { /* ignore */ }
  });
}
/** Short alt suggestions from nearby section copy + filename (instant). */
function suggestImageAltTexts(props = {}, opts = {}){
  const site = (typeof active === "function" && active()) || null;
  const siteName = cleanAltSeed(site?.name || site?.data?.seo?.title || "");
  const heading = cleanAltSeed(props.heading || props.title || props.label || "");
  const caption = cleanAltSeed(props.caption || props.leftCaption || props.rightCaption || "");
  const sub = cleanAltSeed(props.sub || props.body || props.text || "");
  const who = cleanAltSeed(props.who || props.name || "");
  const brand = cleanAltSeed(props.brand || "");
  const role = cleanAltSeed(opts.role || "");
  const imgUrl = imageUrlForAltProps(props, opts.imgField);
  let fromFile = "";
  try {
    const base = String(imgUrl).split("?")[0].split("#")[0].split("/").pop() || "";
    const stem = decodeURIComponent(base)
      .replace(/\.[a-z0-9]{2,5}$/i, "")
      .replace(/[-_]+/g, " ")
      .replace(/\b(img|image|photo|pic|dsc|asset|file|untitled|copy|final|v\d+)\b/gi, " ")
      .replace(/\b\d{3,}\b/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (stem.length >= 4 && stem.length <= 72) {
      fromFile = stem.charAt(0).toUpperCase() + stem.slice(1);
    }
  } catch (_) { /* ignore bad URLs */ }

  const out = [];
  const push = s => {
    let t = cleanAltSeed(s);
    if (!t || t.length < 3) return;
    t = t.replace(/^(an?\s+)?(image|photo|picture|graphic|screenshot)\s+(of|showing)\s+/i, "");
    t = cleanAltSeed(t);
    if (!t || t.length < 3 || t.length > 125) return;
    if (/^(image|photo|picture|graphic|logo|icon)s?$/i.test(t)) return;
    if (out.some(x => x.toLowerCase() === t.toLowerCase())) return;
    out.push(t);
  };

  if (caption) push(caption);
  if (heading) {
    push(heading);
    if (siteName && heading.toLowerCase() !== siteName.toLowerCase()
      && !heading.toLowerCase().includes(siteName.toLowerCase())) {
      push(heading + " for " + siteName);
    }
  }
  if (who) {
    push(who.includes(" ") ? who : "Portrait of " + who);
    if (heading && !who.toLowerCase().includes(heading.toLowerCase())) push(who + ", " + heading);
  }
  if (brand) push(brand + " logo");
  if (role === "before") push(heading ? heading + " — before" : "Before comparison");
  if (role === "after") push(heading ? heading + " — after" : "After comparison");
  if (fromFile) push(fromFile);
  if (sub && heading) {
    const clip = sub.split(/[.!?]/)[0].slice(0, 72);
    if (clip.length > 18) push(heading + ": " + clip);
  }
  if (!out.length && siteName) push(siteName + " photo");
  return out.slice(0, 4);
}
function altSuggestRoleForKey(altKey, imgField){
  const k = String(altKey || imgField?.k || "");
  if (/^before/i.test(k)) return "before";
  if (/^after/i.test(k)) return "after";
  return "";
}
const _altVisionCache = new Map();
async function fetchAltFromImage(url, context = ""){
  const key = String(url || "").trim();
  if (!key) throw new Error("Missing image URL");
  if (_altVisionCache.has(key)) return _altVisionCache.get(key);
  const data = await openAIRequest({
    action: "describe-image",
    url: key,
    context: String(context || "").slice(0, 240),
    profile: lumenNormalizeProfile(lumenPrefs().modelProfile)
  });
  const alt = cleanAltSeed(data.alt || "");
  if (!alt) throw new Error("No alt returned");
  _altVisionCache.set(key, alt);
  return alt;
}
function mountAltSuggestions(host, fieldWrap, props, altKey, onProp, opts = {}){
  const box = document.createElement("div");
  box.className = "insp-alt-suggest";
  const role = altSuggestRoleForKey(altKey, opts.imgField);
  let trackedUrl = "";
  let tips = [];
  let visionBusy = false;
  let visionReady = false;
  let lastAutoAlt = "";
  let visionTimer = 0;
  let visionGen = 0;

  const currentUrl = () => imageUrlForAltProps(props, opts.imgField);
  const currentAlt = () => String(props[altKey] ?? "").trim();
  const rebuildTips = (visionAlt = "") => {
    const local = suggestImageAltTexts(props, { role, imgField: opts.imgField });
    const seed = cleanAltSeed(visionAlt || (_altVisionCache.get(trackedUrl) || ""));
    tips = seed
      ? [seed, ...local.filter(t => t.toLowerCase() !== seed.toLowerCase())].slice(0, 4)
      : local.slice(0, 4);
  };

  const applyAlt = (v, { silent = false } = {}) => {
    const next = String(v ?? "");
    const inp = fieldWrap.querySelector("input, textarea");
    if (inp) {
      inp.value = next;
      inp.dispatchEvent(new Event("input", { bubbles: true }));
    }
    props[altKey] = next;
    onProp(altKey, next, true);
    lastAutoAlt = next;
    box.querySelectorAll(".insp-alt-chip[data-alt]").forEach(b => {
      b.classList.toggle("on", (b.getAttribute("data-alt") || "") === next);
    });
    if (!silent) toast(next ? "Alt text filled" : "Marked decorative", { kind: "success" });
  };

  const paintChips = () => {
    const canVision = canDescribeImageUrl(trackedUrl);
    const chips = tips.map(t =>
      `<button type="button" class="insp-alt-chip" data-alt="${esc(t)}" title="Use this description">${esc(t)}</button>`
    ).join("");
    const visionLabel = visionBusy
      ? "Looking at image…"
      : (visionReady ? "Refresh from image" : "Describe image…");
    const visionBtn = canVision
      ? `<button type="button" class="insp-alt-chip insp-alt-chip--vision" data-vision="1"${visionBusy ? " disabled" : ""} title="Generate alt text from the image">${esc(visionLabel)}</button>`
      : "";
    box.innerHTML = `
      <p class="insp-alt-tip">Updates with the image and section copy. Skip &ldquo;image of&hellip;&rdquo;. <kbd>Alt</kbd>+<kbd>A</kbd> fills the top suggestion.</p>
      <div class="insp-alt-chips" role="group" aria-label="Suggested alt text">
        ${visionBtn}
        ${chips}
        <button type="button" class="insp-alt-chip insp-alt-chip--empty" data-alt="" title="Mark as decorative (empty alt)">Decorative</button>
      </div>`;
    box.querySelectorAll(".insp-alt-chip[data-alt]").forEach(btn => {
      btn.addEventListener("pointerdown", e => {
        e.preventDefault();
        e.stopPropagation();
        applyAlt(btn.getAttribute("data-alt") || "");
      });
      btn.addEventListener("click", e => { e.preventDefault(); e.stopPropagation(); });
    });
    const visionEl = box.querySelector("[data-vision]");
    if (visionEl) {
      visionEl.addEventListener("pointerdown", e => {
        e.preventDefault();
        e.stopPropagation();
        runVision(true);
      });
      visionEl.addEventListener("click", e => { e.preventDefault(); e.stopPropagation(); });
    }
    const cur = currentAlt();
    box.querySelectorAll(".insp-alt-chip[data-alt]").forEach(b => {
      b.classList.toggle("on", (b.getAttribute("data-alt") || "") === cur);
    });
  };

  const runVision = async (force = false, { autoFill = false } = {}) => {
    const url = trackedUrl;
    if (!canDescribeImageUrl(url) || visionBusy) return;
    if (force) _altVisionCache.delete(url);
    const gen = ++visionGen;
    visionBusy = true;
    paintChips();
    try {
      const ctx = [props.heading, props.sub, props.caption, props.kicker, props.body].filter(Boolean).join(" · ");
      const alt = await fetchAltFromImage(url, ctx);
      if (gen !== visionGen || trackedUrl !== url) return;
      rebuildTips(alt);
      visionReady = true;
      const cur = currentAlt();
      const shouldFill = autoFill && (!cur || cur === lastAutoAlt || tips.slice(1).some(t => t === cur));
      if (shouldFill) applyAlt(alt, { silent: true });
    } catch (err) {
      if (gen === visionGen) {
        toast(humanErr(err, "Could not describe image"), { kind: "error" });
      }
    } finally {
      if (gen === visionGen) {
        visionBusy = false;
        paintChips();
      }
    }
  };

  const scheduleVision = (force = false, autoFill = false) => {
    window.clearTimeout(visionTimer);
    visionTimer = window.setTimeout(() => runVision(force, { autoFill }), force ? 0 : 450);
  };

  const refresh = () => {
    const nextUrl = currentUrl();
    const urlChanged = nextUrl !== trackedUrl;
    trackedUrl = nextUrl;
    visionReady = _altVisionCache.has(trackedUrl);
    rebuildTips(visionReady ? _altVisionCache.get(trackedUrl) : "");
    paintChips();

    const emptyOrSynced = !currentAlt() || currentAlt() === lastAutoAlt;
    if (urlChanged && canDescribeImageUrl(trackedUrl)) {
      scheduleVision(false, emptyOrSynced);
    } else if (!visionReady && canDescribeImageUrl(trackedUrl) && emptyOrSynced) {
      scheduleVision(false, true);
    }
  };

  box._bdAltRefresh = refresh;
  refresh();

  const inp = fieldWrap.querySelector("input, textarea");
  if (inp && !inp.dataset.altHotkey) {
    inp.dataset.altHotkey = "1";
    inp.addEventListener("keydown", e => {
      if (!(e.altKey && !e.ctrlKey && !e.metaKey && String(e.key || "").toLowerCase() === "a")) return;
      e.preventDefault();
      const top = tips[0];
      if (top == null) {
        toast("No suggestion yet", { kind: "error" });
        return;
      }
      applyAlt(top);
    });
    inp.addEventListener("input", () => {
      // User is editing manually — stop treating value as auto-synced.
      const v = String(inp.value || "").trim();
      if (v !== lastAutoAlt) lastAutoAlt = "";
    });
  }

  host.appendChild(box);
}
/** Mount a content field, and for image URLs also mount Alt text. */
function appendBlockContentField(host, props, f, onProp, opts = {}){
  if (f.t === "note") {
    host.appendChild(fieldEl(f, "", () => {}, opts));
    return;
  }
  const notify = (key, v, final) => {
    onProp(key, v, final);
    queueMicrotask(() => refreshAltSuggestionsIn(host));
  };
  const el = fieldEl(f, props[f.k], (v, final) => notify(f.k, v, final), opts);
  host.appendChild(el);
  if (isAltTextField(f)) {
    // Prefer pairing with a sibling image URL field when alt is declared separately.
    const imgField = Array.isArray(opts.fields)
      ? (opts.fields.find(x => x && isImgUrlField(x) && imgUrlAltKey(x) === f.k) || null)
      : null;
    mountAltSuggestions(host, el, props, f.k, notify, { ...opts, imgField });
    return;
  }
  if (!isImgUrlField(f)) return;
  const altKey = imgUrlAltKey(f);
  const declared = Array.isArray(opts.fields) && opts.fields.some(x => x && x.k === altKey);
  if (declared) return;
  const altEl = fieldEl(
    { t: "text", label: "Alt text", placeholder: "Describe who or what is shown, and why" },
    props[altKey] ?? "",
    (v, final) => notify(altKey, v, final),
    opts
  );
  host.appendChild(altEl);
  mountAltSuggestions(host, altEl, props, altKey, notify, { ...opts, imgField: f });
}

/* ---------- Footer location (owner place, baked into content) ---------- */
const FOOTER_PLACEHOLDER_NOTES = new Set([
  "Made with care.",
  "Made in Johannesburg, South Africa.",
  "Built with Browns Digital Studio.",
]);
function footerNoteFromPlace(place){
  if (!place) return null;
  const city = String(place.city || "").trim();
  const country = String(place.country || "").trim();
  const bits = [];
  if (city) bits.push(city);
  if (country && country.toLowerCase() !== city.toLowerCase()) bits.push(country);
  if (!bits.length) return null;
  return `Made in ${bits.join(", ")}.`;
}
async function fetchPlaceFromIp(){
  const r = await fetch("https://get.geojs.io/v1/ip/geo.json", { credentials: "omit" });
  if (!r.ok) throw new Error("IP lookup failed");
  const j = await r.json();
  return {
    city: j.city || j.region || "",
    region: j.region || "",
    country: j.country || j.country_code || ""
  };
}
function fetchPlaceFromGeolocation(){
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation){
      reject(new Error("This browser cannot share location."));
      return;
    }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      try {
        const { latitude, longitude } = pos.coords;
        const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${encodeURIComponent(latitude)}&longitude=${encodeURIComponent(longitude)}&localityLanguage=en`;
        const r = await fetch(url, { credentials: "omit" });
        if (!r.ok) throw new Error("Could not resolve place name");
        const j = await r.json();
        resolve({
          city: j.city || j.locality || j.principalSubdivision || "",
          region: j.principalSubdivision || "",
          country: j.countryName || ""
        });
      } catch (e){ reject(e); }
    }, err => {
      if (err?.code === 1) reject(new Error("Location permission denied — allow it in the browser prompt, or type the city in Small print."));
      else if (err?.code === 2) reject(new Error("Location unavailable right now."));
      else if (err?.code === 3) reject(new Error("Location request timed out."));
      else reject(err || new Error("Could not read location."));
    }, { enableHighAccuracy: false, timeout: 12000, maximumAge: 600000 });
  });
}
async function resolveStudioPlace({ precise = false } = {}){
  if (precise) return fetchPlaceFromGeolocation();
  try { return await fetchPlaceFromIp(); }
  catch {
    /* IP blocked/offline — fall through to precise if the user asked for it elsewhere */
    throw new Error("Could not detect location from your network.");
  }
}
function applyFooterPlace(blk, place, { toastOk = true } = {}){
  const note = footerNoteFromPlace(place);
  if (!note || !blk?.props) return false;
  commit();
  blk.props.note = note;
  markDirty();
  renderCanvas();
  renderInspector();
  icons();
  if (toastOk) toast(`Footer updated — ${note.replace(/\.$/, "")}`);
  return true;
}
async function queueFooterLocationFill(blk, { silent = false, precise = false } = {}){
  if (!blk || blk.type !== "footer") return;
  const cur = String(blk.props?.note || "").trim();
  if (silent && cur && !FOOTER_PLACEHOLDER_NOTES.has(cur)) return;
  try {
    const place = await resolveStudioPlace({ precise });
    if (!applyFooterPlace(blk, place, { toastOk: !silent })){
      if (!silent) toast("Could not read a city from that location", { kind: "error" });
    }
  } catch (e){
    if (!silent) toast(humanErr(e, "Could not detect location"), { kind: "error" });
  }
}
function appendFooterLocationControls(host, blk){
  const wrap = document.createElement("div");
  wrap.className = "field field--footer-loc";
  wrap.innerHTML = `
    <label>Location line</label>
    <p class="hint" style="margin:0 0 10px">Uses your place (not visitors’) and saves it into Small print. Approximate from your network needs no permission; precise GPS asks the browser.</p>
    <div class="row" style="gap:8px;flex-wrap:wrap">
      <button type="button" class="btn btn-ghost btn-sm" data-x="ip">${lu("globe","lu14")} Detect from network</button>
      <button type="button" class="btn btn-ghost btn-sm" data-x="gps">${lu("map-pin","lu14")} Use precise location</button>
    </div>`;
  wrap.querySelector('[data-x="ip"]').onclick = () => queueFooterLocationFill(blk, { precise: false });
  wrap.querySelector('[data-x="gps"]').onclick = () => queueFooterLocationFill(blk, { precise: true });
  host.appendChild(wrap);
  icons();
  /* Soft-upgrade legacy Johannesburg / placeholder notes once when the editor opens. */
  if (FOOTER_PLACEHOLDER_NOTES.has(String(blk.props?.note || "").trim())){
    queueFooterLocationFill(blk, { silent: true, precise: false });
  }
}

function isLinkField(f){
  if (!f || f.t === "list" || f.t === "area" || f.t === "seg" || f.t === "pick") return false;
  const k = String(f.k || "");
  const lab = String(f.label || "");
  if (/image|img|favicon|poster|mockup|share image|og image|map/i.test(lab + " " + k)) return false;
  return /href$/i.test(k) || /button link|^link url$|page link|cta link/i.test(lab);
}
function attachBareWebHint(parent, input, commit){
  const suggestionOf = () => (typeof bdBareWebUrl === "function" ? bdBareWebUrl(input.value) : "");
  const sync = () => {
    const suggestion = suggestionOf();
    let hint = parent.querySelector(":scope > .link-domain-hint");
    if (!suggestion){ hint?.remove(); return; }
    if (!hint){
      hint = document.createElement("button");
      hint.type = "button";
      hint.className = "link-domain-hint";
      parent.appendChild(hint);
      hint.addEventListener("mousedown", e => {
        e.preventDefault();
        const next = hint.dataset.url || "";
        if (!next) return;
        input.value = next;
        commit(next);
        hint.remove();
      });
    }
    hint.dataset.url = suggestion;
    hint.textContent = "Did you mean " + suggestion + "?";
  };
  input.addEventListener("input", sync);
  input.addEventListener("blur", () => {
    const suggestion = suggestionOf();
    if (!suggestion) return;
    input.value = suggestion;
    commit(suggestion);
    parent.querySelector(":scope > .link-domain-hint")?.remove();
  });
  sync();
}
function fieldEl(f, val, onChange, opts){
  const live = !!(opts && opts.live);
  const w = document.createElement("div"); w.className = "field" + (live ? " field--live" : "");
  if (f.t === "note") {
    const note = document.createElement("p");
    note.className = "hint";
    note.textContent = f.text || "";
    w.appendChild(note);
    return w;
  }
  if (f.t === "pick" || f.t === "seg") w.classList.add("field--choice");
  if (f.label){ const lab = document.createElement("label"); lab.textContent = f.label; w.appendChild(lab); }
  if (f.t === "area"){
    const ta = document.createElement("textarea"); ta.value = val ?? "";
    ta.style.resize = "none"; ta.style.overflow = "hidden"; ta.style.minHeight = live ? "88px" : "72px";
    function autoH(){ ta.style.height = "auto"; ta.style.height = ta.scrollHeight + "px"; }
    ta.addEventListener("input", () => { autoH(); });
    requestAnimationFrame(autoH);
    let armed = false;
    ta.addEventListener("focus", () => armed = true);
    ta.addEventListener("input", () => { onChange(ta.value, armed); armed = false; });
    w.appendChild(ta);
  } else if (f.t === "list"){
    w.appendChild(listEditor(f, val, onChange));
  } else if (f.t === "pick"){
    const grid = document.createElement("div");
    grid.className = "insp-pick";
    grid.style.setProperty("--insp-pick-cols", String(f.cols || Math.min(4, f.opts.length)));
    f.opts.forEach(o => {
      const optionValue = Array.isArray(o) ? o[0] : o;
      const b = document.createElement("button");
      b.type = "button";
      b.className = "insp-pick-item" + (val === optionValue ? " on" : "");
      const label = Array.isArray(o) ? o[1] : ((f.segLabels && f.segLabels[o]) || (o[0].toUpperCase() + o.slice(1)));
      b.title = label;
      b.setAttribute("aria-pressed", val === optionValue ? "true" : "false");
      b.dataset.pickVal = optionValue;
      const sw = f.pickSwatch && f.pickSwatch[optionValue];
      const icon = f.pickIcons && f.pickIcons[optionValue];
      b.innerHTML = `${sw ? `<span class="insp-pick-swatch" aria-hidden="true" style="background:${sw}"></span>` : ""}
        ${icon ? `<span class="insp-pick-ico" aria-hidden="true">${lu(icon,"lu14")}</span>` : ""}
        <span class="insp-pick-label">${esc(label)}</span>`;
      const choose = e => {
        e.preventDefault();
        e.stopPropagation();
        if (val === optionValue) return;
        val = optionValue;
        [...grid.children].forEach(x => {
          const on = x === b;
          x.classList.toggle("on", on);
          x.setAttribute("aria-pressed", on ? "true" : "false");
        });
        onChange(optionValue, true);
      };
      b.addEventListener("pointerdown", choose);
      b.addEventListener("click", e => { e.preventDefault(); e.stopPropagation(); });
      grid.appendChild(b);
    });
    w.appendChild(grid);
    requestAnimationFrame(icons);
  } else if (f.t === "seg"){
    const seg = document.createElement("div");
    seg.className = "seg seg--wrap" + (f.opts.length > 4 ? " seg-multi" : "");
    f.opts.forEach(o => {
      const optionValue = Array.isArray(o) ? o[0] : o;
      const b = document.createElement("button");
      b.type = "button";
      const label = Array.isArray(o) ? o[1] : ((f.segLabels && f.segLabels[o]) || (o[0].toUpperCase() + o.slice(1)));
      b.textContent = label;
      b.title = label;
      b.classList.toggle("on", val === optionValue);
      const choose = e => {
        e.preventDefault();
        e.stopPropagation();
        if (val === optionValue) return;
        val = optionValue;
        [...seg.children].forEach(x => x.classList.toggle("on", x === b));
        onChange(optionValue, true);
      };
      b.addEventListener("pointerdown", choose);
      b.addEventListener("click", e => { e.preventDefault(); e.stopPropagation(); });
      seg.appendChild(b);
    });
    w.appendChild(seg);
  } else if (f.t === "select"){
    w.appendChild(bdSelect(f.opts, val, (v, final) => { if (final && current && page === "builder-edit") commit(); onChange(v, final); }));
  } else if (f.t === "color"){
    function commitMaybe(){ if (current && page === "builder-edit") commit(); }
    const row = document.createElement("div");
    row.className = "insp-color-row";
    const seed = /^#[0-9a-fA-F]{3,8}$/.test(val || "") ? val : "#1d1d1f";
    row.appendChild(bdColorPicker(seed, v => onChange(v, false), v => { commitMaybe(); onChange(v, true); }, { portal: true }));
    if (f.clearable){
      const clr = document.createElement("button");
      clr.type = "button";
      clr.className = "insp-color-clear";
      clr.title = "Use site text colour";
      clr.innerHTML = `${lu("rotate-ccw","lu14")}<span>Reset</span>`;
      clr.addEventListener("pointerdown", e => {
        e.preventDefault();
        e.stopPropagation();
        onChange("", true);
      });
      clr.addEventListener("click", e => { e.preventDefault(); e.stopPropagation(); });
      row.appendChild(clr);
    }
    w.appendChild(row);
    requestAnimationFrame(icons);
  } else {
    const inp = document.createElement("input"); inp.type = "text"; inp.value = val ?? "";
    if (f.placeholder) inp.placeholder = f.placeholder;
    let armed = false;
    inp.addEventListener("focus", () => armed = true);
    inp.addEventListener("input", () => { onChange(inp.value, armed); armed = false; });
    w.appendChild(inp);
    if (isLinkField(f)) attachBareWebHint(w, inp, next => onChange(next, true));
  }
  return w;
}
/* ---------- history & shortcuts ---------- */
function blockLabel(b){
  if (!b) return "Section";
  const name = BLOCKS[b.type]?.name || b.type;
  const tip = b.props?.heading || b.props?.brand || b.props?.quote || b.props?.label || b.props?.text || "";
  const short = String(tip).replace(/\s+/g, " ").trim().slice(0, 36);
  return short ? `${name} “${short}${String(tip).trim().length > 36 ? "…" : ""}”` : name;
}
function propPreview(v){
  const s = String(v ?? "").replace(/\s+/g, " ").trim();
  if (!s) return "(empty)";
  return s.length > 48 ? s.slice(0, 48) + "…" : s;
}
/** Diff snap A → B: what changed going from A to B (human-readable rows). */
function diffSiteData(a, b){
  const out = [];
  if (!a || !b) return out;
  const push = (kind, icon, text, detail) => out.push({ kind, icon, text, detail: detail || "" });

  const ta = a.tokens || {}, tb = b.tokens || {};
  const tokenKeys = new Set([...Object.keys(ta), ...Object.keys(tb)]);
  tokenKeys.forEach(k => {
    if (String(ta[k] ?? "") === String(tb[k] ?? "")) return;
    const label = k === "accent" ? "Accent colour"
      : k === "pageBgMode" ? "Page background"
      : k === "pageBgA" ? "Background from"
      : k === "pageBgB" ? "Background to"
      : k === "pageBgAngle" ? "Background direction"
      : k === "mode" ? "Theme" : k.replace(/_/g, " ");
    push("token", "palette", `Changed ${label}`, `${propPreview(ta[k])} → ${propPreview(tb[k])}`);
  });

  const seoA = a.seo || {}, seoB = b.seo || {};
  ["title", "description"].forEach(k => {
    if (String(seoA[k] ?? "") === String(seoB[k] ?? "")) return;
    push("seo", "search", `Changed site ${k}`, `${propPreview(seoA[k])} → ${propPreview(seoB[k])}`);
  });

  ["header", "footer"].forEach(slot => {
    const sa = a.shared?.[slot] || null, sb = b.shared?.[slot] || null;
    if (!sa && !sb) return;
    if (!sa && sb){ push("shared", "link", `Added shared ${slot}`, blockLabel(sb)); return; }
    if (sa && !sb){ push("shared", "unlink", `Removed shared ${slot}`, blockLabel(sa)); return; }
    if (sa.type !== sb.type){ push("shared", "refresh-cw", `Replaced shared ${slot}`, `${blockLabel(sa)} → ${blockLabel(sb)}`); return; }
    const keys = new Set([...Object.keys(sa.props || {}), ...Object.keys(sb.props || {})]);
    const changed = [];
    keys.forEach(k => {
      if (k === "_style") return;
      if (JSON.stringify(sa.props?.[k]) !== JSON.stringify(sb.props?.[k])) changed.push(k);
    });
    if (sa.hidden !== sb.hidden) changed.push(sb.hidden ? "hidden" : "shown");
    if (changed.length) push("shared", "pencil", `Edited shared ${slot}`, changed.slice(0, 4).join(", "));
  });

  const pagesA = a.pages || [], pagesB = b.pages || [];
  const byIdA = Object.fromEntries(pagesA.map(p => [p.id, p]));
  const byIdB = Object.fromEntries(pagesB.map(p => [p.id, p]));
  pagesB.forEach(pb => {
    if (!byIdA[pb.id]) push("page", "file-plus", `Added page “${pb.name}”`, pb.slug ? `/${pb.slug}` : "Home");
  });
  pagesA.forEach(pa => {
    if (!byIdB[pa.id]) push("page", "file-x", `Removed page “${pa.name}”`, pa.slug ? `/${pa.slug}` : "Home");
  });
  pagesB.forEach(pb => {
    const pa = byIdA[pb.id]; if (!pa) return;
    if (pa.name !== pb.name) push("page", "text-cursor-input", `Renamed page`, `“${pa.name}” → “${pb.name}”`);
    if ((pa.slug || "") !== (pb.slug || "")) push("page", "link", `Changed address for “${pb.name}”`, `/${pa.slug || ""} → /${pb.slug || ""}`);
    const ba = pa.blocks || [], bb = pb.blocks || [];
    const bA = Object.fromEntries(ba.map(x => [x.id, x]));
    const bB = Object.fromEntries(bb.map(x => [x.id, x]));
    const orderA = ba.map(x => x.id).join(",");
    const orderB = bb.map(x => x.id).join(",");
    bb.forEach(blk => {
      if (!bA[blk.id]) push("block", "plus", `Added ${blockLabel(blk)}`, `on “${pb.name}”`);
    });
    ba.forEach(blk => {
      if (!bB[blk.id]) push("block", "minus", `Removed ${blockLabel(blk)}`, `from “${pb.name}”`);
    });
    if (orderA !== orderB && ba.length && bb.length){
      const onlyReorder = ba.every(x => bB[x.id]) && bb.every(x => bA[x.id]) && ba.length === bb.length;
      if (onlyReorder) push("block", "list-ordered", `Reordered sections on “${pb.name}”`, `${ba.length} sections`);
    }
    bb.forEach(blk => {
      const prev = bA[blk.id]; if (!prev) return;
      if (prev.type !== blk.type){
        push("block", "refresh-cw", `Replaced section on “${pb.name}”`, `${blockLabel(prev)} → ${blockLabel(blk)}`);
        return;
      }
      const keys = new Set([...Object.keys(prev.props || {}), ...Object.keys(blk.props || {})]);
      const fields = [];
      keys.forEach(k => {
        if (k === "_style") return;
        if (JSON.stringify(prev.props?.[k]) !== JSON.stringify(blk.props?.[k])) fields.push(k);
      });
      if (!!prev.hidden !== !!blk.hidden) fields.push(blk.hidden ? "visibility" : "visibility");
      if (!fields.length) return;
      const sample = fields[0];
      const detail = fields.length === 1 && sample !== "visibility"
        ? `${sample}: ${propPreview(prev.props?.[sample])} → ${propPreview(blk.props?.[sample])}`
        : fields.slice(0, 5).join(", ");
      push("block", "pencil", `Edited ${blockLabel(blk)}`, `on “${pb.name}” · ${detail}`);
    });
  });

  return out;
}
function currentSiteSnap(){
  return { tokens: current.data.tokens, seo: current.data.seo, shared: current.data.shared, pages: current.data.pages };
}
function diffSummaryLine(changes){
  if (!changes.length) return "Identical to your current draft";
  const n = changes.length;
  const head = changes[0].text;
  return n === 1 ? head : `${n} changes · ${head}`;
}
function reviewRevisionModal(revIndex){
  const revs = current.data._revisions || [];
  const r = revs[revIndex];
  if (!r) return;
  const now = currentSiteSnap();
  /* Diff: revision → current (what differs from this snapshot) */
  const changes = diffSiteData(r.snap, now);
  const revLabel = `Revision ${revs.length - revIndex}${revIndex === 0 ? " · latest" : ""}`;
  const list = changes.length
    ? changes.map(c => `<div class="hist-diff-row hist-diff-row--${esc(c.kind)}">
        <span class="hist-diff-ic">${lu(c.icon || "git-commit-horizontal")}</span>
        <span class="hist-diff-main"><strong>${esc(c.text)}</strong>${c.detail ? `<em>${esc(c.detail)}</em>` : ""}</span>
      </div>`).join("")
    : `<div class="hist-empty hist-empty--compact">
        <span class="hist-empty-ic">${lu("check")}</span>
        <strong>No differences</strong>
        <p>This revision matches your current draft.</p>
      </div>`;
  const m = openModal(`
    <div class="hist-modal hist-review">
      <div class="hist-head">
        <div>
          <h2 id="modal-title">Review ${esc(revLabel)}</h2>
          <p class="sub">How your current draft differs from this save · ${esc(fmtWhen(r.ts))}${r.trigger ? ` · ${esc(r.trigger.toLowerCase())}` : ""}</p>
        </div>
        <button type="button" class="icon-close-btn" data-x="c" aria-label="Close">${lu("x","lu16")}</button>
      </div>
      <div class="hist-sec-label"><span>What changed</span>${changes.length ? `<em>${changes.length}</em>` : ""}</div>
      <div class="hist-panel hist-diff-panel">${list}</div>
      <div class="hist-foot hist-foot--split">
        <button type="button" class="btn btn-ghost btn-sm" data-x="back">${lu("arrow-left","lu14")} Back</button>
        <div class="hist-actions">
          <button type="button" class="btn btn-ghost btn-sm" data-x="open">${lu("eye","lu14")} Open preview</button>
          <button type="button" class="btn btn-cta btn-sm" data-x="rest">Restore</button>
        </div>
      </div>
    </div>`, true);
  m.classList.add("hist-modal-box");
  m.querySelector("[data-x=c]").onclick = closeModal;
  m.querySelector("[data-x=back]").onclick = () => { closeModal(); historyModal(); };
  m.querySelector("[data-x=open]").onclick = () => {
    previewSite({ name: current.name, data: Object.assign({}, current.data, r.snap) });
  };
  m.querySelector("[data-x=rest]").onclick = () => {
    commit();
    pushRevisionFor(current, "RESTORED", "Before restore");
    Object.assign(current.data, JSON.parse(JSON.stringify(r.snap)));
    if (!current.data.pages.some(x => x.id === curPageId)) curPageId = current.data.pages[0]?.id;
    selected = null;
    closeModal(); afterHistory();
    log(current.id, "restored", `“${current.name}” restored to an earlier revision`);
    toast("Revision restored — your previous state was kept as a new revision");
  };
  icons();
}
function historyModal(){
  const steps = past.length;
  const revs = current.data._revisions || [];
  const now = currentSiteSnap();
  const sessionEmpty = !past.length;
  const revEmpty = !revs.length;
  const sessionRows = past.length ? past.map((s, i) => {
    const d = JSON.parse(s);
    const changes = diffSiteData(d, now);
    return `<div class="hist-row">
      <span class="hist-ic">${lu("history")}</span>
      <span class="hist-main"><strong>Step ${i + 1}</strong><em>${esc(diffSummaryLine(changes))}</em></span>
      <button type="button" class="btn btn-ghost btn-sm" data-j="${i}">Jump here</button>
    </div>`;
  }).reverse().join("") : "";
  const revRows = revs.length ? revs.map((r, i) => {
    const changes = diffSiteData(r.snap, now);
    return `<div class="hist-row">
      <span class="hist-ic hist-ic--rev">${lu("git-branch")}</span>
      <span class="hist-main"><strong>Revision ${revs.length - i}${i === 0 ? " · latest" : ""}</strong><em>${esc(diffSummaryLine(changes))} · ${fmtWhen(r.ts)}</em></span>
      <div class="hist-actions">
        <button type="button" class="btn btn-ghost btn-sm" data-rprev="${i}">Review</button>
        <button type="button" class="btn btn-ghost btn-sm" data-rrest="${i}">Restore</button>
      </div>
    </div>`;
  }).join("") : "";
  const m = openModal(`
    <div class="hist-modal">
      <div class="hist-head">
        <div>
          <h2 id="modal-title">History &amp; revisions</h2>
          <p class="sub">${steps
            ? `${steps} undo step${steps === 1 ? "" : "s"} this session — jump back anytime; redo stays available.`
            : "Undo steps live here while you edit. Saved revisions persist across sessions."}</p>
        </div>
        <button type="button" class="icon-close-btn" data-x="c" aria-label="Close">${lu("x","lu16")}</button>
      </div>
      <section class="hist-sec">
        <div class="hist-sec-label"><span>This session</span>${steps ? `<em>${steps}</em>` : ""}</div>
        <div class="hist-panel">${sessionEmpty
          ? `<div class="hist-empty">
              <span class="hist-empty-ic">${lu("history")}</span>
              <strong>No edits yet</strong>
              <p>Change something on the canvas — each edit becomes an undo step you can jump back to.</p>
            </div>`
          : sessionRows}</div>
      </section>
      <section class="hist-sec">
        <div class="hist-sec-label"><span>Saved revisions</span>${revs.length ? `<em>${revs.length}</em>` : ""}</div>
        <div class="hist-panel">${revEmpty
          ? `<div class="hist-empty">
              <span class="hist-empty-ic">${lu("save")}</span>
              <strong>No revisions yet</strong>
              <p>Press <kbd>Save</kbd> in the toolbar to capture a restorable snapshot.</p>
              <button type="button" class="btn btn-cta btn-sm" data-x="save">Save now</button>
            </div>`
          : revRows}</div>
      </section>
      <div class="hist-foot">
        <button type="button" class="btn btn-ghost btn-sm" data-x="c2">Done</button>
      </div>
    </div>`, true);
  m.classList.add("hist-modal-box");
  const close = () => closeModal();
  m.querySelectorAll("[data-x=c], [data-x=c2]").forEach(b => b.onclick = close);
  m.querySelector("[data-x=save]")?.addEventListener("click", () => { closeModal(); saveNow(false); });
  m.querySelectorAll("[data-j]").forEach(b => b.onclick = () => {
    const i = +b.dataset.j;
    while (past.length > i) { future.push(snapshot()); restoreSnap(past.pop()); }
    closeModal(); afterHistory();
  });
  m.querySelectorAll("[data-rprev]").forEach(b => b.onclick = () => {
    closeModal();
    reviewRevisionModal(+b.dataset.rprev);
  });
  m.querySelectorAll("[data-rrest]").forEach(b => b.onclick = () => {
    const r = revs[+b.dataset.rrest];
    commit();
    pushRevisionFor(current, "RESTORED", "Before restore");
    Object.assign(current.data, JSON.parse(JSON.stringify(r.snap)));
    if (!current.data.pages.some(x => x.id === curPageId)) curPageId = current.data.pages[0]?.id;
    selected = null;
    closeModal(); afterHistory();
    log(current.id, "restored", `“${current.name}” restored to an earlier revision`);
    toast("Revision restored — your previous state was kept as a new revision");
  });
}
function shortcutsModal(){
  const kb = keybinds();
  const kbd = c => c.split("+").map(k => `<kbd>${esc(k)}</kbd>`).join("");
  const m = openModal(`<h2>Keyboard shortcuts.</h2>
    <table class="sc-table">
      <tr><td>Command menu</td><td><kbd>Ctrl</kbd><kbd>K</kbd></td></tr>
      ${Object.entries(KEY_LABELS).map(([k, label]) => `<tr><td>${esc(label)}</td><td>${kbd(kb[k])}</td></tr>`).join("")}
      <tr><td>Edit text in place</td><td>Double-click it</td></tr>
      <tr><td>Section options</td><td>Right-click it</td></tr>
      <tr><td>This panel</td><td><kbd>?</kbd></td></tr>
    </table>
    <div class="row"><button class="btn btn-ghost btn-sm" id="sc-edit">Edit keybinds</button><button class="btn btn-ghost btn-sm" id="sc-x">Close</button></div>`);
  m.querySelector("#sc-x").onclick = closeModal;
  m.querySelector("#sc-edit").onclick = () => { closeModal(); goAccountShortcuts(); };
}

/* ============================ COMMAND MENU ============================ */
function cmdkActions(){
  const acts = [];
  const kb = keybinds();
  if (active()){
    const p = active(), stg = stagingOf(p.id);
    acts.push({ h: "Publish" });
    acts.push({ icon: "upload", label: "Publish…", k: "Ctrl Shift P", run: () => openPublishModal(p, { intent: "preview" }) });
    acts.push({ icon: "layers", label: "Update preview", k: "Ctrl Shift S", run: () => openPublishModal(p, { intent: "preview" }) });
    acts.push({ icon: "rocket", label: prodOf(p.id) ? "Update live" : "Push to live", k: "Ctrl Shift L", run: () => openPublishModal(p, { intent: "live" }), disabled: !stg && !prodOf(p.id) });
  }
  acts.push({ h: "Theme" });
  acts.push({ icon: "palette", label: "Open Customize", run: () => openCustomizeModal() });
  acts.push({ h: "Site" });
  acts.push({ icon: "plus", label: "New site", run: newSiteFlow });
  projects.forEach(p => acts.push({ icon: "panel-top", label: "Switch site: " + p.name, run: () => { activeId = p.id; persistActive(); go("dashboard"); } }));
  if (active() && stagingOf(active().id)) acts.push({ icon: "external-link", label: "Open preview URL", run: () => openDeploy(stagingOf(active().id)) });
  acts.push({ h: "Go to" });
  const icmap = { dashboard:"home", agent:"sparkles", health:"zap", team:"users", settings:"settings", sites:"layout-grid", activity:"activity" };
  Object.entries(PAGES).forEach(([k, v]) => {
    if (["builder-edit","account","builder"].includes(k)) return;
    acts.push({ icon: icmap[k], label: v.label, run: () => go(k) });
  });
  acts.push({ h: "Account" });
  acts.push({ icon: "circle-user", label: "Account settings", run: () => (!DEMO && !QA_MODE && typeof BD_ACCOUNT !== "undefined" ? BD_ACCOUNT.go() : go("account")) });
  acts.push({ icon: "keyboard", label: "Keyboard shortcuts", k: "", run: () => goAccountShortcuts() });
  acts.push({ icon: "log-out", label: "Sign out", run: signOutAll });
  if (page === "builder-edit" && current){
    acts.push({ h: "Canvas" });
    acts.push({ icon: "save", label: "Save revision", k: kb.save.replace(/\+/g, " "), run: () => saveNow(false) });
    BLOCK_ORDER.forEach(t => {
      if (typeof isLibraryMainBlock === "function" && !isLibraryMainBlock(t)) return;
      acts.push({ icon: BLOCKS[t].icon, label: "Add " + BLOCKS[t].name, run: () => addBlock(t, activePage().blocks.length) });
    });
  }
  return acts.filter(a => !a.disabled);
}
let cmdkIdx = 0, cmdkFiltered = [];
function openCmdk(){
  if (!$("#view-app").classList.contains("active")) return;
  $("#cmdk-veil").classList.add("show");
  const inp = $("#cmdk-input"); inp.value = ""; inp.focus();
  paintCmdk("");
}
function closeCmdk(){ $("#cmdk-veil").classList.remove("show"); }
function paintCmdk(q){
  const list = $("#cmdk-list");
  const all = cmdkActions();
  let html = ""; cmdkFiltered = [];
  all.forEach(a => {
    if (a.h){ if (!q) html += `<div class="cmdk-h">${a.h}</div>`; return; }
    if (q && !a.label.toLowerCase().includes(q)) return;
    const i = cmdkFiltered.length; cmdkFiltered.push(a);
    html += `<div class="cmdk-item${i===0?" on":""}" data-i="${i}"><span class="ci">${lu(a.icon)}</span>${esc(a.label)}${a.k?`<span class="k">${a.k}</span>`:""}</div>`;
  });
  cmdkIdx = 0;
  list.innerHTML = html || `<div class="cmdk-item">Nothing matches.</div>`;
  icons();
  $$("#cmdk-list .cmdk-item[data-i]").forEach(el => el.onclick = () => runCmdk(parseInt(el.dataset.i)));
}
function runCmdk(i){ const a = cmdkFiltered[i]; closeCmdk(); a && a.run(); }
$("#cmdk-input").addEventListener("input", e => paintCmdk(e.target.value.trim().toLowerCase()));
$("#cmdk-input").addEventListener("keydown", e => {
  if (e.key === "ArrowDown"){ e.preventDefault(); cmdkIdx = Math.min(cmdkIdx+1, cmdkFiltered.length-1); }
  else if (e.key === "ArrowUp"){ e.preventDefault(); cmdkIdx = Math.max(cmdkIdx-1, 0); }
  else if (e.key === "Enter"){ e.preventDefault(); runCmdk(cmdkIdx); return; }
  else if (e.key === "Escape"){ closeCmdk(); return; }
  else return;
  $$("#cmdk-list .cmdk-item[data-i]").forEach((el,i) => {
    el.classList.toggle("on", i === cmdkIdx);
    if (i === cmdkIdx) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
  });
});
$("#cmdk-veil").addEventListener("click", e => { if (e.target.id === "cmdk-veil") closeCmdk(); });
$("#open-cmdk").onclick = openCmdk;

document.addEventListener("keydown", e => {
  const inApp = $("#view-app").classList.contains("active");
  const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName) || document.activeElement?.isContentEditable;
  const combo = comboOf(e);
  if (combo === "Ctrl+K"){ e.preventDefault(); $("#cmdk-veil").classList.contains("show") ? closeCmdk() : openCmdk(); return; }
  if (inApp && combo === "Ctrl+Shift+P" && !typing){ e.preventDefault(); openQuickPublishModal(); return; }
  if (inApp && combo === "Ctrl+Shift+S" && !typing && active()){ e.preventDefault(); openPublishModal(active(), { intent: "preview" }); return; }
  if (inApp && combo === "Ctrl+Shift+L" && !typing && active() && (stagingOf(active().id) || prodOf(active().id))){ e.preventDefault(); openPublishModal(active(), { intent: "live" }); return; }
  if (!inApp || page !== "builder-edit") return;
  const kb = keybinds();
  if (combo === kb.save){ e.preventDefault(); saveNow(false); return; }
  if (typing) return;
  if (e.key === "?"){ e.preventDefault(); shortcutsModal(); return; }
  if (combo === kb.undo){ e.preventDefault(); undo(); return; }
  if (combo === kb.redo){ e.preventDefault(); redo(); return; }
  if (combo === kb.preview){
    e.preventDefault();
    if (current) previewSite(current, activePage()?.slug || "");
    return;
  }
  if (combo === kb.live){ e.preventDefault(); setLiveEdit(!liveEdit); return; }
  if (combo === kb.search){ e.preventDefault(); searchPopover(); return; }
  if ((combo === kb.delete || (kb.delete === "Backspace" && combo === "Delete")) && selected?.k === "b"){
    e.preventDefault();
    const del = () => blockAction("del", activePage().blocks.findIndex(b => b.id === selected.id));
    if (bprefs().confirmDelete === "yes") confirmModal("Delete this section?", "You can undo until you leave the builder.", "Delete", del);
    else del();
    return;
  }
  if (combo === kb.duplicate && selected?.k === "b"){ e.preventDefault(); blockAction("dup", activePage().blocks.findIndex(b => b.id === selected.id)); return; }
  if (combo === kb.exit || e.key === "Escape"){
    /* Esc layers: editing → close insp → deselect → popover → exit live → exit preview → exit builder */
    if (document.activeElement?.isContentEditable){
      e.preventDefault();
      document.activeElement.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
      return;
    }
    if (liveEdit && $("#inspector")?.classList.contains("live-open")){
      e.preventDefault();
      $("#inspector").classList.remove("live-open");
      syncLiveInspector();
      syncLiveSelBar();
      icons();
      return;
    }
    if (selected){ e.preventDefault(); selected = null; clearMultiSel(); paintSelection(); renderInspector(); renderLayers(); icons(); return; }
    if (!$("#ctx-menu").hidden){ e.preventDefault(); closeCtx(); return; }
    if ($("#modal-veil").classList.contains("show")){ return; }
    if (liveEdit){ e.preventDefault(); setLiveEdit(false); return; }
    if (previewMode){ e.preventDefault(); setPreviewMode(false); return; }
    exitBuilder();
  }
});
window.addEventListener("beforeunload", e => { if (dirty){ e.preventDefault(); e.returnValue = ""; } });

/* ======================== BUILDER SETTINGS ========================
   Editor preferences only — never mixed with App Settings (#/settings).
   Keybinds, preferences, canvas options, debugging, appearance.
   Persisted to localStorage + mirrored to profiles.builder_prefs.   */
function builderSettingsModal(startTab){
  if (startTab === "keys"){ goAccountShortcuts(); return; }
  let tab = startTab || "prefs";
  const m = openModal(`
    <div class="bs-modal">
      <div class="bs-head">
        <div>
          <h2 id="modal-title">Builder settings</h2>
          <p class="sub">Editor preferences for this browser — separate from Account settings.</p>
        </div>
        <button type="button" class="icon-close-btn" data-x="c" aria-label="Close">${lu("x","lu16")}</button>
      </div>
      <nav class="bs-tabs" id="bs-tabs" role="tablist" aria-label="Builder settings sections">
        <button type="button" role="tab" data-t="prefs">Preferences</button>
        <button type="button" role="tab" data-t="canvas">Canvas</button>
        <button type="button" role="tab" data-t="debug">Debugging</button>
        <button type="button" role="tab" data-t="look">Appearance</button>
      </nav>
      <div id="bs-body" class="bs-body"></div>
      <div class="bs-foot">
        <button type="button" class="btn btn-ghost btn-sm" data-x="c2">Done</button>
      </div>
    </div>`, true);
  m.classList.add("bs-modal-box");
  const close = () => closeModal();
  m.querySelectorAll("[data-x=c], [data-x=c2]").forEach(b => b.onclick = close);
  const body = m.querySelector("#bs-body");
  const segRow = (label, key, opts, labels) => {
    const cur = bprefs()[key];
    return `<div class="bs-field">
      <label>${esc(label)}</label>
      <div class="seg bs-seg">${opts.map((o, i) =>
        `<button type="button" data-bp-k="${key}" data-bp-v="${o}" class="${cur === o ? "on" : ""}">${esc(labels ? labels[i] : o)}</button>`
      ).join("")}</div>
    </div>`;
  };
  function paint(){
    m.querySelectorAll("#bs-tabs button").forEach(b => {
      const on = b.dataset.t === tab;
      b.classList.toggle("on", on);
      b.setAttribute("aria-selected", on ? "true" : "false");
    });
    if (tab === "prefs"){
      body.innerHTML =
        segRow("Autosave interval", "autosave", ["off","30","60","120"], ["Off","30s","60s","120s"]) +
        segRow("Confirm before deleting a section", "confirmDelete", ["yes","no"], ["Confirm","Just delete"]);
    }
    if (tab === "canvas"){
      body.innerHTML =
        segRow("Section outlines on hover", "outlines", ["yes","no"], ["Show","Hide"]) +
        segRow("Section name labels on hover", "hoverLabels", ["no","yes"], ["Selected only","On hover"]) +
        segRow("Show section IDs on labels", "showIds", ["no","yes"], ["Hide","Show"]);
    }
    if (tab === "debug"){
      body.innerHTML =
        segRow("Verbose builder logging", "verbose", ["no","yes"], ["Off","On"]) +
        `<div class="bs-actions">
          <button type="button" class="btn btn-ghost btn-sm" id="bs-tour">${lu("compass")} Restart tour</button>
          <button type="button" class="btn btn-ghost btn-sm" id="bs-debug">${lu("bug")} Open debug panel</button>
        </div>
        <p class="bs-hint">Verbose logging records every edit in the debug panel’s Console tab — useful when reporting an issue to ${esc(CONTACT)}.</p>`;
      body.querySelector("#bs-tour").onclick = () => { closeModal(); localStorage.removeItem("bd_tour_done"); startTour(); };
      body.querySelector("#bs-debug").onclick = () => { closeModal(); toggleDebugPanel(true); };
    }
    if (tab === "look"){
      body.innerHTML = segRow("Builder toolbar", "chrome", ["light","dark","match"], ["Light","Dark","Match app"]);
    }
    body.querySelectorAll("[data-bp-k]").forEach(b => b.onclick = () => {
      setBpref(b.dataset.bpK, b.dataset.bpV);
      paint();
    });
    icons();
  }
  m.querySelectorAll("#bs-tabs button").forEach(b => b.onclick = () => { tab = b.dataset.t; paint(); });
  paint();
}

/* ============================ DEBUG PANEL ============================ */
let debugTab = "structure";
function toggleDebugPanel(force){
  const p = $("#debug-panel"); if (!p) return;
  const open = force === true ? true : !p.classList.contains("open");
  p.classList.toggle("open", open);
  if (open) paintDebug();
}
function paintDebug(){
  const p = $("#debug-panel"); if (!p || !current) return;
  const tabs = [["structure","Structure"],["tokens","Tokens"],["seo","SEO"],["a11y","A11y"],["perf","Performance"],["console","Console"]];
  let html = `<div class="dbg-head"><strong>${lu("bug","lu14")} Debug</strong><button id="dbg-x">${lu("x","lu14")}</button></div>
    <div class="dbg-tabs">${tabs.map(([k,l]) => `<button data-dt="${k}" class="${debugTab===k?"on":""}">${l}</button>`).join("")}</div>
    <div class="dbg-body">`;
  const pg = activePage();
  if (debugTab === "structure"){
    const tree = pg.blocks.map((b, i) => `${i}. ${b.type}${b.hidden ? " (hidden)" : ""} #${b.id}\n` +
      Object.entries(b.props).filter(([k]) => k !== "_style").map(([k, v]) => `   ${k}: ${String(v).replace(/\n/g, " ").slice(0, 60)}`).join("\n")).join("\n");
    html += `<p class="note">Page “${esc(pg.name)}” — ${pg.blocks.length} section${pg.blocks.length===1?"":"s"}${current.data.shared.header?" + shared header":""}${current.data.shared.footer?" + shared footer":""}</p>
      <pre class="dbg-pre">${esc(tree || "Empty page.")}</pre>`;
  }
  if (debugTab === "tokens"){
    html += `<table class="sc-table">${Object.entries(current.data.tokens).map(([k,v]) => `<tr><td>${esc(k)}</td><td style="font-family:var(--mono);font-size:11px">${esc(String(v))}</td></tr>`).join("")}</table>
      <p class="impact" style="margin-top:12px">${esc(impactLine(current))}</p>`;
  }
  if (debugTab === "seo"){
    const a = auditSite(current);
    const seoF = a.findings.filter(x => /title|description|address|share|favicon|sitemap|404/i.test(x.msg));
    html += seoF.length ? seoF.map(x => `<div class="dbg-row ${x.sev}">${lu(x.sev==="crit"?"octagon-alert":"triangle-alert","lu14")}<span>${esc(x.msg)}<em>${esc(x.area)}</em></span></div>`).join("")
      : `<p class="note">No SEO findings — titles, descriptions, and addresses look healthy.</p>`;
  }
  if (debugTab === "a11y"){
    const ratio = contrast(current.data.tokens.accent, current.data.tokens.mode === "dark" ? "#0b0b0d" : "#ffffff").toFixed(2);
    const noImg = pg.blocks.filter(b => ["image","split","casestudy"].includes(b.type) && !b.props.img).length;
    html += `<div class="dbg-row ${ratio < 3 ? "crit" : "ok"}">${lu(ratio < 3 ? "octagon-alert" : "badge-check","lu14")}<span>Accent contrast on background: ${ratio}:1 ${ratio < 3 ? "— below WCAG 3:1 for UI elements" : "— passes WCAG 3:1"}</span></div>
      <div class="dbg-row ${noImg ? "warn" : "ok"}">${lu(noImg ? "triangle-alert" : "badge-check","lu14")}<span>${noImg ? noImg + " image slot" + (noImg>1?"s":"") + " on this page still show placeholders" : "All image slots on this page are filled"}</span></div>
      <p class="note" style="margin-top:10px">Alt text is generated from headings and captions automatically; heading order is enforced by the block engine.</p>`;
  }
  if (debugTab === "perf"){
    const files = buildSiteFiles(current, "https://preview.local/");
    const total = files.reduce((n, f) => n + f.content.length, 0);
    const scripts = files.filter(f => f.path.endsWith(".js")).length + (siteLegalPack(current).plausibleOn ? 1 : 0);
    const imgs = (JSON.stringify(current.data.pages).match(/https?:[^"]+\.(jpg|jpeg|png|webp|gif)/gi) || []).length;
    const embeds = current.data.pages.reduce((n, x) => n + x.blocks.filter(b => b.type === "video" && b.props.url).length, 0);
    html += `<table class="sc-table">
      <tr><td>Pages in bundle</td><td>${current.data.pages.length}</td></tr>
      <tr><td>Sections (all pages)</td><td>${current.data.pages.reduce((n,x)=>n+x.blocks.length,0)}</td></tr>
      <tr><td>Bundle size</td><td>${(total/1024).toFixed(1)} KB</td></tr>
      <tr><td>External images</td><td>${imgs}</td></tr>
      <tr><td>Video embeds</td><td>${embeds}</td></tr>
      <tr><td>Scripts shipped</td><td>${scripts} (${files.some(f => f.path === "_bd/bd-motion.js") ? "same-origin motion" : "motion off"}${files.some(f => f.path === "_bd/bd-request.js") ? " + API request" : ""}${siteLegalPack(current).plausibleOn ? " + Plausible" : ""})</td></tr></table>`;
  }
  if (debugTab === "console"){
    html += BUILDER_LOG.length ? BUILDER_LOG.map(l => `<div class="dbg-row"><span style="font-family:var(--mono);font-size:10.5px;color:var(--subtle)">${esc(l.t)}</span><span>${esc(l.msg)}</span></div>`).join("")
      : `<p class="note">Builder warnings and (with verbose logging on) every edit land here. Nothing yet.</p>`;
  }
  html += `</div>`;
  p.innerHTML = html;
  p.querySelector("#dbg-x").onclick = () => toggleDebugPanel();
  p.querySelectorAll("[data-dt]").forEach(b => b.onclick = () => { debugTab = b.dataset.dt; paintDebug(); });
  icons();
}

/* ============================ DEVTOOLS TOUR ============================
   First-run spotlight walkthrough of the whole builder — replayable from
   Builder settings → Debugging. */
const TOUR_STEPS = [
  { sel:"#b-pill",        title:"The toolbar",        body:"Everything lives in this pill: search the canvas, browse history, undo and redo, share, save, and publish." },
  { sel:"#panel-left",    title:"Pages & Library", body:"Switch between your pages and the section library. Drag sections onto the canvas, then reorder them there with the grip or ↑↓." },
  { sel:"#block-library", title:"The section library", body:"Drag any section onto the canvas, or click to add it at the end. Use the chips to filter by category." },
  { sel:"#canvas-wrap",   title:"The canvas",          body:"This is the block editor — each section is a card you can reorder. Use Live edit when you want the produced site look." },
  { sel:"#inspector",     title:"Component editor",    body:"In the block editor the inspector stays on the right. In Live edit, open Edit on the floating bar (or double-click) for Content, Style, Design, and SEO." },
  { sel:"#pb-preview",    title:"Interactive preview", body:"Opens your full site in a new tab — nav, buttons, forms, and anchors work like they will when published." },
  { sel:"#pb-live",       title:"Live edit",           body:"The produced version of your site — click text to edit, select a section for the floating bar, Edit for the inspector. Done or Esc returns to the block editor." },
  { sel:"#pb-add",        title:"Add a section",       body:"Open the section picker anytime — same library as the left rail." },
  { sel:"#btn-publish",   title:"Publish",             body:"Stage first to a private preview URL, then promote to live. What you previewed is exactly what ships. You can also Stage and Promote from Console." },
  { sel:"#pb-exit",       title:"Back to the dashboard", body:"Exit returns you to Studio. If you have unsaved changes, we’ll ask before anything is lost." }
];
function startTour(){
  document.querySelector(".tour-veil")?.remove();
  let step = 0;
  const veil = document.createElement("div");
  veil.className = "tour-veil";
  veil.innerHTML = `<div class="tour-spot"></div>
    <div class="tour-card">
      <div class="tc-step"></div><h3></h3><p></p>
      <label class="tc-skip"><input type="checkbox" id="tour-dont" checked> Don't show this tour again</label>
      <div class="tc-row">
        <button type="button" class="btn btn-ghost btn-sm tour-skip-btn" id="tour-x">Skip tour</button>
        <div class="tc-actions">
          <button type="button" class="btn btn-ghost btn-sm" id="tour-back">Back</button>
          <button type="button" class="btn btn-cta btn-sm" id="tour-next">Next</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(veil);
  let steps = [];
  const done = () => {
    if (veil.querySelector("#tour-dont").checked){
      localStorage.setItem("bd_tour_done", "1");
      setBpref("tourDone", "yes");
    }
    veil.remove();
    window.removeEventListener("resize", paint);
  };
  /** Place the tip beside the highlight — never over what it's showcasing. */
  function placeTourCard(card, r){
    const GAP = 18, PAD = 12, CW = 340;
    card.style.left = "0px";
    card.style.top = "0px";
    const CH = Math.max(card.offsetHeight || 200, 160);
    const vw = innerWidth, vh = innerHeight;
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(v, hi));
    const tall = r.height > vh * 0.42;
    const shortWide = r.height < 140 && r.width > vw * 0.4;
    const candidates = [
      {
        left: r.right + GAP,
        top: clamp(r.top + Math.min(28, r.height * 0.06), PAD, vh - CH - PAD),
        score: (vw - r.right - GAP - PAD >= CW ? 120 : -120) + Math.min(vw - r.right - GAP, CW) + (tall ? 90 : 0)
      },
      {
        left: r.left - GAP - CW,
        top: clamp(r.top + Math.min(28, r.height * 0.06), PAD, vh - CH - PAD),
        score: (r.left - GAP - PAD >= CW ? 120 : -120) + Math.min(r.left - GAP, CW) + (tall ? 90 : 0)
      },
      {
        left: clamp(r.left + r.width / 2 - CW / 2, PAD, vw - CW - PAD),
        top: r.bottom + GAP,
        score: (vh - r.bottom - GAP - PAD >= CH ? 100 : -100) + Math.min(vh - r.bottom - GAP, CH) + (shortWide ? 80 : 0)
      },
      {
        left: clamp(r.left + r.width / 2 - CW / 2, PAD, vw - CW - PAD),
        top: r.top - GAP - CH,
        score: (r.top - GAP - PAD >= CH ? 80 : -120) + Math.min(r.top - GAP, CH)
      }
    ];
    for (const c of candidates){
      c.left = clamp(c.left, PAD, vw - CW - PAD);
      c.top = clamp(c.top, PAD, vh - CH - PAD);
      const ox = Math.max(0, Math.min(c.left + CW, r.right) - Math.max(c.left, r.left));
      const oy = Math.max(0, Math.min(c.top + CH, r.bottom) - Math.max(c.top, r.top));
      const area = ox * oy;
      if (area > 200) c.score -= 250 + area / 80;
    }
    candidates.sort((a, b) => b.score - a.score);
    card.style.left = candidates[0].left + "px";
    card.style.top = candidates[0].top + "px";
  }
  function paint(){
    steps = TOUR_STEPS.filter(s => { const el = document.querySelector(s.sel); return el && el.getBoundingClientRect().width > 0; });
    if (!steps.length) return done();
    if (step >= steps.length) step = steps.length - 1;
    const s = steps[step];
    const el = document.querySelector(s.sel);
    try { el.scrollIntoView({ block:"nearest", inline:"nearest", behavior:"smooth" }); } catch {}
    const r = el.getBoundingClientRect();
    const spot = veil.querySelector(".tour-spot");
    spot.style.cssText = `left:${r.left - 8}px;top:${r.top - 8}px;width:${r.width + 16}px;height:${r.height + 16}px`;
    const card = veil.querySelector(".tour-card");
    card.querySelector(".tc-step").textContent = `Step ${step + 1} of ${steps.length}`;
    card.querySelector("h3").textContent = s.title;
    card.querySelector("p").textContent = s.body;
    placeTourCard(card, r);
    veil.querySelector("#tour-back").style.visibility = step === 0 ? "hidden" : "visible";
    veil.querySelector("#tour-next").textContent = step === steps.length - 1 ? "Finish" : "Next";
  }
  veil.querySelector("#tour-next").onclick = () => { if (step >= steps.length - 1) return done(); step++; paint(); };
  veil.querySelector("#tour-back").onclick = () => { step = Math.max(0, step - 1); paint(); };
  veil.querySelector("#tour-x").onclick = done;
  window.addEventListener("resize", paint);
  paint();
}

/* ========================= PLATFORM ADMIN =========================
   Owner-only (#/admin…). Client-side gate: admin flag. Server-side
   gate: api/admin.php re-verifies JWT + profiles.is_admin with the
   service role on EVERY call, rate-limits, and writes admin_audit.
   Demo mode operates on this browser's localStorage tenants.        */
let adminTimer = null;
function armAdminTimeout(){
  clearTimeout(adminTimer);
  adminTimer = setTimeout(() => {
    if (page.startsWith("admin")) toast("You’ve had admin tools open for 15 minutes — consider closing them when done.");
  }, 15 * 60 * 1000);
}
function disarmAdminTimeout(){ clearTimeout(adminTimer); }
function adminSecurityBanner(el){
  card(el, `<div class="card-row"><div style="display:flex;gap:12px;align-items:flex-start">${lu("shield-alert")}
    <div><strong style="font-size:13.5px">Admin account hygiene</strong>
    <p class="c-sub" style="margin:3px 0 0">Use a unique password and enable 2FA on this account. Every action on these pages is written to the platform audit log${DEMO ? " (demo: stored locally)" : ""}.</p></div></div></div>`, "admin-banner");
}
function adminErr(el, e){
  card(el, `<p class="note" style="margin:0;color:var(--bad)">${esc(humanErr(e))}</p>`);
}
async function pageAdmin(el){
  head(el, "shield", "Platform admin", "Cross-tenant operations for the Browns Digital Studio platform. Everything here is audited.");
  adminSecurityBanner(el);
  try {
    const m = await api.admin("metrics");
    const stats = document.createElement("div");
    stats.className = "stat-cards reveal";
    stats.innerHTML = `
      <div class="stat-card"><div class="sc-l">Users ${lu("users")}</div><div class="sc-n">${m.users}</div><div class="sc-d">registered accounts</div></div>
      <div class="stat-card"><div class="sc-l">Sites ${lu("globe")}</div><div class="sc-n">${m.sites}</div><div class="sc-d">${m.published} live · ${m.staged} staged</div></div>
      <div class="stat-card"><div class="sc-l">Domains ${lu("link")}</div><div class="sc-n">${m.domains}</div><div class="sc-d">custom domains connected</div></div>
      <div class="stat-card"><div class="sc-l">Plan mix ${lu("credit-card")}</div><div class="sc-n" style="font-size:18px;padding-top:8px">${m.perPlan.free||0} · ${m.perPlan.pro||0} · ${m.perPlan.studio||0}</div><div class="sc-d">free · pro · studio</div></div>`;
    el.appendChild(stats);

    const links = card(el, `<h2>Admin areas</h2><div class="kv" style="margin-top:12px">
      <button class="kvr" data-go="admin-users"><span>Users — search, plans, suspension, GDPR</span><strong>${lu("users","lu16")}</strong></button>
      <button class="kvr" data-go="admin-sites"><span>All tenant sites — God-mode editor, force unpublish</span><strong>${lu("globe","lu16")}</strong></button>
      <button class="kvr" data-go="admin-domains"><span>Domains — verify and revoke</span><strong>${lu("link","lu16")}</strong></button>
      <button class="kvr" data-go="admin-audit"><span>Audit log — every admin and tenant action</span><strong>${lu("scroll-text","lu16")}</strong></button>
    </div>`);
    links.querySelectorAll("[data-go]").forEach(b => b.onclick = () => go(b.dataset.go));

    card(el, `<div class="card-row"><div><h2>Plans</h2><p class="c-sub">Change a user’s plan from the Users page — it takes effect on their next request.</p></div>
      <a class="btn btn-ghost btn-sm" href="${URLS.pricing}" target="_blank" rel="noopener">Public pricing ›</a></div>
      <table class="sc-table" style="margin-top:6px">
        <tr><td><strong>Free</strong> — 1 site, full builder</td><td>${m.perPlan.free || 0} users</td></tr>
        <tr><td><strong>Pro</strong> — 3 sites, custom CSS, 3 collaborators</td><td>${m.perPlan.pro || 0} users</td></tr>
        <tr><td><strong>Studio</strong> — unlimited, custom domains</td><td>${m.perPlan.studio || 0} users</td></tr></table>`);
  } catch (e) { adminErr(el, e); }
}
async function pageAdminUsers(el){
  head(el, "users", QA_MODE ? "QA users" : "Users",
    QA_MODE ? "Invite testers, revoke access, and remove accounts. Every change is audited." : "Search, change plans, suspend, impersonate read-only, and delete (GDPR). Every change is audited.",
    `<button class="btn btn-cta btn-sm" id="au-add">${lu("user-plus")} Add user</button>
     <button class="btn btn-ghost btn-sm" id="au-csv">${lu("download")} Export CSV</button>`);
  let rows = [];
  try { rows = (await api.admin("list_users")).users; } catch (e) { return adminErr(el, e); }
  const c = card(el, `<div class="adm-filters">
      <input id="au-q" type="search" placeholder="Search name or email…">
      <div id="au-plan-wrap"></div>
      <div id="au-status-wrap"></div>
    </div>
    <div class="adm-table-wrap"><table class="adm-table"><thead><tr>
      <th>Name</th><th>Email</th><th>Plan</th><th>Sites</th><th>Joined</th><th>Last sign-in</th><th>Status</th><th></th>
    </tr></thead><tbody id="au-body"></tbody></table></div>`);
  const planFilter = bdSelect(
    [
      ["", "All plans"],
      ["free", "Free"],
      ["build", "Build"],
      ["forge", "Forge"],
      ["architect", "Architect"],
      ["pro", "Pro (legacy)"],
      ["studio", "Studio (legacy)"]
    ],
    "",
    () => paint(),
    { className: "bd-select--filter", ariaLabel: "Filter by plan" }
  );
  const statusOpts = [["", "Any status"], ["active", "Active"], ["suspended", "Suspended"]];
  if (QA_MODE) statusOpts.push(["qa", "QA access"], ["noqa", "No QA access"]);
  const statusFilter = bdSelect(statusOpts, "", () => paint(), { className: "bd-select--filter", ariaLabel: "Filter by status" });
  c.querySelector("#au-plan-wrap").appendChild(planFilter);
  c.querySelector("#au-status-wrap").appendChild(statusFilter);
  const paint = () => {
    const q = c.querySelector("#au-q").value.trim().toLowerCase();
    const pf = planFilter.getValue();
    const sf = statusFilter.getValue();
    const list = rows.filter(u =>
      (!q || (u.name + " " + u.email).toLowerCase().includes(q)) &&
      (!pf || u.plan === pf) &&
      (!sf || (sf === "suspended" ? !!u.suspended : sf === "active" ? !u.suspended : sf === "qa" ? !!u.qa_access : sf === "noqa" ? !u.qa_access : true)));
    c.querySelector("#au-body").innerHTML = list.length ? list.map(u => `
      <tr class="${u.suspended ? "suspended" : ""}">
        <td>${esc(u.name || "—")}</td>
        <td class="mono">${esc(u.email)}</td>
        <td><span class="badge ${u.plan}">${esc(u.plan)}</span></td>
        <td>${u.sites}</td>
        <td>${u.created_at ? fmtWhen(u.created_at) : "—"}</td>
        <td>${u.last_sign_in ? fmtWhen(u.last_sign_in) : "—"}</td>
        <td>${u.suspended ? `<span class="badge" style="color:var(--bad);border-color:rgba(255,59,48,.4)">Suspended</span>` : QA_MODE && u.qa_access ? `<span class="badge live">QA</span>` : `<span class="badge live">Active</span>`}</td>
        <td><button class="icon-btn" data-menu="${esc(u.email)}">${lu("ellipsis")}</button></td>
      </tr>`).join("") : `<tr><td colspan="8" class="note" style="padding:18px">No users match.</td></tr>`;
    icons();
    c.querySelectorAll("[data-menu]").forEach(b => b.onclick = e => {
      e.stopPropagation();
      const u = rows.find(x => x.email === b.dataset.menu);
      const m = openCtx(e.clientX, e.clientY, `
        <button data-a="view">${lu("eye","lu14")} View details</button>
        <button data-a="plan">${lu("credit-card","lu14")} Change plan</button>
        <button data-a="imp">${lu("venetian-mask","lu14")} Impersonate (read-only)</button>
        <button data-a="susp">${lu(u.suspended ? "play" : "pause","lu14")} ${u.suspended ? "Unsuspend" : "Suspend"}</button>
        ${QA_MODE ? `<button data-a="qa">${lu(u.qa_access ? "user-x" : "user-check","lu14")} ${u.qa_access ? "Revoke QA access" : "Grant QA access"}</button>` : ""}
        <button data-a="del" class="danger">${lu("trash-2","lu14")} Delete (GDPR)</button>`);
      m.querySelector('[data-a="view"]').onclick = () => { closeCtx(); userDrawer(u, refresh); };
      m.querySelector('[data-a="plan"]').onclick = () => { closeCtx(); changePlanModal(u, refresh); };
      m.querySelector('[data-a="imp"]').onclick = () => { closeCtx(); impersonateModal(u); };
      m.querySelector('[data-a="susp"]').onclick = () => {
        closeCtx();
        confirmModal(u.suspended ? `Unsuspend ${esc(u.email)}?` : `Suspend ${esc(u.email)}?`,
          u.suspended ? "They regain access on their next sign-in." : "They are locked out of Studio until unsuspended. Published sites stay live — take them down separately if needed.",
          u.suspended ? "Unsuspend" : "Suspend",
          async () => { try { await api.admin("suspend", { email: u.email, suspended: !u.suspended }); toast("Done"); refresh(); } catch (er){ toast(humanErr(er), { kind: "error" }); } });
      };
      const qaBtn = m.querySelector('[data-a="qa"]');
      if (qaBtn) qaBtn.onclick = () => {
        closeCtx();
        confirmModal(u.qa_access ? `Revoke QA access for ${esc(u.email)}?` : `Grant QA access to ${esc(u.email)}?`,
          u.qa_access ? "They will be locked out of Studio on their next page load." : "They can sign in and use Studio immediately.",
          u.qa_access ? "Revoke access" : "Grant access",
          async () => { try { await api.admin("set_qa_access", { email: u.email, qa_access: !u.qa_access }); toast("Done"); refresh(); } catch (er){ toast(humanErr(er), { kind: "error" }); } });
      };
      m.querySelector('[data-a="del"]').onclick = () => {
        closeCtx();
        confirmModal(`Delete ${esc(u.email)} permanently?`, "GDPR delete — the account, all sites, deploys, and domains are removed. This cannot be undone.", "Delete everything",
          async () => { try { await api.admin("delete_user", { email: u.email }); toast("User deleted"); refresh(); } catch (er){ toast(humanErr(er), { kind: "error" }); } });
      };
    });
  };
  async function refresh(){ try { rows = (await api.admin("list_users")).users; paint(); } catch (e) { toast(humanErr(e)); } }
  el.querySelector("#au-add").onclick = () => createUserModal(refresh);
  c.querySelector("#au-q").addEventListener("input", paint);
  el.querySelector("#au-csv").onclick = () => {
    const csv = "name,email,plan,sites,suspended,created_at,last_sign_in\n" + rows.map(u =>
      [u.name, u.email, u.plan, u.sites, u.suspended, u.created_at || "", u.last_sign_in || ""].map(x => `"${String(x).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "studio-users.csv"; a.click(); URL.revokeObjectURL(a.href);
    toast("CSV exported (admin only)");
  };
  paint();
}
function createUserModal(refresh){
  const m = openModal(`<h2>${QA_MODE ? "Invite QA tester" : "Add user"}</h2>
    <p class="sub">${QA_MODE ? "Creates a verified account with QA access and the plan you choose. Share the email and password securely." : "Creates a verified account with the plan you choose."}</p>
    <div class="field"><label>Full name</label><input id="cu-name" autocomplete="name" placeholder="Alex Brown"></div>
    <div class="field"><label>Email</label><input id="cu-email" type="email" autocomplete="off" placeholder="tester@company.com"></div>
    <div class="field field-pass"><label>Temporary password</label><div class="pass-wrap"><input id="cu-pass" type="password" autocomplete="new-password" placeholder="At least 8 characters"></div></div>
    <div class="field field-plan"><label>Plan</label></div>
    <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button><button class="btn btn-cta btn-sm" data-x="ok">Create account</button></div>`);
  const planSel = bdSelect(
    [
      ["free", "Free"],
      ["build", "Build"],
      ["forge", "Forge"],
      ["architect", "Architect"],
      ["pro", "Pro (legacy)"],
      ["studio", "Studio (legacy)"]
    ],
    "architect",
    () => {},
    { ariaLabel: "Plan" }
  );
  m.querySelector(".field-plan").appendChild(planSel);
  m.querySelector('[data-x="c"]').onclick = closeModal;
  m.querySelector('[data-x="ok"]').onclick = async () => {
    const name = m.querySelector("#cu-name").value.trim();
    const email = m.querySelector("#cu-email").value.trim().toLowerCase();
    const password = m.querySelector("#cu-pass").value;
    const planVal = planSel.getValue();
    if (!name || !email) return toast("Name and email are required.");
    if (password.length < 8) return toast("Use a password of at least 8 characters.");
    const btn = m.querySelector('[data-x="ok"]');
    btn.disabled = true; btn.textContent = "Creating…";
    try {
      await api.admin("create_user", { name, email, password, plan: planVal });
      closeModal(); toast("Account created — share the sign-in details securely."); refresh();
    } catch (e) { toast(humanErr(e)); }
    btn.disabled = false; btn.textContent = "Create account";
  };
}
function changePlanModal(u, refresh){
  const m = openModal(`<h2>Change plan — ${esc(u.email)}.</h2>
    <p class="sub">Takes effect immediately; the server re-checks plans on every publish.</p>
    <div class="field field-plan"><label>Plan</label></div>
    <div class="field"><label>Reason (required — written to the audit log)</label><input id="cp-reason" placeholder="e.g. EFT payment received, invoice #1042"></div>
    <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button><button class="btn btn-cta btn-sm" data-x="ok">Apply plan</button></div>`);
  const planSel = bdSelect(
    [
      ["free", "Free"],
      ["build", "Build"],
      ["forge", "Forge"],
      ["architect", "Architect"],
      ["pro", "Pro (legacy)"],
      ["studio", "Studio (legacy)"]
    ],
    u.plan,
    () => {},
    { ariaLabel: "Plan" }
  );
  m.querySelector(".field-plan").appendChild(planSel);
  m.querySelector('[data-x="c"]').onclick = closeModal;
  m.querySelector('[data-x="ok"]').onclick = async () => {
    const reason = m.querySelector("#cp-reason").value.trim();
    if (!reason) return toast("A reason is required for plan overrides.");
    try {
      await api.admin("set_plan", { email: u.email, plan: planSel.getValue(), reason });
      closeModal(); toast("Plan updated"); refresh();
    } catch (e) { toast(humanErr(e)); }
  };
}
async function userDrawer(u, refresh){
  let sites = [];
  try { sites = (await api.admin("list_sites")).sites.filter(s => s.owner === u.email); } catch {}
  const m = openModal(`<h2>${esc(u.name || u.email)}</h2>
    <p class="sub mono" style="font-family:var(--mono)">${esc(u.email)}</p>
    <div class="kv">
      <div class="kvr"><span>Plan</span><strong><span class="badge ${u.plan}">${esc(u.plan)}</span></strong></div>
      <div class="kvr"><span>Status</span><strong>${u.suspended ? "Suspended" : QA_MODE && u.qa_access ? "QA access" : "Active"}</strong></div>
      <div class="kvr"><span>Joined</span><strong>${u.created_at ? fmtWhen(u.created_at) : "—"}</strong></div>
      <div class="kvr"><span>Last sign-in</span><strong>${u.last_sign_in ? fmtWhen(u.last_sign_in) : "—"}</strong></div>
      <div class="kvr"><span>Sites</span><strong>${u.sites}</strong></div>
    </div>
    <h3 style="font-size:11px;letter-spacing:.07em;text-transform:uppercase;color:var(--subtle);margin:16px 0 4px">Sites</h3>
    <div class="list-rows">${sites.length ? sites.map(s =>
      `<div class="lr"><span class="ic">${lu("globe")}</span><span class="lr-main"><strong>${esc(s.name)}</strong><em class="mono" style="font-family:var(--mono)">${esc(s.slug || "no slug")} · ${s.pages} pages</em></span>${s.live ? `<span class="badge live">Live</span>` : s.staging ? `<span class="badge staging">Staged</span>` : `<span class="badge draft">Draft</span>`}</div>`).join("")
      : `<p class="note" style="padding:8px 0">No sites yet.</p>`}</div>
    <div class="row"><button class="btn btn-ghost btn-sm" data-x="plan">Change plan</button><button class="btn btn-ghost btn-sm" data-x="c">Close</button></div>`, true);
  m.querySelector('[data-x="c"]').onclick = closeModal;
  m.querySelector('[data-x="plan"]').onclick = () => { closeModal(); changePlanModal(u, refresh); };
  icons();
}
async function impersonateModal(u){
  let sites = [];
  try { sites = (await api.admin("list_sites")).sites.filter(s => s.owner === u.email); } catch (e) { return toast(humanErr(e)); }
  const m = openModal(`<h2>Impersonating ${esc(u.email)} <span class="badge" style="margin-left:6px">Read-only</span></h2>
    <p class="sub">Preview their sites exactly as they render. Nothing here can modify their data — use the God-mode editor in All sites for changes (audited).</p>
    <div class="list-rows">${sites.length ? sites.map((s, i) =>
      `<div class="lr"><span class="ic">${lu("globe")}</span><span class="lr-main"><strong>${esc(s.name)}</strong><em>${s.pages} pages · updated ${fmtWhen(s.updated_at)}</em></span><button class="view-all" data-pv="${i}">Preview</button></div>`).join("")
      : `<p class="note">This user has no sites.</p>`}</div>
    <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Close</button></div>`, true);
  m.querySelector('[data-x="c"]').onclick = closeModal;
  m.querySelectorAll("[data-pv]").forEach(b => b.onclick = async () => {
    const s = sites[+b.dataset.pv];
    try {
      const { site } = await api.admin("get_site", { owner: u.email, project_id: s.id });
      previewSite({ name: site.name, data: site.data });
    } catch (e) { toast(humanErr(e)); }
  });
  icons();
}
async function pageAdminSites(el){
  head(el, "globe", "All tenant sites", "Every site on the platform. God-mode edits and force unpublish are service-role operations — always audited.");
  let rows = [];
  try { rows = (await api.admin("list_sites")).sites; } catch (e) { return adminErr(el, e); }
  const c = card(el, `<div class="adm-filters"><input id="as-q" type="search" placeholder="Search site, owner, or slug…"></div>
    <div class="adm-table-wrap"><table class="adm-table"><thead><tr>
      <th>Site</th><th>Owner</th><th>Slug</th><th>Pages</th><th>Last edited</th><th>Status</th><th></th>
    </tr></thead><tbody id="as-body"></tbody></table></div>`);
  const paint = () => {
    const q = c.querySelector("#as-q").value.trim().toLowerCase();
    const list = rows.filter(s => !q || (s.name + " " + s.owner + " " + s.slug).toLowerCase().includes(q));
    c.querySelector("#as-body").innerHTML = list.length ? list.map((s, i) => `
      <tr><td>${esc(s.name)}</td><td class="mono">${esc(s.owner)}</td><td class="mono">${esc(s.slug || "—")}</td>
      <td>${s.pages}</td><td>${fmtWhen(s.updated_at)}</td>
      <td>${s.live ? `<span class="badge live">Live</span>` : s.staging ? `<span class="badge staging">Staged</span>` : `<span class="badge draft">Draft</span>`}</td>
      <td><button class="icon-btn" data-menu="${i}">${lu("ellipsis")}</button></td></tr>`).join("")
      : `<tr><td colspan="7" class="note" style="padding:18px">No sites match.</td></tr>`;
    icons();
    c.querySelectorAll("[data-menu]").forEach(b => b.onclick = e => {
      e.stopPropagation();
      const s = list[+b.dataset.menu];
      const m = openCtx(e.clientX, e.clientY, `
        <button data-a="god">${lu("code-xml","lu14")} God-mode editor</button>
        <button data-a="pv">${lu("eye","lu14")} Preview render</button>
        <button data-a="stg" ${s.staging?"":"disabled style='opacity:.4'"}>${lu("upload","lu14")} View staging</button>
        <button data-a="flag">${lu("flag","lu14")} Flag for review</button>
        <button data-a="un" class="danger" ${s.live?"":"disabled style='opacity:.4'"}>${lu("circle-off","lu14")} Force unpublish</button>`);
      m.querySelector('[data-a="god"]').onclick = () => { closeCtx(); godModeEditor(s); };
      m.querySelector('[data-a="pv"]').onclick = async () => {
        closeCtx();
        try { const { site } = await api.admin("get_site", { owner: s.owner, project_id: s.id });
          previewSite({ name: site.name, data: site.data }); } catch (er){ toast(humanErr(er), { kind: "error" }); }
      };
      if (s.staging) m.querySelector('[data-a="stg"]').onclick = () => { closeCtx(); window.open(s.staging, "_blank"); };
      m.querySelector('[data-a="flag"]').onclick = () => {
        closeCtx();
        const md = openModal(`<h2>Flag “${esc(s.name)}” for review.</h2><div class="field"><label>Note (written to the audit log)</label><input id="fl-note" placeholder="e.g. reported content, ToS check"></div>
          <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button><button class="btn btn-cta btn-sm" data-x="ok">Flag</button></div>`);
        md.querySelector('[data-x="c"]').onclick = closeModal;
        md.querySelector('[data-x="ok"]').onclick = async () => {
          try { await api.admin("force_unpublish", { owner: s.owner, project_id: "__flag__" + s.id, reason: "FLAG ONLY: " + md.querySelector("#fl-note").value }); } catch {}
          closeModal(); toast("Flagged — see the audit log");
        };
      };
      if (s.live) m.querySelector('[data-a="un"]').onclick = () => {
        closeCtx();
        confirmModal(`Force unpublish “${esc(s.name)}”?`, "The live site goes offline immediately. The owner keeps their project and staging.", "Force unpublish",
          async () => { try { await api.admin("force_unpublish", { owner: s.owner, project_id: s.id, reason: "Admin force unpublish" }); toast("Taken offline"); rows = (await api.admin("list_sites")).sites; paint(); } catch (er){ toast(humanErr(er), { kind: "error" }); } });
      };
    });
  };
  c.querySelector("#as-q").addEventListener("input", paint);
  paint();
}
async function godModeEditor(s){
  let site;
  try { site = (await api.admin("get_site", { owner: s.owner, project_id: s.id })).site; }
  catch (e) { return toast(humanErr(e)); }
  const m = openModal(`<h2>God mode — ${esc(s.name)} <span class="badge" style="margin-left:6px">${esc(s.owner)}</span></h2>
    <p class="sub">Raw tenant project JSON. Validated before save; the change is audited and the tenant’s revision history is preserved.</p>
    <textarea id="gm-json" style="width:100%;min-height:320px;border:1px solid var(--line);border-radius:12px;background:var(--bg-soft);padding:14px;font-family:var(--mono);font-size:11px;color:var(--ink)" spellcheck="false"></textarea>
    <div class="row"><button class="btn btn-ghost btn-sm" data-x="c">Cancel</button><button class="btn btn-cta btn-sm" data-x="ok">Validate & save</button></div>`, true);
  m.querySelector("#gm-json").value = JSON.stringify(site.data, null, 2);
  m.querySelector('[data-x="c"]').onclick = closeModal;
  m.querySelector('[data-x="ok"]').onclick = async () => {
    try {
      const next = JSON.parse(m.querySelector("#gm-json").value);
      if (!Array.isArray(next.pages) || !next.tokens) throw new Error("Shape check failed: needs tokens + pages[].");
      await api.admin("patch_site", { owner: s.owner, project_id: s.id, data: next });
      closeModal(); toast("Patched & audited");
    } catch (e) { toast(humanErr(e, "That data looks invalid."), { kind: "error" }); }
  };
}
async function pageAdminDomains(el){
  head(el, "link", "Custom domains", "All custom_domains rows across tenants. Manual verify and revoke are support tools — DNS verification stays the normal path.");
  let rows = [];
  try { rows = (await api.admin("list_domains")).domains; } catch (e) { return adminErr(el, e); }
  const c = card(el, `<div class="adm-table-wrap"><table class="adm-table"><thead><tr>
      <th>Domain</th><th>Owner</th><th>Project</th><th>Status</th><th>DNS token</th><th></th>
    </tr></thead><tbody>${rows.length ? rows.map((d, i) => `
      <tr><td class="mono">${esc(d.domain)}</td><td class="mono">${esc(d.owner || "—")}</td><td class="mono">${esc(String(d.project_id).slice(0, 12))}…</td>
      <td><span class="badge ${d.status === "verified" ? "live" : "staging"}">${esc(d.status)}</span></td>
      <td class="mono" style="font-size:10px">${esc(String(d.token).slice(0, 18))}…</td>
      <td><button class="view-all" data-v="${i}" ${d.status==="verified"?"disabled style='opacity:.4'":""}>Verify</button>
          <button class="view-all" data-r="${i}" style="color:var(--bad)">Revoke</button></td></tr>`).join("")
      : `<tr><td colspan="6" class="note" style="padding:18px">No custom domains yet.</td></tr>`}</tbody></table></div>`);
  c.querySelectorAll("[data-v]").forEach(b => b.onclick = async () => {
    const d = rows[+b.dataset.v];
    try { await api.admin("domain_set", { domain: d.domain, status: "verified" }); toast("Manually verified — audited"); go("admin-domains"); }
    catch (e) { toast(humanErr(e)); }
  });
  c.querySelectorAll("[data-r]").forEach(b => b.onclick = () => {
    const d = rows[+b.dataset.r];
    confirmModal(`Revoke ${esc(d.domain)}?`, "The domain record is removed; the site stays live on its Browns Digital address.", "Revoke",
      async () => { try { await api.admin("domain_set", { domain: d.domain, status: "removed" }); toast("Revoked"); go("admin-domains"); } catch (e){ toast(humanErr(e)); } });
  });
}
async function pageAdminAudit(el){
  head(el, "scroll-text", "Audit log", "Platform-wide history: admin actions (plan changes, suspensions, God-mode patches) plus recent tenant activity.");
  let rows = [];
  try { rows = (await api.admin("audit_log")).log; } catch (e) { return adminErr(el, e); }
  const c = card(el, `<div class="adm-filters">
      <input id="al-q" type="search" placeholder="Filter by action, target, or admin…">
      <div id="al-kind-wrap"></div>
    </div><div class="list-rows" id="al-list"></div>`);
  const kindFilter = bdSelect(
    [["", "Everything"], ["admin", "Admin actions only"], ["tenant", "Tenant activity only"]],
    "",
    () => paint(),
    { className: "bd-select--filter", ariaLabel: "Filter audit log" }
  );
  c.querySelector("#al-kind-wrap").appendChild(kindFilter);
  const ICON = { plan_change:"credit-card", suspend:"pause", unsuspend:"play", delete_user:"trash-2", create_user:"user-plus", grant_qa:"user-check", revoke_qa:"user-x", patch_site:"code-xml", force_unpublish:"circle-off", domain_verified:"badge-check", domain_removed:"trash-2" };
  const paint = () => {
    const q = c.querySelector("#al-q").value.trim().toLowerCase();
    const kf = kindFilter.getValue();
    const list = rows.filter(r =>
      (!q || (r.action + " " + r.target + " " + (r.detail||"") + " " + (r.admin||"")).toLowerCase().includes(q)) &&
      (!kf || (kf === "tenant") === !!r.tenant));
    c.querySelector("#al-list").innerHTML = list.length ? list.slice(0, 120).map(r => `
      <div class="lr"><span class="ic ${r.tenant ? "" : "b"}">${lu(ICON[r.action] || ACT_ICON[r.action] || "dot")}</span>
        <span class="lr-main"><strong>${esc(r.detail || r.action)}</strong><em>${r.tenant ? "tenant" : "admin"} · ${esc(r.admin || "—")} → ${esc(String(r.target || "").slice(0, 40))}</em></span>
        <time>${fmtWhen(r.created_at)}</time></div>`).join("")
      : `<p class="note" style="padding:14px 4px">Nothing matches.</p>`;
    icons();
  };
  c.querySelector("#al-q").addEventListener("input", paint);
  paint();
}

/* ================================ BOOT ================================ */
function sessionPinStorageKey(email){
  return "bd_session_pin_" + String(email || "").trim().toLowerCase();
}
function sessionPinUnlockKey(email){
  return "bd_session_pin_unlocked_" + String(email || "").trim().toLowerCase();
}
function sessionPinCandidateKeys(email){
  const raw = String(email || "").trim();
  const lower = raw.toLowerCase();
  return [...new Set([
    "bd_session_pin_" + lower,
    raw ? "bd_session_pin_" + raw : "",
  ].filter(Boolean))];
}
function readSessionPin(email){
  for (const key of sessionPinCandidateKeys(email)) {
    const value = localStorage.getItem(key);
    if (value) return { key, value };
  }
  return null;
}
function writeSessionPin(email, value){
  const keys = sessionPinCandidateKeys(email);
  const canon = sessionPinStorageKey(email);
  localStorage.setItem(canon, value);
  keys.forEach(k => { if (k !== canon) localStorage.removeItem(k); });
}
function clearSessionPin(email){
  sessionPinCandidateKeys(email).forEach(k => localStorage.removeItem(k));
  try { sessionStorage.removeItem(sessionPinUnlockKey(email)); } catch {}
}
/** Lock Studio behind the session PIN (same screen as refresh unlock). */
async function lockStudioScreen(){
  const email = me?.email || "";
  if (!email){
    toast("Sign in to use Lock screen", { kind: "error" });
    return;
  }
  if (!readSessionPin(email)){
    toast("Set a Session PIN in Account settings first", { kind: "error" });
    try {
      $("#user-menu").hidden = true;
      $("#side-user")?.setAttribute("aria-expanded", "false");
      if (!DEMO && !QA_MODE && typeof BD_ACCOUNT !== "undefined") BD_ACCOUNT.go();
      else go("account");
    } catch {}
    return;
  }
  try {
    closeSiteMenu();
    $("#user-menu").hidden = true;
    $("#bell-menu").hidden = true;
    $("#side-user")?.setAttribute("aria-expanded", "false");
  } catch {}
  try { sessionStorage.removeItem(sessionPinUnlockKey(email)); } catch {}
  document.body.classList.remove("pin-just-unlocked");
  pinUnlockBusyAt = 0;
  const ok = await requireSessionPinUnlock();
  if (!ok) return;
  await settleSessionPinBusy();
  document.body.classList.remove("pin-just-unlocked");
  show("app");
}
/** Timestamp when PIN UI entered busy/spinner state (0 = PIN was not used this boot). */
let pinUnlockBusyAt = 0;
let pinUnlockInFlight = null;
async function settleSessionPinBusy(minMs = 1100){
  if (!pinUnlockBusyAt) return;
  const left = minMs - (performance.now() - pinUnlockBusyAt);
  if (left > 0) await new Promise(r => setTimeout(r, left));
  pinUnlockBusyAt = 0;
}
async function requireTotpElevation(){
  if (DEMO) return true;
  /* QR phone already verified MFA — magic-link desktop session stays AAL1. */
  let qrMfaOk = false;
  try { qrMfaOk = sessionStorage.getItem("bd_device_signin_mfa_ok") === "1"; } catch {}
  if (qrMfaOk) {
    try { sessionStorage.removeItem("bd_device_signin_mfa_ok"); } catch {}
    return true;
  }
  const aal = await api.getAalState();
  if (!aal.needsMfa) return true;
  const mfa = await api.getMfaState();
  if (!mfa.totpEnabled) return true;
  /* Hide every shell before the challenge — do not leave Console chrome under the veil. */
  document.body.classList.add("mfa-lock");
  ["app","pin","gate","onboard","auth"].forEach(name => {
    const el = $("#view-" + name);
    if (!el) return;
    el.classList.remove("active");
    el.hidden = true;
  });
  try {
    const code = await promptAuthenticatorCode({
      title: "Enter authenticator code",
      product: "Browns Digital",
      maxAttempts: 5,
      lockClose: true,
      showPasskeyOption: true,
      showSupport: true,
      showCancel: false,
      verify: (c) => api.mfaElevateTotp(c)
    });
    if (!code) show("auth");
    return !!code;
  } finally {
    document.body.classList.remove("mfa-lock");
  }
}
async function requireSessionPinUnlock(){
  if (pinUnlockInFlight) return pinUnlockInFlight;
  pinUnlockInFlight = (async () => {
  const email = me?.email || "";
  const savedRow = readSessionPin(email);
  try { sessionStorage.removeItem("bd_passkey_unlock_once"); } catch {}
  if (!savedRow) return true;
  try {
    if (sessionStorage.getItem(sessionPinUnlockKey(email)) === "1") return true;
  } catch {}
  const saved = savedRow.value;
  /* Migrate legacy casing to the canonical key. */
  if (savedRow.key !== sessionPinStorageKey(email)) writeSessionPin(email, saved);

  show("pin");
  return new Promise(resolve => {
    const root = $("#view-pin");
    if (!root){ resolve(true); return; }
    let done = false;
    const cells = [...root.querySelectorAll("[data-pin-cell]")];
    const err = root.querySelector("#pin-unlock-error");
    const entry = root.querySelector("#pin-unlock-entry");
    const spinner = root.querySelector("#pin-unlock-spinner");
    const setBusy = busy => {
      entry?.classList.toggle("is-busy", !!busy);
      root.classList.toggle("is-busy", !!busy);
      if (spinner){
        spinner.hidden = !busy;
        spinner.setAttribute("aria-busy", busy ? "true" : "false");
      }
      root.setAttribute("aria-busy", busy ? "true" : "false");
    };
    const syncFilled = () => {
      cells.forEach(c => c.classList.toggle("is-filled", !!c.value));
    };
    const clearCells = () => {
      cells.forEach(c => { c.value = ""; c.classList.remove("is-filled"); });
      cells[0]?.focus();
    };
    const readPin = () => cells.map(c => c.value).join("");
    const showErr = msg => {
      if (!err) return;
      err.textContent = msg || "";
      err.hidden = !msg;
      root.classList.toggle("is-error", !!msg);
    };
    const finish = ok => {
      if (done) return;
      done = true;
      cells.forEach(c => { c.oninput = null; c.onkeydown = null; c.onpaste = null; c.disabled = true; });
      resolve(ok);
    };
    const tryUnlock = () => {
      const value = readPin();
      if (done || value.length < 4) return;
      if (!/^\d{4}$/.test(value) || btoa(value) !== saved){
        showErr("That PIN is incorrect.");
        clearCells();
        return;
      }
      showErr("");
      try { sessionStorage.setItem(sessionPinUnlockKey(email), "1"); } catch {}
      /* Stay on PIN with a spinner until boot finishes — force a paint before continuing. */
      cells.forEach(c => { c.oninput = null; c.onkeydown = null; c.onpaste = null; c.disabled = true; });
      setBusy(true);
      pinUnlockBusyAt = performance.now();
      document.body.classList.add("pin-just-unlocked");
      requestAnimationFrame(() => {
        requestAnimationFrame(() => finish(true));
      });
    };
    const onInput = (e, i) => {
      const el = cells[i];
      const v = String(el.value || "").replace(/\D/g, "").slice(-1);
      el.value = v;
      syncFilled();
      showErr("");
      if (v && i < cells.length - 1) cells[i + 1].focus();
      if (readPin().length === 4) tryUnlock();
    };
    const onKey = (e, i) => {
      if (e.key === "Backspace" && !cells[i].value && i > 0){
        cells[i - 1].focus();
        cells[i - 1].value = "";
        syncFilled();
        e.preventDefault();
      } else if (e.key === "Enter"){
        e.preventDefault();
        tryUnlock();
      } else if (/^\d$/.test(e.key) && cells[i].value && i < cells.length - 1){
        /* Some browsers fire keydown before replacing the digit — advance immediately. */
        cells[i + 1].focus();
      }
    };
    const onPaste = e => {
      const text = String(e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, 4);
      if (!text) return;
      e.preventDefault();
      cells.forEach((c, i) => { c.value = text[i] || ""; });
      syncFilled();
      if (text.length === 4) tryUnlock();
      else cells[Math.min(text.length, cells.length - 1)]?.focus();
    };

    setBusy(false);
    cells.forEach((c, i) => {
      c.disabled = false;
      c.oninput = e => onInput(e, i);
      c.onkeydown = e => onKey(e, i);
      c.onpaste = onPaste;
      c.value = "";
      c.classList.remove("is-filled");
    });
    showErr("");
    clearCells();
  });
  })();
  try { return await pinUnlockInFlight; }
  finally { pinUnlockInFlight = null; }
}
/** Allowlisted post-auth redirect back to a brownsdigital.org URL. */
function isBdExternalReturn(url) {
  try {
    const u = new URL(url);
    return /^([a-z0-9-]+\.)?brownsdigital\.org$/i.test(u.hostname);
  } catch {
    return false;
  }
}

function isDeviceSignInReturn(url) {
  try {
    const u = new URL(url);
    return u.hostname === "brownsdigital.org"
      && u.pathname.replace(/\/+$/, "") === "/platform/device-sign-in";
  } catch {
    return false;
  }
}

function markDeviceSignInFreshIfNeeded() {
  const q = new URLSearchParams(location.search);
  let ret = q.get("return");
  if (!ret) {
    try { ret = sessionStorage.getItem("bd_external_return"); } catch {}
  }
  if (ret && isDeviceSignInReturn(ret)) {
    try { sessionStorage.setItem("bd_device_signin_fresh", "1"); } catch {}
  }
}

function triggerDevicePasskeyIfRequested() {
  if (new URLSearchParams(location.search).get("bd_passkey") !== "1") return;
  const q = new URLSearchParams(location.search);
  q.delete("bd_passkey");
  const qs = q.toString();
  history.replaceState(null, "", location.pathname + (qs ? "?" + qs : ""));
  setTimeout(() => document.getElementById("btn-signin-passkey")?.click(), 150);
}

/** Persist ?return= before auth/OAuth so external handoff survives sign-in. */
function stashExternalReturn() {
  const q = new URLSearchParams(location.search);
  const ret = q.get("return");
  if (!ret || !isBdExternalReturn(ret)) return null;
  try { sessionStorage.setItem("bd_external_return", ret); } catch {}
  return ret;
}

async function consumeExternalReturn() {
  const q = new URLSearchParams(location.search);
  const fromQuery = q.get("return");
  let ret = null;
  if (fromQuery && isBdExternalReturn(fromQuery)) ret = fromQuery;
  else {
    try { ret = sessionStorage.getItem("bd_external_return"); } catch {}
  }
  if (!ret || !isBdExternalReturn(ret)) {
    try { sessionStorage.removeItem("bd_external_return"); } catch {}
    return false;
  }
  if (isDeviceSignInReturn(ret)) {
    let fresh = false;
    try { fresh = sessionStorage.getItem("bd_device_signin_fresh") === "1"; } catch {}
    if (!fresh) {
      try { sessionStorage.removeItem("bd_external_return"); } catch {}
      return false;
    }
    try { sessionStorage.removeItem("bd_device_signin_fresh"); } catch {}
  }
  try { sessionStorage.removeItem("bd_external_return"); } catch {}
  if (fromQuery) {
    q.delete("return");
    const qs = q.toString();
    history.replaceState(null, "", location.pathname + (qs ? "?" + qs : ""));
  }
  let url = ret;
  if (typeof BD_AUTH !== "undefined" && api.getSupabase()) {
    try {
      const handoff = await BD_AUTH.createHandoff(api.getSupabase());
      if (handoff) {
        const u = new URL(ret);
        u.searchParams.set("bd_h", handoff);
        url = u.toString();
      }
    } catch {}
  }
  location.replace(url);
  return true;
}

async function startQrSignIn(){
  if (DEMO || QA_MODE || typeof BD_DEVICE_AUTH === "undefined" || !api.getSupabase()) return;
  try {
    await BD_DEVICE_AUTH.init({
      supabase: api.getSupabase(),
      app: "studio"
    });
  } catch (_) {}
}

function showLocalSignIn(){
  const authView = $("#view-auth");
  if (authView) authView.hidden = false;
  show("auth");
  authShow("auth-signin");
  paintAuthMode();
  triggerDevicePasskeyIfRequested();
}

let _studioBlocks = null;
function loadStudioBlocks(){
  if (typeof BLOCKS !== "undefined" && typeof TEMPLATES !== "undefined") return Promise.resolve();
  if (_studioBlocks) return _studioBlocks;
  const pending = import("/assets/js/dist/builder-loader.js?v=20261023n")
    .then(mod => mod.loadBuilder())
    .then(() => {
      if (typeof BLOCKS === "undefined" || typeof TEMPLATES === "undefined") {
        throw new Error("Could not load the builder.");
      }
    })
    .catch(err => {
      if (_studioBlocks === pending) _studioBlocks = null;
      throw err;
    });
  _studioBlocks = pending;
  return pending;
}
let studioBooting = false;
async function boot(){
  if (studioBooting) return;
  studioBooting = true;
  showStudioBoot();
  try {
  if (typeof BD_ACCOUNT !== "undefined" && BD_ACCOUNT.redirectPath()) return;
  if (location.pathname.replace(/\/$/, "") === "/sign-in") {
    const q = new URLSearchParams(location.search);
    const ret = q.get("return");
    const dest = (ret && isBdExternalReturn(ret)) ? ret : location.origin + "/";
    if (usesCredentialSignIn()) {
      history.replaceState(null, "", "/");
      showLocalSignIn();
      return;
    }
    if (typeof BD_AUTH !== "undefined") { BD_AUTH.signInRedirect(dest); return; }
  }
  stashPaystackReturn();
  stashCoworkReturn();
  stashExternalReturn();
  await bdInitApi();
  /* Redeem SSO handoff from central auth (same as My) before cookie hydrate. */
  const handoffCode = new URLSearchParams(location.search).get("bd_h");
  if (handoffCode && typeof BD_AUTH !== "undefined" && api.getSupabase()) {
    try { await BD_AUTH.redeemHandoff(api.getSupabase(), handoffCode); } catch {}
    try {
      const u = new URL(location.href);
      u.searchParams.delete("bd_h");
      const qs = u.searchParams.toString();
      history.replaceState(null, "", u.pathname + (qs ? "?" + qs : "") + u.hash);
    } catch {}
  }
  if (typeof BD_AUTH !== "undefined" && api.getSupabase()) {
    try { await BD_AUTH.hydrate(api.getSupabase()); }
    catch (err) {
      if (err && err.code === "bd_unreachable") {
        showStudioBoot("offline");
        return;
      }
    }
  }
  const authRedirect = await api.handleAuthRedirect();
  const recovery = authRedirect.recovery || api.isRecovery() || await api.isRecoverySession();
  me = await api.session();
  const oauthErr = new URLSearchParams(location.search).get("error_description")
    || new URLSearchParams(location.search).get("error");
  if (oauthErr && !me){
    if (usesCredentialSignIn()) {
      showLocalSignIn();
      authErr("signin-error", humanErr(decodeURIComponent(String(oauthErr).replace(/\+/g, " ")), "Sign-in didn’t complete. Please try again."));
    history.replaceState(null, "", location.pathname);
    return;
    }
    if (typeof BD_AUTH !== "undefined") { BD_AUTH.signInRedirect(location.href); return; }
  }
  if (recovery){
    if (usesCredentialSignIn()) {
    show("auth"); authShow("auth-forgot");
    showRecoveryPasswordForm();
    await prepareRecoveryForm();
    return;
  }
    if (typeof BD_AUTH !== "undefined") { BD_AUTH.signInRedirect(location.href); return; }
  }
  if (!me){
    if (usesCredentialSignIn()) {
      showLocalSignIn();
      if (authRedirect.verified) toast("Email confirmed — sign in to continue.");
      return;
    }
    if (typeof BD_AUTH !== "undefined") { BD_AUTH.signInRedirect(location.href); return; }
  }
  if (typeof BD_DEVICE_AUTH !== "undefined") BD_DEVICE_AUTH.cancel();
  /* Hide the shell immediately when a PIN is configured — before MFA / load work. */
  if (readSessionPin(me.email)) show("pin");
  const profileP = api.warmProfile();
  if (!(await requireTotpElevation())) return;
  if (!(await requireSessionPinUnlock())) return;
  const creditsP = hydrateCredits();
  if (authRedirect.handled) markDeviceSignInFreshIfNeeded();
  if (authRedirect.verified) toast("Email confirmed — welcome to Studio.");
  await profileP;
  const [planValue, suspended, qaOk, adminFlag] = await Promise.all([
    api.plan(),
    api.isSuspended(),
    QA_MODE ? api.hasQaAccess() : Promise.resolve(true),
    api.isAdmin(me.email)
  ]);
  plan = planValue;
  if (QA_MODE && !qaOk){ await settleSessionPinBusy(); showQaGate(); return; }
  if (suspended || !ALLOWED_PLANS.includes(plan)){
    await settleSessionPinBusy();
    showPlanGate(suspended);
    return;
  }
  if (await consumeExternalReturn()) return;
  admin = adminFlag;
  await Promise.all([bdHydrateThemeFromAccount(api), hydrateAvatar(), hydrateBuilderPrefs(), hydrateAccountPrefs(), creditsP]);
  if (creditBalance && bdNormalizePlan(creditBalance.plan) === "free") plan = "free";
  applyMotionPref();
  if (me?.email && typeof BD_PREFS !== "undefined") BD_PREFS.apply(me.email);
  const payReturn = await handlePaystackReturn();
  const coworkSite = await handleCoworkReturn();
  applyTheme();
  const [projs, deps] = await Promise.all([api.listProjects(), api.listDeploys()]);
  if (projs.some(p => p?.data && p.data.v !== 4)) await loadStudioBlocks();
  await attachOwnerNames(projs);
  applyProjectRows(projs);
  deploys = deps;
  activeId = coworkSite || localStorage.getItem("bd_active_" + me.email) || projects[0]?.id || null;
  if (!projects.some(p => p.id === activeId)) activeId = projects[0]?.id || null;
  if (!projects.length && !localStorage.getItem(obKey())){ await settleSessionPinBusy(); startOnboarding(); return; }
  if (payReturn) {
    if (!DEMO && !QA_MODE && typeof BD_ACCOUNT !== "undefined") { await settleSessionPinBusy(); BD_ACCOUNT.go("subscription"); return; }
    sessionStorage.setItem("bd_acct_scroll", "subscription");
  }
  await settleSessionPinBusy();
  enterApp(payReturn ? "account" : undefined);
  } finally {
    studioBooting = false;
  }
}
boot().catch(onStudioBootFail);

/* Back-forward cache restores the unlocked shell without re-running boot — re-lock. */
window.addEventListener("pageshow", async (e) => {
  if (!e.persisted) return;
  if (!me?.email || !readSessionPin(me.email)) return;
  document.body.classList.remove("pin-just-unlocked");
  if (!(await requireSessionPinUnlock())) return;
  show("app");
});
